import { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { CapacitorHealthkit, SampleNames } from "@perfood/capacitor-healthkit";
import { registerPlugin } from '@capacitor/core';
import { drawGhidhaSummaryCard } from './ShareCard';
import { writeCalorieData, writeVitalsData, writeFrequentMeals } from '../siratIntents';
import { hapticTap, hapticConfirm, hapticHayahSuccess, hapticHayahSelection, hapticHayahSwipe, hapticHayahToggle, hapticWarning, hapticSelectionChange } from '../hayahHaptics';
import GhidhaImmersive from './GhidhaImmersive';
import GhidhaLogView from './GhidhaLogView';
import ProteinFocusOverlay from './ProteinFocusOverlay';
import CappuccinoFocusOverlay, { quickLogLastCappuccino } from './CappuccinoFocusOverlay';
import { MedicationsCard, MedicationsDueRow } from './Medications';
import AICalorieTarget, { AICalorieTargetBanner, maybeAutoRecompute, loadAITarget } from './AICalorieTarget';
import MealPlanView from './MealPlan';
import { getGeminiKey, callGemini, callGeminiVision } from './geminiAI';
import {
  COACH_VOICE,
  EVIDENCE_STANDARD,
  OUTPUT_JSON_STRICT,
  OUTPUT_BULLETS_BOLD,
  composeSystem,
  halalFilterInstruction,
  regionFoodHint,
} from './hayah/hayahPrompts';
import AIBulletText from './hayah/AIBulletText';
import ReportInsightCard from './hayah/ReportInsightCard';
import * as aiJob from './hayah/aiBackgroundJob';
import { getUserProfile, formatProfileForPrompt } from './hayah/userProfile';
import { readAllData, buildCoachReportContext, buildPatternInsightsContext, buildPhotoContext } from './hayah/aiContext';
import { MODELS } from './hayah/aiConfig';
import AICostBadge from './hayah/AICostBadge';
import AIAnalysingPipeline from './hayah/AIAnalysingPipeline';
import { publishSources, photoSources, mealParseSources, bodyCompositionSources } from './hayah/pipelineSources';
import SupplementReminderBanner from './hayah/SupplementReminderBanner';
import StreakInfoSheet from './hayah/StreakInfoSheet';
import { initSupplementReminders, getMissingToday, isPastReminderTime, isDismissedToday, dismissToday, getReminderTime } from './hayah/supplementReminders';
import WeeklyChecksBanner from './hayah/WeeklyChecksBanner';
import {
  initWeeklyChecksReminders,
  getDueWeeklyChecks,
  isPastReminderTime as isPastWeeklyChecksTime,
  isDismissedToday  as isWeeklyChecksDismissedToday,
  dismissToday      as dismissWeeklyChecksToday,
} from './hayah/weeklyChecks';
import { evaluateAndScheduleFastToast } from './hayahFastToast';
import { computeFastingWindow, computeCurrentMealWindow, FASTING_PROTOCOLS, FASTING_STAGES, stageForHours } from './hayahPrefs';
import {
  DEFAULT_JUNK_THRESHOLD,
  computeDayJunk,
  computeJunkStreak,
  computeTodayEarnedCheat,
  computeRangeJunkStats,
  isCheatClaimedToday,
  claimCheatToday,
} from './hayah/junkStats';
import { isDictationAvailable, isPermissionError, openIOSSettings } from '../dictate';
import { useDictation } from '../useDictation';

const GhidhaHealth = registerPlugin('GhidhaHealth');
// Camera plugin — registered via core so @capacitor/camera npm pkg is not required at build time.
// CameraResultType.DataUrl = 'dataUrl', CameraSource.Camera = 'CAMERA'
const CapacitorCamera = registerPlugin('Camera');

const localDateStr = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
};
const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

// ─── AI Photo Log Redesign — design tokens & helper components ────────
const AI_T = {
  bg0:'#090909', bg1:'#0E0E10', bg2:'#141417', bg3:'#1A1A1E', bg4:'#212126',
  line:'#27272F', lineB:'#32323C',
  // Text tiers brightened for low-brightness bedtime readability —
  // ink3 / ink4 bumped so cards in the Calories surface stay legible
  // at minimum screen brightness in dark mode.
  ink1:'#F4EFE0', ink2:'#C6BFAe', ink3:'#A09C8E', ink4:'#6B665D',
  amber:'#E0A030', amberS:'#F0C26B', amberD:'rgba(224,160,48,0.13)',
  sage:'#7CC4A0', sageD:'rgba(124,196,160,0.13)', coral:'#B84035',
  mono:"'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
};

const AI_KEYFRAMES = `
@keyframes aiShimmer{0%{background-position:-300% center}100%{background-position:300% center}}
@keyframes aiFadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes aiFadeIn{from{opacity:0}to{opacity:1}}
@keyframes aiPulse{0%,100%{opacity:0.25}50%{opacity:1}}
@keyframes aiScaleIn{from{opacity:0;transform:scale(0.85)}60%{transform:scale(1.04)}to{opacity:1;transform:scale(1)}}
@keyframes aiLogFlash{0%{opacity:0;transform:scale(0.8)}60%{transform:scale(1.06)}100%{opacity:1;transform:scale(1)}}
@keyframes aiAmberPulse{0%,100%{box-shadow:0 4px 24px rgba(224,160,48,0.22)}50%{box-shadow:0 4px 32px rgba(224,160,48,0.42)}}
`;

function AiShim({ w='100%', h=12, r=6 }) {
  return <div style={{width:w,height:h,borderRadius:r,flexShrink:0,background:'linear-gradient(90deg,#1A1A1E 25%,#252530 50%,#1A1A1E 75%)',backgroundSize:'300% 100%',animation:'aiShimmer 1.7s ease infinite'}}/>;
}

function AiMacro({ label, value }) {
  const T = AI_T;
  return (
    <div style={{display:'flex',alignItems:'baseline',gap:2}}>
      <span style={{fontFamily:T.mono,fontSize:10,color:T.ink4,fontWeight:600,letterSpacing:'0.04em'}}>{label}</span>
      <span style={{fontFamily:T.mono,fontSize:11,color:T.ink3,fontVariantNumeric:'tabular-nums'}}>{value}g</span>
    </div>
  );
}

function AiAnalysingCard({ thumbDataUrl }) {
  const T = AI_T;
  return (
    <div style={{background:T.bg3,border:`1px solid ${T.line}`,borderRadius:17,overflow:'hidden',animation:'aiFadeUp 0.32s cubic-bezier(0.2,0.8,0.3,1)',boxShadow:'0 2px 14px rgba(0,0,0,0.22)'}}>
      <div style={{display:'flex',gap:13,padding:'14px 14px 12px',alignItems:'flex-start'}}>
        {thumbDataUrl ? (
          <img src={thumbDataUrl} alt="" style={{width:80,height:80,borderRadius:12,objectFit:'cover',flexShrink:0,filter:'brightness(0.7)'}}/>
        ) : (
          <div style={{width:80,height:80,borderRadius:12,flexShrink:0,background:'linear-gradient(90deg,#1A1A1E 25%,#252530 50%,#1A1A1E 75%)',backgroundSize:'300% 100%',animation:'aiShimmer 1.7s ease infinite'}}/>
        )}
        <div style={{flex:1,display:'flex',flexDirection:'column',gap:9,paddingTop:4}}>
          <AiShim w="62%" h={13}/>
          <AiShim w="38%" h={10}/>
          <AiShim w="44%" h={22}/>
          <div style={{display:'flex',alignItems:'center',gap:7,marginTop:2}}>
            <div style={{display:'flex',gap:4}}>
              {[0,1,2].map(i=>(<div key={i} style={{width:5,height:5,borderRadius:'50%',background:T.amber,animation:`aiPulse 1.4s ease-in-out ${i*0.2}s infinite`}}/>))}
            </div>
            <span style={{fontFamily:'inherit',fontSize:11,color:T.ink4,letterSpacing:'-0.01em'}}>Identifying food…</span>
          </div>
        </div>
      </div>
      <div style={{padding:'9px 14px',borderTop:`1px solid ${T.line}`,background:T.bg2}}>
        <AiShim w="42%" h={10}/>
      </div>
    </div>
  );
}

function AiErrorCard({ photo, onRetry, onRemove }) {
  const T = AI_T;
  return (
    <div style={{background:`rgba(184,64,53,0.08)`,border:`1px solid rgba(184,64,53,0.35)`,borderRadius:17,padding:'14px',display:'flex',alignItems:'center',gap:12,animation:'aiFadeUp 0.3s ease'}}>
      <img src={photo.dataUrl} alt="" style={{width:48,height:48,borderRadius:10,objectFit:'cover',flexShrink:0}}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontFamily:'inherit',fontSize:12,fontWeight:700,color:T.coral}}>Couldn't analyse</div>
        <div style={{fontFamily:'inherit',fontSize:10.5,color:T.ink3,marginTop:2}}>{photo.error || 'Tap retry to try again'}</div>
      </div>
      <button onClick={onRetry} style={{height:30,padding:'0 11px',background:T.amberD,border:`1px solid rgba(224,160,48,0.3)`,borderRadius:9,color:T.amber,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Retry</button>
      <button onClick={onRemove} aria-label="Remove" style={{width:24,height:24,borderRadius:'50%',background:T.bg4,border:`1px solid ${T.line}`,color:T.ink4,fontSize:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,padding:0,cursor:'pointer',fontFamily:'inherit'}}>✕</button>
    </div>
  );
}

function AiFoodCard({ item, logged, onRemove, onLog, onUpdate, onRedescribe }) {
  const T = AI_T;
  const [editKcal, setEditK] = useState(false);
  const [editName, setEditN] = useState(false);
  const [tmpKcal, setTmpK] = useState(item.calories);
  const [tmpName, setTmpN] = useState(item.name);
  const [describing, setDescribing] = useState(false);
  const [descText, setDescText] = useState('');
  const kcalRef = useRef(null);
  const nameRef = useRef(null);
  const descRef = useRef(null);
  useEffect(() => { if (editKcal && kcalRef.current) { kcalRef.current.focus(); kcalRef.current.select(); } }, [editKcal]);
  useEffect(() => { if (editName && nameRef.current) nameRef.current.focus(); }, [editName]);
  useEffect(() => { if (describing && descRef.current) descRef.current.focus(); }, [describing]);
  useEffect(() => { if (!editKcal) setTmpK(item.calories); }, [item.calories, editKcal]);
  useEffect(() => { if (!editName) setTmpN(item.name); }, [item.name, editName]);
  const commitKcal = () => { const n = parseFloat(tmpKcal); if (Number.isFinite(n) && n >= 0) onUpdate({ calories: n }); setEditK(false); };
  const commitName = () => { const v = (tmpName || '').trim(); if (v) onUpdate({ name: v }); setEditN(false); };

  if (logged) {
    return (
      <div style={{background:T.sageD,border:`1px solid rgba(124,196,160,0.2)`,borderRadius:17,height:56,display:'flex',alignItems:'center',justifyContent:'center',gap:9,animation:'aiLogFlash 0.4s cubic-bezier(0.2,0.8,0.3,1)'}}>
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
          <path d="M1 7L7 13L17 1" stroke={T.sage} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{fontFamily:'inherit',fontSize:13,color:T.sage,fontWeight:600}}>Logged</span>
      </div>
    );
  }

  return (
    <div style={{background:T.bg3,border:`1px solid ${T.line}`,borderRadius:17,overflow:'hidden',boxShadow:'0 2px 14px rgba(0,0,0,0.2)',animation:item.fresh?'aiFadeUp 0.38s cubic-bezier(0.2,0.8,0.3,1)':'none'}}>
      <div style={{display:'flex',gap:13,padding:'14px 14px 12px',alignItems:'flex-start'}}>
        {item.photoDataUrl ? (
          <img src={item.photoDataUrl} alt="" style={{width:82,height:82,borderRadius:12,objectFit:'cover',flexShrink:0,boxShadow:'inset 0 -3px 10px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.06)'}}/>
        ) : (
          <div style={{width:82,height:82,borderRadius:12,background:T.bg4,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:32}}>🍽️</div>
        )}
        <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:3}}>
          {editName ? (
            <input ref={nameRef} value={tmpName} onChange={e=>setTmpN(e.target.value)} onBlur={commitName} onKeyDown={e=>e.key==='Enter'&&commitName()}
              style={{fontFamily:'inherit',fontSize:14,fontWeight:600,color:T.ink1,background:'transparent',border:'none',borderBottom:`1px solid ${T.lineB}`,outline:'none',width:'100%',padding:'2px 0',lineHeight:1.3}}/>
          ) : (
            <div onClick={()=>setEditN(true)} title="Tap to edit" style={{fontFamily:'inherit',fontSize:14,fontWeight:600,color:T.ink1,lineHeight:1.3,cursor:'text'}}>{item.name}</div>
          )}
          <div style={{fontFamily:'inherit',fontSize:11,color:T.ink4,marginBottom:4}}>{item.servingLabel || `${item.servingG||100}g`}</div>
          <div style={{display:'flex',alignItems:'baseline',gap:5}}>
            {editKcal ? (
              <input ref={kcalRef} type="number" inputMode="decimal" enterKeyHint="done" value={tmpKcal} onChange={e=>setTmpK(e.target.value)} onBlur={commitKcal} onKeyDown={e=>e.key==='Enter'&&commitKcal()}
                style={{fontFamily:T.mono,fontSize:26,fontWeight:600,color:T.amber,background:'transparent',border:'none',borderBottom:`1.5px solid ${T.amber}`,outline:'none',width:90,padding:'2px 0',lineHeight:1}}/>
            ) : (
              <button onClick={()=>setEditK(true)} title="Tap to edit calories" style={{fontFamily:T.mono,fontSize:26,fontWeight:600,color:T.amber,background:'none',padding:0,lineHeight:1,border:'none',cursor:'text',textShadow:'0 0 24px rgba(224,160,48,0.28)'}}>{Math.round(item.calories)}</button>
            )}
            <span style={{fontFamily:'inherit',fontSize:10,color:T.ink4,fontWeight:600,letterSpacing:'0.1em',textTransform:'uppercase'}}>kcal</span>
          </div>
        </div>
        <button onClick={onRemove} aria-label="Remove" style={{width:24,height:24,borderRadius:'50%',background:T.bg4,border:`1px solid ${T.line}`,color:T.ink4,fontSize:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,lineHeight:1,padding:0,cursor:'pointer',fontFamily:'inherit'}}>✕</button>
      </div>
      {describing && (
        <div style={{padding:'10px 14px',borderTop:`1px solid ${T.line}`,background:T.bg2,display:'flex',gap:8,alignItems:'center'}}>
          <input
            ref={descRef}
            value={descText}
            onChange={e => setDescText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && descText.trim()) { onRedescribe(item.id, descText.trim(), item.photoDataUrl); setDescribing(false); setDescText(''); } if (e.key === 'Escape') { setDescribing(false); setDescText(''); } }}
            placeholder="e.g. lamb curry with basmati rice"
            style={{flex:1,fontFamily:'inherit',fontSize:12,color:T.ink1,background:T.bg4,border:`1px solid ${T.lineB}`,borderRadius:8,padding:'7px 10px',outline:'none',minWidth:0}}
          />
          <button onClick={() => { if (descText.trim()) { onRedescribe(item.id, descText.trim(), item.photoDataUrl); setDescribing(false); setDescText(''); } }}
            style={{height:32,padding:'0 12px',background:T.amberD,border:`1px solid rgba(224,160,48,0.28)`,borderRadius:8,color:T.amber,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>
            Re-analyse
          </button>
        </div>
      )}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 14px',borderTop:`1px solid ${T.line}`,background:T.bg2}}>
        <div style={{display:'flex',gap:12,alignItems:'center'}}>
          <AiMacro label="P" value={Math.round(item.protein||0)}/>
          <div style={{width:1,height:10,background:T.line}}/>
          <AiMacro label="C" value={Math.round(item.carbs||0)}/>
          <div style={{width:1,height:10,background:T.line}}/>
          <AiMacro label="F" value={Math.round(item.fat||0)}/>
        </div>
        <div style={{display:'flex',gap:7,alignItems:'center'}}>
          <button onClick={() => setDescribing(d => !d)} title="Describe to re-analyse"
            style={{height:32,padding:'0 10px',background:describing?T.amberD:'transparent',border:`1px solid ${describing?'rgba(224,160,48,0.28)':T.line}`,borderRadius:9,color:describing?T.amber:T.ink4,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>
            Wrong?
          </button>
          <button onClick={onLog} style={{height:32,padding:'0 14px',background:T.amberD,border:`1px solid rgba(224,160,48,0.28)`,borderRadius:9,color:T.amber,fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:5,letterSpacing:'-0.01em',cursor:'pointer',fontFamily:'inherit'}}>
            Log
            <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
              <path d="M1 4.5h8M6.5 1l3 3.5-3 3.5" stroke={T.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
// ───────────────────────────────────────────────────────────────────────

const DEFAULT_GOALS = { calories: 2000, protein: 150, carbs: 225, fat: 65, fiber: 30, water: 8, steps: 12000, burn: 3000, sleep: 8 };

const MEAL_SLOTS = [
  { key: "meal",        label: "Meal",         labelFasting: "Meal",         icon: "🍽",  glyph: "🍽️", color: "#ffb300", typicalTime: "—"     },
  { key: "drinks",      label: "Drinks",       labelFasting: "Drinks",       icon: "☕", glyph: "☕", color: "#7CC5F3", typicalTime: "—"     },
  { key: "supplements", label: "Supplements",  labelFasting: "Supplements",  icon: "💊", glyph: "💊", color: "#b88df8", typicalTime: "—"     },
  { key: "whey",        label: "Whey Protein", labelFasting: "Whey Protein", icon: "💪", glyph: "🥤", color: "#4FD9C2", typicalTime: "—"     },
  { key: "snacks",      label: "Junk Food",    labelFasting: "Junk Food",    icon: "✦",  glyph: "🍩", color: "#82c83c", typicalTime: "—"     },
];

const SUNNAH_FOODS = [
  { id:"s1", name:"Ajwa Date",        nameAr:"تمر عجوة",   calories:23,  protein:0.2, carbs:6,   fat:0,    fiber:0.6, sugar:5,   sodium:0,   servingG:7,   servingLabel:"1 date",     halal:true },
  { id:"s2", name:"Medjool Date",     nameAr:"تمر مجهول",  calories:66,  protein:0.4, carbs:18,  fat:0.1,  fiber:1.6, sugar:16,  sodium:0,   servingG:24,  servingLabel:"1 date",     halal:true },
  { id:"s3", name:"Honey (raw)",      nameAr:"عسل",         calories:64,  protein:0.1, carbs:17,  fat:0,    fiber:0,   sugar:17,  sodium:1,   servingG:21,  servingLabel:"1 tbsp",     halal:true },
  { id:"s4", name:"Olive Oil",        nameAr:"زيت زيتون",   calories:119, protein:0,   carbs:0,   fat:13.5, fiber:0,   sugar:0,   sodium:0,   servingG:14,  servingLabel:"1 tbsp",     halal:true },
  { id:"s5", name:"Black Seed",       nameAr:"حبة البركة",  calories:9,   protein:0.4, carbs:1,   fat:0.7,  fiber:0.4, sugar:0,   sodium:1,   servingG:2,   servingLabel:"1/2 tsp",    halal:true },
  { id:"s6", name:"Barley Bread",     nameAr:"خبز الشعير", calories:75,  protein:2.5, carbs:15,  fat:0.7,  fiber:1.5, sugar:0.5, sodium:140, servingG:30,  servingLabel:"1 slice",    halal:true },
  { id:"s7", name:"Fig (fresh)",      nameAr:"تين",         calories:37,  protein:0.4, carbs:9.6, fat:0.1,  fiber:1.5, sugar:8,   sodium:1,   servingG:50,  servingLabel:"1 medium",   halal:true },
  { id:"s8", name:"Pomegranate",      nameAr:"رمان",        calories:83,  protein:1.7, carbs:19,  fat:1.2,  fiber:4,   sugar:14,  sodium:3,   servingG:174, servingLabel:"1 cup arils",halal:true },
  { id:"s9", name:"Zamzam Water",     nameAr:"ماء زمزم",   calories:0,   protein:0,   carbs:0,   fat:0,    fiber:0,   sugar:0,   sodium:11,  servingG:250, servingLabel:"1 cup",      halal:true },
  { id:"s10",name:"Milk (whole)",     nameAr:"حليب",        calories:61,  protein:3.2, carbs:4.8, fat:3.3,  fiber:0,   sugar:5,   sodium:43,  servingG:100, servingLabel:"100ml",      halal:true },
];

const SA_FOODS = [
  // Staples
  { id:"saf1",  category:"Staples", name:"Pap (stiff, cooked)",         calories:100, protein:1.5, carbs:21,  fat:0.5, fiber:0.5, sugar:0,  sodium:5,   servingG:200, servingLabel:"1 cup (200g)",    halal:true },
  { id:"saf2",  category:"Staples", name:"Pap (soft/porridge, cooked)", calories:75,  protein:1.2, carbs:16,  fat:0.3, fiber:0.3, sugar:0,  sodium:3,   servingG:200, servingLabel:"1 cup (200g)",    halal:true },
  { id:"saf3",  category:"Staples", name:"Tastic White Rice (cooked)",  calories:130, protein:2.5, carbs:28,  fat:0.3, fiber:0.3, sugar:0,  sodium:1,   servingG:180, servingLabel:"1 cup (180g)",    halal:true },
  { id:"saf4",  category:"Staples", name:"Jungle Oats (cooked)",        calories:66,  protein:2.8, carbs:11,  fat:1.4, fiber:1.5, sugar:0,  sodium:2,   servingG:250, servingLabel:"1 bowl (250g)",   halal:true },
  { id:"saf5",  category:"Staples", name:"Pro-Nutro (Original, dry)",   calories:380, protein:18,  carbs:60,  fat:7,   fiber:5,   sugar:15, sodium:130, servingG:50,  servingLabel:"1 serving (50g)", halal:true },
  { id:"saf6",  category:"Staples", name:"Future Life (dry)",           calories:390, protein:20,  carbs:58,  fat:8,   fiber:6,   sugar:12, sodium:150, servingG:50,  servingLabel:"1 serving (50g)", halal:true },
  { id:"saf7",  category:"Staples", name:"Albany White Bread",          calories:90,  protein:3,   carbs:18,  fat:1,   fiber:0.8, sugar:2,  sodium:160, servingG:36,  servingLabel:"1 slice (36g)",   halal:true },
  { id:"saf8",  category:"Staples", name:"Albany Brown Bread",          calories:85,  protein:3.5, carbs:17,  fat:1,   fiber:1.5, sugar:1.5,sodium:155, servingG:36,  servingLabel:"1 slice (36g)",   halal:true },
  { id:"saf_ab1",category:"Staples", name:"Almond Breeze Unsweetened",   calories:30,  protein:1,   carbs:1,   fat:2.5, fiber:0.5, sugar:0,  sodium:150, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf_ab2",category:"Staples", name:"Almond Breeze Original",      calories:60,  protein:1,   carbs:8,   fat:2.5, fiber:0.5, sugar:7,  sodium:150, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf_ab3",category:"Staples", name:"Almond Breeze Vanilla",       calories:80,  protein:1,   carbs:11,  fat:2.5, fiber:0.5, sugar:10, sodium:150, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf_ab4",category:"Staples", name:"Almond Breeze Chocolate",     calories:100, protein:1.5, carbs:16,  fat:2.5, fiber:1,   sugar:14, sodium:160, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf_ab5",category:"Staples", name:"Almond Breeze Barista",       calories:40,  protein:1,   carbs:3,   fat:2.5, fiber:0.5, sugar:0,  sodium:120, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9",  category:"Staples", name:"Clover Full Cream Milk",       calories:165, protein:8.5, carbs:12,  fat:9,   fiber:0,   sugar:12, sodium:105, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9b", category:"Staples", name:"Clover Low Fat Milk (2%)",     calories:120, protein:9,   carbs:12,  fat:4.5, fiber:0,   sugar:12, sodium:110, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9_ff", category:"Staples", name:"Clover Fat Free Milk",       calories:85,  protein:9,   carbs:12,  fat:0.3, fiber:0,   sugar:12, sodium:115, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9c", category:"Staples", name:"Clover Steri Stumpie (choc)",  calories:195, protein:7,   carbs:31,  fat:5,   fiber:0,   sugar:30, sodium:120, servingG:350, servingLabel:"1 bottle (350ml)",halal:true },
  { id:"saf9d", category:"Staples", name:"Woolworths Full Cream Milk",   calories:165, protein:8.5, carbs:12,  fat:9,   fiber:0,   sugar:12, sodium:105, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9e", category:"Staples", name:"Woolworths Low Fat Milk",      calories:118, protein:9,   carbs:12,  fat:4,   fiber:0,   sugar:12, sodium:110, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf9f", category:"Staples", name:"Woolworths Lactose-free Low Fat Milk", calories:120, protein:9,   carbs:12,  fat:4,   fiber:0,   sugar:12, sodium:110, servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf4b", category:"Staples", name:"Jungle Oats Instant (1 sachet)",calories:154,protein:5,   carbs:26,  fat:3.5, fiber:2.5, sugar:6,  sodium:115, servingG:42,  servingLabel:"1 sachet (42g)",  halal:true },
  { id:"saf11", category:"Staples", name:"Eggs (large, fried)",          calories:90,  protein:6.3, carbs:0.4, fat:7,   fiber:0,   sugar:0,  sodium:95,  servingG:46,  servingLabel:"1 egg",           halal:true },
  { id:"saf12", category:"Staples", name:"Eggs (large, boiled)",         calories:78,  protein:6.3, carbs:0.6, fat:5.3, fiber:0,   sugar:0,  sodium:62,  servingG:50,  servingLabel:"1 egg",           halal:true },
  { id:"saf12b",category:"Staples", name:"Eggs (large, scrambled w/milk)",calories:102,protein:7,   carbs:1.5, fat:7.5, fiber:0,   sugar:0.5,sodium:140, servingG:60,  servingLabel:"1 egg",           halal:true },
  // SA Home Cooking
  { id:"saf13", category:"Home Cooking", name:"Lamb Curry & Rice",           calories:580, protein:30,  carbs:70,  fat:18,  fiber:3,   sugar:4,  sodium:680, servingG:400, servingLabel:"1 plate (400g)",  halal:true },
  { id:"saf14", category:"Home Cooking", name:"Chicken Biryani",             calories:520, protein:35,  carbs:58,  fat:16,  fiber:2,   sugar:3,  sodium:720, servingG:350, servingLabel:"1 plate (350g)",  halal:true },
  { id:"saf15", category:"Home Cooking", name:"Mutton Biryani",              calories:560, protein:38,  carbs:56,  fat:19,  fiber:2,   sugar:3,  sodium:740, servingG:350, servingLabel:"1 plate (350g)",  halal:true },
  { id:"saf16", category:"Home Cooking", name:"Dhal (cooked)",               calories:220, protein:14,  carbs:36,  fat:2,   fiber:12,  sugar:2,  sodium:400, servingG:200, servingLabel:"1 cup (200g)",    halal:true },
  { id:"saf17", category:"Home Cooking", name:"Samoosa (fried, lamb)",       calories:130, protein:5,   carbs:14,  fat:6,   fiber:1,   sugar:0,  sodium:180, servingG:45,  servingLabel:"1 samoosa",       halal:true },
  { id:"saf18", category:"Home Cooking", name:"Samoosa (fried, vegetable)",  calories:105, protein:2.5, carbs:14,  fat:5,   fiber:1.5, sugar:1,  sodium:140, servingG:40,  servingLabel:"1 samoosa",       halal:true },
  { id:"saf19", category:"Home Cooking", name:"Roti (SA style)",             calories:150, protein:4,   carbs:25,  fat:4,   fiber:1.5, sugar:0,  sodium:180, servingG:50,  servingLabel:"1 roti",          halal:true },
  { id:"saf20", category:"Home Cooking", name:"Halal Sosaties (lamb skewer)",calories:220, protein:22,  carbs:8,   fat:12,  fiber:0.5, sugar:6,  sodium:380, servingG:120, servingLabel:"1 skewer",        halal:true },
  { id:"saf21", category:"Home Cooking", name:"Halal Braai Chicken (thigh)", calories:260, protein:28,  carbs:0,   fat:16,  fiber:0,   sugar:0,  sodium:310, servingG:150, servingLabel:"1 piece",         halal:true },
  { id:"saf22", category:"Home Cooking", name:"Cape Malay Chicken Curry",    calories:420, protein:35,  carbs:18,  fat:22,  fiber:2,   sugar:5,  sodium:680, servingG:300, servingLabel:"1 serving (300g)", halal:true },
  { id:"saf23",  category:"Home Cooking", name:"Pea Achar (1 tbsp)",              calories:20,  protein:0.5, carbs:3,   fat:0.5, fiber:0.8, sugar:1,  sodium:140, servingG:15,  servingLabel:"1 tbsp",          halal:true },
  { id:"saf_tc1",category:"Home Cooking", name:"Tandoori Chicken (boneless)",     calories:220, protein:32,  carbs:5,   fat:8,   fiber:0.5, sugar:2,  sodium:580, servingG:150, servingLabel:"1 serving (150g)", halal:true },
  { id:"saf_tc2",category:"Home Cooking", name:"Tandoori Chicken (on bone)",      calories:280, protein:38,  carbs:6,   fat:12,  fiber:0.5, sugar:2,  sodium:640, servingG:200, servingLabel:"1/4 chicken",      halal:true },
  { id:"saf_kc1",category:"Home Cooking", name:"Kebab Chutney (green coriander)", calories:20,  protein:0.5, carbs:3,   fat:0.5, fiber:0.5, sugar:1,  sodium:180, servingG:30,  servingLabel:"2 tbsp (30g)",     halal:true },
  { id:"saf_kc2",category:"Home Cooking", name:"Kebab Chutney (tamarind/sweet)",  calories:45,  protein:0.3, carbs:11,  fat:0,   fiber:0.3, sugar:10, sodium:140, servingG:30,  servingLabel:"2 tbsp (30g)",     halal:true },
  { id:"saf_rb1",category:"Home Cooking", name:"Roti (butter)",                   calories:185, protein:4,   carbs:25,  fat:7,   fiber:1.5, sugar:0,  sodium:220, servingG:55,  servingLabel:"1 roti",           halal:true },
  { id:"saf_rb2",category:"Home Cooking", name:"Roti (tandoori, restaurant)",     calories:145, protein:5,   carbs:28,  fat:2,   fiber:1.5, sugar:0,  sodium:160, servingG:60,  servingLabel:"1 roti",           halal:true },
  { id:"saf_bn1",category:"Home Cooking", name:"Beef Noodles with Soy Sauce",     calories:520, protein:32,  carbs:60,  fat:16,  fiber:3,   sugar:6,  sodium:1280,servingG:400, servingLabel:"1 plate (400g)",   halal:true },
  { id:"saf_cn1",category:"Home Cooking", name:"Chicken Noodles with Soy Sauce",  calories:470, protein:32,  carbs:60,  fat:11,  fiber:3,   sugar:6,  sodium:1200,servingG:400, servingLabel:"1 plate (400g)",   halal:true },
  // SA Street Food
  { id:"saf24", category:"Street Food", name:"Vetkoek (plain, fried)",       calories:250, protein:5,   carbs:35,  fat:10,  fiber:1,   sugar:1,  sodium:220, servingG:80,  servingLabel:"1 vetkoek",       halal:true },
  { id:"saf25", category:"Street Food", name:"Vetkoek with Mince Filling",   calories:380, protein:18,  carbs:36,  fat:18,  fiber:1.5, sugar:2,  sodium:480, servingG:130, servingLabel:"1 vetkoek",       halal:true },
  { id:"saf26", category:"Street Food", name:"Bunny Chow (1/4 loaf, lamb)", calories:620, protein:32,  carbs:82,  fat:18,  fiber:5,   sugar:6,  sodium:920, servingG:380, servingLabel:"1/4 bunny",       halal:true },
  { id:"saf27", category:"Street Food", name:"Bunny Chow (1/4 loaf, bean)", calories:520, protein:20,  carbs:90,  fat:8,   fiber:10,  sugar:5,  sodium:760, servingG:380, servingLabel:"1/4 bunny",       halal:true },
  // Snacks & Bakery
  { id:"saf28", category:"Snacks", name:"Simba Chips (Original)",            calories:530, protein:7,   carbs:55,  fat:32,  fiber:3,   sugar:1,  sodium:520, servingG:100, servingLabel:"100g",            halal:true },
  { id:"saf29", category:"Snacks", name:"Flings (Cheese Puffs)",             calories:540, protein:7,   carbs:58,  fat:32,  fiber:1,   sugar:2,  sodium:680, servingG:100, servingLabel:"100g",            halal:true },
  { id:"saf30", category:"Snacks", name:"Koeksister (plaited)",              calories:180, protein:2,   carbs:32,  fat:5,   fiber:0.5, sugar:22, sodium:90,  servingG:60,  servingLabel:"1 koeksister",    halal:true },
  { id:"saf31", category:"Snacks", name:"Milk Tart (melktert) slice",        calories:230, protein:5,   carbs:35,  fat:8,   fiber:0.5, sugar:20, sodium:160, servingG:100, servingLabel:"1 slice",         halal:true },
  { id:"saf32", category:"Snacks", name:"Rusks (beskuit)",                   calories:370, protein:8,   carbs:65,  fat:8,   fiber:2,   sugar:22, sodium:280, servingG:80,  servingLabel:"1 rusk",          halal:true },
  { id:"saf33",     category:"Snacks", name:"Tennis Biscuits",                      calories:480, protein:6,   carbs:68,  fat:21,  fiber:2,   sugar:28, sodium:310, servingG:100, servingLabel:"100g (~6 biscuits)", halal:true },
  // Dates
  { id:"saf_dat1",  category:"Snacks", name:"Zamli Dates (per date)",               calories:22,  protein:0.2, carbs:5.8, fat:0,   fiber:0.6, sugar:5,  sodium:0,   servingG:8,   servingLabel:"1 date",             halal:true },
  { id:"saf_dat2",  category:"Snacks", name:"Zamli Dates (100g)",                   calories:277, protein:2.5, carbs:73,  fat:0.4, fiber:7,   sugar:63, sodium:2,   servingG:100, servingLabel:"100g",               halal:true },
  // Doughnuts & Bakery
  { id:"saf_don1",  category:"Snacks", name:"Checkers Fresh Cream Doughnut",        calories:370, protein:5,   carbs:42,  fat:20,  fiber:1,   sugar:18, sodium:220, servingG:110, servingLabel:"1 doughnut",         halal:true },
  // Cadbury Chocolates
  { id:"saf_choc1", category:"Snacks", name:"Cadbury Dairy Milk (80g slab)",        calories:430, protein:7,   carbs:52,  fat:23,  fiber:0,   sugar:48, sodium:80,  servingG:80,  servingLabel:"1 slab (80g)",       halal:true },
  { id:"saf_choc2", category:"Snacks", name:"Cadbury Dairy Milk (4 squares)",       calories:108, protein:1.8, carbs:13,  fat:5.8, fiber:0,   sugar:12, sodium:20,  servingG:20,  servingLabel:"4 squares (20g)",    halal:true },
  { id:"saf_choc3", category:"Snacks", name:"Cadbury Top Deck (80g slab)",          calories:435, protein:6,   carbs:53,  fat:23,  fiber:0,   sugar:49, sodium:75,  servingG:80,  servingLabel:"1 slab (80g)",       halal:true },
  { id:"saf_choc4", category:"Snacks", name:"Cadbury Lunch Bar",                    calories:245, protein:4,   carbs:31,  fat:12,  fiber:0.5, sugar:26, sodium:75,  servingG:49,  servingLabel:"1 bar (49g)",        halal:true },
  { id:"saf_choc5", category:"Snacks", name:"Cadbury Flake",                        calories:174, protein:2.5, carbs:20,  fat:9.6, fiber:0,   sugar:19, sodium:35,  servingG:32,  servingLabel:"1 bar (32g)",        halal:true },
  { id:"saf_choc6", category:"Snacks", name:"Cadbury Crunchie",                     calories:190, protein:2,   carbs:28,  fat:7.5, fiber:0.5, sugar:21, sodium:60,  servingG:40,  servingLabel:"1 bar (40g)",        halal:true },
  { id:"saf_choc7", category:"Snacks", name:"Cadbury 5 Star",                       calories:240, protein:3,   carbs:35,  fat:10,  fiber:0.5, sugar:29, sodium:65,  servingG:50,  servingLabel:"1 bar (50g)",        halal:true },
  { id:"saf_choc8", category:"Snacks", name:"Bar One",                              calories:255, protein:3,   carbs:38,  fat:9.5, fiber:0.5, sugar:32, sodium:70,  servingG:55,  servingLabel:"1 bar (55g)",        halal:true },
  { id:"saf_choc9", category:"Snacks", name:"Tex Bar",                              calories:240, protein:4,   carbs:29,  fat:12,  fiber:1,   sugar:22, sodium:60,  servingG:48,  servingLabel:"1 bar (48g)",        halal:true },
  { id:"saf_choc10",category:"Snacks", name:"Kit Kat (2 finger)",                   calories:107, protein:1.5, carbs:13,  fat:5.6, fiber:0.3, sugar:11, sodium:30,  servingG:21,  servingLabel:"2 fingers (21g)",    halal:true },
  { id:"saf_choc11",category:"Snacks", name:"Kit Kat (4 finger)",                   calories:212, protein:2.9, carbs:26,  fat:11,  fiber:0.5, sugar:22, sodium:58,  servingG:41,  servingLabel:"4 fingers (41.5g)",  halal:true },
  // Maynard's Sweets
  { id:"saf_sw1",   category:"Snacks", name:"Maynard's Wine Gums (30g)",            calories:96,  protein:1.2, carbs:22,  fat:0,   fiber:0,   sugar:14, sodium:15,  servingG:30,  servingLabel:"30g (~6 pieces)",    halal:true },
  { id:"saf_sw2",   category:"Snacks", name:"Maynard's Sour Worms (30g)",           calories:99,  protein:1.5, carbs:23,  fat:0.5, fiber:0,   sugar:15, sodium:20,  servingG:30,  servingLabel:"30g (~4 worms)",     halal:true },
  { id:"saf_sw3",   category:"Snacks", name:"Beacon Jelly Tots (30g)",              calories:84,  protein:0.1, carbs:21,  fat:0,   fiber:0,   sugar:17, sodium:10,  servingG:30,  servingLabel:"30g",                halal:true },
  { id:"saf_sw4",   category:"Snacks", name:"Maynard's Fruit Pastilles (30g)",      calories:105, protein:0.5, carbs:25,  fat:0,   fiber:0,   sugar:20, sodium:10,  servingG:30,  servingLabel:"30g (~8 pastilles)", halal:true },
  { id:"saf_sw5",   category:"Snacks", name:"Maynard's Jelly Babies (30g)",         calories:98,  protein:1.4, carbs:22,  fat:0,   fiber:0,   sugar:15, sodium:12,  servingG:30,  servingLabel:"30g (~5 babies)",    halal:true },
  { id:"saf_sw6",   category:"Snacks", name:"Maynard's Sports Mix (30g)",           calories:97,  protein:1.3, carbs:22,  fat:0,   fiber:0,   sugar:14, sodium:15,  servingG:30,  servingLabel:"30g",                halal:true },
  { id:"saf_sw7",   category:"Snacks", name:"Maynard's Fuzzy Peaches (30g)",        calories:100, protein:1.2, carbs:23,  fat:0,   fiber:0,   sugar:15, sodium:14,  servingG:30,  servingLabel:"30g (~4 peaches)",   halal:true },
  { id:"saf_sw8",   category:"Snacks", name:"Maynard's Sour Cherry Blasters (30g)", calories:99,  protein:1.2, carbs:23,  fat:0,   fiber:0,   sugar:15, sodium:18,  servingG:30,  servingLabel:"30g",                halal:true },
  { id:"saf_sw9",   category:"Snacks", name:"Maynard's Swedish Fish (30g)",         calories:102, protein:0,   carbs:25,  fat:0,   fiber:0,   sugar:20, sodium:20,  servingG:30,  servingLabel:"30g (~7 fish)",      halal:true },
  { id:"saf_sw10",  category:"Snacks", name:"Maynard's Snakes Alive (30g)",         calories:97,  protein:1.3, carbs:22,  fat:0,   fiber:0,   sugar:14, sodium:15,  servingG:30,  servingLabel:"30g (~3 snakes)",    halal:true },
  { id:"saf_sw11",  category:"Snacks", name:"Maynard's Gummy Bears (30g)",          calories:98,  protein:1.5, carbs:22,  fat:0,   fiber:0,   sugar:14, sodium:12,  servingG:30,  servingLabel:"30g (~10 bears)",    halal:true },
  { id:"saf_sw12",  category:"Snacks", name:"Maynard's Sour Keys (30g)",            calories:100, protein:1.2, carbs:23,  fat:0,   fiber:0,   sugar:16, sodium:18,  servingG:30,  servingLabel:"30g (~3 keys)",      halal:true },
  { id:"saf_sw13",  category:"Snacks", name:"Maynard's Sour Cola Bottles (30g)",    calories:99,  protein:1.2, carbs:23,  fat:0,   fiber:0,   sugar:15, sodium:18,  servingG:30,  servingLabel:"30g (~5 bottles)",   halal:true },
  { id:"saf_sw14",  category:"Snacks", name:"Maynard's Liquorice Allsorts (30g)",   calories:105, protein:1,   carbs:23,  fat:1,   fiber:0.5, sugar:15, sodium:50,  servingG:30,  servingLabel:"30g (~4 pieces)",    halal:true },
  { id:"saf_sw15",  category:"Snacks", name:"Maynard's Sour Mix (30g)",             calories:99,  protein:1.2, carbs:23,  fat:0,   fiber:0,   sugar:15, sodium:20,  servingG:30,  servingLabel:"30g",                halal:true },
  { id:"saf_sw16",  category:"Snacks", name:"Maynard's Wine Gums (125g bag)",       calories:400, protein:5,   carbs:92,  fat:0,   fiber:0,   sugar:58, sodium:63,  servingG:125, servingLabel:"1 bag (125g)",       halal:true },
  { id:"saf_sw17",  category:"Snacks", name:"Maynard's Fruit Pastilles (125g bag)", calories:438, protein:2,   carbs:104, fat:0,   fiber:0,   sugar:83, sodium:42,  servingG:125, servingLabel:"1 bag (125g)",       halal:true },
  { id:"saf_sw18",  category:"Snacks", name:"Maynard's Sour Worms (125g bag)",      calories:413, protein:6,   carbs:96,  fat:2,   fiber:0,   sugar:63, sodium:83,  servingG:125, servingLabel:"1 bag (125g)",       halal:true },
  { id:"saf_sw19",  category:"Snacks", name:"Sally Williams Nougat (Almond)",       calories:110, protein:1.5, carbs:18,  fat:4,   fiber:0.2, sugar:14, sodium:20,  servingG:25,  servingLabel:"1 bar (25g)",        halal:true },
  { id:"saf_sw20",  category:"Snacks", name:"Sally Williams Nougat (Cranberry)",    calories:108, protein:1.4, carbs:19,  fat:3.5, fiber:0.3, sugar:15, sodium:18,  servingG:25,  servingLabel:"1 bar (25g)",        halal:true },
  { id:"saf_sw21",  category:"Snacks", name:"Woolworths Strawberry Hearts (sweets)",calories:90,  protein:1,   carbs:21,  fat:0,   fiber:0,   sugar:17, sodium:12,  servingG:25,  servingLabel:"25g (~5 hearts)",    halal:true },
  { id:"saf_sw22",  category:"Snacks", name:"Woolworths Strawberry Hearts (200g bag)", calories:720, protein:8, carbs:168, fat:0,  fiber:0,   sugar:135,sodium:96,  servingG:200, servingLabel:"1 bag (200g)",       halal:true },
  { id:"saf_sw23",  category:"Snacks", name:"Kinder Bueno Ice Cream",               calories:245, protein:3,   carbs:22,  fat:16,  fiber:0.5, sugar:18, sodium:60,  servingG:70,  servingLabel:"1 bar (70g)",        halal:true },
  // Simba Chips flavours (snack pack)
  { id:"saf_sim1",  category:"Snacks", name:"Simba Original (30g snack pack)",      calories:159, protein:2.1, carbs:16,  fat:9.6, fiber:1,   sugar:0.5,sodium:156, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim2",  category:"Snacks", name:"Simba Braai (30g snack pack)",         calories:155, protein:2,   carbs:17,  fat:9,   fiber:1,   sugar:0.5,sodium:180, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim3",  category:"Snacks", name:"Simba Cheese & Onion (30g)",           calories:158, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:0.5,sodium:190, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim4",  category:"Snacks", name:"Simba Chicken (30g snack pack)",       calories:156, protein:2,   carbs:17,  fat:9,   fiber:1,   sugar:0.5,sodium:175, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  // Simba Lays flavours
  { id:"saf_lays1", category:"Snacks", name:"Simba Lays Classic Salted (30g)",      calories:162, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:0,  sodium:120, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays2", category:"Snacks", name:"Simba Lays Cheese & Onion (30g)",      calories:160, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:0.5,sodium:185, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays3", category:"Snacks", name:"Simba Lays Salt & Vinegar (30g)",      calories:158, protein:1.8, carbs:17,  fat:9.5, fiber:1,   sugar:0.5,sodium:220, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays4", category:"Snacks", name:"Simba Lays Tomato (30g)",              calories:160, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:180, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays5", category:"Snacks", name:"Simba Lays Prawn Cocktail (30g)",      calories:160, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:1,  sodium:195, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays6", category:"Snacks", name:"Simba Lays Sour Cream & Onion (30g)",  calories:162, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:0.5,sodium:175, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays7", category:"Snacks", name:"Simba Lays Sweet Chilli (30g)",        calories:159, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:2,  sodium:180, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays8", category:"Snacks", name:"Simba Lays BBQ (30g)",                 calories:160, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:190, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays9", category:"Snacks", name:"Simba Lays Biltong (30g)",             calories:160, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:0.5,sodium:210, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays10",category:"Snacks", name:"Simba Lays Cheddar & Chilli (30g)",    calories:162, protein:2,   carbs:16,  fat:10,  fiber:1,   sugar:0.5,sodium:200, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_lays11",category:"Snacks", name:"Simba Lays Classic Salted (75g)",      calories:405, protein:5,   carbs:40,  fat:25,  fiber:2.5, sugar:0,  sodium:300, servingG:75,  servingLabel:"1 bag (75g)",        halal:true },
  { id:"saf_lays12",category:"Snacks", name:"Simba Lays Cheese & Onion (75g)",      calories:400, protein:5,   carbs:40,  fat:25,  fiber:2.5, sugar:1,  sodium:463, servingG:75,  servingLabel:"1 bag (75g)",        halal:true },
  { id:"saf_lays13",category:"Snacks", name:"Simba Lays Salt & Vinegar (75g)",      calories:395, protein:4.5, carbs:43,  fat:24,  fiber:2.5, sugar:1,  sodium:550, servingG:75,  servingLabel:"1 bag (75g)",        halal:true },
  { id:"saf_lays14",category:"Snacks", name:"Simba Lays Prawn Cocktail (75g)",      calories:400, protein:5,   carbs:40,  fat:25,  fiber:2.5, sugar:2.5,sodium:488, servingG:75,  servingLabel:"1 bag (75g)",        halal:true },
  { id:"saf_lays15",category:"Snacks", name:"Simba Lays Sweet Chilli (75g)",        calories:398, protein:5,   carbs:43,  fat:24,  fiber:2.5, sugar:5,  sodium:450, servingG:75,  servingLabel:"1 bag (75g)",        halal:true },
  // Simba — other flavours + Big Korn Bites
  { id:"saf_sim5",  category:"Snacks", name:"Simba Mexican Chilli (30g)",            calories:158, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:210, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim6",  category:"Snacks", name:"Simba Spring Onion (30g)",              calories:157, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:0.8,sodium:195, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim7",  category:"Snacks", name:"Simba Salt & Vinegar (30g)",            calories:155, protein:2,   carbs:17,  fat:9,   fiber:1,   sugar:0.5,sodium:240, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim8",  category:"Snacks", name:"Simba Hot 'n Spicy (30g)",              calories:159, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:215, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim9",  category:"Snacks", name:"Simba Bacon (30g)",                     calories:160, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:200, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim10", category:"Snacks", name:"Simba Big Korn Bites BBQ (30g)",        calories:140, protein:2,   carbs:18,  fat:7,   fiber:1,   sugar:1.5,sodium:200, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim11", category:"Snacks", name:"Simba Big Korn Bites Sweet Chilli (30g)",calories:142,protein:2,   carbs:18,  fat:7,   fiber:1,   sugar:2,  sodium:200, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_sim12", category:"Snacks", name:"Simba Big Korn Bites Cheese (30g)",     calories:145, protein:2.5, carbs:17,  fat:7.5, fiber:1,   sugar:1,  sodium:210, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  // Willards
  { id:"saf_will1", category:"Snacks", name:"Willards Cheas Curls Original (30g)",   calories:160, protein:2,   carbs:17,  fat:10,  fiber:0.5, sugar:1,  sodium:230, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will2", category:"Snacks", name:"Willards Cheas Curls Sweet Chilli (30g)",calories:160,protein:2,   carbs:17,  fat:10,  fiber:0.5, sugar:1.5,sodium:225, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will3", category:"Snacks", name:"Willards Cheas Curls Sour Cream (30g)", calories:160, protein:2,   carbs:17,  fat:10,  fiber:0.5, sugar:1,  sodium:215, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will4", category:"Snacks", name:"Willards Big Korn Bites BBQ (30g)",     calories:140, protein:2,   carbs:18,  fat:7,   fiber:1,   sugar:1.5,sodium:200, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will5", category:"Snacks", name:"Willards Big Korn Bites Cheese (30g)",  calories:145, protein:2.5, carbs:17,  fat:7.5, fiber:1,   sugar:1,  sodium:210, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will6", category:"Snacks", name:"Willards Big Korn Bites Boerie (30g)",  calories:142, protein:2.5, carbs:18,  fat:7,   fiber:1,   sugar:1.5,sodium:220, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will7", category:"Snacks", name:"Willards Sweet Chilli Pepper (30g)",    calories:155, protein:2,   carbs:18,  fat:8.5, fiber:1,   sugar:2,  sodium:210, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will8", category:"Snacks", name:"Willards Tomato Sauce (30g)",           calories:152, protein:2,   carbs:18,  fat:8,   fiber:1,   sugar:2,  sodium:215, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will9", category:"Snacks", name:"Willards Salt & Vinegar (30g)",         calories:152, protein:2,   carbs:18,  fat:8,   fiber:1,   sugar:0.5,sodium:240, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will10",category:"Snacks", name:"Willards Bacon Crunchies (30g)",        calories:158, protein:2,   carbs:17,  fat:9.5, fiber:1,   sugar:1,  sodium:230, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  { id:"saf_will11",category:"Snacks", name:"Willards Wozzles (30g)",                calories:155, protein:2,   carbs:18,  fat:8.5, fiber:1,   sugar:1,  sodium:215, servingG:30,  servingLabel:"1 snack pack (30g)", halal:true },
  // Kit Kat
  { id:"saf_kk1",   category:"Snacks", name:"Kit Kat 2-Finger",                      calories:107, protein:1.3, carbs:13,  fat:5.5, fiber:0.3, sugar:10, sodium:15,  servingG:21,  servingLabel:"1 bar (21g)",        halal:true },
  { id:"saf_kk2",   category:"Snacks", name:"Kit Kat 4-Finger",                      calories:214, protein:2.6, carbs:26,  fat:11,  fiber:0.6, sugar:20, sodium:30,  servingG:41,  servingLabel:"1 bar (41g)",        halal:true },
  // Yogurts
  { id:"saf_yog1",  category:"Snacks", name:"Clover Tropika (175g tub)",            calories:120, protein:4,   carbs:20,  fat:3,   fiber:0,   sugar:18, sodium:60,  servingG:175, servingLabel:"1 tub (175g)",       halal:true },
  { id:"saf_yog2",  category:"Snacks", name:"Danone Nutriday Plain (175g)",         calories:105, protein:6,   carbs:13,  fat:3,   fiber:0,   sugar:12, sodium:75,  servingG:175, servingLabel:"1 tub (175g)",       halal:true },
  { id:"saf_yog3",  category:"Snacks", name:"Danone Nutriday Flavoured (175g)",     calories:145, protein:5,   carbs:22,  fat:4,   fiber:0,   sugar:20, sodium:70,  servingG:175, servingLabel:"1 tub (175g)",       halal:true },
  { id:"saf_yog4",  category:"Snacks", name:"Danone Activia Plain (175g)",          calories:110, protein:6.5, carbs:11,  fat:3.5, fiber:0,   sugar:10, sodium:80,  servingG:175, servingLabel:"1 tub (175g)",       halal:true },
  { id:"saf_yog5",  category:"Snacks", name:"Woolworths Greek Style Yoghurt",       calories:140, protein:10,  carbs:6,   fat:9,   fiber:0,   sugar:5,  sodium:60,  servingG:200, servingLabel:"200g",               halal:true },
  { id:"saf_yog6",  category:"Snacks", name:"Woolworths Double Cream Yoghurt",      calories:120, protein:3,   carbs:8,   fat:8,   fiber:0,   sugar:7,  sodium:50,  servingG:100, servingLabel:"100g",               halal:true },
  // Beverages
  { id:"saf34", category:"Beverages", name:"Ceres Apple Juice",              calories:120, protein:0,   carbs:29,  fat:0,   fiber:0,   sugar:27, sodium:10,  servingG:250, servingLabel:"1 box (250ml)",   halal:true },
  { id:"saf35", category:"Beverages", name:"Ceres Mango Juice",              calories:130, protein:0.5, carbs:31,  fat:0,   fiber:0,   sugar:29, sodium:5,   servingG:250, servingLabel:"1 box (250ml)",   halal:true },
  { id:"saf36", category:"Beverages", name:"Oros Orange (diluted, 1:6)",     calories:45,  protein:0,   carbs:11,  fat:0,   fiber:0,   sugar:10, sodium:5,   servingG:250, servingLabel:"1 glass (250ml)", halal:true },
  { id:"saf37",     category:"Beverages", name:"Rooibos Tea (plain)",             calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:2,   servingG:250, servingLabel:"1 cup",            halal:true },
  { id:"saf38",     category:"Beverages", name:"Stoney Ginger Beer",              calories:150, protein:0,   carbs:36,  fat:0,   fiber:0,   sugar:36, sodium:35,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf39",     category:"Beverages", name:"Appletiser (sparkling apple)",    calories:130, protein:0,   carbs:31,  fat:0,   fiber:0,   sugar:29, sodium:10,  servingG:275, servingLabel:"1 bottle (275ml)", halal:true },
  // Jacobs Cappuccino: one searchable entry, milk type chosen via picker
  // sheet on tap (handled by `pickerKey` branch in addFoodEntry). Display
  // numbers are the full-cream variant — actual macros come from the
  // selected option in `milkOptions`.
  { id:"saf_bev1",  category:"Beverages", name:"Jacobs Cappuccino", calories:80, protein:3.5, carbs:7,  fat:4.5, fiber:0, sugar:6,  sodium:55,  servingG:200, servingLabel:"1 cup (200ml)",    halal:true,
    pickerKey: "jacobs-cappuccino-milk",
    milkOptions: [
      { key:"full",        label:"Full cream",             icon:"🥛", calories:80, protein:3.5, carbs:7,   fat:4.5, fiber:0,   sugar:6,   sodium:55 },
      { key:"lowfat",      label:"Low fat",                icon:"🥛", calories:70, protein:3.5, carbs:7,   fat:3,   fiber:0,   sugar:6,   sodium:55 },
      { key:"fatfree",     label:"Fat free",               icon:"💧", calories:55, protein:3.5, carbs:7,   fat:0.5, fiber:0,   sugar:6.5, sodium:55 },
      { key:"lactose",     label:"Low fat (lactose-free)", icon:"🌿", calories:70, protein:3.5, carbs:6,   fat:3,   fiber:0,   sugar:4.5, sodium:55 },
      { key:"almond_b",    label:"Almond milk (barista)",  icon:"🥛", calories:60, protein:1.5, carbs:6,   fat:2.5, fiber:0.3, sugar:4,   sodium:65 },
      { key:"almond_u",    label:"Almond milk (unsweetened)", icon:"🌰", calories:45, protein:1.5, carbs:3,   fat:2.5, fiber:0.3, sugar:0,   sodium:65 },
      { key:"oat",         label:"Oat milk",               icon:"🌾", calories:65, protein:2,   carbs:9,   fat:2.5, fiber:0.5, sugar:5,   sodium:60 },
    ],
  },
  { id:"saf_bev3",  category:"Beverages", name:"Green Tea (plain)",               calories:2,   protein:0,   carbs:0.5, fat:0,   fiber:0,   sugar:0,  sodium:2,   servingG:250, servingLabel:"1 cup",            halal:true },
  { id:"saf_bev4",  category:"Beverages", name:"Chamomile Tea (plain)",           calories:2,   protein:0,   carbs:0.5, fat:0,   fiber:0,   sugar:0,  sodium:2,   servingG:250, servingLabel:"1 cup",            halal:true },
  // Soft Drinks
  { id:"saf_sd1",   category:"Beverages", name:"Coca-Cola (330ml can)",           calories:139, protein:0,   carbs:35,  fat:0,   fiber:0,   sugar:35, sodium:10,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd2",   category:"Beverages", name:"Coca-Cola Zero (330ml)",          calories:1,   protein:0,   carbs:0.1, fat:0,   fiber:0,   sugar:0,  sodium:10,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd3",   category:"Beverages", name:"Sprite (330ml can)",              calories:132, protein:0,   carbs:33,  fat:0,   fiber:0,   sugar:33, sodium:14,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd4",   category:"Beverages", name:"Sprite Zero (330ml)",             calories:1,   protein:0,   carbs:0.1, fat:0,   fiber:0,   sugar:0,  sodium:14,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd5",   category:"Beverages", name:"Fanta Orange (330ml)",            calories:145, protein:0,   carbs:36,  fat:0,   fiber:0,   sugar:36, sodium:14,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd6",   category:"Beverages", name:"Fanta Grape (330ml)",             calories:140, protein:0,   carbs:35,  fat:0,   fiber:0,   sugar:35, sodium:14,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd7",   category:"Beverages", name:"Krest Bitter Lemon (330ml)",      calories:112, protein:0,   carbs:28,  fat:0,   fiber:0,   sugar:27, sodium:18,  servingG:330, servingLabel:"1 can (330ml)",    halal:true },
  { id:"saf_sd8",   category:"Beverages", name:"Powerade (500ml)",                calories:130, protein:0,   carbs:33,  fat:0,   fiber:0,   sugar:33, sodium:160, servingG:500, servingLabel:"1 bottle (500ml)", halal:true },
  { id:"saf_sd9",   category:"Beverages", name:"Minute Maid Orange (300ml)",      calories:132, protein:0.5, carbs:32,  fat:0,   fiber:0,   sugar:30, sodium:10,  servingG:300, servingLabel:"1 bottle (300ml)", halal:true },
  { id:"saf_sd10",  category:"Beverages", name:"Slo Jo Coffee Freezo (400ml)",    calories:280, protein:5,   carbs:48,  fat:8,   fiber:0,   sugar:42, sodium:80,  servingG:400, servingLabel:"1 cup (400ml)",    halal:true },
  { id:"saf_rev1",  category:"Beverages", name:"Revive Electrolytes Sachet",      calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:1000, servingG:6,  servingLabel:"1 sachet (750ml water)", halal:true },
];

const SA_SUPPLEMENTS = [
  { id:"sup1",  brand:"USN",          name:"USN 100% Whey Protein",        calories:120, protein:25,  carbs:4,   fat:1,   fiber:0,   sugar:2,  sodium:130, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup2",  brand:"USN",          name:"USN Muscle Fuel Anabolic",     calories:600, protein:50,  carbs:90,  fat:6,   fiber:3,   sugar:20, sodium:380, servingG:150, servingLabel:"1 serving (150g)",halal:true },
  { id:"sup3",  brand:"USN",          name:"USN Phedra-Cut Ultra XT",      calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:0,   servingLabel:"1 capsule",       halal:true },
  { id:"sup4",  brand:"Evox",         name:"Evox 100% Whey Protein",       calories:118, protein:24,  carbs:4,   fat:1.5, fiber:0,   sugar:2,  sodium:120, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup5",  brand:"Evox",         name:"Evox Ripped Whey",             calories:110, protein:25,  carbs:2,   fat:0.8, fiber:0,   sugar:1,  sodium:125, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup6",  brand:"Biogen",       name:"Biogen ISO Whey Ultra",        calories:120, protein:24,  carbs:2,   fat:2,   fiber:0,   sugar:1,  sodium:110, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup7",  brand:"Biogen",       name:"Biogen Whey Protein",          calories:125, protein:23,  carbs:5,   fat:2,   fiber:0,   sugar:2,  sodium:140, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup8",  brand:"NPL",          name:"NPL Whey Protein",             calories:130, protein:26,  carbs:4,   fat:1.5, fiber:0,   sugar:2,  sodium:130, servingG:33,  servingLabel:"1 scoop (33g)",   halal:true },
  { id:"sup9",  brand:"NPL",          name:"NPL Mass Muscle",              calories:560, protein:40,  carbs:82,  fat:5,   fiber:2,   sugar:18, sodium:340, servingG:140, servingLabel:"1 serving (140g)",halal:true },
  { id:"sup10", brand:"Weider",       name:"Weider Protein 80+",           calories:108, protein:22,  carbs:5,   fat:0.8, fiber:0,   sugar:2,  sodium:120, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup11", brand:"Dis-Chem",     name:"Dis-Chem Whey Protein",        calories:120, protein:24,  carbs:4,   fat:1.5, fiber:0,   sugar:2,  sodium:120, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup12",  brand:"Optimum Nutrition", name:"Gold Standard 100% Whey",               calories:120, protein:24,  carbs:3,   fat:1,   fiber:1,   sugar:1,   sodium:130, servingG:30, servingLabel:"1 scoop (30g)",  halal:true },
  { id:"sup12b", brand:"Optimum Nutrition", name:"Gold Standard Extreme Milk Chocolate",  calories:120, protein:24,  carbs:3,   fat:1,   fiber:1,   sugar:1.5, sodium:130, servingG:30, servingLabel:"1 scoop (30g)",  halal:true },
  { id:"sup12c", brand:"Optimum Nutrition", name:"Gold Standard Whey (Cookies & Cream)",  calories:122, protein:24,  carbs:3.5, fat:1,   fiber:1,   sugar:2,   sodium:135, servingG:30, servingLabel:"1 scoop (30g)",  halal:true },
  { id:"sup12d", brand:"Optimum Nutrition", name:"Gold Standard Whey (Vanilla)",          calories:120, protein:24,  carbs:3,   fat:1,   fiber:1,   sugar:1.5, sodium:130, servingG:30, servingLabel:"1 scoop (30g)",  halal:true },
  { id:"sup13",  brand:"Optimum Nutrition", name:"Gold Standard Casein",                  calories:120, protein:24,  carbs:4,   fat:1,   fiber:1,   sugar:1,   sodium:280, servingG:33, servingLabel:"1 scoop (33g)",  halal:true },
  { id:"sup14", brand:"Muscle Tech",  name:"MuscleTech Nitro Tech Whey",   calories:160, protein:30,  carbs:4,   fat:2.5, fiber:0,   sugar:2,  sodium:160, servingG:42,  servingLabel:"1 scoop (42g)",   halal:true },
  { id:"sup15", brand:"USN",          name:"USN Creatine Monohydrate",     calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:5,   servingLabel:"1 tsp (5g)",      halal:true },
  { id:"sup16", brand:"General",      name:"BCAA (per scoop)",             calories:20,  protein:5,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:7,   servingLabel:"1 scoop (7g)",    halal:true },
  { id:"sup17", brand:"Biogen",      name:"Biogen Multivitamin",          calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:0,   servingLabel:"1 tablet",        halal:true },
  { id:"sup19", brand:"Biogen",      name:"Biogen Supreme Omega-3",       calories:18,  protein:0,   carbs:0,   fat:2,   fiber:0,   sugar:0,  sodium:0,   servingG:2,   servingLabel:"2 softgels",      halal:true },
  { id:"sup20", brand:"Biogen",      name:"Biogen Collagen Capsules",     calories:6,   protein:1.5, carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:1.5, servingLabel:"3 capsules",      halal:true },
  { id:"sup21", brand:"Biogen",      name:"Biogen Magnesium Glycinate",   calories:0,   protein:0,   carbs:0,   fat:0,   fiber:0,   sugar:0,  sodium:0,   servingG:1,   servingLabel:"2 capsules",      halal:true },
  { id:"sup18", brand:"USN",        name:"USN ISO Zero Whey",            calories:108, protein:25,  carbs:1,   fat:0.3, fiber:0,   sugar:0.5,sodium:140, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup18v",brand:"USN",        name:"USN Iso Zero Whey (Vanilla)",  calories:108, protein:25,  carbs:1,   fat:0.3, fiber:0,   sugar:0.5,sodium:140, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
  { id:"sup18c",brand:"USN",        name:"USN Iso Zero Whey (Chocolate)",calories:110, protein:25,  carbs:1.5, fat:0.4, fiber:0,   sugar:0.8,sodium:145, servingG:30,  servingLabel:"1 scoop (30g)",   halal:true },
];

// ─── PROTEIN FOCUS OVERLAY: curated picker lists ─────────────────────
// Hand-ordered so ON Gold Standard variants appear first; mass-gainers
// and odd outliers are excluded (focus is whey, not bulkers).
// Curated focus list shown in the Protein Focus overlay.
// Limited to the user's preferred SKUs.
const WHEY_FOCUS_IDS = ['sup12b','sup12c','sup12d','sup18v','sup18c'];
const PROTEIN_FOCUS_WHEY_PRODUCTS = WHEY_FOCUS_IDS
  .map(id => SA_SUPPLEMENTS.find(f => f.id === id))
  .filter(Boolean);
const MILK_FOCUS_IDS = ['saf9','saf9b','saf9_ff'];
const PROTEIN_FOCUS_MILK_PRODUCTS = MILK_FOCUS_IDS
  .map(id => SA_FOODS.find(f => f.id === id))
  .filter(Boolean);

// Cappuccino quick-log milk shortlist — matches Yusuf's typical rotation:
// almond, Woolies lactose-free, full cream, fat free. Order = popularity.
const CAPPUCCINO_MILK_IDS = ['saf_ab5', 'saf9f', 'saf9', 'saf9_ff'];
const CAPPUCCINO_MILK_PRODUCTS = CAPPUCCINO_MILK_IDS
  .map(id => SA_FOODS.find(f => f.id === id))
  .filter(Boolean);

// Generic everyday foods. Per-typical-serving values from USDA FoodData Central.
// servingG is the typical portion (1 medium fruit, 1 cup cooked, etc) so the
// values shown ARE the calories at that servingG (matches SA_FOODS contract).
const GENERIC_FOODS = [
  // Fruits
  { id:"gf_banana",      category:"Fruits", name:"Banana (medium)",         calories:105, protein:1.3, carbs:27,  fat:0.4,  fiber:3.1, sugar:14,  sodium:1,   servingG:118, servingLabel:"1 medium",   halal:true },
  { id:"gf_apple",       category:"Fruits", name:"Apple (medium)",          calories:95,  protein:0.5, carbs:25,  fat:0.3,  fiber:4.4, sugar:19,  sodium:2,   servingG:182, servingLabel:"1 medium",   halal:true },
  { id:"gf_orange",      category:"Fruits", name:"Orange (medium)",         calories:62,  protein:1.2, carbs:15,  fat:0.2,  fiber:3.1, sugar:12,  sodium:0,   servingG:131, servingLabel:"1 medium",   halal:true },
  { id:"gf_strawberry",  category:"Fruits", name:"Strawberries",            calories:49,  protein:1,   carbs:12,  fat:0.5,  fiber:3,   sugar:7.4, sodium:2,   servingG:152, servingLabel:"1 cup",      halal:true },
  { id:"gf_blueberry",   category:"Fruits", name:"Blueberries",             calories:84,  protein:1.1, carbs:21,  fat:0.5,  fiber:3.6, sugar:15,  sodium:1,   servingG:148, servingLabel:"1 cup",      halal:true },
  { id:"gf_grape",       category:"Fruits", name:"Grapes",                  calories:104, protein:1.1, carbs:27,  fat:0.2,  fiber:1.4, sugar:23,  sodium:3,   servingG:151, servingLabel:"1 cup",      halal:true },
  { id:"gf_mango",       category:"Fruits", name:"Mango (chunks)",          calories:99,  protein:1.4, carbs:25,  fat:0.6,  fiber:2.6, sugar:23,  sodium:2,   servingG:165, servingLabel:"1 cup",      halal:true },
  { id:"gf_watermelon",  category:"Fruits", name:"Watermelon",              calories:46,  protein:0.9, carbs:11,  fat:0.2,  fiber:0.6, sugar:9,   sodium:2,   servingG:152, servingLabel:"1 cup",      halal:true },
  { id:"gf_avocado_h",   category:"Fruits", name:"Avocado (half)",          calories:160, protein:2,   carbs:9,   fat:15,   fiber:7,   sugar:0.7, sodium:7,   servingG:100, servingLabel:"½ medium",   halal:true },
  { id:"gf_pineapple",   category:"Fruits", name:"Pineapple (chunks)",      calories:82,  protein:0.9, carbs:22,  fat:0.2,  fiber:2.3, sugar:16,  sodium:2,   servingG:165, servingLabel:"1 cup",      halal:true },
  { id:"gf_pear",        category:"Fruits", name:"Pear (medium)",           calories:101, protein:0.6, carbs:27,  fat:0.3,  fiber:5.5, sugar:17,  sodium:2,   servingG:178, servingLabel:"1 medium",   halal:true },
  { id:"gf_peach",       category:"Fruits", name:"Peach (medium)",          calories:58,  protein:1.4, carbs:14,  fat:0.4,  fiber:2.3, sugar:13,  sodium:0,   servingG:150, servingLabel:"1 medium",   halal:true },
  { id:"gf_kiwi",        category:"Fruits", name:"Kiwi",                    calories:42,  protein:0.8, carbs:10,  fat:0.4,  fiber:2.1, sugar:6.2, sodium:2,   servingG:69,  servingLabel:"1 fruit",    halal:true },
  // Vegetables
  { id:"gf_broccoli",    category:"Vegetables", name:"Broccoli (raw)",      calories:31,  protein:2.6, carbs:6,   fat:0.3,  fiber:2.4, sugar:1.5, sodium:30,  servingG:91,  servingLabel:"1 cup",      halal:true },
  { id:"gf_carrot",      category:"Vegetables", name:"Carrot (medium)",     calories:25,  protein:0.6, carbs:6,   fat:0.1,  fiber:1.7, sugar:2.9, sodium:42,  servingG:61,  servingLabel:"1 medium",   halal:true },
  { id:"gf_spinach",     category:"Vegetables", name:"Spinach (raw)",       calories:7,   protein:0.9, carbs:1.1, fat:0.1,  fiber:0.7, sugar:0.1, sodium:24,  servingG:30,  servingLabel:"1 cup",      halal:true },
  { id:"gf_tomato",      category:"Vegetables", name:"Tomato (medium)",     calories:22,  protein:1.1, carbs:4.8, fat:0.2,  fiber:1.5, sugar:3.2, sodium:6,   servingG:123, servingLabel:"1 medium",   halal:true },
  { id:"gf_cucumber",    category:"Vegetables", name:"Cucumber (sliced)",   calories:16,  protein:0.7, carbs:3.8, fat:0.1,  fiber:0.5, sugar:1.7, sodium:2,   servingG:119, servingLabel:"1 cup",      halal:true },
  { id:"gf_potato_b",    category:"Vegetables", name:"Potato (boiled)",     calories:130, protein:3,   carbs:30,  fat:0.2,  fiber:2.7, sugar:1.4, sodium:8,   servingG:156, servingLabel:"1 medium",   halal:true },
  { id:"gf_sweetpotato", category:"Vegetables", name:"Sweet Potato (baked)",calories:112, protein:2,   carbs:26,  fat:0.1,  fiber:3.8, sugar:5.4, sodium:72,  servingG:151, servingLabel:"1 medium",   halal:true },
  { id:"gf_lettuce",     category:"Vegetables", name:"Lettuce (shredded)",  calories:5,   protein:0.5, carbs:1,   fat:0.1,  fiber:0.5, sugar:0.4, sodium:5,   servingG:36,  servingLabel:"1 cup",      halal:true },
  { id:"gf_onion",       category:"Vegetables", name:"Onion (chopped)",     calories:64,  protein:1.8, carbs:15,  fat:0.2,  fiber:2.7, sugar:6.8, sodium:6,   servingG:160, servingLabel:"1 cup",      halal:true },
  // Proteins
  { id:"gf_chicken_b",   category:"Proteins", name:"Chicken Breast (cooked)", calories:165, protein:31,   carbs:0,   fat:3.6, fiber:0,   sugar:0,   sodium:74,  servingG:100, servingLabel:"100g cooked", halal:true },
  { id:"gf_chicken_t",   category:"Proteins", name:"Chicken Thigh (cooked)",  calories:209, protein:26,   carbs:0,   fat:11,  fiber:0,   sugar:0,   sodium:88,  servingG:100, servingLabel:"100g cooked", halal:true },
  { id:"gf_egg",         category:"Proteins", name:"Egg (large)",             calories:72,  protein:6.3,  carbs:0.4, fat:4.8, fiber:0,   sugar:0.2, sodium:71,  servingG:50,  servingLabel:"1 large",     halal:true },
  { id:"gf_egg_white",   category:"Proteins", name:"Egg Whites (3 large)",    calories:51,  protein:11,   carbs:0.7, fat:0.2, fiber:0,   sugar:0.6, sodium:165, servingG:99,  servingLabel:"3 whites",    halal:true },
  { id:"gf_beef_lean",   category:"Proteins", name:"Lean Beef Mince (cooked)",calories:217, protein:26,   carbs:0,   fat:12,  fiber:0,   sugar:0,   sodium:75,  servingG:100, servingLabel:"100g cooked", halal:true },
  { id:"gf_salmon",      category:"Proteins", name:"Salmon (cooked)",         calories:206, protein:22,   carbs:0,   fat:13,  fiber:0,   sugar:0,   sodium:61,  servingG:100, servingLabel:"100g cooked", halal:true },
  { id:"gf_tuna",        category:"Proteins", name:"Tuna (canned in water)",  calories:116, protein:26,   carbs:0,   fat:1,   fiber:0,   sugar:0,   sodium:320, servingG:100, servingLabel:"100g drained",halal:true },
  { id:"gf_lamb_chop",   category:"Proteins", name:"Lamb Chop (grilled)",     calories:294, protein:25,   carbs:0,   fat:21,  fiber:0,   sugar:0,   sodium:72,  servingG:100, servingLabel:"100g cooked", halal:true },
  // Grains / starches
  { id:"gf_white_rice",  category:"Grains", name:"White Rice (cooked)",     calories:205, protein:4.3, carbs:45,  fat:0.4, fiber:0.6, sugar:0,   sodium:2,   servingG:158, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_brown_rice",  category:"Grains", name:"Brown Rice (cooked)",     calories:218, protein:5,   carbs:45,  fat:1.6, fiber:3.5, sugar:0.7, sodium:2,   servingG:195, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_oats_dry",    category:"Grains", name:"Rolled Oats (dry)",       calories:152, protein:5,   carbs:27,  fat:2.6, fiber:4,   sugar:1,   sodium:2,   servingG:40,  servingLabel:"½ cup dry",    halal:true },
  { id:"gf_pasta",       category:"Grains", name:"Pasta (cooked)",          calories:220, protein:8,   carbs:43,  fat:1.3, fiber:2.5, sugar:1,   sodium:6,   servingG:140, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_quinoa",      category:"Grains", name:"Quinoa (cooked)",         calories:222, protein:8,   carbs:39,  fat:3.6, fiber:5,   sugar:1.6, sodium:13,  servingG:185, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_couscous",    category:"Grains", name:"Couscous (cooked)",       calories:176, protein:6,   carbs:36,  fat:0.3, fiber:2.2, sugar:0.1, sodium:8,   servingG:157, servingLabel:"1 cup cooked", halal:true },
  // Dairy
  { id:"gf_greek_yog",   category:"Dairy", name:"Greek Yogurt (full fat)",  calories:148, protein:17,  carbs:6,   fat:7,   fiber:0,   sugar:5,   sodium:65,  servingG:170, servingLabel:"1 cup",       halal:true },
  { id:"gf_yogurt",      category:"Dairy", name:"Plain Yogurt",             calories:100, protein:6,   carbs:11,  fat:4,   fiber:0,   sugar:11,  sodium:75,  servingG:170, servingLabel:"1 cup",       halal:true },
  { id:"gf_cottage",     category:"Dairy", name:"Cottage Cheese",           calories:110, protein:13,  carbs:4.5, fat:5,   fiber:0,   sugar:4,   sodium:330, servingG:113, servingLabel:"½ cup",       halal:true },
  { id:"gf_cheddar",     category:"Dairy", name:"Cheddar Cheese",           calories:113, protein:7,   carbs:0.4, fat:9.3, fiber:0,   sugar:0.1, sodium:174, servingG:28,  servingLabel:"1 oz (28g)",  halal:true },
  { id:"gf_mozz",        category:"Dairy", name:"Mozzarella (low-moisture)",calories:78,  protein:6.3, carbs:1,   fat:5.7, fiber:0,   sugar:0.4, sodium:138, servingG:28,  servingLabel:"1 oz (28g)",  halal:true },
  // Nuts / seeds
  { id:"gf_almonds",     category:"Nuts", name:"Almonds",                   calories:164, protein:6,   carbs:6.1, fat:14,  fiber:3.5, sugar:1.2, sodium:0,   servingG:28,  servingLabel:"1 oz (~23 nuts)", halal:true },
  { id:"gf_pb",          category:"Nuts", name:"Peanut Butter",             calories:188, protein:8,   carbs:7,   fat:16,  fiber:2,   sugar:3,   sodium:152, servingG:32,  servingLabel:"2 tbsp",      halal:true },
  { id:"gf_walnuts",     category:"Nuts", name:"Walnuts",                   calories:185, protein:4.3, carbs:3.9, fat:18.5,fiber:1.9, sugar:0.7, sodium:1,   servingG:28,  servingLabel:"1 oz (~14 halves)", halal:true },
  { id:"gf_cashews",     category:"Nuts", name:"Cashews",                   calories:157, protein:5.2, carbs:8.6, fat:12.4,fiber:0.9, sugar:1.7, sodium:3,   servingG:28,  servingLabel:"1 oz",        halal:true },
  { id:"gf_chia",        category:"Nuts", name:"Chia Seeds",                calories:138, protein:4.7, carbs:12,  fat:8.7, fiber:9.8, sugar:0,   sodium:5,   servingG:28,  servingLabel:"1 oz (2 tbsp)",halal:true },
  // Legumes
  { id:"gf_chickpeas",   category:"Legumes", name:"Chickpeas (cooked)",     calories:269, protein:14.5,carbs:45,  fat:4.2, fiber:12.5,sugar:7.9, sodium:11,  servingG:164, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_lentils",     category:"Legumes", name:"Lentils (cooked)",       calories:230, protein:18,  carbs:40,  fat:0.8, fiber:15.6,sugar:3.6, sodium:4,   servingG:198, servingLabel:"1 cup cooked", halal:true },
  { id:"gf_black_beans", category:"Legumes", name:"Black Beans (cooked)",   calories:227, protein:15,  carbs:41,  fat:0.9, fiber:15,  sugar:0.6, sodium:2,   servingG:172, servingLabel:"1 cup cooked", halal:true },
  // Beverages
  { id:"gf_coffee",      category:"Beverages", name:"Coffee (black)",       calories:2,   protein:0.3, carbs:0,   fat:0,   fiber:0,   sugar:0,   sodium:5,   servingG:240, servingLabel:"1 cup",       halal:true },
  { id:"gf_tea",         category:"Beverages", name:"Tea (black, plain)",   calories:2,   protein:0,   carbs:0.7, fat:0,   fiber:0,   sugar:0,   sodium:7,   servingG:240, servingLabel:"1 cup",       halal:true },
  { id:"gf_oj",          category:"Beverages", name:"Orange Juice",         calories:112, protein:1.7, carbs:26,  fat:0.5, fiber:0.5, sugar:21,  sodium:2,   servingG:248, servingLabel:"1 cup",       halal:true },
  // Fats
  { id:"gf_butter",      category:"Fats", name:"Butter",                    calories:102, protein:0.1, carbs:0,   fat:11.5,fiber:0,   sugar:0,   sodium:91,  servingG:14,  servingLabel:"1 tbsp",      halal:true },
];

const SA_RESTAURANTS = [
  { id:"n1", restaurant:"Nando's",  name:"1/4 Chicken (Plain)",          calories:239, protein:32, carbs:1,  fat:12, fiber:0, sugar:0,  sodium:380,  servingG:182, servingLabel:"1/4 chicken",       halal:true },
  { id:"n2", restaurant:"Nando's",  name:"1/2 Chicken (Plain)",          calories:478, protein:64, carbs:2,  fat:24, fiber:0, sugar:0,  sodium:760,  servingG:364, servingLabel:"1/2 chicken",       halal:true },
  { id:"n3", restaurant:"Nando's",  name:"Chicken Butterfly",            calories:349, protein:45, carbs:2,  fat:17, fiber:0, sugar:0,  sodium:520,  servingG:210, servingLabel:"1 piece",           halal:true },
  { id:"n4", restaurant:"Nando's",  name:"Peri-Peri Chips (Reg)",        calories:365, protein:5,  carbs:48, fat:17, fiber:4, sugar:1,  sodium:480,  servingG:150, servingLabel:"regular",           halal:true },
  { id:"n4s",restaurant:"Nando's",  name:"Peri Fries (Small)",            calories:255, protein:3.5,carbs:34, fat:12, fiber:3, sugar:1,  sodium:330,  servingG:105, servingLabel:"small (~105g)",     halal:true },
  { id:"n4r",restaurant:"Nando's",  name:"Peri Fries (Regular)",          calories:365, protein:5,  carbs:48, fat:17, fiber:4, sugar:1,  sodium:480,  servingG:150, servingLabel:"regular (~150g)",   halal:true },
  { id:"n4l",restaurant:"Nando's",  name:"Peri Fries (Large)",            calories:560, protein:7.5,carbs:74, fat:26, fiber:6, sugar:1.5,sodium:740,  servingG:230, servingLabel:"large (~230g)",     halal:true },
  { id:"n5", restaurant:"Nando's",  name:"Coleslaw",                     calories:138, protein:1,  carbs:16, fat:8,  fiber:2, sugar:13, sodium:200,  servingG:120, servingLabel:"1 side",            halal:true },
  { id:"n6", restaurant:"Nando's",  name:"Chicken Wrap",                 calories:520, protein:35, carbs:48, fat:20, fiber:3, sugar:4,  sodium:920,  servingG:280, servingLabel:"1 wrap",            halal:true },
  { id:"n7", restaurant:"Nando's",  name:"Fino Roll",                    calories:130, protein:4,  carbs:25, fat:2,  fiber:1, sugar:1,  sodium:240,  servingG:60,  servingLabel:"1 roll",            halal:true },
  { id:"n8", restaurant:"Nando's",  name:"1/4 Chicken & Chips",          calories:604, protein:37, carbs:50, fat:29, fiber:4, sugar:1,  sodium:860,  servingG:332, servingLabel:"1/4 chkn + chips",  halal:true },
  { id:"n9", restaurant:"Nando's",  name:"1/2 Chicken & Chips",          calories:843, protein:69, carbs:50, fat:41, fiber:4, sugar:1,  sodium:1240, servingG:514, servingLabel:"1/2 chkn + chips",  halal:true },
  { id:"n9b",restaurant:"Nando's",  name:"Chicken Burger & Chips",       calories:915, protein:39, carbs:98, fat:39, fiber:7, sugar:6,  sodium:1460, servingG:410, servingLabel:"burger + chips",    halal:true },
  { id:"n10",restaurant:"Nando's",  name:"1/4 Leg",                      calories:270, protein:28, carbs:1,  fat:17, fiber:0, sugar:0,  sodium:400,  servingG:190, servingLabel:"1 leg quarter",      halal:true },
  { id:"n11",restaurant:"Nando's",  name:"1/4 Breast",                   calories:210, protein:36, carbs:1,  fat:7,  fiber:0, sugar:0,  sodium:360,  servingG:174, servingLabel:"1 breast quarter",   halal:true },
  { id:"n12",restaurant:"Nando's",  name:"Portuguese Roll",              calories:190, protein:6,  carbs:36, fat:3,  fiber:1, sugar:2,  sodium:300,  servingG:75,  servingLabel:"1 roll",            halal:true },
  { id:"n13",restaurant:"Nando's",  name:"Peri Wedges",                  calories:390, protein:5,  carbs:54, fat:17, fiber:5, sugar:1,  sodium:500,  servingG:170, servingLabel:"regular",           halal:true },
  { id:"n14",restaurant:"Nando's",  name:"Chicken Wings (4 pcs)",        calories:340, protein:30, carbs:2,  fat:24, fiber:0, sugar:1,  sodium:620,  servingG:200, servingLabel:"4 wings",           halal:true },
  { id:"n15",restaurant:"Nando's",  name:"Peri Tatoes",                  calories:300, protein:4,  carbs:42, fat:13, fiber:4, sugar:2,  sodium:440,  servingG:180, servingLabel:"1 side",            halal:true },
  { id:"n16",restaurant:"Nando's",  name:"Chicken Burger",               calories:550, protein:34, carbs:50, fat:22, fiber:3, sugar:5,  sodium:980,  servingG:260, servingLabel:"1 burger",          halal:true },
  { id:"n17",restaurant:"Nando's",  name:"Barone Milkshake",             calories:521, protein:8,  carbs:59, fat:27, fiber:0, sugar:52, sodium:180,  servingG:400, servingLabel:"1 shake",            halal:true },
  { id:"k1", restaurant:"KFC SA",          name:"Original Recipe Breast", calories:320, protein:36, carbs:9,  fat:16, fiber:0,   sugar:0,  sodium:710, servingG:160, servingLabel:"1 piece",     halal:true },
  { id:"k2", restaurant:"KFC SA",          name:"Original Recipe Thigh",  calories:290, protein:22, carbs:9,  fat:19, fiber:0,   sugar:0,  sodium:620, servingG:130, servingLabel:"1 piece",     halal:true },
  { id:"k3", restaurant:"KFC SA",          name:"Zinger Burger",          calories:540, protein:29, carbs:52, fat:24, fiber:3,   sugar:6,  sodium:1100,servingG:240, servingLabel:"1 burger",    halal:true },
  { id:"k4", restaurant:"KFC SA",          name:"Streetwise 2 (Hot Wings)",calories:560,protein:32, carbs:30, fat:34, fiber:2,   sugar:0,  sodium:1280,servingG:230, servingLabel:"meal",        halal:true },
  { id:"k5", restaurant:"KFC SA",          name:"Regular Chips",          calories:310, protein:5,  carbs:42, fat:14, fiber:3,   sugar:0,  sodium:380, servingG:130, servingLabel:"regular",     halal:true },
  { id:"s1r",restaurant:"Steers",          name:"Straight Up Burger",     calories:490, protein:28, carbs:42, fat:22, fiber:2,   sugar:6,  sodium:880, servingG:230, servingLabel:"1 burger",    halal:true },
  { id:"s2r",restaurant:"Steers",          name:"Wacky Wednesday (2)",    calories:740, protein:46, carbs:64, fat:30, fiber:3,   sugar:9,  sodium:1320,servingG:380, servingLabel:"2 burgers",   halal:true },
  { id:"s3r",restaurant:"Steers",          name:"Boerie Roll",            calories:440, protein:18, carbs:38, fat:24, fiber:2,   sugar:5,  sodium:920, servingG:200, servingLabel:"1 roll",      halal:true },
  { id:"s4r",restaurant:"Steers",          name:"Chicken Burger",         calories:520, protein:32, carbs:48, fat:21, fiber:2,   sugar:7,  sodium:980, servingG:240, servingLabel:"1 burger",    halal:true },
  { id:"s5r",restaurant:"Steers",          name:"Chips (Regular)",        calories:380, protein:5,  carbs:52, fat:18, fiber:4,   sugar:1,  sodium:340, servingG:150, servingLabel:"regular",     halal:true },
  { id:"c1", restaurant:"Chicken Licken",  name:"Soul Burger",            calories:580, protein:28, carbs:56, fat:27, fiber:3,   sugar:8,  sodium:1080,servingG:260, servingLabel:"1 burger",    halal:true },
  { id:"c2", restaurant:"Chicken Licken",  name:"Hot Wings (4 pcs)",      calories:530, protein:40, carbs:20, fat:34, fiber:1,   sugar:0,  sodium:1180,servingG:200, servingLabel:"4 wings",     halal:true },
  { id:"c3", restaurant:"Chicken Licken",  name:"Streetwise 4",           calories:620, protein:42, carbs:24, fat:38, fiber:1,   sugar:0,  sodium:1320,servingG:280, servingLabel:"4 pieces",    halal:true },
  { id:"c4", restaurant:"Chicken Licken",  name:"Chips (Regular)",        calories:345, protein:4,  carbs:46, fat:16, fiber:3,   sugar:0,  sodium:360, servingG:140, servingLabel:"regular",     halal:true },
  { id:"d1", restaurant:"Debonairs",       name:"Triple Decker (slice)",  calories:310, protein:14, carbs:36, fat:12, fiber:2,   sugar:4,  sodium:720, servingG:135, servingLabel:"1 slice (M)", halal:true },
  { id:"d2", restaurant:"Debonairs",       name:"BBQ Crunch (slice)",     calories:290, protein:13, carbs:34, fat:11, fiber:2,   sugar:6,  sodium:680, servingG:130, servingLabel:"1 slice (M)", halal:true },
  { id:"d3", restaurant:"Debonairs",       name:"Margherita (slice)",     calories:260, protein:12, carbs:33, fat:9,  fiber:2,   sugar:4,  sodium:580, servingG:120, servingLabel:"1 slice (M)", halal:true },
  { id:"sp1",restaurant:"Spur",            name:"Classic Burger",                calories:650,  protein:38, carbs:48, fat:32, fiber:3,   sugar:8,  sodium:1140, servingG:280, servingLabel:"1 burger",      halal:true },
  { id:"sp2",restaurant:"Spur",            name:"Grilled Chicken Breast",        calories:320,  protein:45, carbs:6,  fat:13, fiber:1,   sugar:2,  sodium:520,  servingG:220, servingLabel:"1 portion",     halal:true },
  { id:"sp3",restaurant:"Spur",            name:"Onion Rings",                   calories:340,  protein:4,  carbs:40, fat:18, fiber:2,   sugar:5,  sodium:520,  servingG:120, servingLabel:"1 side",        halal:true },
  { id:"sp4",restaurant:"Spur",            name:"Cheddamelt Steak (with rings & chips)", calories:1100, protein:55, carbs:72, fat:58, fiber:5, sugar:6, sodium:1480, servingG:580, servingLabel:"1 meal",   halal:true },
  { id:"sp5",restaurant:"Spur",            name:"Buffalo Wings (6 pcs)",         calories:520,  protein:36, carbs:18, fat:34, fiber:1,   sugar:4,  sodium:1040, servingG:260, servingLabel:"6 wings",       halal:true },
  { id:"sp6",restaurant:"Spur",            name:"Crumbed Mushrooms",             calories:380,  protein:8,  carbs:36, fat:22, fiber:3,   sugar:3,  sodium:620,  servingG:180, servingLabel:"1 starter",     halal:true },
  { id:"sp7",restaurant:"Spur",            name:"Lazy Aged Rump (300g)",         calories:580,  protein:62, carbs:2,  fat:36, fiber:0,   sugar:0,  sodium:420,  servingG:300, servingLabel:"300g steak",    halal:true },
  { id:"sp8",restaurant:"Spur",            name:"Crème Brûlée",                  calories:380,  protein:6,  carbs:38, fat:22, fiber:0,   sugar:32, sodium:80,   servingG:160, servingLabel:"1 dessert",     halal:true },
  { id:"sp9",restaurant:"Spur",            name:"Waffles (with cream & syrup)",  calories:580,  protein:10, carbs:78, fat:26, fiber:2,   sugar:42, sodium:280,  servingG:280, servingLabel:"1 portion",     halal:true },
  { id:"sp10",restaurant:"Spur",           name:"Barone Milkshake",              calories:620,  protein:12, carbs:88, fat:26, fiber:0,   sugar:80, sodium:220,  servingG:400, servingLabel:"1 shake",       halal:true },
  { id:"w1", restaurant:"Wimpy",  name:"Classic Burger",              calories:580,  protein:32, carbs:48, fat:26, fiber:2, sugar:6,  sodium:1020, servingG:260, servingLabel:"1 burger",       halal:true },
  { id:"w2", restaurant:"Wimpy",  name:"Grilled Chicken Pita",        calories:380,  protein:38, carbs:20, fat:16, fiber:2, sugar:4,  sodium:680,  servingG:230, servingLabel:"1 pita",         halal:true },
  { id:"w3", restaurant:"Wimpy",  name:"Classic Burger & Chips",      calories:880,  protein:36, carbs:86, fat:40, fiber:5, sugar:6,  sodium:1400, servingG:410, servingLabel:"1 meal",         halal:true },
  { id:"w4", restaurant:"Wimpy",  name:"Cheese Burger & Chips",       calories:950,  protein:38, carbs:87, fat:46, fiber:5, sugar:7,  sodium:1520, servingG:430, servingLabel:"1 meal",         halal:true },
  { id:"w5", restaurant:"Wimpy",  name:"Double Cheese Burger & Chips",calories:1180, protein:56, carbs:89, fat:62, fiber:5, sugar:8,  sodium:1840, servingG:560, servingLabel:"1 meal",         halal:true },
  { id:"w6", restaurant:"Wimpy",  name:"Full Breakfast",              calories:820,  protein:38, carbs:52, fat:48, fiber:4, sugar:4,  sodium:1380, servingG:480, servingLabel:"1 plate",        halal:true },
  { id:"o1", restaurant:"Ocean Basket",  name:"Hake & Chips",                calories:620, protein:38, carbs:52, fat:26, fiber:4, sugar:1, sodium:780,  servingG:380, servingLabel:"1 plate",        halal:true },
  { id:"o2", restaurant:"Ocean Basket",  name:"Calamari (Reg)",              calories:380, protein:24, carbs:28, fat:18, fiber:2, sugar:1, sodium:680,  servingG:220, servingLabel:"regular",        halal:true },
  { id:"o3", restaurant:"Ocean Basket",  name:"Prawns (6 pieces)",           calories:240, protein:28, carbs:2,  fat:14, fiber:0, sugar:0, sodium:480,  servingG:180, servingLabel:"6 prawns",       halal:true },
  { id:"o4", restaurant:"Ocean Basket",  name:"Prince Prawns & Chips",       calories:680, protein:42, carbs:52, fat:28, fiber:4, sugar:1, sodium:980,  servingG:420, servingLabel:"1 meal",         halal:true },
  { id:"o5", restaurant:"Ocean Basket",  name:"Prince Prawns & Rice",        calories:580, protein:44, carbs:58, fat:16, fiber:2, sugar:1, sodium:860,  servingG:400, servingLabel:"1 meal",         halal:true },
  { id:"o6", restaurant:"Ocean Basket",  name:"Garden Salad",                calories:120, protein:3,  carbs:14, fat:6,  fiber:3, sugar:8, sodium:180,  servingG:180, servingLabel:"1 side",         halal:true },
  { id:"o7", restaurant:"Ocean Basket",  name:"Calamari & Chips",            calories:720, protein:28, carbs:68, fat:38, fiber:4, sugar:1, sodium:980,  servingG:420, servingLabel:"1 meal",         halal:true },
  { id:"o8", restaurant:"Ocean Basket",  name:"Greek Salad",                 calories:220, protein:6,  carbs:12, fat:16, fiber:3, sugar:8, sodium:480,  servingG:220, servingLabel:"1 salad",        halal:true },
  // Prawn Star
  { id:"ps1",restaurant:"Prawn Star",    name:"Prawn Fries (6 prawns + chips + rice)", calories:835, protein:39, carbs:94, fat:33, fiber:5, sugar:2, sodium:1300, servingG:480, servingLabel:"1 meal",         halal:true },
  { id:"f1", restaurant:"Fishaways",      name:"Hake (1 piece, battered)",calories:280, protein:22, carbs:18, fat:13, fiber:1,   sugar:0,  sodium:520, servingG:180, servingLabel:"1 piece",     halal:true },
  { id:"f2", restaurant:"Fishaways",      name:"Fish & Chips (medium)",   calories:650, protein:28, carbs:70, fat:28, fiber:4,   sugar:1,  sodium:780, servingG:400, servingLabel:"medium meal", halal:true },
  { id:"f3", restaurant:"Fishaways",      name:"Calamari rings",          calories:340, protein:20, carbs:26, fat:16, fiber:1,   sugar:0,  sodium:540, servingG:200, servingLabel:"regular",     halal:true },
  { id:"r1", restaurant:"Romans Pizza",   name:"Classic Margherita (slice)",calories:230,protein:10, carbs:30, fat:8,  fiber:2,   sugar:3,  sodium:520, servingG:110, servingLabel:"1 slice (M)", halal:true },
  { id:"r2", restaurant:"Romans Pizza",   name:"BBQ Chicken (slice)",     calories:270, protein:14, carbs:32, fat:10, fiber:2,   sugar:5,  sodium:640, servingG:125, servingLabel:"1 slice (M)", halal:true },
  { id:"m1", restaurant:"McDonald's SA",  name:"McFeast Burger",          calories:680, protein:38, carbs:52, fat:35, fiber:3,   sugar:10, sodium:1120,servingG:300, servingLabel:"1 burger",    halal:true },
  { id:"m2", restaurant:"McDonald's SA",  name:"McChicken Sandwich",      calories:420, protein:22, carbs:44, fat:17, fiber:2,   sugar:5,  sodium:820, servingG:190, servingLabel:"1 sandwich",  halal:true },
  { id:"m3", restaurant:"McDonald's SA",  name:"Medium Fries",            calories:320, protein:4,  carbs:44, fat:14, fiber:3,   sugar:0,  sodium:210, servingG:130, servingLabel:"medium",      halal:true },
  { id:"ba1",restaurant:"Barcelos",       name:"1/4 Chicken (flame-gr.)", calories:240, protein:35, carbs:1,  fat:11, fiber:0,   sugar:0,  sodium:560, servingG:180, servingLabel:"1/4 chicken", halal:true },
  { id:"ba2",restaurant:"Barcelos",       name:"Chicken Burger",          calories:520, protein:30, carbs:46, fat:22, fiber:2,   sugar:4,  sodium:880, servingG:250, servingLabel:"1 burger",    halal:true },
  { id:"ba3",restaurant:"Barcelos",       name:"Chicken Wrap",            calories:480, protein:32, carbs:45, fat:18, fiber:3,   sugar:4,  sodium:820, servingG:270, servingLabel:"1 wrap",      halal:true },
  { id:"ba4",restaurant:"Barcelos",       name:"Peri Chips",              calories:350, protein:4,  carbs:48, fat:16, fiber:4,   sugar:1,  sodium:380, servingG:150, servingLabel:"regular",     halal:true },
  { id:"hl1",restaurant:"Hungry Lion",    name:"2 Piece Chicken",         calories:440, protein:36, carbs:18, fat:24, fiber:1,   sugar:0,  sodium:980, servingG:220, servingLabel:"2 pieces",    halal:true },
  { id:"hl2",restaurant:"Hungry Lion",    name:"Chicken Burger",          calories:500, protein:26, carbs:48, fat:22, fiber:2,   sugar:5,  sodium:960, servingG:240, servingLabel:"1 burger",    halal:true },
  { id:"hl3",restaurant:"Hungry Lion",    name:"Chips (Regular)",         calories:330, protein:4,  carbs:44, fat:15, fiber:3,   sugar:0,  sodium:340, servingG:140, servingLabel:"regular",     halal:true },
  { id:"bk1",restaurant:"Burger King SA", name:"Whopper",                 calories:630, protein:28, carbs:50, fat:34, fiber:3,   sugar:10, sodium:1080,servingG:290, servingLabel:"1 burger",    halal:true },
  { id:"bk2",restaurant:"Burger King SA", name:"Crispy Chicken Burger",   calories:560, protein:26, carbs:52, fat:26, fiber:3,   sugar:6,  sodium:1020,servingG:260, servingLabel:"1 burger",    halal:true },
  { id:"bk3",restaurant:"Burger King SA", name:"Medium Fries",            calories:340, protein:4,  carbs:46, fat:15, fiber:3,   sugar:0,  sodium:360, servingG:130, servingLabel:"medium",      halal:true },
  { id:"ph1",restaurant:"Pizza Hut SA",   name:"Margherita (slice, M)",   calories:240, protein:10, carbs:30, fat:9,  fiber:2,   sugar:3,  sodium:530, servingG:112, servingLabel:"1 slice (M)", halal:true },
  { id:"ph2",restaurant:"Pizza Hut SA",   name:"BBQ Chicken (slice, M)",  calories:275, protein:15, carbs:32, fat:11, fiber:2,   sugar:6,  sodium:660, servingG:128, servingLabel:"1 slice (M)", halal:true },
  { id:"ph3",restaurant:"Pizza Hut SA",   name:"Stuffed Crust (slice, M)",calories:310, protein:14, carbs:36, fat:13, fiber:2,   sugar:4,  sodium:710, servingG:138, servingLabel:"1 slice (M)", halal:true },
  { id:"mo1",restaurant:"Mochachos",      name:"Chicken Wrap",            calories:480, protein:28, carbs:54, fat:16, fiber:3,   sugar:4,  sodium:840, servingG:270, servingLabel:"1 wrap",      halal:true },
  { id:"mo2",restaurant:"Mochachos",      name:"Beef Burrito",            calories:580, protein:32, carbs:62, fat:20, fiber:4,   sugar:5,  sodium:960, servingG:310, servingLabel:"1 burrito",   halal:true },
  { id:"mo3",restaurant:"Mochachos",      name:"Nachos (loaded)",         calories:580, protein:18, carbs:64, fat:28, fiber:4,   sugar:4,  sodium:840, servingG:280, servingLabel:"1 portion",   halal:true },
  { id:"ka1",restaurant:"Kauai",          name:"Grilled Chicken Wrap",    calories:420, protein:35, carbs:38, fat:14, fiber:4,   sugar:5,  sodium:680, servingG:260, servingLabel:"1 wrap",      halal:true },
  { id:"ka2",restaurant:"Kauai",          name:"Acai Power Bowl",         calories:380, protein:8,  carbs:68, fat:9,  fiber:6,   sugar:36, sodium:80,  servingG:300, servingLabel:"1 bowl",      halal:true },
  { id:"ka3",restaurant:"Kauai",          name:"Protein Smoothie",        calories:320, protein:22, carbs:44, fat:5,  fiber:3,   sugar:30, sodium:120, servingG:400, servingLabel:"large",       halal:true },
  { id:"ka4",restaurant:"Kauai",          name:"Steak & Avo Quesadilla",  calories:650, protein:38, carbs:42, fat:32, fiber:4,   sugar:3,  sodium:820, servingG:300, servingLabel:"1 quesadilla",halal:true },
  { id:"ka5",restaurant:"Kauai",          name:"Princess Wrap",           calories:520, protein:32, carbs:44, fat:22, fiber:4,   sugar:4,  sodium:760, servingG:280, servingLabel:"1 wrap",      halal:true },
  { id:"ka6",restaurant:"Kauai",          name:"Chicken Caesar Salad",    calories:420, protein:38, carbs:18, fat:22, fiber:3,   sugar:3,  sodium:780, servingG:280, servingLabel:"1 salad",     halal:true },
  { id:"ka7",restaurant:"Kauai",          name:"Vietnamese Steak Poke Bowl",calories:580,protein:40, carbs:54, fat:18, fiber:4,   sugar:6,  sodium:860, servingG:380, servingLabel:"1 bowl",      halal:true },
  { id:"ka8",restaurant:"Kauai",          name:"Vietnamese Steak Wrap",   calories:560, protein:36, carbs:48, fat:22, fiber:3,   sugar:5,  sodium:820, servingG:310, servingLabel:"1 wrap",      halal:true },
  { id:"ka9",restaurant:"Kauai",          name:"Teriyaki Chicken Poke Bowl",calories:540,protein:38, carbs:58, fat:12, fiber:4,   sugar:8,  sodium:780, servingG:370, servingLabel:"1 bowl",      halal:true },
  { id:"pa1",restaurant:"Panarotti's",    name:"Margherita (slice, M)",   calories:250, protein:11, carbs:31, fat:9,  fiber:2,   sugar:3,  sodium:540, servingG:115, servingLabel:"1 slice (M)", halal:true },
  { id:"pa2",restaurant:"Panarotti's",    name:"Chicken & Mushroom (sl)", calories:290, protein:15, carbs:32, fat:12, fiber:2,   sugar:3,  sodium:640, servingG:128, servingLabel:"1 slice (M)", halal:true },
  { id:"mu1",restaurant:"Mugg & Bean",    name:"Breakfast (2 eggs, toast)",calories:520,protein:22, carbs:44, fat:28, fiber:3,   sugar:4,  sodium:860, servingG:280, servingLabel:"1 plate",     halal:true },
  { id:"mu2",restaurant:"Mugg & Bean",    name:"Grilled Chicken Wrap",    calories:460, protein:36, carbs:40, fat:16, fiber:3,   sugar:5,  sodium:760, servingG:260, servingLabel:"1 wrap",      halal:true },
  { id:"mu3",restaurant:"Mugg & Bean",    name:"Club Sandwich",           calories:580, protein:30, carbs:52, fat:26, fiber:3,   sugar:5,  sodium:1020,servingG:300, servingLabel:"1 sandwich",  halal:true },
  { id:"rm1",restaurant:"RocoMamas",      name:"Smash Burger",            calories:680, protein:38, carbs:48, fat:36, fiber:3,   sugar:8,  sodium:1080,servingG:300, servingLabel:"1 burger",    halal:true },
  { id:"rm2",restaurant:"RocoMamas",      name:"Chicken Burger",          calories:580, protein:32, carbs:46, fat:26, fiber:2,   sugar:6,  sodium:980, servingG:260, servingLabel:"1 burger",    halal:true },
  { id:"rm3",restaurant:"RocoMamas",      name:"Smash Fries",             calories:420, protein:6,  carbs:52, fat:20, fiber:4,   sugar:1,  sodium:540, servingG:180, servingLabel:"1 portion",   halal:true },
  { id:"rm4",restaurant:"RocoMamas",      name:"Onion Rings",             calories:360, protein:5,  carbs:42, fat:18, fiber:2,   sugar:4,  sodium:480, servingG:150, servingLabel:"1 side",      halal:true },
  { id:"rm5",restaurant:"RocoMamas",      name:"Mac Daddy Burger",        calories:760, protein:44, carbs:52, fat:42, fiber:3,   sugar:9,  sodium:1240,servingG:340, servingLabel:"1 burger",    halal:true },
  { id:"rm6",restaurant:"RocoMamas",      name:"Chicken Wings (6 pcs)",   calories:380, protein:32, carbs:12, fat:24, fiber:0,   sugar:2,  sodium:740, servingG:240, servingLabel:"6 wings",     halal:true },
  { id:"rm7",restaurant:"RocoMamas",      name:"Chicken Wings (12 pcs)",  calories:760, protein:64, carbs:24, fat:48, fiber:0,   sugar:4,  sodium:1480,servingG:480, servingLabel:"12 wings",    halal:true },
  { id:"rm8",restaurant:"RocoMamas",      name:"Beef Ribs (half rack)",   calories:680, protein:48, carbs:8,  fat:52, fiber:0,   sugar:4,  sodium:980, servingG:380, servingLabel:"half rack",   halal:true },
  { id:"rm9",restaurant:"RocoMamas",      name:"Beef Ribs (full rack)",   calories:1360,protein:96, carbs:16, fat:104,fiber:0,   sugar:8,  sodium:1960,servingG:760, servingLabel:"full rack",   halal:true },
  { id:"ch1",restaurant:"Checkers",       name:"Rotisserie Chicken (half)",calories:580,protein:62, carbs:0,  fat:36, fiber:0,   sugar:0,  sodium:980, servingG:400, servingLabel:"half chicken", halal:true },
  { id:"ch2",restaurant:"Checkers",       name:"Freshly Baked Roll",      calories:180, protein:5,  carbs:36, fat:2,  fiber:1.5, sugar:2,  sodium:280, servingG:65,  servingLabel:"1 roll",      halal:true },
  { id:"ch3",restaurant:"Checkers",       name:"Deli Coleslaw",           calories:175, protein:1,  carbs:11, fat:14, fiber:1.5, sugar:9,  sodium:260, servingG:100, servingLabel:"100g",        halal:true },
  { id:"ch4",restaurant:"Checkers",       name:"Deli Pasta Salad",        calories:185, protein:4,  carbs:22, fat:9,  fiber:1.5, sugar:3,  sodium:320, servingG:100, servingLabel:"100g",        halal:true },
  { id:"ch5",restaurant:"Checkers",       name:"Chicken Wrap (deli)",     calories:420, protein:28, carbs:42, fat:14, fiber:2,   sugar:4,  sodium:640, servingG:220, servingLabel:"1 wrap",      halal:true },
  { id:"ch6",restaurant:"Checkers",       name:"Sushi (8-piece salmon)",  calories:280, protein:12, carbs:48, fat:4,  fiber:1,   sugar:4,  sodium:560, servingG:160, servingLabel:"8 pieces",    halal:true },
  { id:"ch7",restaurant:"Checkers",       name:"Yoghurt (plain, low fat)",calories:65,  protein:4,  carbs:7,  fat:2.5,fiber:0,   sugar:6,  sodium:55,  servingG:100, servingLabel:"100g",        halal:true },
  { id:"ch8",restaurant:"Checkers",       name:"Cheese & Onion Vetkoek",  calories:310, protein:7,  carbs:38, fat:14, fiber:1,   sugar:2,  sodium:380, servingG:100, servingLabel:"1 vetkoek",   halal:true },
  { id:"ch9",restaurant:"Checkers",       name:"Chicken Breast Fillets",  calories:165, protein:31, carbs:0,  fat:3.6,fiber:0,   sugar:0,  sodium:74,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ch10",restaurant:"Checkers",      name:"Lean Beef Mince",         calories:175, protein:20, carbs:0,  fat:10, fiber:0,   sugar:0,  sodium:70,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ch11",restaurant:"Checkers",      name:"Lamb Chops",              calories:245, protein:20, carbs:0,  fat:18, fiber:0,   sugar:0,  sodium:65,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ch12",restaurant:"Checkers",      name:"Full Cream Milk",         calories:165, protein:8.5,carbs:12, fat:9,  fiber:0,   sugar:12, sodium:105, servingG:250, servingLabel:"1 glass (250ml)",halal:true },
  { id:"ch13",restaurant:"Checkers",      name:"Cheddar Cheese",          calories:395, protein:24, carbs:0,  fat:33, fiber:0,   sugar:0,  sodium:620, servingG:100, servingLabel:"100g",        halal:true },
  { id:"ch14",restaurant:"Checkers",      name:"Large Eggs (per egg)",    calories:78,  protein:6.3,carbs:0.6,fat:5.3,fiber:0,   sugar:0,  sodium:62,  servingG:50,  servingLabel:"1 egg",       halal:true },
  { id:"ch15",restaurant:"Checkers",      name:"White Bread (sliced)",    calories:90,  protein:3,  carbs:18, fat:1,  fiber:0.8, sugar:2,  sodium:160, servingG:36,  servingLabel:"1 slice",     halal:true },
  { id:"ch16",restaurant:"Checkers",      name:"Butter (salted)",         calories:74,  protein:0.1,carbs:0,  fat:8.3,fiber:0,   sugar:0,  sodium:82,  servingG:10,  servingLabel:"1 tsp (10g)", halal:true },
  { id:"ww1",restaurant:"Woolworths",     name:"Rotisserie Chicken (half)",calories:560,protein:65, carbs:0,  fat:32, fiber:0,   sugar:0,  sodium:920, servingG:400, servingLabel:"half chicken", halal:true },
  { id:"ww2",restaurant:"Woolworths",     name:"Biltong (original)",      calories:270, protein:53, carbs:1,  fat:5,  fiber:0,   sugar:0,  sodium:1400,servingG:100, servingLabel:"100g",        halal:true },
  { id:"ww3",restaurant:"Woolworths",     name:"Chicken Wrap (deli)",     calories:380, protein:26, carbs:40, fat:12, fiber:3,   sugar:5,  sodium:600, servingG:220, servingLabel:"1 wrap",      halal:true },
  { id:"ww4",restaurant:"Woolworths",     name:"Butter Chicken (ready meal)",calories:380,protein:28,carbs:36,fat:14, fiber:2,   sugar:8,  sodium:760, servingG:300, servingLabel:"1 serving",   halal:true },
  { id:"ww5",restaurant:"Woolworths",     name:"Double Cream Yoghurt",    calories:120, protein:3,  carbs:8,  fat:8,  fiber:0,   sugar:7,  sodium:50,  servingG:100, servingLabel:"100g",        halal:true },
  { id:"ww6",restaurant:"Woolworths",     name:"Grilled Chicken Salad",   calories:280, protein:28, carbs:12, fat:14, fiber:3,   sugar:6,  sodium:520, servingG:250, servingLabel:"1 salad",     halal:true },
  { id:"ww7",restaurant:"Woolworths",     name:"Milk Tart slice",         calories:230, protein:5,  carbs:34, fat:8,  fiber:0.5, sugar:20, sodium:160, servingG:100, servingLabel:"1 slice",     halal:true },
  { id:"ww8",restaurant:"Woolworths",     name:"Sushi (8-piece)",         calories:260, protein:10, carbs:46, fat:3,  fiber:1,   sugar:4,  sodium:540, servingG:160, servingLabel:"8 pieces",    halal:true },
  { id:"ww9",restaurant:"Woolworths",     name:"Chicken Breast Fillets",  calories:165, protein:31, carbs:0,  fat:3.6,fiber:0,   sugar:0,  sodium:74,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ww10",restaurant:"Woolworths",    name:"Lean Beef Mince",         calories:175, protein:20, carbs:0,  fat:10, fiber:0,   sugar:0,  sodium:70,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ww11",restaurant:"Woolworths",    name:"Lamb Chops",              calories:245, protein:20, carbs:0,  fat:18, fiber:0,   sugar:0,  sodium:65,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"ww12",restaurant:"Woolworths",    name:"Full Cream Milk",         calories:165, protein:8.5,carbs:12, fat:9,  fiber:0,   sugar:12, sodium:105, servingG:250, servingLabel:"1 glass (250ml)",halal:true },
  { id:"ww13",restaurant:"Woolworths",    name:"Mature Cheddar",          calories:410, protein:25, carbs:0,  fat:34, fiber:0,   sugar:0,  sodium:640, servingG:100, servingLabel:"100g",        halal:true },
  { id:"ww14",restaurant:"Woolworths",    name:"Free Range Eggs (per egg)",calories:78, protein:6.3,carbs:0.6,fat:5.3,fiber:0,   sugar:0,  sodium:62,  servingG:50,  servingLabel:"1 egg",       halal:true },
  { id:"ww15",restaurant:"Woolworths",    name:"Sliced White Bread",      calories:90,  protein:3,  carbs:18, fat:1,  fiber:0.8, sugar:2,  sodium:155, servingG:36,  servingLabel:"1 slice",     halal:true },
  { id:"ww16",restaurant:"Woolworths",    name:"Hummus (original)",       calories:230, protein:7,  carbs:14, fat:17, fiber:4,   sugar:2,  sodium:380, servingG:100, servingLabel:"100g",        halal:true },
  { id:"ww17",restaurant:"Woolworths",    name:"Butter (salted)",         calories:74,  protein:0.1,carbs:0,  fat:8.3,fiber:0,   sugar:0,  sodium:82,  servingG:10,  servingLabel:"1 tsp (10g)", halal:true },
  { id:"ww18",restaurant:"Woolworths",    name:"Salmon Fillet",           calories:208, protein:20, carbs:0,  fat:13, fiber:0,   sugar:0,  sodium:59,  servingG:100, servingLabel:"100g (raw)",  halal:true },
  { id:"pp1",restaurant:"Pick n Pay",     name:"Rotisserie Chicken (half)",calories:570,protein:60, carbs:0,  fat:35, fiber:0,   sugar:0,  sodium:950, servingG:400, servingLabel:"half chicken", halal:true },
  { id:"pp2",restaurant:"Pick n Pay",     name:"PnP Biltong",             calories:265, protein:52, carbs:1,  fat:5,  fiber:0,   sugar:0,  sodium:1380,servingG:100, servingLabel:"100g",        halal:true },
  { id:"pp3",restaurant:"Pick n Pay",     name:"Deli Chicken Wrap",       calories:410, protein:27, carbs:41, fat:14, fiber:2,   sugar:4,  sodium:620, servingG:220, servingLabel:"1 wrap",      halal:true },
  { id:"pp4",restaurant:"Pick n Pay",     name:"Yoghurt (plain)",         calories:68,  protein:4,  carbs:7,  fat:2.5,fiber:0,   sugar:6,  sodium:55,  servingG:100, servingLabel:"100g",        halal:true },
  { id:"pp5",restaurant:"Pick n Pay",     name:"Pasta Salad (deli)",      calories:180, protein:4,  carbs:21, fat:9,  fiber:1.5, sugar:3,  sodium:310, servingG:100, servingLabel:"100g",        halal:true },
  { id:"pp6",restaurant:"Pick n Pay",     name:"Freshly Baked Bread Roll", calories:175,protein:5,  carbs:35, fat:2,  fiber:1.5, sugar:2,  sodium:270, servingG:65,  servingLabel:"1 roll",      halal:true },
  { id:"pp7",restaurant:"Pick n Pay",     name:"Cheddar Cheese",          calories:410, protein:25, carbs:0,  fat:34, fiber:0,   sugar:0,  sodium:620, servingG:100, servingLabel:"100g",        halal:true },
  { id:"pp11",restaurant:"Pick n Pay",    name:"Full Cream Milk",         calories:165, protein:8.5,carbs:12, fat:9,  fiber:0,   sugar:12, sodium:105, servingG:250, servingLabel:"1 glass (250ml)",halal:true },
  { id:"pp12",restaurant:"Pick n Pay",    name:"Free Range Eggs (per egg)",calories:78, protein:6.3,carbs:0.6,fat:5.3,fiber:0,   sugar:0,  sodium:62,  servingG:50,  servingLabel:"1 egg",       halal:true },
  { id:"pp13",restaurant:"Pick n Pay",    name:"White Bread (sliced)",    calories:90,  protein:3,  carbs:18, fat:1,  fiber:0.8, sugar:2,  sodium:160, servingG:36,  servingLabel:"1 slice",     halal:true },
  { id:"pp14",restaurant:"Pick n Pay",    name:"Butter (salted)",         calories:74,  protein:0.1,carbs:0,  fat:8.3,fiber:0,   sugar:0,  sodium:82,  servingG:10,  servingLabel:"1 tsp (10g)", halal:true },
  // Simply Asia
  { id:"sa514c",restaurant:"Simply Asia", name:"514 Cashew Nut Noodles (Chicken)",  calories:970, protein:46, carbs:43, fat:99, fiber:3, sugar:6,  sodium:1240, servingG:450, servingLabel:"1 serving",  halal:true },
  { id:"sa514p",restaurant:"Simply Asia", name:"514 Cashew Nut Noodles (Prawn)",    calories:920, protein:42, carbs:43, fat:90, fiber:3, sugar:6,  sodium:1180, servingG:450, servingLabel:"1 serving",  halal:true },
  { id:"sa505c",restaurant:"Simply Asia", name:"505 Sweet Soya Noodles (Chicken)",  calories:882, protein:47, carbs:40, fat:73, fiber:2, sugar:8,  sodium:1100, servingG:420, servingLabel:"1 serving",  halal:true },
  { id:"sa505p",restaurant:"Simply Asia", name:"505 Sweet Soya Noodles (Prawn)",    calories:840, protein:44, carbs:40, fat:66, fiber:2, sugar:8,  sodium:1040, servingG:420, servingLabel:"1 serving",  halal:true },
  { id:"sa715",  restaurant:"Simply Asia", name:"715 Kids Sweet Soy Noodles",       calories:441, protein:24, carbs:20, fat:36, fiber:1, sugar:4,  sodium:550,  servingG:210, servingLabel:"kids portion",halal:true },

  // ── Bird & Co (SA chicken + wood-fired pizza chain, halaal-certified) ──
  // Chicken
  { id:"bc1",  restaurant:"Bird & Co", name:"Deboned Half Chicken (Secret Sauce)",            calories:560,  protein:62, carbs:8,   fat:30, fiber:0, sugar:5,  sodium:780,  servingG:280, servingLabel:"1/2 chicken",         halal:true },
  { id:"bc2",  restaurant:"Bird & Co", name:"Deboned Full Chicken (Secret Sauce)",            calories:1120, protein:124,carbs:16,  fat:60, fiber:0, sugar:10, sodium:1560, servingG:560, servingLabel:"1 chicken",           halal:true },
  { id:"bc3",  restaurant:"Bird & Co", name:"Deboned Full Chicken + 2 Sides + 4 Rolls",       calories:1980, protein:148,carbs:170, fat:92, fiber:8, sugar:14, sodium:2400, servingG:1100,servingLabel:"feast for 2-3",       halal:true },
  { id:"bc4",  restaurant:"Bird & Co", name:"Bone-in Leg Quarter",                            calories:390,  protein:40, carbs:6,   fat:23, fiber:0, sugar:3,  sodium:560,  servingG:280, servingLabel:"1 leg quarter",       halal:true },
  { id:"bc5",  restaurant:"Bird & Co", name:"Two Deboned Chicken Thighs (Skewered) + Side",   calories:680,  protein:46, carbs:48,  fat:34, fiber:4, sugar:3,  sodium:880,  servingG:380, servingLabel:"2 thighs + side",     halal:true },
  { id:"bc6",  restaurant:"Bird & Co", name:"Chicken Fillet Strips + Chips",                  calories:710,  protein:42, carbs:56,  fat:32, fiber:4, sugar:2,  sodium:920,  servingG:380, servingLabel:"strips + chips",      halal:true },
  { id:"bc7",  restaurant:"Bird & Co", name:"Chicken Winglets (Basted)",                      calories:640,  protein:54, carbs:8,   fat:42, fiber:0, sugar:5,  sodium:980,  servingG:260, servingLabel:"~8 wings",            halal:true },
  // Burgers
  { id:"bc8",  restaurant:"Bird & Co", name:"Regular Chicken Burger + Side",                  calories:880,  protein:42, carbs:92,  fat:38, fiber:5, sugar:7,  sodium:1240, servingG:430, servingLabel:"burger + chips",      halal:true },
  { id:"bc9",  restaurant:"Bird & Co", name:"Battered Chicken Breast Burger (Slaw) + Side",   calories:920,  protein:40, carbs:96,  fat:42, fiber:5, sugar:9,  sodium:1320, servingG:450, servingLabel:"burger + chips",      halal:true },
  { id:"bc10", restaurant:"Bird & Co", name:"Chicken Burger (Bacon, Peri Mayo, Avo, Feta) + Side", calories:1080, protein:52, carbs:88, fat:56, fiber:6, sugar:7,  sodium:1480, servingG:480, servingLabel:"burger + chips", halal:true },
  // Wraps
  { id:"bc11", restaurant:"Bird & Co", name:"Grilled Chicken Wrap (Avo, Peppadew, Sweet Chilli Mayo)", calories:560, protein:38, carbs:52, fat:22, fiber:5, sugar:8, sodium:920, servingG:290, servingLabel:"1 wrap",      halal:true },
  { id:"bc12", restaurant:"Bird & Co", name:"Grilled Halloumi Wrap",                          calories:520,  protein:22, carbs:54,  fat:24, fiber:4, sugar:8,  sodium:1040, servingG:280, servingLabel:"1 wrap",              halal:true },
  // Salads
  { id:"bc13", restaurant:"Bird & Co", name:"B&C's Famous Chicken Salad",                     calories:420,  protein:38, carbs:18,  fat:24, fiber:6, sugar:7,  sodium:680,  servingG:380, servingLabel:"1 salad",             halal:true },
  // Sides
  { id:"bc14", restaurant:"Bird & Co", name:"Chips (Regular)",                                calories:380,  protein:5,  carbs:50,  fat:18, fiber:4, sugar:1,  sodium:520,  servingG:160, servingLabel:"regular side",        halal:true },
  { id:"bc15", restaurant:"Bird & Co", name:"Coleslaw",                                       calories:160,  protein:1.5,carbs:14,  fat:11, fiber:2, sugar:10, sodium:220,  servingG:120, servingLabel:"1 side",              halal:true },
  { id:"bc16", restaurant:"Bird & Co", name:"Garden Salad",                                   calories:80,   protein:2,  carbs:10,  fat:3,  fiber:3, sugar:4,  sodium:140,  servingG:160, servingLabel:"1 side",              halal:true },
  { id:"bc17", restaurant:"Bird & Co", name:"Bread Roll",                                     calories:160,  protein:5,  carbs:30,  fat:2,  fiber:1, sugar:2,  sodium:280,  servingG:70,  servingLabel:"1 roll",              halal:true },
  // Wood-fired Pizza (whole pizza ~30cm thin crispy base)
  { id:"bc18", restaurant:"Bird & Co", name:"Margherita Pizza (Wood-Fired)",                  calories:880,  protein:34, carbs:108, fat:32, fiber:6, sugar:9,  sodium:1480, servingG:380, servingLabel:"1 pizza",             halal:true },
  { id:"bc19", restaurant:"Bird & Co", name:"Hawaiian Pizza (Chicken & Pineapple)",           calories:1020, protein:54, carbs:108, fat:38, fiber:6, sugar:14, sodium:1740, servingG:480, servingLabel:"1 pizza",             halal:true },
  { id:"bc20", restaurant:"Bird & Co", name:"Chicken & Mushroom Pizza",                       calories:1000, protein:58, carbs:104, fat:36, fiber:6, sugar:8,  sodium:1700, servingG:480, servingLabel:"1 pizza",             halal:true },
  { id:"bc21", restaurant:"Bird & Co", name:"BBQ Chicken Pizza",                              calories:1060, protein:54, carbs:120, fat:36, fiber:6, sugar:18, sodium:1880, servingG:500, servingLabel:"1 pizza",             halal:true },
  { id:"bc22", restaurant:"Bird & Co", name:"Peri-Peri Chicken Pizza",                        calories:1020, protein:54, carbs:108, fat:38, fiber:6, sugar:9,  sodium:1820, servingG:480, servingLabel:"1 pizza",             halal:true },
];

// ─── Vitals cluster — module-level so function refs are stable ───────
export const VitalsCtx = createContext(null);

function _cwArc(cx, cy, r, startDeg, endDeg) {
  const _pt2 = (deg) => { const rad = deg * Math.PI / 180; return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }; };
  const s = _pt2(startDeg), e = _pt2(endDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}
function _pt(cx, cy, r, deg) {
  const rad = deg * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function fmtMinsV(m) {
  if (!m) return "—";
  const h = Math.floor(m / 60), min = m % 60;
  if (h > 0 && min > 0) return `${h}h ${min}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
function useAnimatedValue(target, dur, delay) {
  const [v, setV] = useState(0);
  useEffect(() => {
    setV(0);
    let raf, startTs;
    const t = setTimeout(() => {
      const step = (ts) => {
        if (startTs === undefined) startTs = ts;
        const progress = Math.min(1, (ts - startTs) / (dur || 900));
        const eased = 1 - Math.pow(1 - progress, 3);
        setV((target || 0) * eased);
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay || 0);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return v;
}
function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = e => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}
// Animate 0→pct on mount, then jump directly on subsequent changes.
// Mirrors HayahToday's useRingFill so the GlowRing entrance animation
// only fires once per mount, not on every data update.
function useRingFillV(pct, delay = 300, dur = 1500) {
  const [f, setF] = useState(0);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) { setF(pct); return; }
    mountedRef.current = true;
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setF((1 - Math.pow(1 - p, 3)) * pct);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); if (raf) cancelAnimationFrame(raf); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pct]);
  return f;
}
function _hhmmToMins(s) {
  if (!s || typeof s !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return null;
  return parseInt(m[1],10)*60 + parseInt(m[2],10);
}
function AnimatedNumber({ value, dur = 950, delay = 0, decimals = 0, format }) {
  const v = useAnimatedValue(value || 0, dur, delay);
  if (typeof format === "function") return format(v);
  if (decimals > 0) return (Math.round(v * Math.pow(10, decimals)) / Math.pow(10, decimals)).toFixed(decimals);
  return Math.round(v).toLocaleString();
}
function AnimatedKcalBar({ kcal, x, width = 28, maxBar, label, goalKcal, colors, delay, onTap, tapped, showLabel = true, showValue = true, hitWidth }) {
  const animK = useAnimatedValue(kcal || 0, 950, delay);
  const h = (animK / maxBar) * 100;
  const y = 130 - h;
  const overGoal = kcal > goalKcal;
  const fill = kcal === 0 ? colors.border : (overGoal ? colors.danger : colors.gold);
  const hitW = hitWidth || (width + 10);
  const hitX = x + width / 2 - hitW / 2;
  return (
    <g onClick={onTap} style={{ cursor: onTap ? 'pointer' : 'default', touchAction:'manipulation' }}>
      {onTap && <rect x={hitX} y={28} width={hitW} height={130 - 28} fill="transparent"/>}
      <rect x={x} y={y} width={width} height={Math.max(0, h)} rx={Math.min(3, width/2)} fill={fill} stroke={tapped ? colors.text : 'none'} strokeWidth={tapped ? 1.5 : 0}/>
      {showLabel && <text x={x + width / 2} y="148" fontSize="9" fill={tapped ? colors.text : colors.muted} textAnchor="middle" fontWeight={tapped ? 800 : 600}>{label}</text>}
      {(showValue || tapped) && animK > 0.5 && <text x={x + width / 2} y={y - 3} fontSize="8" fill={colors.text} textAnchor="middle" fontWeight="700">{Math.round(animK)}</text>}
    </g>
  );
}
function AnimatedMacroBar({ p, c, f, x, width = 28, maxK, label, colors, delay, onTap, tapped, showLabel = true, showValue = true, hitWidth }) {
  const animP = useAnimatedValue(p || 0, 950, delay);
  const animC = useAnimatedValue(c || 0, 950, delay);
  const animF = useAnimatedValue(f || 0, 950, delay);
  const yBase = 140;
  const pH = (animP * 4 / maxK) * 120;
  const cH = (animC * 4 / maxK) * 120;
  const fH = (animF * 9 / maxK) * 120;
  const total = p * 4 + c * 4 + f * 9;
  const animTotal = animP * 4 + animC * 4 + animF * 9;
  const totalH = (animTotal / maxK) * 120;
  const hitW = hitWidth || (width + 10);
  const hitX = x + width / 2 - hitW / 2;
  return (
    <g onClick={onTap} style={{ cursor: onTap ? 'pointer' : 'default', touchAction:'manipulation' }}>
      {onTap && <rect x={hitX} y={20} width={hitW} height={140 - 20} fill="transparent"/>}
      <rect x={x} y={yBase - fH} width={width} height={Math.max(0, fH)} fill={colors.accent}/>
      <rect x={x} y={yBase - fH - cH} width={width} height={Math.max(0, cH)} fill={colors.teal}/>
      <rect x={x} y={yBase - fH - cH - pH} width={width} height={Math.max(0, pH)} fill={colors.gold}/>
      {tapped && <rect x={x-1} y={yBase - fH - cH - pH - 1} width={width+2} height={Math.max(0, fH+cH+pH)+2} fill="none" stroke={colors.text} strokeWidth="1"/>}
      {showLabel && <text x={x + width / 2} y="158" fontSize="9" fill={tapped ? colors.text : colors.muted} textAnchor="middle" fontWeight={tapped ? 800 : 600}>{label}</text>}
      {(showValue || tapped) && total > 0 && <text x={x + width / 2} y={yBase - totalH - 3} fontSize="8" fill={colors.text} textAnchor="middle" fontWeight="700">{Math.round(animTotal)}</text>}
    </g>
  );
}
function AnimatedProgressBar({ value, max, color, bg, height = 5, dur = 950, delay = 0 }) {
  const v = useAnimatedValue(value || 0, dur, delay);
  const pct = Math.min((v / (max || 1)) * 100, 100);
  return (
    <div style={{ height, borderRadius: height, background: bg, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: height }}/>
    </div>
  );
}

// ─── HAYAH-TODAY PALETTE (shared with HayahToday.js) ─────────────────────────
// These tokens must stay in sync with the C/FS/FM constants in HayahToday.js.
const VH = {
  ink:      '#f4eee0',
  dim:      'rgba(244,238,224,0.62)',
  faint:    'rgba(244,238,224,0.55)',
  amber:    '#f5b342',
  teal:     '#5fd1b9',
  coral:    '#f08a6b',
  cream:    '#e9dfc7',
  green:    '#00e676',
  deepBlue: '#6ea8ff',
  panel:    'rgba(255,255,255,0.03)',
  panelBdr: 'rgba(255,255,255,0.07)',
};
const VH_FS = "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const VH_FM = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

// Full-circle glow ring matching HayahToday's GlowRing component.
function VGlowRing({ value, max, color, size = 140, sw = 10, ticks = false,
  glowBlur = 10, centerNode, delay = 300, uid = 'vgr' }) {
  const r = (size - sw) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const rawPct = Math.min(value / (max || 1), 1);
  const fill = useRingFillV(rawPct, delay) * circ;
  const endA = (fill / circ) * 2 * Math.PI - Math.PI / 2;
  const ex = cx + Math.cos(endA) * r, ey = cy + Math.sin(endA) * r;
  const tr = 'stroke-dasharray 1.5s cubic-bezier(0.25,0.46,0.45,0.94)';
  const kf = `@keyframes vgbg-${uid}{0%,100%{filter:drop-shadow(0 0 ${glowBlur}px ${color})}50%{filter:drop-shadow(0 0 ${glowBlur * 2.2}px ${color})}}`;
  const tickEls = [];
  if (ticks) for (let i = 0; i < 36; i++) {
    const a = (i / 36) * 2 * Math.PI - Math.PI / 2, big = i % 9 === 0;
    const r1 = r + sw / 2 + 3, r2 = r + sw / 2 + (big ? 8 : 5);
    tickEls.push(<line key={i}
      x1={cx + Math.cos(a) * r1} y1={cy + Math.sin(a) * r1}
      x2={cx + Math.cos(a) * r2} y2={cy + Math.sin(a) * r2}
      stroke={big ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)'}
      strokeWidth={big ? 1.4 : 0.7} />);
  }
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <style>{kf}</style>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: 'visible', transform: 'rotate(-90deg)' }}>
        {tickEls}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color}
          strokeWidth={sw + glowBlur * 2} strokeDasharray={`${fill} ${circ}`}
          style={{ filter: `blur(${glowBlur}px)`, opacity: 0.2, transition: tr }} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeLinecap="round" strokeDasharray={`${fill} ${circ}`}
          style={{ animation: `vgbg-${uid} 3.2s ease-in-out infinite`, transition: tr }} />
        {fill > 8 && <circle cx={ex} cy={ey} r={sw / 2 + 1.5} fill={color}
          style={{ filter: `drop-shadow(0 0 6px ${color})` }} />}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {centerNode}
      </div>
    </div>
  );
}

function VitalsScanline() {
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:1, overflow:'hidden', borderRadius:18 }}>
      <div style={{ position:'absolute', left:0, right:0, height:2, background:'linear-gradient(90deg, transparent 0%, #00e676 50%, transparent 100%)', opacity:0.4, animation:'vitalsScan 1100ms cubic-bezier(0.22,0.61,0.36,1) forwards' }}/>
      <style>{`@keyframes vitalsScan{0%{top:0;opacity:0}10%{opacity:0.7}90%{opacity:0.5}100%{top:100%;opacity:0}}@keyframes barRise{0%{transform:scaleY(0);opacity:0}100%{transform:scaleY(1)}}@keyframes caloriesHaloOut{0%{opacity:0.85;transform:scale(0.45)}50%{opacity:0.55}100%{opacity:0;transform:scale(2.4)}}@keyframes caloriesRayBurst{0%{opacity:0;stroke-dasharray:0 7;stroke-dashoffset:0}30%{opacity:1;stroke-dasharray:7 0}100%{opacity:0;stroke-dasharray:7 0;transform:translateZ(0)}}@keyframes ghidhaPulseFade{0%,100%{opacity:0.35;transform:scale(0.85)}50%{opacity:1;transform:scale(1)}} @keyframes ghidhaSyncSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes ghidha-edit-slide{0%{opacity:0;transform:translateY(-6px) scaleY(0.92);transform-origin:top}100%{opacity:1;transform:translateY(0) scaleY(1);transform-origin:top}}@keyframes macroCardIn{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
function VitalsGridLines() {
  return (
    <>
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:0, background:'radial-gradient(ellipse at 50% -5%, rgba(0,230,118,0.09), transparent 55%)' }}/>
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', zIndex:1, backgroundImage:'repeating-linear-gradient(0deg, rgba(0,230,118,0.022) 0 1px, transparent 1px 4px)' }}/>
    </>
  );
}
function relativeSyncLabel(ts) {
  if (!ts) return null;
  const diff = Math.max(0, Date.now() - ts);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "JUST NOW";
  if (mins < 60) return `${mins}M AGO`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}H AGO`;
  const days = Math.floor(hrs / 24);
  return `${days}D AGO`;
}
function VitalsClusterHeader() {
  // eslint-disable-next-line no-unused-vars
  const { healthAvailable, healthPulling, lastHealthSyncAt, syncTick, triggerHealthSync, openShareCard } = useContext(VitalsCtx);
  const syncLabel = relativeSyncLabel(lastHealthSyncAt);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'0 2px' }}>
      <div style={{ width:14, height:1, background:VH.green, opacity:0.5 }}/>
      <span style={{ fontFamily:VH_FM, fontSize:9.5, color:VH.green, letterSpacing:'1.8px', textTransform:'uppercase', opacity:0.8 }}>01 · ENERGY BALANCE</span>
      <div style={{ flex:1, height:1, background:VH.panelBdr }}/>
      {healthAvailable && triggerHealthSync ? (
        <button onClick={triggerHealthSync} disabled={healthPulling}
          style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 7px', borderRadius:999,
            border:`0.5px solid ${healthPulling ? VH.teal : VH.panelBdr}`,
            background: healthPulling ? `${VH.teal}1f` : 'transparent',
            color: healthPulling ? VH.teal : VH.faint, cursor:healthPulling?'wait':'pointer', fontFamily:'inherit',
          }}>
          <svg width="9" height="9" viewBox="0 0 16 16" fill="none"
            style={healthPulling ? { animation:'ghidhaSyncSpin 900ms linear infinite', transformOrigin:'center' } : {}}>
            <path d="M13 4.5A6 6 0 1 0 14 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/>
            <path d="M14.5 2v3h-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <span style={{ fontFamily:VH_FM, fontSize:8, letterSpacing:'1.2px' }}>{healthPulling ? 'SYNCING' : (syncLabel || 'SYNC')}</span>
        </button>
      ) : (
        <span style={{ fontFamily:VH_FM, fontSize:8, color:VH.faint, letterSpacing:'1px' }}>LIVE</span>
      )}
      {openShareCard && (
        <button onClick={openShareCard} style={{ width:22, height:22, borderRadius:6, border:`0.5px solid ${VH.panelBdr}`, background:'transparent', color:VH.amber, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, fontFamily:'inherit' }}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M6 8V1.5M6 1.5L3.5 4M6 1.5L8.5 4M2 7v2.5A1.5 1.5 0 003.5 11h5A1.5 1.5 0 0010 9.5V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}
function VitalsSectionLabel({ num, label, right, color = VH.green }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
      <div style={{ width:14, height:1, background:color, opacity:0.5 }}/>
      <span style={{ fontFamily:VH_FM, fontSize:9.5, color, letterSpacing:'1.8px', textTransform:'uppercase', opacity:0.8 }}>{num} · {label}</span>
      <div style={{ flex:1, height:1, background:VH.panelBdr }}/>
      {right && <span style={{ fontFamily:VH_FM, fontSize:8.5, color:VH.faint }}>{right}</span>}
    </div>
  );
}
function HeroRing_UNUSED() {
  const { darkMode, gold, text, muted, ink4, lineSoft, danger, totalCalories, goals, compactVitals } = useContext(VitalsCtx);
  const pct = Math.min(1, totalCalories / (goals.calories || 1));
  const remaining = goals.calories - Math.round(totalCalories);
  const animPct    = useAnimatedValue(pct, 1100, 80);
  const animIntake = useAnimatedValue(totalCalories, 1100, 80);
  const size = compactVitals ? 148 : 192, stroke = 10, r = (size - stroke) / 2 - 6, cx = size / 2, cy = size / 2;
  const C = 2 * Math.PI * r;
  const ringColor = remaining < 0 ? danger : gold;
  const ticks = [];
  for (let i = 0; i < 72; i++) {
    const angle = (i / 72) * 360 - 90, major = i % 6 === 0;
    const inner = r + stroke / 2 + 3, outer = inner + (major ? 5 : 2.5);
    const a1 = _pt(cx, cy, inner, angle), a2 = _pt(cx, cy, outer, angle);
    ticks.push(<line key={i} x1={a1.x} y1={a1.y} x2={a2.x} y2={a2.y} stroke={major ? ink4 : lineSoft} strokeWidth={major ? 0.9 : 0.5}/>);
  }
  const dot = _pt(cx, cy, r, -90 + animPct * 360);
  // Fire a sparkle once when the ring's fill animation finishes (1100ms dur + 80ms delay).
  // Re-keys whenever pct changes so a fresh log re-triggers the burst.
  const [sparkleKey, setSparkleKey] = useState(0);
  useEffect(() => {
    if (pct < 0.01) return;
    const t = setTimeout(() => setSparkleKey(k => k + 1), 1180);
    return () => clearTimeout(t);
  }, [pct]);
  const sparkleAngles = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <div style={{ position:'relative', width:size, height:size }}>
      <svg width={size} height={size} style={{ overflow:'visible' }}>
        {ticks}
        <circle cx={cx} cy={cy} r={r} stroke={darkMode?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)'} strokeWidth={stroke} fill="none"/>
        <circle cx={cx} cy={cy} r={r} stroke={ringColor} strokeWidth={stroke} fill="none" strokeDasharray={`${C*animPct} ${C}`} strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}/>
        {animPct > 0.005 && (
          <>
            <circle cx={dot.x} cy={dot.y} r="6" fill="none" stroke={ringColor} strokeWidth="0.7" opacity="0.45"/>
            <circle cx={dot.x} cy={dot.y} r="4.5" fill={ringColor} style={{ filter:`drop-shadow(0 0 9px ${ringColor})` }}/>
            <circle cx={dot.x} cy={dot.y} r="2" fill={darkMode ? '#fff' : '#fff'} opacity="0.85"/>
          </>
        )}
        {sparkleKey > 0 && pct > 0.01 && (
          <g key={sparkleKey} style={{ pointerEvents:'none' }}>
            <circle cx={dot.x} cy={dot.y} r="5" fill="none" stroke={ringColor} strokeWidth="1.4"
              style={{ animation:'caloriesHaloOut 950ms cubic-bezier(0.16,0.84,0.44,1) forwards', transformBox:'fill-box', transformOrigin:'center' }}/>
            <circle cx={dot.x} cy={dot.y} r="5" fill="none" stroke={ringColor} strokeWidth="1"
              style={{ animation:'caloriesHaloOut 1200ms cubic-bezier(0.16,0.84,0.44,1) 120ms forwards', transformBox:'fill-box', transformOrigin:'center' }}/>
            {sparkleAngles.map(angle => {
              const rad = angle * Math.PI / 180;
              const x1 = dot.x + Math.cos(rad) * 7;
              const y1 = dot.y + Math.sin(rad) * 7;
              const x2 = dot.x + Math.cos(rad) * 13;
              const y2 = dot.y + Math.sin(rad) * 13;
              const major = angle % 90 === 0;
              return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={ringColor} strokeWidth={major ? 1.2 : 0.7} strokeLinecap="round"
                style={{ animation:`caloriesRayBurst ${major ? 720 : 540}ms cubic-bezier(0.16,0.84,0.44,1) ${angle * 0.6}ms forwards` }}/>;
            })}
          </g>
        )}
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <div style={{ fontSize:compactVitals?8.5:10.5, color:ink4, letterSpacing:'0.16em', marginBottom:4, fontFamily:'monospace' }}>INTAKE · KCAL</div>
        <div style={{ fontSize:compactVitals?34:44, fontWeight:300, color:text, letterSpacing:'-0.04em', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{Math.round(animIntake).toLocaleString()}</div>
        <div style={{ fontSize:compactVitals?9.5:11, color:muted, marginTop:5, letterSpacing:'0.08em', fontFamily:'monospace' }}>{Math.round(animPct*100)}% / {goals.calories.toLocaleString()}</div>
      </div>
    </div>
  );
}
// Orbit-cluster calorie summary — three GlowRings (intake large, burned small,
// remaining small) connected by dashed SVG guide lines. Matches HayahToday's
// OrbitCluster layout exactly so the two screens feel like one design system.
function VitalsCalorieCluster({ onRingTap, onMetric }) {
  const { goals, totalCalories, totalBurned, danger, intel, trainingActive } = useContext(VitalsCtx);
  const remaining = Math.max(0, goals.calories - Math.round(totalCalories));
  const intake = Math.round(totalCalories);
  const burned = Math.round(totalBurned);
  const intakePct = goals.calories > 0 ? Math.round((intake / goals.calories) * 100) : 0;
  const kcalIn   = useAnimatedValue(intake,   1400, 300);
  const kcalBurn = useAnimatedValue(burned,   1400, 420);
  const kcalRem  = useAnimatedValue(remaining,1400, 540);
  // Orbit anchor coordinates (matches HayahToday OrbitCluster exactly)
  const W = 358, H = 182;
  const cx = 179, cy = 81, lx = 54, ly = 134, rx = 304, ry = 134;
  const tap = (key) => (e) => { e.stopPropagation(); try { hapticTap(); } catch {} onMetric && onMetric(key); };
  return (
    <div style={{ position:'relative', width:W, height:H, margin:'0 auto', maxWidth:'100%' }}>
      <svg style={{ position:'absolute', inset:0, overflow:'visible', pointerEvents:'none' }} width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <line x1={cx} y1={cy} x2={lx} y2={ly} stroke="rgba(0,230,118,0.2)" strokeWidth={1} strokeDasharray="4 6"/>
        <line x1={cx} y1={cy} x2={rx} y2={ry} stroke="rgba(245,179,66,0.2)" strokeWidth={1} strokeDasharray="4 6"/>
        <line x1={lx} y1={ly} x2={rx} y2={ry} stroke="rgba(255,255,255,0.07)" strokeWidth={1} strokeDasharray="5 9"/>
        <ellipse cx={cx} cy={cy} rx={76} ry={76} fill="none" stroke="rgba(0,230,118,0.04)" strokeWidth={1}/>
      </svg>

      {/* Streak chip — top-left of the cluster, under the 01 ENERGY BALANCE
          header. Pulled out of the VitalsIntel strip below so it isn't
          visually covered by the BURNED ring at the cluster's bottom-left. */}
      {intel && (
        <div onClick={onMetric ? tap('calories') : undefined} role={onMetric ? "button" : undefined} aria-label={intel.streak > 0 ? "Open streak detail" : "Start a streak"}
          style={{ position:'absolute', top:2, left:2, display:'flex', alignItems:'center', gap:6,
                   padding:'5px 11px 5px 9px', borderRadius:999,
                   background: intel.streak > 0 ? 'rgba(245,179,66,0.18)' : 'rgba(255,255,255,0.04)',
                   border: '0.5px solid ' + (intel.streak > 0 ? 'rgba(245,179,66,0.6)' : 'rgba(255,255,255,0.14)'),
                   boxShadow: intel.streak > 0 ? '0 0 14px rgba(245,179,66,0.22)' : 'none',
                   cursor:onMetric?'pointer':'default', touchAction:'manipulation', WebkitTapHighlightColor:'transparent', zIndex:3 }}>
          <span style={{ fontSize:13, lineHeight:1, opacity: intel.streak > 0 ? 1 : 0.45, filter: intel.streak > 0 ? 'none' : 'grayscale(1)' }}>🔥</span>
          <span style={{ fontFamily:VH_FM, fontSize:13, color: intel.streak > 0 ? VH.amber : VH.faint, fontWeight:700, fontVariantNumeric:'tabular-nums', letterSpacing:'-0.02em', lineHeight:1 }}>{intel.streak}</span>
          <span style={{ fontFamily:VH_FM, fontSize:9, color:VH.faint, letterSpacing:'1.2px', textTransform:'uppercase', lineHeight:1 }}>day{intel.streak===1?'':'s'}</span>
        </div>
      )}

      {/* Training-day badge — top-right, mirrors the streak chip. Only shows
          when the training-day override is active (enabled + workout today). */}
      {trainingActive && (
        <div aria-label="Training-day target active"
          style={{ position:'absolute', top:2, right:2, display:'flex', alignItems:'center', gap:5,
                   padding:'5px 10px 5px 8px', borderRadius:999,
                   background:'rgba(0,201,167,0.16)', border:'0.5px solid rgba(0,201,167,0.6)',
                   boxShadow:'0 0 14px rgba(0,201,167,0.20)', zIndex:3 }}>
          <span style={{ fontSize:11, lineHeight:1 }}>⚡</span>
          <span style={{ fontFamily:VH_FM, fontSize:9, color:VH.teal||'#00c9a7', fontWeight:800, letterSpacing:'1.2px', textTransform:'uppercase', lineHeight:1 }}>Training</span>
        </div>
      )}

      {/* Large intake ring (center) */}
      <div onClick={onRingTap} role={onRingTap?"button":undefined} aria-label="Show today's meals"
        style={{ position:'absolute', top:cy-75, left:cx-75, cursor:onRingTap?'pointer':'default', touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}>
        <VGlowRing value={intake} max={goals.calories} color={VH.amber} size={150} sw={10}
          ticks uid="vc-a" glowBlur={12} delay={300}
          centerNode={
            <div style={{ textAlign:'center' }}>
              <div style={{ fontFamily:VH_FM, fontSize:9, color:VH.faint, letterSpacing:'1.6px', textTransform:'uppercase' }}>INTAKE</div>
              <div style={{ fontFamily:VH_FM, fontSize:32, color:VH.amber, fontWeight:600, letterSpacing:'-0.6px', lineHeight:1, marginTop:2 }}>
                {Math.round(kcalIn).toLocaleString()}
              </div>
              <div style={{ fontFamily:VH_FM, fontSize:9, color:VH.dim, letterSpacing:'0.6px', marginTop:2 }}>{intakePct}% · {goals.calories.toLocaleString()}</div>
            </div>
          }/>
      </div>

      {/* Burned (bottom-left) */}
      <div onClick={onMetric?tap('burned'):undefined} role={onMetric?"button":undefined} aria-label="Open burned calories detail"
        style={{ position:'absolute', top:ly-42, left:lx-42, cursor:onMetric?'pointer':'default', touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}>
        <VGlowRing value={burned} max={goals.calories || 2000} color={VH.green} size={84} sw={6}
          uid="vc-b" glowBlur={9} delay={450}
          centerNode={
            <div style={{ textAlign:'center' }}>
              <div style={{ fontFamily:VH_FM, fontSize:7.5, color:VH.faint, letterSpacing:'1.2px', textTransform:'uppercase', lineHeight:1 }}>BURNED</div>
              <div style={{ fontFamily:VH_FM, fontSize:18, color:VH.ink, fontWeight:600, letterSpacing:'-0.3px', lineHeight:1.1, marginTop:2 }}>
                {Math.round(kcalBurn).toLocaleString()}
              </div>
              <div style={{ fontFamily:VH_FM, fontSize:7.5, color:VH.green, letterSpacing:'0.5px' }}>kcal</div>
            </div>
          }/>
      </div>

      {/* Remaining (bottom-right) */}
      <div onClick={onMetric?tap('remaining'):undefined} role={onMetric?"button":undefined} aria-label="Open remaining calories detail"
        style={{ position:'absolute', top:ry-42, left:rx-42, cursor:onMetric?'pointer':'default', touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}>
        <VGlowRing value={remaining} max={goals.calories || 2000} color={VH.coral} size={84} sw={6}
          uid="vc-c" glowBlur={9} delay={550}
          centerNode={
            <div style={{ textAlign:'center' }}>
              <div style={{ fontFamily:VH_FM, fontSize:7.5, color:VH.faint, letterSpacing:'1.2px', textTransform:'uppercase', lineHeight:1 }}>LEFT</div>
              <div style={{ fontFamily:VH_FM, fontSize:18, color:VH.ink, fontWeight:600, letterSpacing:'-0.3px', lineHeight:1.1, marginTop:2 }}>
                {Math.round(kcalRem).toLocaleString()}
              </div>
              <div style={{ fontFamily:VH_FM, fontSize:7.5, color:VH.coral, letterSpacing:'0.5px' }}>kcal</div>
            </div>
          }/>
      </div>
    </div>
  );
}
// Vertical LED-bar macro meter — matches HayahToday's LedMeter component so
// macro tiles look identical whether seen in the Calories tab or Hayah Today.
function VitalsMacroDial({ label, val, goal: macroGoal, color: barColor, macroKey, delay: dialDelay }) {
  const { danger } = useContext(VitalsCtx);
  const rawPct = val / (macroGoal || 1);
  const over = rawPct > 1;
  const seg = 12;
  const fillPct = useRingFillV(Math.min(rawPct, 1), dialDelay || 0, 1200);
  const disp    = useAnimatedValue(val, 1100, dialDelay || 0);
  const lit = Math.round(fillPct * seg);
  const effectiveColor = over ? danger : barColor;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
      <div style={{ fontFamily:VH_FM, fontSize:9, color:effectiveColor, textTransform:'uppercase', letterSpacing:'1.4px' }}>{label}</div>
      <div style={{ display:'flex', flexDirection:'column-reverse', gap:2.5 }}>
        {Array.from({ length: seg }).map((_, i) => {
          const on = i < lit, pI = i / seg;
          const bc = over ? danger : (pI > 0.75 ? '#ff6b6b' : pI > 0.5 ? VH.amber : barColor);
          return <div key={i} style={{
            width:28, height:3, borderRadius:2,
            background: on ? bc : 'rgba(255,255,255,0.07)',
            boxShadow: on ? `0 0 5px ${bc}` : 'none',
          }}/>;
        })}
      </div>
      <div style={{ fontFamily:VH_FM, fontSize:13, color:VH.ink, fontWeight:600, lineHeight:1 }}>
        {Math.round(disp)}<span style={{ fontSize:8, color:VH.dim }}>g</span>
      </div>
      <div style={{ fontFamily:VH_FM, fontSize:8, color:VH.faint }}>/{macroGoal}g</div>
    </div>
  );
}
// BurnCard-style activity panel — matches HayahToday's BurnCard component.
function VitalsActivity({ compact = false, stretch = false }) {
  const { todayData } = useContext(VitalsCtx);
  const total  = Math.round((todayData.caloriesBurned || 0) + (todayData.basalBurned || 0));
  const active = Math.round(todayData.caloriesBurned || 0);
  const steps  = todayData.steps || 0;
  const color  = VH.green;
  const dT = useAnimatedValue(total,  1200, 600);
  const dA = useAnimatedValue(active, 1000, 700);
  const pA = total > 0 ? active / total : 0;
  const fA = useRingFillV(pA, 600, 1000);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:14, background:VH.panel, border:`1px solid ${VH.panelBdr}`, borderRadius:14, padding:'9px 12px', ...(stretch ? { height:'100%', boxSizing:'border-box' } : {}) }}>
      <div style={{ position:'relative', width:52, height:52, flexShrink:0 }}>
        <svg width={52} height={52} viewBox="0 0 52 52" style={{ transform:'rotate(-90deg)', overflow:'visible' }}>
          <circle cx={26} cy={26} r={20} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={6}/>
          <circle cx={26} cy={26} r={20} fill="none" stroke={color} strokeWidth={6}
            strokeLinecap="round"
            strokeDasharray={`${fA * 2 * Math.PI * 20} ${2 * Math.PI * 20}`}
            style={{ transition:'stroke-dasharray 1.2s ease', filter:`drop-shadow(0 0 5px ${color})` }}/>
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:VH_FM, fontSize:9, color }}>
          {Math.round(pA * 100)}%
        </div>
      </div>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:6, fontFamily:VH_FS, fontWeight:300, fontSize:24, color:VH.ink, letterSpacing:'-0.8px', lineHeight:1 }}>
          {Math.round(dT).toLocaleString()}
          <span style={{ fontFamily:VH_FM, fontSize:9, color:VH.dim, letterSpacing:'1px' }}>KCAL BURNED</span>
        </div>
        <div style={{ marginTop:7, display:'flex', gap:14, alignItems:'center' }}>
          {[
            { l:'ACTIVE', v: Math.round(dA), suffix:'kcal', c:color },
            { l:'STEPS',  v: steps.toLocaleString(), suffix:'', c:VH.faint },
          ].map(({ l, v, suffix, c }) => (
            <div key={l}>
              <div style={{ fontFamily:VH_FM, fontSize:8, color:c, letterSpacing:'1.4px', textTransform:'uppercase' }}>{l}</div>
              <div style={{ fontFamily:VH_FM, fontSize:11, color:VH.ink, marginTop:2 }}>
                {v}{suffix && <span style={{ fontSize:7, color:VH.dim }}>{suffix}</span>}
              </div>
            </div>
          ))}
          <div style={{ flex:1 }}>
            <div style={{ height:4, borderRadius:4, background:'rgba(255,255,255,0.07)', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${fA * 100}%`, background:color, borderRadius:4, transition:'width 1.2s ease', boxShadow:`0 0 6px ${color}` }}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// SleepCard-style panel — matches HayahToday's SleepCard component.
function VitalsSleep({ compact = false, stretch = false }) {
  const { todayData, goals } = useContext(VitalsCtx);
  const totalMins = todayData.sleepMinutes || 0;
  const deepMins  = todayData.deepMinutes  || 0;
  const remMins   = todayData.remMinutes   || 0;
  const awakeMins = todayData.awakeMinutes || 0;
  const lightMins = Math.max(0, totalMins - deepMins - remMins);
  const goalHours = goals.sleep || 8;
  const safeTotal = totalMins > 0 ? totalMins : 1;
  const stages = [
    { label:'DEEP',  mins:deepMins,  color:VH.deepBlue,              pct:deepMins  / safeTotal },
    { label:'REM',   mins:remMins,   color:VH.green,                 pct:remMins   / safeTotal },
    { label:'LIGHT', mins:lightMins, color:'rgba(0,230,118,0.3)',     pct:lightMins / safeTotal },
    { label:'AWAKE', mins:awakeMins, color:'rgba(255,255,255,0.18)',  pct:awakeMins / safeTotal },
  ];
  const totalFill = useRingFillV(1, 800, 1200);
  const sleepH    = useAnimatedValue(Math.floor(totalMins / 60), 1000, 800);
  const sleepM    = useAnimatedValue(totalMins % 60,             1100, 850);
  let cursor = 0;
  const barSegs = stages.map(s => { const seg = { ...s, start: cursor }; cursor += s.pct; return seg; });
  return (
    <div style={{ background:VH.panel, border:`1px solid ${VH.panelBdr}`, borderRadius:14, padding:'9px 12px', ...(stretch ? { height:'100%', boxSizing:'border-box' } : {}) }}>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
          <span style={{ fontFamily:VH_FS, fontWeight:300, fontSize:24, color:VH.ink, letterSpacing:'-0.8px', lineHeight:1 }}>
            {totalMins > 0 ? `${Math.floor(sleepH)}h ${Math.round(sleepM)}m` : '—'}
          </span>
          <span style={{ fontFamily:VH_FM, fontSize:9, color:VH.dim, letterSpacing:'1px' }}>/ {goalHours}H GOAL</span>
        </div>
      </div>
      {totalMins > 0 ? (
        <>
          <div style={{ display:'flex', height:8, borderRadius:4, overflow:'hidden', gap:1, marginBottom:8 }}>
            {barSegs.map(s => (
              <div key={s.label} style={{
                flex: s.pct * totalFill, minWidth:0, background:s.color, borderRadius:2,
                transition:'flex 1.4s cubic-bezier(0.25,0.46,0.45,0.94)',
                boxShadow: s.label === 'REM' || s.label === 'DEEP' ? `0 0 6px ${s.color}` : 'none',
              }}/>
            ))}
          </div>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            {stages.map(s => (
              <div key={s.label} style={{ display:'flex', flexDirection:'column', gap:2 }}>
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <div style={{ width:6, height:6, borderRadius:6, background:s.color,
                    boxShadow: s.label === 'DEEP' || s.label === 'REM' ? `0 0 5px ${s.color}` : 'none' }}/>
                  <span style={{ fontFamily:VH_FM, fontSize:8, color:VH.faint, letterSpacing:'1.2px', textTransform:'uppercase' }}>{s.label}</span>
                </div>
                <span style={{ fontFamily:VH_FM, fontSize:10, color:VH.ink, fontWeight:600 }}>
                  {s.mins >= 60 ? `${Math.floor(s.mins / 60)}h ${s.mins % 60}m` : `${s.mins}m`}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign:'center', padding:'8px 0', fontFamily:VH_FM, fontSize:9, color:VH.faint, letterSpacing:'0.08em' }}>NO SLEEP DATA</div>
      )}
    </div>
  );
}
function MacroDetailPanel({ macroKey, color, onClose }) {
  const { darkMode, gold, text, muted, border, ink4, s2, danger, todayData, goals } = useContext(VitalsCtx);
  const macroLabel = { protein:'Protein', carbs:'Carbs', fat:'Fat' }[macroKey] || macroKey;
  const goal = goals[macroKey] || 1;
  const mealBreakdown = MEAL_SLOTS.map(slot => {
    const entries = (todayData?.meals?.[slot.key] || []);
    const val = entries.reduce((s, e) => s + (e[macroKey] || 0), 0);
    return { key:slot.key, label:slot.label, icon:slot.icon, slotColor:slot.color,
      entries: entries.filter(e=>(e[macroKey]||0)>0.05).sort((a,b)=>(b[macroKey]||0)-(a[macroKey]||0)), val };
  }).filter(m => m.val > 0.05);
  const total = mealBreakdown.reduce((s,m)=>s+m.val, 0);
  const over = total > goal;
  const pctOfGoal = Math.min(1, total/(goal||1));
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
        <button onClick={onClose} style={{ background:'none', border:'none', color:muted, cursor:'pointer', padding:'2px 6px 2px 0', fontSize:16, lineHeight:1, display:'flex', alignItems:'center' }}>‹</button>
        <div style={{ fontSize:9, color, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:800 }}>{macroLabel.toUpperCase()} · BREAKDOWN</div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'baseline', gap:4 }}>
          <span style={{ fontSize:16, fontWeight:600, color:over?danger:text, fontVariantNumeric:'tabular-nums' }}>{Math.round(total)}</span>
          <span style={{ fontSize:9, color:ink4, fontFamily:'monospace' }}>/ {goal}g</span>
        </div>
      </div>
      <div style={{ height:3, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)', borderRadius:2, marginBottom:14, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pctOfGoal*100}%`, background:over?danger:color, borderRadius:2, transition:'width 0.7s cubic-bezier(0.22,0.61,0.36,1)' }}/>
      </div>
      {mealBreakdown.length === 0 ? (
        <div style={{ textAlign:'center', padding:'16px 0', color:ink4, fontSize:11, fontFamily:'monospace', letterSpacing:'0.08em' }}>NO MEALS LOGGED YET</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {mealBreakdown.map(meal => {
            const shareOfTotal = total>0 ? meal.val/total : 0;
            return (
              <div key={meal.key}>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                  <span style={{ fontSize:13 }}>{meal.icon}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:text, flex:1, letterSpacing:'0.02em' }}>{meal.label}</span>
                  <span style={{ fontSize:12, fontWeight:600, color, fontFamily:'monospace' }}>{Math.round(meal.val)}g</span>
                  <span style={{ fontSize:8.5, color:ink4, fontFamily:'monospace', width:26, textAlign:'right' }}>{Math.round(shareOfTotal*100)}%</span>
                </div>
                <div style={{ height:3, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)', borderRadius:2, overflow:'hidden', marginBottom:5 }}>
                  <div style={{ height:'100%', width:`${shareOfTotal*100}%`, background:`${meal.slotColor}cc`, borderRadius:2, transition:'width 0.65s cubic-bezier(0.22,0.61,0.36,1)' }}/>
                </div>
                {meal.entries.slice(0,2).map(e=>(
                  <div key={e.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'2px 0 2px 20px' }}>
                    <span style={{ fontSize:10, color:muted, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.name}</span>
                    <span style={{ fontSize:10, color:ink4, fontFamily:'monospace', flexShrink:0, marginLeft:8 }}>{Math.round(e[macroKey]||0)}g</span>
                  </div>
                ))}
                {meal.entries.length > 2 && (
                  <div style={{ padding:'1px 0 0 20px', fontSize:8.5, color:ink4, fontFamily:'monospace' }}>+{meal.entries.length-2} more</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Horizontal swipe row.
//   Left  swipe past 72px → DELETE (single tier, single colour).
//   Right swipe past 72px → QUICK ADD (teal); push past 160px and the reveal
//     flips gold to "RE-LOG NOW" — release commits whichever was last shown.
// Snaps back if the gesture doesn't pass any threshold. Taps fall through.
function SwipeRow({ children, onDelete, onQuickAdd, onRelog, onTap, danger, gold, teal, expanded }) {
  const [dx, setDx] = useState(0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTRef = useRef(0);
  const movedRef = useRef(false);
  const lockedRef = useRef(null); // 'horizontal' | 'vertical' | null
  const lastTierRef = useRef(0);  // last haptic tier so we tick exactly once per threshold cross
  const DELETE_T = -72;
  const QUICK_T  = 72;
  const RELOG_T  = 160;

  // -1 delete · 0 idle · 1 quick-add · 2 re-log
  const tierFor = (x) => {
    if (x <= DELETE_T) return -1;
    if (x >= RELOG_T)  return 2;
    if (x >= QUICK_T)  return 1;
    return 0;
  };

  const onPointerDown = (e) => {
    if (expanded) return;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startTRef.current = Date.now();
    movedRef.current = false;
    lockedRef.current = null;
    lastTierRef.current = 0;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (expanded) return;
    if (!startTRef.current) return;
    const ddx = e.clientX - startXRef.current;
    const ddy = e.clientY - startYRef.current;
    if (!lockedRef.current) {
      if (Math.abs(ddx) > 6 && Math.abs(ddx) > Math.abs(ddy)) lockedRef.current = 'horizontal';
      else if (Math.abs(ddy) > 6) lockedRef.current = 'vertical';
    }
    if (lockedRef.current === 'horizontal') {
      e.preventDefault();
      movedRef.current = true;
      // Allow further travel on the right side so "RE-LOG" stays reachable.
      const clamped = Math.max(-120, Math.min(220, ddx));
      setDx(clamped);
      const tier = tierFor(clamped);
      if (tier !== lastTierRef.current) {
        try { hapticHayahSelection(); } catch {}
        lastTierRef.current = tier;
      }
    }
  };
  const onPointerUp = () => {
    if (expanded) return;
    if (movedRef.current) {
      const tier = tierFor(dx);
      if (tier === -1 && onDelete) {
        // Slide off the right side then fire delete so the row visually exits.
        setDx(-360);
        try { hapticHayahSwipe(); } catch {}
        setTimeout(() => onDelete(), 180);
      } else if (tier === 2 && onRelog) {
        try { hapticHayahSwipe(); } catch {}
        setDx(0);
        onRelog();
      } else if (tier === 1 && onQuickAdd) {
        try { hapticHayahSwipe(); } catch {}
        setDx(0);
        onQuickAdd();
      } else {
        setDx(0);
      }
    } else {
      setDx(0);
    }
    startTRef.current = 0;
    lockedRef.current = null;
  };
  const onClick = (e) => {
    if (movedRef.current) { e.preventDefault(); e.stopPropagation(); return; }
    onTap && onTap();
  };

  const rightAtRelog = dx >= RELOG_T;
  const rightActive  = dx > 8;
  const rightColor   = rightAtRelog ? (gold || '#ffb300') : (teal || '#00c9a7');
  const rightLabel   = rightAtRelog ? 'RE-LOG NOW' : 'QUICK ADD';

  // Compose a screen-reader description that lists whichever gestures the
  // caller wired up — most rows enable all three; some only delete.
  const gestures = [
    onDelete && 'swipe left to delete',
    onQuickAdd && 'swipe right to quick-add',
    onRelog && 'swipe further right to re-log immediately',
  ].filter(Boolean).join(', ');
  return (
    <div role="group" aria-label={gestures ? `Entry — ${gestures}` : undefined} style={{ position:'relative', overflow:'hidden', borderRadius:10 }}>
      {/* Left reveal — delete */}
      <div style={{ position:'absolute', inset:0, background:`linear-gradient(90deg, transparent 30%, ${danger}88 70%, ${danger} 100%)`, display:'flex', alignItems:'center', justifyContent:'flex-end', padding:'0 18px', borderRadius:10, opacity: dx < -8 ? 1 : 0, transition:'opacity 0.15s ease' }}>
        <span style={{ fontSize:11, fontWeight:800, color:'#fff', letterSpacing:'0.14em', fontFamily:'monospace' }}>DELETE</span>
      </div>
      {/* Right reveal — quick-add (teal) → re-log (gold) */}
      <div style={{ position:'absolute', inset:0, background:`linear-gradient(90deg, ${rightColor} 0%, ${rightColor}88 30%, transparent 70%)`, display:'flex', alignItems:'center', justifyContent:'flex-start', padding:'0 18px', borderRadius:10, opacity: rightActive ? 1 : 0, transition:'opacity 0.15s ease, background 0.18s ease' }}>
        <span style={{ fontSize:11, fontWeight:800, color:'#000', letterSpacing:'0.14em', fontFamily:'monospace' }}>{rightLabel}</span>
      </div>
      <div
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ transform:`translateX(${dx}px)`, transition: dx === 0 || dx === -360 ? 'transform 0.22s cubic-bezier(0.22,0.61,0.36,1)' : 'none', touchAction:'pan-y', position:'relative' }}
      >
        {children}
      </div>
    </div>
  );
}

// Inline accordion editor that opens beneath a meal row in the breakdown overlay.
// Treats the entry's logged values as the per-(servingG) baseline and rescales as
// the user drags the slider / picks a multiplier chip.
function InlineEditMealEntry({ entry, onSave, onCancel, theme }) {
  const baseG = entry.servingG || 100;
  const baseLabel = entry.servingLabel || `${baseG}g`;
  const [g, setG] = useState(baseG);
  const [label, setLabel] = useState(baseLabel);
  const [timeVal, setTimeVal] = useState(() => {
    if (!entry.loggedAt) return '';
    try { const d = new Date(entry.loggedAt); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; } catch { return ''; }
  });
  const scale = (g || 0) / (baseG || 1);
  const v = (n) => Math.round((n||0) * scale * 10) / 10;
  const { gold, teal, accent, text, muted, border, s2, s1, danger } = theme;
  const buildLoggedAt = () => {
    if (!timeVal) return entry.loggedAt || new Date().toISOString();
    try {
      const todayStr = localDateStr();
      const base = entry.loggedAt ? entry.loggedAt.split('T')[0] : todayStr;
      return new Date(`${base}T${timeVal}:00`).toISOString();
    } catch { return entry.loggedAt || new Date().toISOString(); }
  };
  return (
    <div style={{ marginTop:8, padding:'12px 12px 14px', background:s1, border:`0.5px solid ${border}`, borderRadius:12, animation:'ghidha-edit-slide 220ms cubic-bezier(0.22,0.61,0.36,1)' }}>
      <div style={{ fontSize:9, fontWeight:800, color:gold, letterSpacing:'0.18em', fontFamily:'monospace', marginBottom:8 }}>EDIT SERVING</div>
      <div style={{ display:'flex', gap:6, marginBottom:8 }}>
        <input type="number" inputMode="decimal" value={g} onChange={e=>setG(parseFloat(e.target.value)||0)}
          style={{flex:1,padding:'9px 10px',borderRadius:8,border:`1px solid ${border}`,background:s2,color:text,fontSize:13,fontFamily:'inherit',fontWeight:700,outline:'none'}}/>
        <input type="text" value={label} onChange={e=>setLabel(e.target.value)} placeholder="label"
          style={{flex:1.5,padding:'9px 10px',borderRadius:8,border:`1px solid ${border}`,background:s2,color:text,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
      </div>
      <div style={{ display:'flex', gap:5, marginBottom:10, flexWrap:'wrap' }}>
        {[0.5, 1, 1.5, 2, 3].map(mult => {
          const newG = Math.round(baseG * mult);
          const active = g === newG;
          const newLabel = mult === 0.5 ? `½ × ${baseLabel}` : mult === 1 ? baseLabel : `${mult}× ${baseLabel}`;
          return (
            <button key={mult} onClick={()=>{setG(newG); setLabel(newLabel);}}
              style={{padding:'5px 12px',background:active?gold:s2,border:`1px solid ${active?gold:border}`,borderRadius:7,color:active?'#000':text,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',touchAction:'manipulation'}}>×{mult}</button>
          );
        })}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1.6fr repeat(3, 1fr)', gap:6, marginBottom:10 }}>
        <div style={{ padding:'8px 10px', background:s2, borderRadius:8 }}>
          <div style={{ fontSize:8.5, color:muted, fontWeight:700, letterSpacing:'0.12em' }}>KCAL</div>
          <div style={{ fontSize:18, fontWeight:800, color:text, fontVariantNumeric:'tabular-nums', lineHeight:1.1 }}>{Math.round(v(entry.calories))}</div>
        </div>
        {[{l:'P',val:v(entry.protein),c:gold},{l:'C',val:v(entry.carbs),c:teal},{l:'F',val:v(entry.fat),c:accent}].map(m => (
          <div key={m.l} style={{ padding:'8px 6px', background:s2, borderRadius:8, textAlign:'center' }}>
            <div style={{ fontSize:8.5, color:muted, fontWeight:700, letterSpacing:'0.12em' }}>{m.l}</div>
            <div style={{ fontSize:13, fontWeight:800, color:m.c, fontVariantNumeric:'tabular-nums' }}>{m.val}<span style={{fontSize:9,color:muted,fontWeight:600}}>g</span></div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'9px 10px', background:s2, borderRadius:8, border:`1px solid ${border}` }}>
        <span style={{ fontSize:8.5, color:muted, fontWeight:700, letterSpacing:'0.12em', fontFamily:'monospace', flexShrink:0 }}>EATEN AT</span>
        <input type="time" value={timeVal} onChange={e=>setTimeVal(e.target.value)}
          style={{ flex:1, background:'transparent', border:'none', color:text, fontSize:14, fontWeight:700, fontFamily:'inherit', outline:'none', textAlign:'right' }}/>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <button onClick={onCancel} style={{flex:1,padding:9,background:'transparent',border:`1px solid ${border}`,borderRadius:8,color:muted,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',touchAction:'manipulation'}}>Cancel</button>
        <button onClick={()=>onSave(g, label, buildLoggedAt())} style={{flex:1.6,padding:9,background:gold,border:'none',borderRadius:8,color:'#000',fontSize:12.5,fontWeight:800,cursor:'pointer',fontFamily:'inherit',touchAction:'manipulation'}}>Save changes</button>
      </div>
      {danger ? null : null}
    </div>
  );
}

function CalorieBreakdownPanel({ onClose }) {
  const ctx = useContext(VitalsCtx);
  const { darkMode, gold, teal, accent, text, muted, border, ink2, ink4, danger, s1, s2, totalCalories, goals, todayData, removeEntryWithUndo, commitEditEntry, startLogMealNow, startLogForSlot, quickAddItems, relogEntry, addEntryToQuickAdd } = ctx;
  const [expandedEntryId, setExpandedEntryId] = useState(null);
  const goal = goals.calories || 0;
  const total = Math.round(totalCalories);
  const remaining = goal - total;
  const over = remaining < 0;
  const pct = Math.min(1, total / (goal || 1));
  const slots = MEAL_SLOTS.map(slot => {
    const entries = (todayData?.meals?.[slot.key] || []);
    const slotKcal = entries.reduce((s, e) => s + (e.calories || 0), 0);
    return { ...slot, entries, slotKcal };
  }).filter(s => s.entries.length > 0);
  const handleLogMeal = () => {
    onClose && onClose();
    setTimeout(() => { startLogMealNow && startLogMealNow(); }, 220);
  };
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <button onClick={onClose} aria-label="Close" style={{ background:'none', border:'none', color:muted, cursor:'pointer', padding:'2px 6px 2px 0', fontSize:18, lineHeight:1, display:'flex', alignItems:'center' }}>‹</button>
        <div style={{ fontSize:9, color:gold, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:800 }}>TODAY · MEALS</div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'baseline', gap:4 }}>
          <span style={{ fontSize:18, fontWeight:600, color:over?danger:text, fontVariantNumeric:'tabular-nums' }}>{total.toLocaleString()}</span>
          <span style={{ fontSize:10, color:ink4, fontFamily:'monospace' }}>/ {goal.toLocaleString()}</span>
        </div>
      </div>
      <div style={{ height:4, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)', borderRadius:3, marginBottom:6, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct*100}%`, background:over?danger:gold, borderRadius:3, transition:'width 0.7s cubic-bezier(0.22,0.61,0.36,1)' }}/>
      </div>
      <div style={{ fontSize:10, color:ink4, fontFamily:'monospace', letterSpacing:'0.08em', marginBottom:14 }}>
        {over ? `+${Math.abs(remaining).toLocaleString()} OVER BUDGET` : `${remaining.toLocaleString()} KCAL REMAINING`}
      </div>
      {startLogMealNow && (
        <button
          onClick={handleLogMeal}
          style={{
            width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            padding:'12px 16px', marginBottom:14,
            borderRadius:12, border:'none',
            background:gold, color:'#000',
            fontSize:13.5, fontWeight:800, letterSpacing:'0.04em',
            cursor:'pointer', fontFamily:'inherit',
            boxShadow:`0 8px 22px ${gold}33`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>Log a meal</span>
        </button>
      )}
      {quickAddItems && quickAddItems.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, padding:'0 2px' }}>
            <div style={{ fontSize:9.5, color:gold, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:800 }}>⚡ QUICK ADD</div>
            <div style={{ fontSize:9, color:ink4, letterSpacing:'0.12em', fontFamily:'monospace' }}>TAP TO LOG NOW</div>
          </div>
          <div className="ghidha-no-sb" style={{ display:'flex', gap:8, overflowX:'auto', paddingBottom:4, marginLeft:-2, marginRight:-2, paddingLeft:2, paddingRight:2 }}>
            {quickAddItems.map(item => (
              <button
                key={item.id}
                onClick={item.onTap}
                style={{
                  flexShrink:0, minWidth:148, textAlign:'left',
                  padding:12, background:s1, border:`0.5px solid ${border}`, borderRadius:12,
                  cursor:'pointer', fontFamily:'inherit', color:text,
                }}
              >
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8, minHeight:14 }}>
                  {item.tag
                    ? <span style={{ fontSize:9, color:gold, fontWeight:700, letterSpacing:'0.14em' }}>{item.tag}</span>
                    : <span/>}
                  <div style={{ width:22, height:22, borderRadius:999, background:gold, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M6 2v8M2 6h8" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div style={{ fontSize:13, color:text, fontWeight:500, lineHeight:1.3, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
                {item.kcal != null && (
                  <div style={{ fontSize:11, color:muted, fontFamily:'monospace' }}>{item.kcal} <span style={{ color:muted, fontSize:9 }}>kcal</span></div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {slots.length === 0 ? (
        <div style={{ textAlign:'center', padding:'24px 12px 12px', color:ink4, fontSize:12, fontFamily:'monospace', letterSpacing:'0.08em' }}>NO MEALS LOGGED YET</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {slots.map(slot => (
            <div key={slot.key}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <span style={{ display:'inline-block', width:10, height:10, background:slot.color, borderRadius:2 }}/>
                <span style={{ fontSize:11, fontWeight:800, color:slot.color, letterSpacing:'0.08em', textTransform:'uppercase' }}>{slot.label}</span>
                <span style={{ fontSize:9, color:ink4, fontFamily:'monospace', letterSpacing:'0.08em', flex:1 }}>· {slot.entries.length} item{slot.entries.length!==1?'s':''}</span>
                <span style={{ fontSize:13, fontWeight:700, color:slot.color, fontVariantNumeric:'tabular-nums' }}>{Math.round(slot.slotKcal)}</span>
                <span style={{ fontSize:9, color:ink4, fontFamily:'monospace' }}>kcal</span>
                {startLogForSlot && (
                  <button
                    onClick={() => { onClose && onClose(); setTimeout(() => startLogForSlot(slot.key), 220); }}
                    title={`Add to ${slot.label}`}
                    aria-label={`Add to ${slot.label}`}
                    style={{ flexShrink:0, width:24, height:24, borderRadius:7, border:'none', background:`${slot.color}1f`, color:slot.color, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', padding:0, marginLeft:4, touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {slot.entries.map(e => {
                  const expanded = expandedEntryId === e.id;
                  const handleTap = () => setExpandedEntryId(expanded ? null : e.id);
                  return (
                    <div key={e.id}>
                      <SwipeRow
                        expanded={expanded}
                        danger={danger}
                        gold={gold}
                        teal={teal}
                        onDelete={() => removeEntryWithUndo && removeEntryWithUndo(slot.key, e)}
                        onQuickAdd={() => addEntryToQuickAdd && addEntryToQuickAdd(e)}
                        onRelog={() => relogEntry && relogEntry(slot.key, e)}
                        onTap={handleTap}
                      >
                        <div
                          role="button"
                          aria-expanded={expanded}
                          aria-label={`Edit ${e.name}`}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', borderRadius:10, background: expanded ? (darkMode?'rgba(255,179,0,0.08)':'rgba(184,112,8,0.06)') : (darkMode?'rgba(255,255,255,0.025)':'rgba(0,0,0,0.025)'), border:`0.5px solid ${expanded ? gold : border}`, cursor:'pointer', userSelect:'none', WebkitUserSelect:'none', transition:'background 0.18s ease, border-color 0.18s ease' }}
                        >
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{e.name}</div>
                            <div style={{ fontSize:10.5, color:ink2, marginTop:1, fontFamily:'monospace', letterSpacing:'0.04em' }}>
                              {e.loggedAt ? `${(() => { try { const d=new Date(e.loggedAt); return d.toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit',hour12:false}); } catch{return '';} })()} · ` : ''}{e.servingLabel || `${e.servingG||0}g`} · P{Math.round(e.protein||0)} C{Math.round(e.carbs||0)} F{Math.round(e.fat||0)}
                            </div>
                          </div>
                          <div style={{ fontSize:14, fontWeight:800, color:text, fontVariantNumeric:'tabular-nums', flexShrink:0 }}>{Math.round(e.calories||0)}</div>
                          <span style={{ fontSize:10, color:muted, fontFamily:'monospace', flexShrink:0 }}>kcal</span>
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flexShrink:0, color:ink4, transition:'transform 0.2s ease', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                            <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </SwipeRow>
                      {expanded && (
                        <InlineEditMealEntry
                          entry={e}
                          theme={{ gold, teal, accent, text, muted, border, s1, s2, danger }}
                          onCancel={() => setExpandedEntryId(null)}
                          onSave={(newG, newLabel, newLoggedAt) => {
                            const synth = {
                              name: e.name, brand: e.brand || '',
                              calories: e.calories || 0, protein: e.protein || 0,
                              carbs: e.carbs || 0, fat: e.fat || 0,
                              fiber: e.fiber || 0, sugar: e.sugar || 0, sodium: e.sodium || 0,
                              servingG: e.servingG || 100,
                              servingLabel: e.servingLabel || `${e.servingG||100}g`,
                              source: e.source || 'edit',
                            };
                            commitEditEntry && commitEditEntry(synth, newG, newLabel, slot.key, e.id, null, newLoggedAt);
                            setExpandedEntryId(null);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function useLast7Days() {
  const { calorieLog } = useContext(VitalsCtx);
  return useMemo(() => {
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i*86400000);
      const k = d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
      const e = (calorieLog && calorieLog[k]) || {};
      const intake = MEAL_SLOTS.reduce((s,slot)=>s+((e.meals?.[slot.key]||[]).reduce((ss,it)=>ss+(it.calories||0),0)),0);
      const sleepSegs = (e.sleepSegments||[]).filter(s=>['ASLEEPCORE','ASLEEPDEEP','ASLEEPREM','ASLEEP'].includes(s.st));
      const rawBedtime = sleepSegs.length>0 ? Math.min(...sleepSegs.map(s=>s.s)) : null;
      const bedtimeMins = rawBedtime!=null ? (()=>{ const bd=new Date(rawBedtime); return bd.getHours()*60+bd.getMinutes(); })() : null;
      out.push({
        key:k,
        dow:d.toLocaleDateString(undefined,{weekday:'short'}).slice(0,1),
        active:e.caloriesBurned||0,
        basal:e.basalBurned||0,
        burned:(e.caloriesBurned||0)+(e.basalBurned||0),
        intake,
        steps:e.steps||0,
        sleep:e.sleepMinutes||0,
        deep:e.deepMinutes||0,
        rem:e.remMinutes||0,
        bedtimeMins,
        isToday: i===0,
      });
    }
    return out;
  }, [calorieLog]);
}

function MetricSparkBars({ values, accent, format, height, unit }) {
  const { ink4, lineSoft, text, border, darkMode } = useContext(VitalsCtx);
  const sparkRef = useRef(null);
  const [sparkCursor, setSparkCursor] = useState(null);
  const _sparkDragging = useRef(false);
  const peak = Math.max(1, ...values.map(v=>v.value||0));
  const h = height || 110;
  const n = values.length;
  const fmt = (v) => format ? format(v) : Math.round(v).toLocaleString();
  const _getSparkFrac = e => { if(!sparkRef.current) return null; const r=sparkRef.current.getBoundingClientRect(); return Math.max(0,Math.min(1,(e.clientX-r.left)/r.width)); };
  const _onSparkDown = e => { _sparkDragging.current=true; try{e.currentTarget.setPointerCapture(e.pointerId);}catch{} const f=_getSparkFrac(e); if(f!=null) setSparkCursor({frac:f}); };
  const _onSparkMove = e => { if(!_sparkDragging.current) return; const f=_getSparkFrac(e); if(f!=null) setSparkCursor({frac:f}); };
  const _onSparkUp = () => { _sparkDragging.current=false; setSparkCursor(null); };
  const sparkIdx = sparkCursor ? Math.min(n-1, Math.floor(sparkCursor.frac * n)) : null;
  const sparkV = sparkIdx != null ? values[sparkIdx] : null;
  return (
    <div style={{ position:'relative' }}>
      <div
        ref={sparkRef}
        style={{ display:'flex', alignItems:'flex-end', gap:8, height:h+40, touchAction:'none', userSelect:'none', WebkitUserSelect:'none', position:'relative' }}
        onPointerDown={_onSparkDown} onPointerMove={_onSparkMove} onPointerUp={_onSparkUp} onPointerCancel={_onSparkUp}
      >
        {values.map((v,i)=>{
          const bh = v.value>0 ? Math.max(3,(v.value/peak)*h) : 2;
          const isToday = v.isToday;
          const isActive = sparkIdx === i;
          return (
            <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
              <div style={{ fontSize:11, color:isActive?accent:(isToday?accent:ink4), fontFamily:'monospace', fontWeight:(isActive||isToday)?700:500, fontVariantNumeric:'tabular-nums' }}>{v.value>0 ? fmt(v.value) : '·'}</div>
              <div style={{ width:'100%', height:h, display:'flex', alignItems:'flex-end' }}>
                <div style={{
                  width:'100%', height:bh, borderRadius:3,
                  background: v.value===0 ? lineSoft : (isActive ? accent : (isToday ? accent : 'rgba(255,255,255,0.14)')),
                  border: v.value===0 ? `0.5px dashed ${border}` : 'none',
                  boxShadow: (isActive||isToday) ? `0 0 12px ${accent}77` : 'none',
                  transformOrigin:'bottom',
                  animation:`barRise 580ms cubic-bezier(0.22,0.61,0.36,1) ${i*40}ms backwards`,
                }}/>
              </div>
              <div style={{ fontSize:12, color:isActive?accent:(isToday?text:ink4), fontFamily:'monospace', fontWeight:(isActive||isToday)?700:500 }}>{v.dow}</div>
            </div>
          );
        })}
      </div>
      {sparkV && (
        <div style={{ marginTop:8, padding:'8px 12px', background:darkMode?'rgba(0,0,0,0.45)':'rgba(255,255,255,0.96)', border:`0.5px solid ${border}`, borderRadius:8, fontFamily:'monospace', fontSize:11, color:text, display:'flex', justifyContent:'space-between', alignItems:'center', gap:10 }}>
          <span style={{ color:ink4, letterSpacing:'0.1em', fontWeight:700 }}>{sparkV.dow}{sparkV.date?` · ${sparkV.date}`:''}</span>
          <span><span style={{ color:accent, fontWeight:800 }}>{fmt(sparkV.value)}</span>{unit?<span style={{ color:ink4 }}> {unit}</span>:null}</span>
        </div>
      )}
    </div>
  );
}

function MetricStatGrid({ items }) {
  const { darkMode, border, text, ink4 } = useContext(VitalsCtx);
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${items.length}, 1fr)`, gap:10 }}>
      {items.map((it,i)=>(
        <div key={i} style={{ padding:'14px 14px 12px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:14 }}>
          <div style={{ fontSize:10, color:it.color||ink4, letterSpacing:'0.16em', fontFamily:'monospace', fontWeight:700, marginBottom:8 }}>{it.label}</div>
          <div style={{ fontSize:26, fontWeight:500, color:text, letterSpacing:'-0.02em', lineHeight:1, fontVariantNumeric:'tabular-nums' }}>{it.value}</div>
          {it.sub && <div style={{ fontSize:10, color:ink4, marginTop:6, fontFamily:'monospace', letterSpacing:'0.06em' }}>{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function MetricSectionHeader({ num, label, right }) {
  const { border, ink2, ink4 } = useContext(VitalsCtx);
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'26px 0 14px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ fontSize:11, color:ink4, letterSpacing:'0.14em', fontFamily:'monospace' }}>{num}</span>
        <span style={{ width:18, height:0.5, background:border, display:'inline-block' }}/>
        <span style={{ fontSize:11, color:ink2, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:700 }}>{label}</span>
      </div>
      {right && <span style={{ fontSize:10, color:ink4, letterSpacing:'0.12em', fontFamily:'monospace' }}>{right}</span>}
    </div>
  );
}

function BurnedOverlayContent() {
  const { darkMode, gold, teal, text, muted, border, ink4, lineSoft, caloriesBurned, totalBurned, todayData, totalCalories, goals } = useContext(VitalsCtx);
  const last7 = useLast7Days();
  const basal = todayData.basalBurned || 0;
  const active = caloriesBurned || 0;
  const total = active + basal;
  const animTotal = useAnimatedValue(total, 700, 60);
  const animActive = useAnimatedValue(active, 700, 120);
  const animBasal = useAnimatedValue(basal, 700, 180);
  const net = totalCalories - total;
  const goalKcal = goals.burn || 3000;
  const remainingToGoal = Math.max(0, goalKcal - active);
  const minsToWalk = Math.round(remainingToGoal / 4);
  const burnRef = useRef(null);
  const [burnCursor, setBurnCursor] = useState(null);
  const _burnDragging = useRef(false);
  const _getBurnFrac = e => { if(!burnRef.current) return null; const r=burnRef.current.getBoundingClientRect(); return Math.max(0,Math.min(1,(e.clientX-r.left)/r.width)); };
  const _onBurnDown = e => { _burnDragging.current=true; try{e.currentTarget.setPointerCapture(e.pointerId);}catch{} const f=_getBurnFrac(e); if(f!=null) setBurnCursor({frac:f}); };
  const _onBurnMove = e => { if(!_burnDragging.current) return; const f=_getBurnFrac(e); if(f!=null) setBurnCursor({frac:f}); };
  const _onBurnUp = () => { _burnDragging.current=false; setBurnCursor(null); };

  const week = last7.map(d=>({ value:d.burned, dow:d.dow, isToday:d.isToday, date:d.key }));
  const avg = Math.round(last7.reduce((s,d)=>s+d.burned,0)/Math.max(1,last7.filter(d=>d.burned>0).length||1));

  // Hourly distribution: weighted spread of the day's totals across hours UP
  // TO and INCLUDING the current hour. Hours after now stay zero — the user
  // hasn't accumulated burn there yet. (When per-hour HealthKit samples
  // become available, swap baseDist/activeDist for actual binned values.)
  const baseDist = [0.9,0.7,0.6,0.6,0.7,0.9,1.1,1.4,1.6,1.6,1.5,1.5,1.4,1.4,1.4,1.4,1.5,1.5,1.4,1.3,1.2,1.1,1.0,1.0];
  const activeDist = [0,0,0,0,0,0,1,3,8,5,3,4,9,8,3,4,12,16,12,6,3,1,0,0];
  const nowH = new Date().getHours();
  const baseSumElapsed = baseDist.slice(0, nowH+1).reduce((a,b)=>a+b,0) || 1;
  const activeSumElapsed = activeDist.slice(0, nowH+1).reduce((a,b)=>a+b,0) || 1;
  const hourly = baseDist.map((b,i) => {
    if (i > nowH) return { active:0, basal:0 };
    return {
      active: (activeDist[i] / activeSumElapsed) * active,
      basal:  (b / baseSumElapsed) * basal,
    };
  });
  const peakH = Math.max(...hourly.map(h=>h.active+h.basal), 1);
  const burnHour = burnCursor ? Math.min(23, Math.floor(burnCursor.frac * 24)) : null;
  const burnHourData = burnHour != null ? hourly[burnHour] : null;

  return (
    <div>
      <MetricStatGrid items={[
        { label:'TOTAL', value: Math.round(animTotal).toLocaleString(), sub:'kcal today', color:teal },
        { label:'ACTIVE', value: Math.round(animActive).toLocaleString(), sub:active>0?'movement':'no data', color:gold },
        { label:'BASAL', value: Math.round(animBasal).toLocaleString(), sub:basal>0?'resting':'estimate', color:ink4 },
      ]}/>

      <MetricSectionHeader num="01" label="HOURLY · ACTIVE + BASAL" right={`PEAK ${Math.round(peakH)} KCAL/H`}/>
      <div
        ref={burnRef}
        style={{ position:'relative', height:180, padding:'8px 0', display:'flex', alignItems:'flex-end', gap:2, background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12, touchAction:'none', userSelect:'none', WebkitUserSelect:'none' }}
        onPointerDown={_onBurnDown} onPointerMove={_onBurnMove} onPointerUp={_onBurnUp} onPointerCancel={_onBurnUp}
      >
        {hourly.map((hr,i)=>{
          const total = hr.active + hr.basal;
          const h = (total/peakH) * 150;
          const aH = (hr.active/peakH) * 150;
          const bH = h - aH;
          const isNow = i===nowH;
          const future = i > nowH;
          return (
            <div key={i} style={{ flex:1, height:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'stretch', gap:0, opacity: future ? 0.25 : 1 }}>
              <div style={{ height:Math.max(0.5,aH), background:gold, opacity:isNow?1:0.85, boxShadow:isNow?`0 0 6px ${gold}`:'none', transformOrigin:'bottom', animation:`barRise 700ms cubic-bezier(0.22,0.61,0.36,1) ${i*16}ms backwards` }}/>
              <div style={{ height:Math.max(1,bH), background:teal, opacity:isNow?0.85:0.55, transformOrigin:'bottom', animation:`barRise 700ms cubic-bezier(0.22,0.61,0.36,1) ${i*16}ms backwards` }}/>
            </div>
          );
        })}
        {burnCursor && (
          <div style={{ position:'absolute', top:0, bottom:0, left:`${burnCursor.frac*100}%`, pointerEvents:'none', zIndex:10 }}>
            <div style={{ position:'absolute', top:0, bottom:0, left:0, width:1.5, background:'rgba(255,255,255,0.8)', opacity:0.85 }}/>
            {burnHourData && (
              <div style={{ position:'absolute', top:6, ...(burnCursor.frac>0.6?{right:6}:{left:6}), background:darkMode?'rgba(10,10,10,0.82)':'rgba(255,255,255,0.96)', border:`0.5px solid ${border}`, borderRadius:8, padding:'6px 10px', fontFamily:'monospace', fontSize:11, color:text, whiteSpace:'nowrap', display:'flex', flexDirection:'column', gap:3, boxShadow:'0 2px 10px rgba(0,0,0,0.4)', pointerEvents:'none' }}>
                <span style={{ color:muted, letterSpacing:'0.1em', fontWeight:700 }}>{String(burnHour).padStart(2,"0")}:00</span>
                <span><span style={{ color:gold, fontWeight:700 }}>{Math.round(burnHourData.active)}</span> <span style={{ color:muted }}>active</span> · <span style={{ color:teal, fontWeight:700 }}>{Math.round(burnHourData.basal)}</span> <span style={{ color:muted }}>basal</span></span>
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontSize:11, color:ink4, fontFamily:"monospace" }}>
        <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
      </div>
      <div style={{ display:'flex', gap:14, marginTop:8, fontSize:11, fontFamily:"monospace", color:muted, letterSpacing:"0.08em", fontWeight:700 }}>
        <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:8, height:2, background:gold, display:'inline-block' }}/> ACTIVE</span>
        <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}><span style={{ width:8, height:2, background:teal, opacity:0.55, display:'inline-block' }}/> BASAL</span>
      </div>

      <MetricSectionHeader num="02" label="LAST 7 DAYS" right={avg>0?`AVG ${avg.toLocaleString()} KCAL`:'NO HISTORY'}/>
      <MetricSparkBars values={week} accent={teal} format={v=>Math.round(v).toLocaleString()} unit="kcal"/>

      <MetricSectionHeader num="03" label="GUIDANCE"/>
      <div style={{ padding:'12px 14px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
        <div style={{ fontSize:11, color:text, lineHeight:1.55 }}>
          {remainingToGoal>0 ? (
            <>To reach <b style={{ color:teal, fontVariantNumeric:'tabular-nums' }}>{goalKcal.toLocaleString()} kcal</b> active burn today, walk briskly for ~<b style={{ color:gold }}>{minsToWalk} min</b> or take ~<b style={{ color:gold }}>{Math.round(remainingToGoal/0.04)} steps</b>.</>
          ) : (
            <>Subḥān Allāh — you've crossed <b style={{ color:gold }}>{goalKcal.toLocaleString()} kcal</b> active burn. Body in motion is a trust well kept.</>
          )}
        </div>
        <div style={{ marginTop:10, paddingTop:10, borderTop:`0.5px solid ${lineSoft}`, fontSize:10, color:muted, fontFamily:'monospace', letterSpacing:'0.06em' }}>
          NET BALANCE · <span style={{ color:net>=0?gold:teal, fontWeight:700 }}>{net>=0?'+':''}{Math.round(net).toLocaleString()} KCAL</span>
        </div>
      </div>
    </div>
  );
}

function RemainingOverlayContent() {
  const { darkMode, gold, text, muted, border, ink4, lineSoft, danger, totalCalories, goals, totalProtein, totalCarbs, totalFat, totalFiber, totalSugar, totalSodium } = useContext(VitalsCtx);
  const last7 = useLast7Days();
  const remaining = goals.calories - Math.round(totalCalories);
  const pct = Math.min(1, totalCalories / (goals.calories||1));
  const animPct = useAnimatedValue(pct, 800, 80);
  const animRem = useAnimatedValue(Math.abs(remaining), 800, 120);

  const now = new Date();
  const minsIntoDay = now.getHours()*60 + now.getMinutes();
  const expectedAt = (() => {
    const pts = [[0,0],[540,0.10],[780,0.40],[1140,0.70],[1320,0.95],[1440,1.0]];
    for (let i=1;i<pts.length;i++){ const [x1,y1]=pts[i-1],[x2,y2]=pts[i]; if (minsIntoDay<=x2){ const t=(minsIntoDay-x1)/(x2-x1); return y1+(y2-y1)*t; } }
    return 1;
  })();
  const youAtPct = totalCalories/(goals.calories||1);
  const aheadBehind = youAtPct - expectedAt;

  const macros = [
    { key:'protein', label:'PROTEIN', val:totalProtein, goal:goals.protein, color:'#e8e2d2' },
    { key:'carbs',   label:'CARBS',   val:totalCarbs,   goal:goals.carbs,   color:gold },
    { key:'fat',     label:'FAT',     val:totalFat,     goal:goals.fat,     color:'#ff8c69' },
    { key:'fiber',   label:'FIBER',   val:totalFiber,   goal:goals.fiber,   color:'#82c83c' },
  ];

  const week = last7.map(d=>({ value:d.intake, dow:d.dow, isToday:d.isToday, date:d.key }));
  const avgIntake = Math.round(last7.slice(0,6).reduce((s,d)=>s+d.intake,0)/Math.max(1,last7.slice(0,6).filter(d=>d.intake>0).length||1));

  return (
    <div>
      <div style={{ padding:'22px 22px 20px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:18 }}>
        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:14 }}>
          <div style={{ fontSize:11, color:remaining<0?danger:gold, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:700 }}>{remaining>=0?'BUDGET LEFT':'OVER BUDGET'}</div>
          <div style={{ fontSize:11, color:ink4, fontFamily:'monospace' }}>{Math.round(animPct*100)}% USED</div>
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
          <div style={{ fontSize:64, fontWeight:300, color:remaining<0?danger:text, letterSpacing:'-0.04em', lineHeight:0.95, fontVariantNumeric:'tabular-nums' }}>{Math.round(animRem).toLocaleString()}</div>
          <div style={{ fontSize:13, color:ink4, fontFamily:'monospace', letterSpacing:'0.1em' }}>kcal · of {goals.calories.toLocaleString()}</div>
        </div>
        <div style={{ position:'relative', height:7, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.07)', borderRadius:4, marginTop:18, overflow:'hidden' }}>
          <div style={{ position:'absolute', left:0, top:0, height:'100%', width:`${animPct*100}%`, background:remaining<0?danger:gold, borderRadius:4, transition:'width 0.7s cubic-bezier(0.22,0.61,0.36,1)' }}/>
          <div title="Where you usually are by now" style={{ position:'absolute', left:`${expectedAt*100}%`, top:-4, height:15, width:1.5, background:lineSoft }}/>
        </div>
        <div style={{ marginTop:10, fontSize:11, color:muted, fontFamily:'monospace', letterSpacing:'0.06em' }}>
          {Math.abs(aheadBehind)<0.05 ? 'ON PACE FOR TODAY' : aheadBehind>0 ? `↑ ${Math.round(aheadBehind*100)}% AHEAD OF TYPICAL PACE` : `↓ ${Math.round(-aheadBehind*100)}% BEHIND TYPICAL PACE`}
          <span style={{ marginLeft:10, color:ink4 }}>· {String(now.getHours()).padStart(2,'0')}:{String(now.getMinutes()).padStart(2,'0')}</span>
        </div>
      </div>

      <MetricSectionHeader num="01" label="MACROS REMAINING"/>
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {macros.map(m=>{
          const mPct = Math.min(1, m.val/(m.goal||1));
          const left = Math.max(0, Math.round((m.goal||0) - m.val));
          const over = m.val > (m.goal||0);
          return (
            <div key={m.key} style={{ padding:'14px 18px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:14 }}>
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ fontSize:11, color:over?danger:m.color, letterSpacing:'0.18em', fontFamily:'monospace', fontWeight:700 }}>{m.label}</span>
                <span style={{ fontSize:15, color:text, fontFamily:'monospace', fontVariantNumeric:'tabular-nums' }}>
                  <b>{Math.round(m.val)}</b><span style={{ color:ink4 }}> / {Math.round(m.goal||0)}g</span>
                </span>
              </div>
              <div style={{ height:5, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.07)', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${Math.min(1,mPct)*100}%`, background:over?danger:m.color, borderRadius:3, transition:'width 0.7s cubic-bezier(0.22,0.61,0.36,1)' }}/>
              </div>
              <div style={{ marginTop:8, fontSize:11, color:over?danger:ink4, fontFamily:'monospace', letterSpacing:'0.06em' }}>{over?`+${Math.round(m.val-(m.goal||0))}g over`:`${left}g left`}</div>
            </div>
          );
        })}
      </div>

      {(totalSodium>0||totalSugar>0) && (<>
        <MetricSectionHeader num="02" label="MICRONUTRIENTS"/>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {totalSodium>0 && (
            <div style={{ padding:'12px 18px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:11, color:muted, letterSpacing:'0.16em', fontFamily:'monospace', fontWeight:700 }}>SODIUM</span>
              <span style={{ fontSize:15, color:text, fontFamily:'monospace' }}><b>{Math.round(totalSodium)}</b><span style={{ color:ink4 }}> mg</span></span>
            </div>
          )}
          {totalSugar>0 && (
            <div style={{ padding:'12px 18px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:11, color:muted, letterSpacing:'0.16em', fontFamily:'monospace', fontWeight:700 }}>SUGAR</span>
              <span style={{ fontSize:15, color:text, fontFamily:'monospace' }}><b>{Math.round(totalSugar)}</b><span style={{ color:ink4 }}> g</span></span>
            </div>
          )}
        </div>
      </>)}

      <MetricSectionHeader num={totalSodium>0||totalSugar>0?'03':'02'} label="LAST 7 DAYS · INTAKE" right={avgIntake>0?`AVG ${avgIntake.toLocaleString()} KCAL`:'—'}/>
      <MetricSparkBars values={week} accent={gold} format={v=>Math.round(v).toLocaleString()} unit="kcal"/>
    </div>
  );
}

// Compact SVG line graph for a body-composition series (weight kg or body fat %).
// Renders the trend over the supplied samples (date-ascending), highlights the
// latest reading, and shows first→last delta. Empty / single-point series fall
// back to a "not enough data" placeholder so callers can render unconditionally.
function BodyTrendLine({ samples, label, accent, format, height }) {
  const { darkMode, border, ink4, lineSoft, text } = useContext(VitalsCtx);
  const [tapped, setTapped] = useState(null);
  const h = height || 90;
  const W = 1000;
  const fmt = v => format ? format(v) : v.toFixed(1);
  if (!samples || samples.length < 2) {
    return (
      <div style={{ height: h+40, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:ink4, fontFamily:'monospace', letterSpacing:'0.12em', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
        NOT ENOUGH {label} DATA
      </div>
    );
  }
  const vals = samples.map(s => s.value);
  const minV = Math.min(...vals), maxV = Math.max(...vals);
  const span = Math.max(0.0001, maxV - minV);
  const tStart = new Date(samples[0].date).getTime();
  const tEnd   = new Date(samples[samples.length-1].date).getTime();
  const tSpan  = Math.max(1, tEnd - tStart);
  const padY = 8;
  const innerH = h - padY * 2;
  const points = samples.map(s => {
    const x = ((new Date(s.date).getTime() - tStart) / tSpan) * W;
    const y = padY + ((maxV - s.value) / span) * innerH;
    return { x, y, v: s.value, date: s.date };
  });
  const path = points.map((p,i) => `${i===0?'M':'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const last = samples[samples.length-1], first = samples[0];
  const delta = last.value - first.value;
  const fmtDate = d => new Date(d).toLocaleDateString(undefined,{month:'short',day:'numeric'}).toUpperCase();
  return (
    <div style={{ padding:'10px 12px 8px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <span style={{ fontSize:10, color:ink4, fontFamily:'monospace', letterSpacing:'0.16em', fontWeight:700 }}>{label}</span>
        <span style={{ fontSize:11, fontFamily:'monospace', fontVariantNumeric:'tabular-nums' }}>
          <span style={{ color:accent, fontWeight:800 }}>{fmt(last.value)}</span>
          <span style={{ color:ink4, marginLeft:8 }}>Δ {delta>=0?'+':''}{delta.toFixed(1)}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="none" style={{ width:'100%', height:h, display:'block', overflow:'visible' }}>
        <path d={`${path} L ${W} ${h} L 0 ${h} Z`} fill={accent} opacity={0.10}/>
        <path d={path} fill="none" stroke={accent} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
        {points.map((p,i) => (
          <circle key={i} cx={p.x} cy={p.y}
            r={i===points.length-1?3.5:1.8}
            fill={i===points.length-1?accent:lineSoft}
            stroke={accent} strokeWidth={i===points.length-1?0:0.8}
            vectorEffect="non-scaling-stroke"
            onClick={()=>setTapped(prev => prev===i ? null : i)}
            style={{ cursor:'pointer' }}/>
        ))}
      </svg>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:9.5, color:ink4, fontFamily:'monospace', letterSpacing:'0.08em', marginTop:4 }}>
        <span>{fmtDate(first.date)}</span><span>{fmtDate(last.date)}</span>
      </div>
      {tapped != null && (
        <div style={{ marginTop:6, padding:'6px 10px', background:darkMode?'rgba(0,0,0,0.45)':'rgba(255,255,255,0.96)', border:`0.5px solid ${border}`, borderRadius:8, fontFamily:'monospace', fontSize:11, color:text, display:'flex', justifyContent:'space-between' }}>
          <span style={{ color:ink4, fontWeight:700 }}>{fmtDate(samples[tapped].date)}</span>
          <span style={{ color:accent, fontWeight:800 }}>{fmt(samples[tapped].value)}</span>
        </div>
      )}
    </div>
  );
}

// ── Body Composition vitals panel ─────────────────────────────────────────
// Self-contained: takes colors as props, no VitalsCtx dependency. Used in
// GoalsView as the "click to see how my body is changing" dashboard.
// Reads sample histories from calorieLog[today].bodyHistory (the latest 30+
// days of Apple Health weight + body-fat samples). Merges in manual weightLog
// entries so pre-HealthKit history isn't lost. Apple Health wins on overlap.

// Drives a 0→1 progress value (cubic ease-out) over ~900ms, restarting only
// when `deps` change. Used to wipe-reveal charts left-to-right on mount.
function useDrawReveal(deps) {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const dur = 900;
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      setT(1 - Math.pow(1 - p, 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setT(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return t;
}

function VitalTrendChart({ samples, accent, format, h, colors }) {
  const { text, muted, border, darkMode } = colors;
  const [tapped, setTapped] = useState(null);
  const clipId = useRef('vtc' + Math.random().toString(36).slice(2)).current;
  const t = useDrawReveal([samples?.length, samples?.[0]?.date, samples?.[samples?.length-1]?.date, samples?.[samples?.length-1]?.value, h]);
  const H = h || 90;
  const W = 1000;
  const fmt = v => format ? format(v) : v.toFixed(1);
  if (!samples || samples.length < 2) {
    return (
      <div style={{ height: H + 24, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:muted, fontFamily:'monospace', letterSpacing:'0.12em', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
        NEED 2+ SAMPLES
      </div>
    );
  }
  const vals = samples.map(s => s.value);
  const minV = Math.min(...vals), maxV = Math.max(...vals);
  const span = Math.max(0.0001, maxV - minV);
  const tStart = new Date(samples[0].date).getTime();
  const tEnd   = new Date(samples[samples.length-1].date).getTime();
  const tSpan  = Math.max(1, tEnd - tStart);
  const padY = 8;
  const innerH = H - padY * 2;
  const points = samples.map(s => {
    const x = ((new Date(s.date).getTime() - tStart) / tSpan) * W;
    const y = padY + ((maxV - s.value) / span) * innerH;
    return { x, y, v: s.value, date: s.date };
  });
  const path = points.map((p,i) => `${i===0?'M':'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const fmtDate = d => new Date(d).toLocaleDateString(undefined,{month:'short',day:'numeric'}).toUpperCase();
  return (
    <div style={{ padding:'8px 10px 6px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width:'100%', height:H, display:'block', overflow:'visible' }}>
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <rect x={0} y={-padY} width={Math.max(0.001, W * t)} height={H + padY*2}/>
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <path d={`${path} L ${W} ${H} L 0 ${H} Z`} fill={accent} opacity={0.10}/>
          <path d={path} fill="none" stroke={accent} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"/>
          {points.map((p,i) => (
            <circle key={i} cx={p.x} cy={p.y}
              r={i===points.length-1?3.5:1.6}
              fill={i===points.length-1?accent:'rgba(255,255,255,0.4)'}
              stroke={accent} strokeWidth={i===points.length-1?0:0.6}
              vectorEffect="non-scaling-stroke"
              onClick={()=>setTapped(prev => prev===i ? null : i)}
              style={{ cursor:'pointer' }}/>
          ))}
        </g>
      </svg>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:9, color:muted, fontFamily:'monospace', letterSpacing:'0.08em', marginTop:3 }}>
        <span>{fmtDate(samples[0].date)}</span><span>{fmtDate(samples[samples.length-1].date)}</span>
      </div>
      {tapped != null && (
        <div style={{ marginTop:5, padding:'5px 9px', background:darkMode?'rgba(0,0,0,0.45)':'rgba(255,255,255,0.96)', border:`0.5px solid ${border}`, borderRadius:7, fontFamily:'monospace', fontSize:10.5, color:text, display:'flex', justifyContent:'space-between' }}>
          <span style={{ color:muted, fontWeight:700 }}>{fmtDate(samples[tapped].date)}</span>
          <span style={{ color:accent, fontWeight:800 }}>{fmt(samples[tapped].value)}</span>
        </div>
      )}
    </div>
  );
}

// Renders **bold** spans inside a short AI string for inline emphasis.
function renderBold(str) {
  if (!str) return null;
  return String(str).split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith('**') && seg.endsWith('**')
      ? <b key={i} style={{ fontWeight: 700 }}>{seg.slice(2, -2)}</b>
      : <span key={i}>{seg}</span>
  );
}

// Hero body-fat ring — a 270° arc gauge (0..max %BF) with a target tick,
// LEAN/HIGH zone labels and an animated fill. Below it, a three-up stat row
// (30D grade · days to deadline · % left to lose). All values are real;
// nothing is hardcoded except the visual scale (max).
function BodyFatRing({ current, target = 15, max = 40, grade, daysLeft, toLose, colors }) {
  const { text, muted, border, gold, teal, danger } = colors;
  const r = 78, cx = 100, cy = 100;
  const circ = 2 * Math.PI * r;
  const arcLen = circ * 0.75;
  const filled = Math.max(0, Math.min(1, (current || 0) / max)) * arcLen;
  const targetFilled = Math.max(0, Math.min(1, (target || 0) / max)) * arcLen;
  const rot = `rotate(135 ${cx} ${cy})`;

  const [anim, setAnim] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (now) => {
      const p = Math.min((now - t0) / 1100, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setAnim(e * filled);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [filled]);

  const gradeColor = grade === 'A' || grade === 'B' ? teal : grade === 'C' ? gold : danger;
  const ang = (135 + (target / max) * 270) * (Math.PI / 180);
  const ox = cx + (r + 10) * Math.cos(ang), oy = cy + (r + 10) * Math.sin(ang);
  const ix = cx + (r - 9)  * Math.cos(ang), iy = cy + (r - 9)  * Math.sin(ang);

  const stats = [
    [grade || '—', '30D SCORE', gradeColor],
    [daysLeft != null ? String(daysLeft) : '—', 'DAYS LEFT', gold],
    [toLose != null && toLose > 0 ? toLose.toFixed(1) + '%' : '0%', 'TO LOSE', muted],
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', paddingTop:8 }}>
      <svg viewBox="0 0 200 200" style={{ width:200, height:200, overflow:'visible' }}>
        <defs>
          <linearGradient id="bfArc" gradientUnits="userSpaceOnUse" x1="30" y1="170" x2="170" y2="30">
            <stop offset="0%" stopColor="#c87510"/><stop offset="50%" stopColor={gold}/><stop offset="100%" stopColor="#ffd060"/>
          </linearGradient>
          <filter id="bfGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#26221c" strokeWidth={14} strokeDasharray={`${arcLen} ${circ-arcLen}`} transform={rot} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={gold+'22'} strokeWidth={14} strokeDasharray={`${targetFilled} ${circ-targetFilled}`} transform={rot} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#bfArc)" strokeWidth={14} strokeDasharray={`${anim} ${circ-anim}`} transform={rot} strokeLinecap="round" filter="url(#bfGlow)"/>
        <line x1={ix} y1={iy} x2={ox} y2={oy} stroke={gold+'cc'} strokeWidth="2" strokeLinecap="round"/>
        <text x={34}  y={172} textAnchor="middle" fill={teal}   fontSize="8" letterSpacing="0.8">LEAN</text>
        <text x={166} y={172} textAnchor="middle" fill={danger} fontSize="8" letterSpacing="0.8">HIGH</text>
        <text x={cx} y={88}  textAnchor="middle" fill={text}   fontSize="42" fontWeight="700" fontFamily="monospace">{current > 0 ? current.toFixed(1) : '—'}</text>
        <text x={cx} y={107} textAnchor="middle" fill={muted}  fontSize="10" letterSpacing="1.8">% BODY FAT</text>
        <text x={cx} y={126} textAnchor="middle" fill={gold+'aa'} fontSize="9" letterSpacing="1">TARGET {target}%</text>
      </svg>
      <div style={{ display:'flex', width:'100%', maxWidth:300, marginTop:4 }}>
        {stats.map(([val,lbl,col],i)=>(
          <div key={i} style={{ flex:1, textAlign:'center', padding:'10px 0', borderLeft: i>0 ? `1px solid ${border}` : 'none' }}>
            <div style={{ fontSize:22, fontWeight:700, color:col, fontFamily:'monospace', lineHeight:1 }}>{val}</div>
            <div style={{ fontSize:8, color:muted, letterSpacing:1, marginTop:3 }}>{lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Body-fat projection toward the target by the deadline. Three straight-line
// trajectories from today's %BF over `daysLeft`: current trend (extrapolated
// from the period slope), the path required to hit target, and a safe
// recommended path (≤0.5 %BF/week).
function ProjectionChart({ start, target, trendEnd, recEnd, daysLeft, startLabel, endLabel, colors }) {
  const { muted, border, gold, teal, danger } = colors;
  const clipId = useRef('proj' + Math.random().toString(36).slice(2)).current;
  const t = useDrawReveal([start, target, trendEnd, recEnd, daysLeft]);
  const W=300, H=110, pX=8, pY=12;
  const span = Math.max(daysLeft || 0, 7);
  const vals = [start, target, trendEnd, recEnd].filter(v => typeof v === 'number');
  const yMax = Math.ceil(Math.max(...vals) + 2);
  const yMin = Math.floor(Math.min(...vals) - 2);
  const toX = d => pX + (d/span)*(W-pX*2);
  const toY = v => pY + (1-(v-yMin)/(yMax-yMin || 1))*(H-pY*2);
  const seg = (a,b) => `M ${toX(0).toFixed(1)},${toY(a).toFixed(1)} L ${toX(span).toFixed(1)},${toY(b).toFixed(1)}`;
  const recArea = `${seg(start,recEnd)} L ${toX(span).toFixed(1)},${H} L ${toX(0).toFixed(1)},${H} Z`;
  const grids = [];
  for (let v = Math.ceil(yMin/5)*5; v < yMax; v += 5) grids.push(v);

  return (
    <div style={{ marginTop:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, padding:'0 2px' }}>
        <span style={{ fontSize:9.5, color:muted, fontFamily:'monospace', letterSpacing:'0.14em', fontWeight:700 }}>BODY FAT PROJECTION</span>
        <span style={{ fontSize:9, color:muted, fontFamily:'monospace' }}>{startLabel} → {endLabel}</span>
      </div>
      <div style={{ display:'flex', gap:14, marginBottom:8, padding:'0 2px' }}>
        {[[danger,true,'CURRENT TREND'],[gold+'8c',true,'REQUIRED'],[teal,false,'RECOMMENDED']].map(([col,dash,lbl])=>(
          <div key={lbl} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke={col} strokeWidth="2" strokeDasharray={dash?'3 2':''}/></svg>
            <span style={{ fontSize:8, color:muted, letterSpacing:0.5 }}>{lbl}</span>
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <linearGradient id="bfRecGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={teal} stopOpacity="0.18"/><stop offset="100%" stopColor={teal} stopOpacity="0.01"/>
          </linearGradient>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <rect x={0} y={0} width={Math.max(0.001, W * t)} height={H}/>
          </clipPath>
        </defs>
        {grids.map(v=>(
          <g key={v}>
            <line x1={pX} y1={toY(v)} x2={W-pX-14} y2={toY(v)} stroke={border} strokeWidth="0.5"/>
            <text x={W-pX-12} y={toY(v)+3} fill={muted} fontSize="7" fontFamily="monospace">{v}%</text>
          </g>
        ))}
        <line x1={pX} y1={toY(target)} x2={W-pX-14} y2={toY(target)} stroke={gold+'4d'} strokeWidth="0.8" strokeDasharray="4 3"/>
        <g clipPath={`url(#${clipId})`}>
          <path d={recArea} fill="url(#bfRecGrad)"/>
          <path d={seg(start,trendEnd)} fill="none" stroke={danger}  strokeWidth="1.5" strokeDasharray="4 3" strokeLinejoin="round"/>
          <path d={seg(start,target)}   fill="none" stroke={gold+'8c'} strokeWidth="1.5" strokeDasharray="4 3" strokeLinejoin="round"/>
          <path d={seg(start,recEnd)}   fill="none" stroke={teal}    strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
          <circle cx={toX(0)}    cy={toY(start)}  r="3.5" fill={gold}/>
          <circle cx={toX(span)} cy={toY(target)} r="3" fill="none" stroke={gold+'99'} strokeWidth="1.5"/>
          <circle cx={toX(span)} cy={toY(recEnd)} r="3" fill={teal}/>
        </g>
      </svg>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:4, padding:'0 2px' }}>
        <span style={{ fontSize:9, color:muted, fontFamily:'monospace' }}>{startLabel}</span>
        <span style={{ fontSize:9, color:muted, fontFamily:'monospace' }}>{endLabel}</span>
      </div>
    </div>
  );
}

export function BodyCompositionPanel({ calorieLog, today, weightLog, heightCm, goalWeightKg, colors, onSync, syncing, hasHealth }) {
  const { text, muted, border, s1, s2, gold, teal, accent, danger, darkMode } = colors;
  const [period, setPeriod] = useState(30); // 7 | 30 | 90 | 365

  // AI analysis state — cached per period in localStorage so switching back
  // doesn't re-spend tokens. Stored as { text, ts }. Generation runs as a
  // background job keyed by cacheKey(period) so leaving the screen doesn't
  // cancel the in-flight Gemini call; a haptic fires on completion.
  const cacheKey = (p) => `hayahBodyCompAnalysis_${p}`;
  const currentKey = cacheKey(period);
  const [analysis, setAnalysis] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cacheKey(30)) || 'null'); } catch { return null; }
  });
  const [analysisLoading, setAnalysisLoading] = useState(() => aiJob.isInFlight(cacheKey(30)));
  const [analysisError, setAnalysisError] = useState(() => aiJob.lastError(cacheKey(30)));
  useEffect(() => {
    // The subscribe callback also fires once immediately at subscribe
    // time, which syncs loading / error / analysis from the
    // authoritative source (module state, with localStorage status as
    // backstop). This closes the race where a job completes between
    // the caller's explicit isInFlight check and subscribe registration.
    const sync = () => {
      setAnalysisLoading(aiJob.isInFlight(currentKey));
      setAnalysisError(aiJob.lastError(currentKey));
      if (!aiJob.isInFlight(currentKey)) {
        try { setAnalysis(JSON.parse(localStorage.getItem(currentKey) || 'null')); } catch {}
      }
    };
    return aiJob.subscribe(currentKey, sync);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  // Merge sources: Apple Health bodyHistory (today's snapshot holds 30+ days)
  // and the manual weightLog. Apple Health wins on overlap; we de-dupe by
  // date string (YYYY-MM-DD).
  const todayLog = calorieLog?.[today] || {};
  const ahWeight  = todayLog.bodyHistory?.weight  || [];
  const ahBodyFat = todayLog.bodyHistory?.bodyFat || [];
  const manualWeight = (weightLog || []).map(w => ({ date: w.date, value: w.kg }));
  const mergeByDate = (a, b) => {
    const seen = new Set();
    const out = [];
    [...a, ...b].sort((x,y) => new Date(x.date) - new Date(y.date)).forEach(s => {
      const k = String(s.date).slice(0, 10);
      if (seen.has(k)) return;
      seen.add(k); out.push(s);
    });
    return out;
  };
  const weightAll  = mergeByDate(ahWeight, manualWeight);
  const bodyFatAll = mergeByDate(ahBodyFat, []);
  const cutoff = Date.now() - period * 86400000;
  const wIn  = weightAll.filter(s => new Date(s.date).getTime() >= cutoff);
  const bfIn = bodyFatAll.filter(s => new Date(s.date).getTime() >= cutoff);

  // Latest snapshot
  const latestW  = weightAll[weightAll.length - 1]  || null;
  const latestBF = bodyFatAll[bodyFatAll.length - 1] || null;
  const wKg   = latestW?.value || 0;
  const bfPct = latestBF?.value || 0;
  const leanMass = (wKg > 0 && bfPct > 0) ? wKg * (1 - bfPct/100) : 0;
  const fatMass  = (wKg > 0 && bfPct > 0) ? wKg * (bfPct/100)     : 0;
  const bmi      = (wKg > 0 && heightCm > 0) ? wKg / Math.pow(heightCm/100, 2) : 0;

  // Exact date + time the latest weight reading was recorded in Apple Health.
  // bodyHistory.weight entries carry the HealthKit sample's ISO timestamp in
  // `.date` (full date+time); manual log entries are day-only (no time).
  const latestAhWeight = [...ahWeight].sort((a, b) => new Date(a.date) - new Date(b.date)).pop() || null;
  const weightStampLabel = (() => {
    const iso = latestAhWeight?.date;
    if (!iso) return null;
    const ts = Date.parse(iso); if (!Number.isFinite(ts)) return null;
    const d = new Date(ts);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    const datePart = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
    const hasTime = /T\d\d:/.test(String(iso));
    return hasTime ? `${datePart}, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : datePart;
  })();

  // Period deltas + min/max/avg
  const stats = (arr) => {
    if (!arr.length) return null;
    const vs = arr.map(s => s.value);
    const first = arr[0].value, last = arr[arr.length-1].value;
    return { first, last, delta: last - first, min: Math.min(...vs), max: Math.max(...vs), avg: vs.reduce((a,b)=>a+b,0)/vs.length, n: arr.length };
  };
  const wStats  = stats(wIn);
  const bfStats = stats(bfIn);
  const leanFirstLast = (() => {
    if (!wStats || !bfStats) return null;
    const firstLean = wStats.first * (1 - bfStats.first/100);
    const lastLean  = wStats.last  * (1 - bfStats.last/100);
    return { delta: lastLean - firstLean, last: lastLean };
  })();
  const fatFirstLast = (() => {
    if (!wStats || !bfStats) return null;
    const firstFat = wStats.first * (bfStats.first/100);
    const lastFat  = wStats.last  * (bfStats.last/100);
    return { delta: lastFat - firstFat, last: lastFat };
  })();

  // BMI bands
  const bmiBand = bmi <= 0 ? null : bmi < 18.5 ? { label:'UNDER', color:teal } : bmi < 25 ? { label:'NORMAL', color:gold } : bmi < 30 ? { label:'OVER', color:'#f0a86b' } : { label:'OBESE', color:danger };

  // Distance to goal weight
  const toGoal = (goalWeightKg && wKg) ? (wKg - goalWeightKg) : null;

  // ── Body-fat goal + projection ──────────────────────────────────────
  // Target %BF and deadline come from calorieGoals when set, else the
  // user's stated goal (15% by 2026-08-01). Macro targets read straight
  // from calorieGoals.
  const goalsCfg = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('calorieGoals') || '{}') || {}; } catch { return {}; }
  }, [calorieLog]);
  const targetBF   = Number(goalsCfg.bodyFatGoal) || 15;
  const bfDeadline = goalsCfg.bodyFatDeadline || '2026-08-01';
  const daysLeft = (() => {
    const d = Math.round((new Date(bfDeadline).getTime() - new Date(today).getTime()) / 86400000);
    return Number.isFinite(d) && d > 0 ? d : 0;
  })();
  const toLoseBF = bfPct > 0 ? Math.max(0, bfPct - targetBF) : null;
  // Trend: extrapolate the period body-fat slope forward to the deadline.
  const bfPerDay = (bfStats && period > 0) ? bfStats.delta / period : 0;
  const trendEnd = bfPct > 0 ? +(bfPct + bfPerDay * daysLeft).toFixed(1) : null;
  // Recommended: lose no faster than 0.5 %BF/week, never below target.
  const recEnd = bfPct > 0 ? +Math.max(targetBF, bfPct - 0.5 * (daysLeft / 7)).toFixed(1) : null;
  // Deterministic grade fallback when no AI grade is cached — lean up & fat
  // down is best (A); lean down & fat up is the worst recomp outcome (F).
  const gradeFallback = (() => {
    const lean = leanFirstLast?.delta;
    const fat  = fatFirstLast?.delta;
    if (lean == null || fat == null) return null;
    if (lean >= 0.1 && fat <= -0.1) return 'A';
    if (lean >= 0    && fat <= 0)    return 'B';
    if (lean < -0.1  && fat > 0.1)   return 'F';
    if (lean < 0     || fat > 0)     return 'D';
    return 'C';
  })();
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short' }).toUpperCase(); } catch { return iso; } };
  const blue = '#6499e9';
  const macroTargets = [
    { lbl:'CALORIES', val: goalsCfg.calories, unit:'kcal', color:gold },
    { lbl:'PROTEIN',  val: goalsCfg.protein,  unit:'g',    color:teal },
    { lbl:'CARBS',    val: goalsCfg.carbs,    unit:'g',    color:blue },
    { lbl:'FAT',      val: goalsCfg.fat,      unit:'g',    color:'#c07840' },
  ];
  const sgn = (n) => (n >= 0 ? '+' : '−') + Math.abs(n).toFixed(1);
  const deltaChips = [
    leanFirstLast && { label:`Lean ${sgn(leanFirstLast.delta)}kg`, color: leanFirstLast.delta >= 0 ? teal : danger },
    fatFirstLast  && { label:`Fat ${sgn(fatFirstLast.delta)}kg`,   color: fatFirstLast.delta  <= 0 ? teal : danger },
    bfStats       && { label:`BF ${sgn(bfStats.delta)}%`,          color: bfStats.delta       <= 0 ? teal : danger },
  ].filter(Boolean);

  const PERIODS = [
    { val: 7,   l: '7D'  },
    { val: 30,  l: '30D' },
    { val: 90,  l: '90D' },
    { val: 365, l: '1Y'  },
  ];
  const chip = (active) => ({
    padding:'5px 10px', borderRadius:8, fontFamily:'monospace', fontSize:'10.5px', fontWeight:700,
    letterSpacing:'0.08em', cursor:'pointer', border:`1px solid ${active?gold:border}`,
    background: active ? gold+'18' : 'transparent', color: active ? gold : muted,
  });
  const hasAnyData = weightAll.length > 0 || bodyFatAll.length > 0;

  // AI analysis handler — runs the Gemini call as a background job keyed
  // by the period cache key, so leaving the screen mid-flight is safe and
  // a haptic fires when it completes. The snapshot is captured at click
  // time so the in-flight job is self-contained even if this component
  // unmounts before the response arrives.
  const runAnalysis = () => {
    if (analysisLoading) return;
    if (!getGeminiKey()) {
      setAnalysisError('Add a Gemini API key in Hayah Settings → AI Assistant.');
      return;
    }
    const snapshot = {
      period_days: period,
      sample_count: wIn.length,
      current: {
        weight_kg:    wKg   > 0 ? +wKg.toFixed(1)      : null,
        body_fat_pct: bfPct > 0 ? +bfPct.toFixed(1)    : null,
        lean_mass_kg: leanMass > 0 ? +leanMass.toFixed(1) : null,
        fat_mass_kg:  fatMass  > 0 ? +fatMass.toFixed(1)  : null,
        bmi:          bmi > 0 ? +bmi.toFixed(1) : null,
        bmi_band:     bmiBand?.label || null,
      },
      period_window: wStats ? {
        start_weight_kg: +wStats.first.toFixed(1),
        end_weight_kg:   +wStats.last.toFixed(1),
        weight_delta_kg: +wStats.delta.toFixed(1),
        weight_min_kg:   +wStats.min.toFixed(1),
        weight_max_kg:   +wStats.max.toFixed(1),
        weight_avg_kg:   +wStats.avg.toFixed(1),
      } : null,
      body_fat_window: bfStats ? {
        start_pct:  +bfStats.first.toFixed(1),
        end_pct:    +bfStats.last.toFixed(1),
        delta_pct:  +bfStats.delta.toFixed(1),
        min_pct:    +bfStats.min.toFixed(1),
        max_pct:    +bfStats.max.toFixed(1),
        avg_pct:    +bfStats.avg.toFixed(1),
      } : null,
      lean_delta_kg: leanFirstLast ? +leanFirstLast.delta.toFixed(1) : null,
      fat_delta_kg:  fatFirstLast  ? +fatFirstLast.delta.toFixed(1)  : null,
      goal_weight_kg: goalWeightKg || null,
      distance_to_goal_kg: toGoal != null ? +toGoal.toFixed(1) : null,
      height_cm: heightCm || null,
      body_fat_goal: {
        target_pct:        targetBF,
        deadline:          bfDeadline,
        days_left:         daysLeft,
        pct_left_to_lose:  toLoseBF != null ? +toLoseBF.toFixed(1) : null,
        projected_pct_at_deadline_current_trend: trendEnd,
        recommended_pct_at_deadline:             recEnd,
      },
      daily_macro_targets: {
        calories_kcal: goalsCfg.calories || null,
        protein_g:     goalsCfg.protein  || null,
        carbs_g:       goalsCfg.carbs    || null,
        fat_g:         goalsCfg.fat       || null,
      },
      // Fitness + early-warning signals from HealthKit. VO2max rising while
      // weight drops is the gold-standard recomp signal (lean cardio
      // capacity preserved); wrist-temp delta lets the AI distinguish
      // genuine over-reaching from illness when other markers wobble.
      fitness: {
        vo2_max_ml_kg_min:        todayLog.vo2Max         || null,
        vo2_max_delta_30d:        todayLog.vo2MaxDelta30d || null,
        wrist_temp_delta_c_today: todayLog.wristTempDeltaC || null,
      },
    };
    const key = currentKey;
    aiJob.runJob(key, async () => {
      const profile = getUserProfile();
      const sys = composeSystem([
        COACH_VOICE,
        formatProfileForPrompt(profile),
        EVIDENCE_STANDARD,
        OUTPUT_JSON_STRICT,
        `Task: Score this body-composition window and prescribe this week's plan toward the body-fat goal. Return JSON matching the schema.

GRADE (A–F) — rate the ${period}-day recomposition outcome:
- A: lean mass up AND fat mass down (textbook recomp)
- B: lean held with fat down, or clear fat loss with stable lean
- C: flat / noisy — no clear direction
- D: lean down OR fat up (one marker wrong)
- F: lean down AND fat up (the worst outcome)
Weigh body_fat_goal.projected_pct_at_deadline_current_trend vs target_pct: if the current trend misses the target, that pulls the grade down.

SUMMARY: one sentence, ≤ 22 words, plain language, naming the decisive direction (cite a number where it sharpens the point). Wrap the single most decisive number/keyword in **double-asterisks**.

ACTIONS: 3–4 imperative, specific items for THIS WEEK. Each ≤ 26 words, cites a concrete number (kg, g protein, kcal deficit, %BF/week, weeks-to-goal), and wraps the decisive number in **double-asterisks**. Cover the highest-leverage levers only: training stimulus / progressive overload if lean is dropping, protein floor, calorie deficit size, and a realistic timeline reality-check (recommended_pct_at_deadline vs the required path). If fitness.vo2_max_ml_kg_min or wrist_temp_delta_c_today is notable, you may fold it into one action. Do not restate the macro targets — the UI shows them.`,
      ]);
      const userMsg = `Body composition window:\n\n${JSON.stringify(snapshot, null, 2)}`;
      try { publishSources("body-composition", bodyCompositionSources(snapshot)); } catch {}
      const out = await callGemini(userMsg, {
        systemInstruction: sys,
        model: MODELS.todayInsights || 'gemini-2.5-flash',
        maxOutputTokens: 4096,
        responseSchema: {
          type: 'object',
          properties: {
            grade:   { type: 'string', enum: ['A','B','C','D','F'] },
            summary: { type: 'string' },
            actions: { type: 'array', items: { type: 'string' }, minItems: 3, maxItems: 4 },
          },
          required: ['grade','summary','actions'],
        },
        feature: 'bodyComposition',
      });
      let parsed;
      try { parsed = JSON.parse(out); } catch { parsed = null; }
      const result = parsed && parsed.grade
        ? { grade: parsed.grade, summary: parsed.summary || '', actions: Array.isArray(parsed.actions) ? parsed.actions : [], ts: Date.now() }
        : { text: out, ts: Date.now() };
      try { localStorage.setItem(key, JSON.stringify(result)); } catch {}
    });
  };

  return (
    <div style={{borderRadius:14, border:`1px solid ${border}`, background:s1, marginBottom:12, padding:'14px 14px 16px'}}>
      {/* Header */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
        <div style={{fontSize:'13px', fontWeight:800, color:text}}>📊 Body Composition</div>
        <span style={{fontSize:9, fontWeight:700, color:muted, fontFamily:'monospace', letterSpacing:'0.12em', opacity:0.7}}>
          {syncing ? 'SCANNING…' : (hasHealth ? '↓ PULL TO SYNC' : '')}
        </span>
      </div>

      {!hasAnyData ? (
        <div style={{padding:'18px 14px', textAlign:'center', background:s2, border:`0.5px dashed ${border}`, borderRadius:10}}>
          <div style={{fontSize:12, color:muted, lineHeight:1.6}}>
            No body composition data yet.<br/>
            {hasHealth ? 'Pull down to scan weight + body fat from Apple Health.' : 'Enable Apple Health on this device to start tracking.'}
          </div>
        </div>
      ) : (
        <>
          {/* Hero — animated body-fat ring + goal stat row */}
          {bfPct > 0 && (
            <>
              <BodyFatRing
                current={bfPct}
                target={targetBF}
                grade={analysis?.grade || gradeFallback}
                daysLeft={daysLeft}
                toLose={toLoseBF}
                colors={colors}
              />
              <div style={{height:1, background:border, margin:'14px 0'}}/>
            </>
          )}

          {/* Current snapshot — weight / lean / fat cards */}
          <div style={{display:'flex', gap:8, marginBottom:10}}>
            {[
              { lbl:'WEIGHT',    val:wKg,      delta:wStats?.delta,          goodDown:true  },
              { lbl:'LEAN MASS', val:leanMass, delta:leanFirstLast?.delta,   goodDown:false },
              { lbl:'FAT MASS',  val:fatMass,  delta:fatFirstLast?.delta,    goodDown:true  },
            ].map(({lbl,val,delta,goodDown}) => {
              const dc = delta == null ? muted : ((goodDown ? delta <= 0 : delta >= 0) ? teal : '#f0a86b');
              return (
                <div key={lbl} style={{flex:1, background:s2, borderRadius:14, padding:'13px 12px'}}>
                  <div style={{fontSize:9, color:muted, letterSpacing:'0.13em', fontWeight:700, marginBottom:6}}>{lbl}</div>
                  <div style={{display:'flex', alignItems:'baseline', gap:3, marginBottom:4}}>
                    <span style={{fontSize:22, fontWeight:700, color:text, fontFamily:'monospace'}}>{val > 0 ? val.toFixed(1) : '—'}</span>
                    <span style={{fontSize:11, color:muted}}>kg</span>
                  </div>
                  {delta != null && (
                    <div style={{fontSize:10, color:dc, fontFamily:'monospace'}}>
                      {sgn(delta)} <span style={{color:muted, fontSize:9}}>{period}D</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Exact Apple Health weight sample timestamp */}
          {weightStampLabel && (
            <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:-2, marginBottom:12, fontSize:10.5, color:muted }}>
              <span style={{ fontSize:10 }}>⌚</span>
              Weight from Apple Health · <span style={{ color:text, fontWeight:600 }}>{weightStampLabel}</span>
            </div>
          )}

          {/* Secondary metrics row — BMI + Goal distance */}
          {(bmi > 0 || toGoal != null) && (
            <div style={{display:'flex', gap:8, marginBottom:12, fontSize:10.5}}>
              {bmi > 0 && (
                <div style={{flex:1, padding:'8px 10px', background:s2, border:`0.5px solid ${border}`, borderRadius:8}}>
                  <span style={{color:muted, fontFamily:'monospace', letterSpacing:'0.12em', fontWeight:700}}>BMI </span>
                  <span style={{color:text, fontWeight:800, fontVariantNumeric:'tabular-nums'}}>{bmi.toFixed(1)}</span>
                  {bmiBand && <span style={{marginLeft:6, color:bmiBand.color, fontFamily:'monospace', fontWeight:700, fontSize:9}}>{bmiBand.label}</span>}
                </div>
              )}
              {toGoal != null && (
                <div style={{flex:1, padding:'8px 10px', background:s2, border:`0.5px solid ${border}`, borderRadius:8}}>
                  <span style={{color:muted, fontFamily:'monospace', letterSpacing:'0.12em', fontWeight:700}}>TO GOAL </span>
                  <span style={{color: Math.abs(toGoal) < 0.5 ? teal : text, fontWeight:800, fontVariantNumeric:'tabular-nums'}}>
                    {toGoal > 0 ? '−' : toGoal < 0 ? '+' : ''}{Math.abs(toGoal).toFixed(1)}kg
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Period selector */}
          <div style={{display:'flex', gap:6, marginBottom:10}}>
            {PERIODS.map(p => (
              <button key={p.val} onClick={()=>setPeriod(p.val)} style={chip(period===p.val)}>{p.l}</button>
            ))}
          </div>

          {/* Trend charts */}
          <div style={{display:'flex', flexDirection:'column', gap:10}}>
            <div>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4, padding:'0 2px'}}>
                <span style={{fontSize:9.5, color:muted, fontFamily:'monospace', letterSpacing:'0.14em', fontWeight:700}}>WEIGHT (kg)</span>
                {wStats && (
                  <span style={{fontSize:10, fontFamily:'monospace', color:muted, letterSpacing:'0.04em'}}>
                    min {wStats.min.toFixed(1)} · max {wStats.max.toFixed(1)} · avg {wStats.avg.toFixed(1)}
                  </span>
                )}
              </div>
              <VitalTrendChart samples={wIn} accent={gold} h={70} colors={colors} />
            </div>
            <div>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4, padding:'0 2px'}}>
                <span style={{fontSize:9.5, color:muted, fontFamily:'monospace', letterSpacing:'0.14em', fontWeight:700}}>BODY FAT (%)</span>
                {bfStats && (
                  <span style={{fontSize:10, fontFamily:'monospace', color:muted, letterSpacing:'0.04em'}}>
                    min {bfStats.min.toFixed(1)} · max {bfStats.max.toFixed(1)} · avg {bfStats.avg.toFixed(1)}
                  </span>
                )}
              </div>
              <VitalTrendChart samples={bfIn} accent={'#f0a86b'} h={70} colors={colors} format={v => v.toFixed(1)+'%'} />
            </div>
          </div>

          {/* Body-fat projection toward the goal by the deadline */}
          {bfPct > 0 && daysLeft > 0 && (
            <ProjectionChart
              start={+bfPct.toFixed(1)}
              target={targetBF}
              trendEnd={trendEnd}
              recEnd={recEnd}
              daysLeft={daysLeft}
              startLabel={fmtDate(today)}
              endLabel={fmtDate(bfDeadline)}
              colors={colors}
            />
          )}

          {/* Period summary — readable diff between start & end */}
          {wStats && (
            <div style={{marginTop:12, padding:'10px 12px', background:s2, border:`0.5px solid ${border}`, borderLeft:`3px solid ${(fatFirstLast?.delta > 0 || leanFirstLast?.delta < 0) ? danger : teal}`, borderRadius:10}}>
              <div style={{fontSize:9.5, color:muted, fontFamily:'monospace', letterSpacing:'0.14em', fontWeight:700, marginBottom:6}}>
                {period}-DAY CHANGE · {wStats.n} sample{wStats.n===1?'':'s'}
              </div>
              <div style={{fontSize:12, color:text, lineHeight:1.55, fontFamily:'monospace'}}>
                <div>Weight: <b>{wStats.first.toFixed(1)} → {wStats.last.toFixed(1)} kg</b> <span style={{color: wStats.delta < 0 ? teal : wStats.delta > 0 ? '#f0a86b' : muted}}>({wStats.delta > 0 ? '+' : ''}{wStats.delta.toFixed(1)} kg)</span></div>
                {bfStats && <div style={{marginTop:2}}>Body fat: <b>{bfStats.first.toFixed(1)} → {bfStats.last.toFixed(1)} %</b> <span style={{color: bfStats.delta < 0 ? teal : bfStats.delta > 0 ? '#f0a86b' : muted}}>({bfStats.delta > 0 ? '+' : ''}{bfStats.delta.toFixed(1)} %)</span></div>}
                {leanFirstLast && <div style={{marginTop:2, color:teal}}>Lean: {leanFirstLast.delta > 0 ? '+' : ''}{leanFirstLast.delta.toFixed(1)} kg</div>}
                {fatFirstLast  && <div style={{color:'#c97e6b'}}>Fat: {fatFirstLast.delta > 0 ? '+' : ''}{fatFirstLast.delta.toFixed(1)} kg</div>}
              </div>
            </div>
          )}

          {/* AI Coach — recomposition grade, this week's actions, and daily
              macro targets. Structured JSON from Gemini; cached per period so
              flipping 7D/30D doesn't re-spend tokens. */}
          {wStats && (() => {
            const grade = analysis?.grade || gradeFallback;
            const gradeColor = grade === 'A' || grade === 'B' ? teal : grade === 'C' ? gold : danger;
            const hasResult = !analysisLoading && (analysis?.grade || analysis?.text);
            return (
            <div style={{marginTop:16, background:s2, border:`1px solid ${gold}2e`, borderRadius:16, overflow:'hidden'}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'13px 14px', borderBottom:`1px solid ${border}`}}>
                <div style={{display:'flex', alignItems:'center', gap:10}}>
                  <div style={{width:30, height:30, borderRadius:8, background:`${gold}1a`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, color:gold}}>✦</div>
                  <div>
                    <div style={{fontSize:11.5, fontWeight:700, color:gold, letterSpacing:'0.04em'}}>AI ANALYSIS</div>
                    <div style={{fontSize:9, color:muted, marginTop:1, fontFamily:'monospace', display:'inline-flex', gap:6, alignItems:'baseline'}}>
                      <span>{period}D{analysis?.ts ? ` · ${new Date(analysis.ts).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}` : ''}</span>
                      <AICostBadge feature="bodyComposition" prefix="·" fontSize={9} color={muted} />
                    </div>
                  </div>
                </div>
                <button onClick={runAnalysis} disabled={analysisLoading}
                  style={{padding:'5px 12px', borderRadius:20, border:`1px solid ${gold}47`, background:analysisLoading?'transparent':`${gold}14`, color:gold, fontSize:10, fontWeight:700, letterSpacing:'0.04em', cursor:'pointer', opacity:analysisLoading?0.55:1, fontFamily:'monospace'}}>
                  {analysisLoading ? 'THINKING…' : analysis ? '⟳ REFRESH' : '✦ ANALYSE'}
                </button>
              </div>

              <div style={{padding:'14px 14px 16px'}}>
                {analysisLoading && (
                  <AIAnalysingPipeline
                    label="Analysing body composition"
                    computeLabel="Reading trend"
                    feature="body-composition"
                    accent="teal"
                    theme={{ teal, amber: gold, ink3: muted, ink4: muted, lineSoft: border, line: border }}
                  />
                )}

                {hasResult && (
                  <>
                    {/* Score */}
                    <div style={{display:'flex', alignItems:'center', gap:14, background:s1, borderRadius:12, padding:14, marginBottom: (analysis?.actions?.length || macroTargets.some(m=>m.val)) ? 18 : 0}}>
                      <div style={{textAlign:'center', minWidth:52}}>
                        <div style={{fontSize:44, fontWeight:800, color:gradeColor, lineHeight:1, fontFamily:'monospace'}}>{grade || '—'}</div>
                        <div style={{fontSize:7.5, color:muted, letterSpacing:'0.08em', marginTop:2}}>{period}D SCORE</div>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12.5, color:text, lineHeight:1.55, marginBottom: (deltaChips.length && analysis?.summary) ? 5 : 0}}>
                          {analysis?.summary
                            ? renderBold(analysis.summary)
                            : (analysis?.text ? <AIBulletText text={analysis.text} accent={teal} textColor={text} fontSize={12.5} /> : null)}
                        </div>
                        {deltaChips.length > 0 && analysis?.summary && (
                          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                            {deltaChips.map(c => <span key={c.label} style={{fontSize:10, color:c.color, fontFamily:'monospace'}}>{c.label}</span>)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* This week's actions */}
                    {analysis?.actions?.length > 0 && (
                      <div style={{marginBottom:18}}>
                        <div style={{fontSize:9, color:muted, letterSpacing:'0.13em', fontWeight:700, marginBottom:12}}>THIS WEEK'S ACTIONS</div>
                        <div style={{display:'flex', flexDirection:'column', gap:12}}>
                          {analysis.actions.map((a,i)=>(
                            <div key={i} style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                              <div style={{minWidth:22, height:22, borderRadius:6, background:`${gold}14`, border:`1px solid ${gold}33`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:gold, marginTop:1, fontFamily:'monospace', flexShrink:0}}>{i+1}</div>
                              <div style={{fontSize:12.5, color:muted, lineHeight:1.6}}>{renderBold(a)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Daily macro targets */}
                    {macroTargets.some(m=>m.val) && (
                      <div>
                        <div style={{fontSize:9, color:muted, letterSpacing:'0.13em', fontWeight:700, marginBottom:10}}>DAILY MACRO TARGETS</div>
                        <div style={{display:'flex', gap:8}}>
                          {macroTargets.map(m=>(
                            <div key={m.lbl} style={{flex:1, background:s1, borderRadius:11, padding:'10px 6px', textAlign:'center'}}>
                              <div style={{fontSize:17, fontWeight:700, color:m.color, fontFamily:'monospace', lineHeight:1}}>{m.val ? (m.lbl === 'CALORIES' ? m.val.toLocaleString() : m.val) : '—'}</div>
                              <div style={{fontSize:9, color:muted, marginTop:2}}>{m.unit}</div>
                              <div style={{fontSize:7.5, color:muted, marginTop:3, letterSpacing:0.5}}>{m.lbl}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!analysisLoading && analysisError && !analysis?.grade && !analysis?.text && (
                  <div style={{fontSize:11, color:danger, lineHeight:1.55}}>{analysisError}</div>
                )}
                {!analysis && !analysisLoading && !analysisError && (
                  <div style={{fontSize:11.5, color:muted, lineHeight:1.55}}>
                    Tap ANALYSE for a {period}-day recomposition grade, this week's actions, and your daily macro targets toward {targetBF}% body fat.
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function ActivityOverlayContent() {
  const { darkMode, gold, teal, text, muted, border, ink4, lineSoft, caloriesBurned, todayData, prayerTimes, goals } = useContext(VitalsCtx);
  const last7 = useLast7Days();
  const steps = todayData.steps||0;
  const goal = goals.steps || 12000;
  const pct = Math.min(1, steps/goal);
  const animSteps = useAnimatedValue(steps, 800, 80);
  const animPct = useAnimatedValue(pct, 800, 80);
  const distanceKm = todayData.distanceMeters > 0 ? todayData.distanceMeters / 1000 : 0;
  const activeMins = todayData.exerciseMinutes || 0;

  const wts = [0,0,0,0,0,0,2,8,18,12,9,14,22,18,7,11,28,42,31,16,9,4,1,0];
  const nowHour = new Date().getHours();
  // Spread today's steps only across hours that have elapsed; future hours
  // render as zero. (Per-hour HealthKit samples would replace this.)
  const wSumElapsed = wts.slice(0, nowHour+1).reduce((a,b)=>a+b,0) || 1;
  const hourly = wts.map((w,i)=> i > nowHour ? 0 : (steps>0 ? Math.round((w/wSumElapsed)*steps) : 0));
  const peak = Math.max(1, ...hourly);
  const actRef = useRef(null);
  const [actCursor, setActCursor] = useState(null);
  const _actDragging = useRef(false);
  const _getActFrac = e => { if(!actRef.current) return null; const r=actRef.current.getBoundingClientRect(); return Math.max(0,Math.min(1,(e.clientX-r.left)/r.width)); };
  const _onActDown = e => { _actDragging.current=true; try{e.currentTarget.setPointerCapture(e.pointerId);}catch{} const f=_getActFrac(e); if(f!=null) setActCursor({frac:f}); };
  const _onActMove = e => { if(!_actDragging.current) return; const f=_getActFrac(e); if(f!=null) setActCursor({frac:f}); };
  const _onActUp = () => { _actDragging.current=false; setActCursor(null); };
  const actHour = actCursor ? Math.min(23, Math.floor(actCursor.frac * 24)) : null;

  const salahHours = useMemo(()=>{
    if (!prayerTimes) return [];
    return ['Fajr','Dhuhr','Asr','Maghrib','Isha'].map(name=>{
      const m = _hhmmToMins(prayerTimes[name]);
      return m==null ? null : { name, hour: m/60 };
    }).filter(Boolean);
  }, [prayerTimes]);

  const week = last7.map(d=>({ value:d.steps, dow:d.dow, isToday:d.isToday, date:d.key }));
  const streak = (()=>{ let s=0; for (let i=last7.length-1;i>=0;i--){ if (last7[i].steps>=goal*0.5) s++; else break; } return s; })();

  const restingHR  = todayData.restingHeartRate || 0;
  const standHoursVal = todayData.standHours || 0;
  const hrItems = [
    restingHR   > 0 ? { label:'HEART RATE', value: restingHR,     sub:'bpm · resting', color:'#ff6b6b' } : null,
    standHoursVal>0 ? { label:'STAND',       value: standHoursVal, sub:'hrs today',      color:teal      } : null,
  ].filter(Boolean);

  // Apple Health body composition — read-only weight (kg) + body fat (%) with
  // 30-day sample history. Persisted on todayData by readAppleHealth.
  const bodyHistory = todayData.bodyHistory || { weight: [], bodyFat: [] };
  const weightSamples  = bodyHistory.weight  || [];
  const bodyFatSamples = bodyHistory.bodyFat || [];
  const bodyMassKg = todayData.bodyMassKg || 0;
  const bodyFatPct = todayData.bodyFatPct || 0;
  const bodyDelta = (samples, days) => {
    if (!samples || samples.length === 0) return null;
    const cutoff = Date.now() - days * 86400000;
    const earlier = samples.filter(s => new Date(s.date).getTime() <= cutoff).slice(-1)[0];
    const latest = samples[samples.length - 1];
    if (!earlier || !latest) return null;
    return latest.value - earlier.value;
  };
  const wD7  = bodyDelta(weightSamples,  7);
  const wD30 = bodyDelta(weightSamples,  30);
  const fD7  = bodyDelta(bodyFatSamples, 7);
  const fD30 = bodyDelta(bodyFatSamples, 30);
  const fmtBodyDelta = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;
  // Days-ago label for the most recent weight sample. "today" if logged
  // today, "yesterday" for the day before, otherwise "N days ago".
  const daysAgoLabel = (samples) => {
    if (!samples?.length) return null;
    const last = samples[samples.length - 1];
    if (!last?.date) return null;
    const ms = new Date().setHours(0,0,0,0) - new Date(last.date).setHours(0,0,0,0);
    const days = Math.round(ms / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
  };
  const weightAgo = daysAgoLabel(weightSamples);
  const bodyFatAgo = daysAgoLabel(bodyFatSamples);
  // Down is "good" for both weight and body fat by default — green on negative delta.
  const bodyTrendColor = v => v == null ? ink4 : (v < 0 ? teal : v > 0 ? gold : ink4);
  const showBodyComp = bodyMassKg > 0 || bodyFatPct > 0 || weightSamples.length > 0 || bodyFatSamples.length > 0;
  const bodyStatItems = [
    bodyMassKg > 0 && { label:'WEIGHT',  value: bodyMassKg.toFixed(1),       sub: `kg · ${weightAgo || 'latest'}`,  color: gold },
    bodyFatPct > 0 && { label:'BODY FAT',value: `${bodyFatPct.toFixed(1)}%`, sub: bodyFatAgo || 'composition',  color: teal },
  ].filter(Boolean);

  return (
    <div>
      <MetricStatGrid items={[
        { label:'STEPS', value: Math.round(animSteps).toLocaleString(), sub:`${Math.round(animPct*100)}% of ${goal/1000}k`, color:gold },
        { label:'DISTANCE', value: distanceKm>0 ? distanceKm.toFixed(1) : '—', sub:'km', color:teal },
        { label:'ACTIVE', value: activeMins>0 ? activeMins : '—', sub:'min today', color:gold },
        { label:'KCAL', value: Math.round(caloriesBurned).toLocaleString(), sub:'from movement', color:teal },
      ]}/>
      {hrItems.length>0 && <MetricStatGrid items={hrItems}/>}

      <MetricSectionHeader num="01" label="HOURLY STEPS" right={salahHours.length?'· SALAH AWARE':'TODAY'}/>
      <div
        ref={actRef}
        style={{ position:'relative', height:180, padding:'10px 4px 0', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12, overflow:'hidden', touchAction:'none', userSelect:'none', WebkitUserSelect:'none' }}
        onPointerDown={_onActDown} onPointerMove={_onActMove} onPointerUp={_onActUp} onPointerCancel={_onActUp}
      >
        {salahHours.map(s=>(
          <div key={s.name} style={{ position:'absolute', top:0, bottom:18, width:1.5, left:`calc(${(s.hour/24)*100}% + 4px)`, background:`${gold}55`, boxShadow:`0 0 4px ${gold}33` }}>
            <div style={{ position:'absolute', top:2, left:3, fontSize:7.5, color:gold, fontFamily:'monospace', letterSpacing:'0.08em', fontWeight:700, whiteSpace:'nowrap', textShadow:'0 0 6px rgba(0,0,0,0.6)' }}>{s.name.slice(0,3).toUpperCase()}</div>
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'flex-end', gap:1.5, height:140 }}>
          {hourly.map((v,i)=>{ const h=v===0?1:Math.max(2,(v/peak)*132); const isNow=i===nowHour; const future=i>nowHour; return (
            <div
              key={i}
              style={{ flex:1, height:h, borderRadius:1, background:v===0?lineSoft:(isNow?gold:'rgba(255,255,255,0.18)'), opacity:future?0.25:(v===0?0.4:(isNow?1:0.85)), boxShadow:isNow?`0 0 6px ${gold}`:'none', transformOrigin:'bottom', animation:`barRise 580ms cubic-bezier(0.22,0.61,0.36,1) ${i*22}ms backwards` }}/>
          );})}
        </div>
        {actCursor && (
          <div style={{ position:'absolute', top:0, bottom:18, left:`${actCursor.frac*100}%`, pointerEvents:'none', zIndex:10 }}>
            <div style={{ position:'absolute', top:0, bottom:0, left:0, width:1.5, background:`${gold}cc`, opacity:0.9 }}/>
            <div style={{ position:'absolute', top:6, ...(actCursor.frac>0.6?{right:6}:{left:6}), background:darkMode?'rgba(10,10,10,0.82)':'rgba(255,255,255,0.96)', border:`0.5px solid ${border}`, borderRadius:8, padding:'6px 10px', fontFamily:'monospace', fontSize:11, color:text, whiteSpace:'nowrap', display:'flex', flexDirection:'column', gap:3, boxShadow:'0 2px 10px rgba(0,0,0,0.4)', pointerEvents:'none' }}>
              <span style={{ color:muted, letterSpacing:'0.1em', fontWeight:700 }}>{String(actHour).padStart(2,"0")}:00</span>
              <span><span style={{ color:gold, fontWeight:700 }}>{hourly[actHour].toLocaleString()}</span> <span style={{ color:muted }}>steps</span></span>
            </div>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0 0', fontSize:11, color:ink4, fontFamily:"monospace" }}>
          <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
        </div>
      </div>

      <MetricSectionHeader num="02" label="LAST 7 DAYS" right={streak>0?`${streak}-DAY STREAK`:'—'}/>
      <MetricSparkBars values={week} accent={gold} format={v=>v>=1000?`${(v/1000).toFixed(1)}k`:Math.round(v)} unit="steps"/>

      {showBodyComp && (
        <>
          <MetricSectionHeader num="03" label="BODY COMPOSITION" right="APPLE HEALTH"/>
          {bodyStatItems.length > 0 && <MetricStatGrid items={bodyStatItems}/>}
          <MetricStatGrid items={[
            { label:'WEIGHT 7D',  value: fmtBodyDelta(wD7),  sub:'vs 7d ago',  color: bodyTrendColor(wD7) },
            { label:'WEIGHT 30D', value: fmtBodyDelta(wD30), sub:'vs 30d ago', color: bodyTrendColor(wD30) },
            { label:'BF% 7D',     value: fmtBodyDelta(fD7),  sub:'vs 7d ago',  color: bodyTrendColor(fD7) },
            { label:'BF% 30D',    value: fmtBodyDelta(fD30), sub:'vs 30d ago', color: bodyTrendColor(fD30) },
          ]}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
            <BodyTrendLine samples={weightSamples}  label="WEIGHT · 30D"   accent={gold} format={v=>`${v.toFixed(1)} kg`}/>
            <BodyTrendLine samples={bodyFatSamples} label="BODY FAT · 30D" accent={teal} format={v=>`${v.toFixed(1)}%`}/>
          </div>
        </>
      )}

      <MetricSectionHeader num={showBodyComp ? "04" : "03"} label="REFLECTION"/>
      <div style={{ padding:'12px 14px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12, fontSize:11, color:text, lineHeight:1.55 }}>
        {steps>=goal ? <>Mā shā' Allāh — <b style={{ color:gold }}>{steps.toLocaleString()}</b> steps. The body the Most Merciful entrusted you is well-honoured today.</>
          : <>{steps>0?<>You've moved <b style={{ color:gold }}>{steps.toLocaleString()}</b> steps. <b>{(goal-steps).toLocaleString()}</b> more steps to reach today's goal.</>:<>No movement logged yet today. A short walk between Maghrib and Isha is a beautiful reset.</>}</>}
      </div>
    </div>
  );
}

function SleepOverlayContent() {
  const { darkMode, gold, text, muted, border, ink4, lineSoft, todayData, prayerTimes, goals } = useContext(VitalsCtx);
  const last7 = useLast7Days();
  const totalMins = todayData.sleepMinutes||0;
  const deepMins = todayData.deepMinutes||0;
  const remMins  = todayData.remMinutes||0;
  const awakeMins = todayData.awakeMinutes||0;
  const cream = '#e8e2d2';
  const animTotal = useAnimatedValue(totalMins, 800, 80);
  const efficiency = totalMins>0 ? Math.round(totalMins / (totalMins + awakeMins) * 100) : 0;
  const goalMins = (goals.sleep || 8) * 60;
  // Net debt: sum of (goal − actual) over tracked nights, NO floor at 0.
  // Oversleeping a night counterbalances a deficit on another night.
  // Missing-data nights are still excluded (they aren't proof of zero
  // sleep, just absence of HealthKit data).
  const trackedDays = last7.filter(d => d.sleep > 0);
  const debt = trackedDays.reduce((s,d)=>s + (goalMins - d.sleep), 0);
  const trackedCount = trackedDays.length;
  const debtAbs = Math.abs(debt);
  const debtIsSurplus = debt < 0;

  const sleepSegments = todayData.sleepSegments || [];
  // Handles string ("ASLEEPDEEP"), numeric (4), and varied formats from capacitor-healthkit fallback
  const normalizeStage = st => {
    const s = String(st).toUpperCase().replace(/[^A-Z0-9]/g,'');
    if (s==='4'||s.includes('DEEP'))                    return 'Deep';
    if (s==='5'||s.includes('REM'))                     return 'REM';
    if (s==='2'||s==='AWAKE')                           return 'Awake';
    if (s==='3'||s.includes('CORE')||s.includes('SLEEP')) return 'Light';
    return null; // INBED(0), generic ASLEEP(1/summary), UNKNOWN
  };
  const stageColor = { Deep: '#7B6FE0', Light: '#2979FF', REM: '#00BFFF', Awake: '#FF6B57' };

  const chartRef = useRef(null);
  const [cursor, setCursor] = useState(null);
  const _LABEL_W = 52, _PAD_R = 8;
  const _getPointerFrac = e => {
    if (!chartRef.current) return null;
    const r = chartRef.current.getBoundingClientRect();
    const svgW = r.width - _LABEL_W - _PAD_R;
    if (svgW <= 0) return null;
    return Math.max(0, Math.min(1, (e.clientX - r.left - _LABEL_W) / svgW));
  };
  const _chartDragging = useRef(false);
  const _onPDown = e => { _chartDragging.current=true; try{e.currentTarget.setPointerCapture(e.pointerId);}catch{} const f=_getPointerFrac(e); if(f!=null) setCursor({frac:f}); };
  const _onPMove = e => { if(!_chartDragging.current) return; const f=_getPointerFrac(e); if(f!=null) setCursor({frac:f}); };
  const _onPUp = () => { _chartDragging.current=false; setCursor(null); };

  const { segs, sleepStartMs, sleepEndMs } = useMemo(()=>{
    const mapped = sleepSegments.map(s => ({ ...s, _stage: normalizeStage(s.st) })).filter(s => s._stage);
    if (mapped.length === 0 || totalMins <= 0) return { segs:[], sleepStartMs:null, sleepEndMs:null };
    const tMin = Math.min(...mapped.map(s=>s.s));
    const tMax = Math.max(...mapped.map(s=>s.e));
    const tRange = tMax - tMin;
    if (tRange <= 0) return { segs:[], sleepStartMs:null, sleepEndMs:null };

    // Subtract Awake/Deep/REM intervals from any overlapping Core (Light) segments so
    // each time slot belongs to exactly one stage — no visual overlap in the chart.
    const priority = mapped.filter(s => s._stage !== 'Light');
    const deoverlapped = mapped.filter(s => s._stage === 'Light').flatMap(cs => {
      let ranges = [{ s: cs.s, e: cs.e }];
      for (const ps of priority) {
        ranges = ranges.flatMap(r => {
          if (ps.e <= r.s || ps.s >= r.e) return [r];
          const out = [];
          if (ps.s > r.s) out.push({ s: r.s, e: ps.s });
          if (ps.e < r.e) out.push({ s: ps.e, e: r.e });
          return out;
        });
      }
      return ranges.filter(r => r.e - r.s > 0).map(r => ({ _stage:'Light', s:r.s, e:r.e }));
    });

    const all = [...priority, ...deoverlapped].sort((a,b)=>a.s-b.s);
    return {
      segs: all.map(seg => ({ stage: seg._stage, x0: (seg.s-tMin)/tRange, x1: (seg.e-tMin)/tRange })),
      sleepStartMs: tMin,
      sleepEndMs:   tMax,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepSegments, totalMins]);

  const fmtHHMM = ms => { const d = new Date(ms); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
  const timeLabels = sleepStartMs && sleepEndMs
    ? [fmtHHMM(sleepStartMs), fmtHHMM((sleepStartMs+sleepEndMs)/2), fmtHHMM(sleepEndMs)]
    : ['23:00', '03:00', '07:00'];

  const cursorMs = (cursor && sleepStartMs && sleepEndMs) ? sleepStartMs + cursor.frac*(sleepEndMs-sleepStartMs) : null;
  const cursorTime = cursorMs ? fmtHHMM(cursorMs) : null;
  const _stagePri = ['Awake','REM','Deep','Light'];
  const cursorHit = cursor ? segs.filter(s=>cursor.frac>=s.x0&&cursor.frac<=s.x1) : [];
  const cursorStage = cursorHit.length ? [...cursorHit].sort((a,b)=>_stagePri.indexOf(a.stage)-_stagePri.indexOf(b.stage))[0].stage : null;

  // Only trust wake time when we have a real sleep-end timestamp from
  // HealthKit. The previous (23:00 + totalMins) fallback was assuming a
  // 23:00 bedtime and producing false Tahajjud detections for users who
  // went to bed later.
  const wakeAtMin = sleepEndMs
    ? (new Date(sleepEndMs).getHours()*60 + new Date(sleepEndMs).getMinutes())
    : null;

  const week = last7.map(d=>({ value:d.sleep, dow:d.dow, isToday:d.isToday, date:d.key }));
  const avgSleep = Math.round(last7.filter(d=>d.sleep>0).reduce((s,d)=>s+d.sleep,0)/Math.max(1,last7.filter(d=>d.sleep>0).length));

  const fajrMin = _hhmmToMins(prayerTimes?.Fajr);
  const ishaMin = _hhmmToMins(prayerTimes?.Isha);
  const fajrDelta = (fajrMin!=null && wakeAtMin!=null) ? (wakeAtMin - fajrMin) : null;

  const tahajjudDetected = (() => {
    if (!ishaMin || !fajrMin || !wakeAtMin) return false;
    const nightDur = fajrMin > ishaMin ? fajrMin - ishaMin : fajrMin + 1440 - ishaMin;
    const lastThirdStart = (ishaMin + Math.round(nightDur * 2 / 3)) % 1440;
    const inWindow = (t, s, e) => s <= e ? (t >= s && t <= e) : (t >= s || t <= e);
    return inWindow(wakeAtMin, lastThirdStart, fajrMin) && fajrDelta != null && fajrDelta < -10;
  })();

  const bedtimes7 = last7.map(d => d.bedtimeMins).filter(b => b != null);
  const consistency = bedtimes7.length >= 3 ? (() => {
    const norm = bedtimes7.map(b => b < 360 ? b + 1440 : b);
    const mean = norm.reduce((a, b) => a + b, 0) / norm.length;
    const stdDev = Math.round(Math.sqrt(norm.map(b => Math.pow(b - mean, 2)).reduce((a, b) => a + b, 0) / norm.length));
    const label = stdDev <= 15 ? 'Excellent' : stdDev <= 30 ? 'Good' : stdDev <= 60 ? 'Fair' : 'Irregular';
    return { stdDev, label };
  })() : null;

  const recoveryItems = [
    todayData.heartRateVariability > 0 && { label: 'HRV', value: Math.round(todayData.heartRateVariability), sub: 'ms · SDNN', color: '#00c9a7' },
    todayData.oxygenSaturation > 0     && { label: 'SPO₂', value: `${todayData.oxygenSaturation.toFixed(1)}%`, sub: 'blood oxygen', color: cream },
    todayData.respiratoryRate > 0      && { label: 'RESP', value: todayData.respiratoryRate.toFixed(1), sub: 'brpm · sleep', color: ink4 },
  ].filter(Boolean);

  return (
    <div>
      <MetricStatGrid items={[
        { label:'TOTAL', value: totalMins>0?`${Math.floor(animTotal/60)}h ${Math.round(animTotal%60)}m`:'—', sub:`/ ${goalMins/60}h goal`, color:cream },
        { label:'DEEP', value: deepMins>0?fmtMinsV(deepMins):'—', sub:totalMins>0?`${Math.round(deepMins/totalMins*100)}%`:'—', color:gold },
        { label:'REM', value: remMins>0?fmtMinsV(remMins):'—', sub:totalMins>0?`${Math.round(remMins/totalMins*100)}%`:'—', color:cream },
        { label:'EFFIC.', value: totalMins>0?`${efficiency}%`:'—', sub:'asleep / in bed', color:ink4 },
      ]}/>
      {recoveryItems.length > 0 && <MetricStatGrid items={recoveryItems}/>}

      <MetricSectionHeader num="01" label="HYPNOGRAM" right={totalMins>0?(segs.length>0?'TONIGHT':'NO STAGES'):'NO DATA'}/>
      <div style={{ background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12, overflow:'hidden', padding:'8px 0 2px' }}>
        {totalMins<=0 ? (
          <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:ink4, fontFamily:'monospace', letterSpacing:'0.1em' }}>NO SLEEP DATA TONIGHT</div>
        ) : segs.length === 0 ? (
          <div style={{ height:120, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, padding:'0 20px', textAlign:'center' }}>
            <div style={{ fontSize:10, color:ink4, fontFamily:'monospace', letterSpacing:'0.1em' }}>NO DETAILED STAGES</div>
            <div style={{ fontSize:11, color:muted, lineHeight:1.5 }}>{Math.floor(totalMins/60)}h {totalMins%60}m of sleep recorded but Apple Health didn't write the per-stage breakdown for this night.</div>
          </div>
        ) : (
          <>
            <div
              ref={chartRef}
              style={{ position:'relative', touchAction:'none', userSelect:'none', WebkitUserSelect:'none' }}
              onPointerDown={_onPDown}
              onPointerMove={_onPMove}
              onPointerUp={_onPUp}
              onPointerCancel={_onPUp}
            >
              {[
                { stage:'Awake', label:'AWAKE', color:'#FF6B57' },
                { stage:'REM',   label:'REM',   color:'#00BFFF' },
                { stage:'Light', label:'CORE',  color:'#2979FF' },
                { stage:'Deep',  label:'DEEP',  color:'#7B6FE0' },
              ].map(({ stage, label, color }, ri) => {
                const rowSegs = segs.filter(s => s.stage === stage);
                return (
                  <div key={stage} style={{ display:'flex', alignItems:'stretch', height:46, borderBottom: ri<3 ? `0.5px solid ${lineSoft}33` : 'none' }}>
                    <div style={{ width:52, display:'flex', alignItems:'center', paddingLeft:10, flexShrink:0 }}>
                      <span style={{ fontSize:9.5, color:ink4, fontFamily:'monospace', letterSpacing:'0.1em', fontWeight:700 }}>{label}</span>
                    </div>
                    <div style={{ flex:1, position:'relative' }}>
                      <svg viewBox="0 0 1000 1" preserveAspectRatio="none" style={{ position:'absolute', inset:0, width:'100%', height:'100%', display:'block' }}>
                        {rowSegs.map((seg, j) => {
                          const x = seg.x0*1000;
                          const naturalW = (seg.x1-seg.x0)*1000;
                          const w = Math.max(30, naturalW);
                          return <rect key={j} x={x} y={0.08} width={w} height={0.84} fill={color} rx="0.1" opacity={naturalW<30?0.85:1}/>;
                        })}
                      </svg>
                    </div>
                  </div>
                );
              })}
              {cursor && (
                <div style={{ position:'absolute', top:0, bottom:0, left:`calc(52px + ${cursor.frac} * (100% - 60px))`, pointerEvents:'none', zIndex:10 }}>
                  <div style={{ position:'absolute', top:0, bottom:0, left:0, width:1.5, background: cursorStage ? stageColor[cursorStage] : 'rgba(255,255,255,0.75)', opacity:0.9 }}/>
                  {cursorTime && (
                    <div style={{
                      position:'absolute', top:4,
                      ...(cursor.frac > 0.65 ? { right:6 } : { left:6 }),
                      background: cursorStage ? stageColor[cursorStage] : 'rgba(255,255,255,0.9)',
                      borderRadius:6, padding:'3px 8px',
                      display:'flex', flexDirection:'column', alignItems:'center', gap:1,
                      boxShadow:'0 2px 10px rgba(0,0,0,0.45)',
                      whiteSpace:'nowrap',
                    }}>
                      <span style={{ fontSize:12, fontWeight:800, color:'#000', fontFamily:'monospace', letterSpacing:'0.02em' }}>{cursorTime}</span>
                      <span style={{ fontSize:9, fontWeight:700, color:'rgba(0,0,0,0.65)', fontFamily:'monospace', letterSpacing:'0.08em' }}>{cursorStage ? (cursorStage==='Light'?'CORE':cursorStage.toUpperCase()) : '—'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ display:'flex', paddingLeft:52, paddingRight:8, paddingTop:4, paddingBottom:2 }}>
              <div style={{ flex:1, display:'flex', justifyContent:'space-between', fontSize:11, color:ink4, fontFamily:'monospace' }}>
                {timeLabels.map((t,i)=><span key={i}>{t}</span>)}
              </div>
            </div>
          </>
        )}
      </div>

      <MetricSectionHeader num="02" label="LAST 7 DAYS" right={avgSleep>0?`AVG ${fmtMinsV(avgSleep)}`:'—'}/>
      <MetricSparkBars values={week} accent={cream} format={v=>v>=60?`${Math.floor(v/60)}h${String(Math.round(v%60)).padStart(2,'0')}`:`${Math.round(v)}m`} unit="sleep"/>
      {consistency && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 14px', marginTop:6, background:darkMode?'rgba(0,0,0,0.15)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:10 }}>
          <div>
            <div style={{ fontSize:10, fontFamily:'monospace', color:muted, letterSpacing:'0.08em' }}>BEDTIME CONSISTENCY</div>
            <div style={{ fontSize:11, color:text, marginTop:2 }}>± {consistency.stdDev} min variance — <b style={{ color: consistency.label==='Excellent'?'#00c9a7':consistency.label==='Good'?cream:consistency.label==='Fair'?gold:'#ff6b6b' }}>{consistency.label}</b></div>
          </div>
          <div style={{ fontSize:10, fontFamily:'monospace', color:muted, letterSpacing:'0.06em' }}>{bedtimes7.length}d</div>
        </div>
      )}

      <MetricSectionHeader num="03" label="DEBT · FAJR ALIGNMENT"/>
      <div style={{ padding:'12px 14px', background:darkMode?'rgba(0,0,0,0.22)':'rgba(0,0,0,0.04)', border:`0.5px solid ${border}`, borderRadius:12 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, fontFamily:'monospace', color:muted, letterSpacing:'0.06em' }}>
          <span>{debtIsSurplus ? '7-DAY SURPLUS' : '7-DAY DEBT'} <span style={{ color:ink4 }}>({trackedCount}/7 nights)</span></span>
          <span style={{ color: debtIsSurplus ? '#00c9a7' : (debtAbs > 240 ? gold : text), fontWeight:700 }}>
            {debtIsSurplus ? '+' : ''}{fmtMinsV(debtAbs)}
          </span>
        </div>
        <div style={{ height:3, background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.07)', borderRadius:2, marginTop:6, overflow:'hidden' }}>
          <div style={{
            height:'100%',
            width: `${Math.min(1, trackedCount > 0 ? debtAbs / (goalMins * trackedCount) : 0) * 100}%`,
            background: debtIsSurplus ? '#00c9a7' : gold,
            borderRadius:2,
            transition:'width 0.7s cubic-bezier(0.22,0.61,0.36,1)',
          }}/>
        </div>
        {fajrMin!=null && (
          <div style={{ marginTop:12, paddingTop:10, borderTop:`0.5px solid ${lineSoft}`, fontSize:11, color:text, lineHeight:1.55 }}>
            {totalMins>0 && fajrDelta!=null ? (
              tahajjudDetected ? <>
                <b style={{ color:gold }}>Tahajjud detected</b> — you woke <b style={{ color:gold }}>{fmtMinsV(-fajrDelta)}</b> before Fajr in the last third of the night. May Allāh accept your prayer and grant you barakah in your sleep.
              </>
              : fajrDelta < -30 ? <>You woke <b style={{ color:gold }}>{fmtMinsV(-fajrDelta)}</b> before Fajr — a great opportunity for tahajjud. Consider a short rest after Fajr if needed.</>
              : Math.abs(fajrDelta) <= 30 ? <>You woke close to <b style={{ color:gold }}>Fajr ({prayerTimes.Fajr})</b>. May Allāh accept your prayer.</>
              : <>You slept past Fajr ({prayerTimes.Fajr}) by <b style={{ color:gold }}>{fmtMinsV(fajrDelta)}</b>. Tonight, try winding down 30 min earlier in shā' Allāh.</>
            ) : <>Fajr is at <b style={{ color:gold }}>{prayerTimes.Fajr}</b>. Aim to be in bed before <b style={{ color:gold }}>{(()=>{const m=(fajrMin-goalMins+1440)%1440;return `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;})()}</b> for a {goals.sleep||8}h rest before Fajr.</>}
          </div>
        )}
      </div>
    </div>
  );
}

function VitalsMetricOverlay({ metricKey, phase, onClose }) {
  const { darkMode, gold, teal, muted, border } = useContext(VitalsCtx);
  const reduced = useReducedMotion();
  const [drag, setDrag] = useState(0);
  const startY = useRef(null);
  const lastTickedThreshold = useRef(false);

  useEffect(() => {
    if (!phase) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [phase]);

  useEffect(() => {
    if (!phase) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, onClose]);

  if (!phase) return null;
  const isOpen = phase === 'open';

  // Body is rendered inline by metricKey rather than via a `Body` field in
  // this config map. Reason: inline arrow wrappers (e.g. `({onClose}) =>
  // <CalorieBreakdownPanel .../>`) get a fresh function identity on every
  // render. React treats that as a different component type and remounts
  // the panel — wiping local state like `expandedEntryId` (meal rows
  // open then snap shut) and tearing down focused <input>s (keyboard
  // pops up then collapses). Rendering the real component directly keeps
  // its identity stable across re-renders.
  const config = {
    burned:    { accent: teal, label: 'BURNED · ENERGY OUT' },
    remaining: { accent: gold, label: 'REMAINING · BUDGET' },
    activity:  { accent: gold, label: 'ACTIVITY · MOVEMENT' },
    sleep:     { accent: '#e8e2d2', label: 'SLEEP · RECOVERY' },
    calories:  { accent: gold, label: 'CALORIES · BREAKDOWN' },
    protein:   { accent: '#e8e2d2', label: 'PROTEIN · MACRO' },
    carbs:     { accent: '#e8e2d2', label: 'CARBS · MACRO' },
    fat:       { accent: '#e8e2d2', label: 'FAT · MACRO' },
  }[metricKey] || {};
  const accent = config.accent || gold;
  let body = null;
  switch (metricKey) {
    case 'burned':    body = <BurnedOverlayContent/>; break;
    case 'remaining': body = <RemainingOverlayContent/>; break;
    case 'activity':  body = <ActivityOverlayContent/>; break;
    case 'sleep':     body = <SleepOverlayContent/>; break;
    case 'calories':  body = <CalorieBreakdownPanel onClose={onClose}/>; break;
    case 'protein':   body = <MacroDetailPanel macroKey="protein" color="#e8e2d2" onClose={onClose}/>; break;
    case 'carbs':     body = <MacroDetailPanel macroKey="carbs" color="#e8e2d2" onClose={onClose}/>; break;
    case 'fat':       body = <MacroDetailPanel macroKey="fat" color="#e8e2d2" onClose={onClose}/>; break;
    default: break;
  }

  const onTouchStart = e => { startY.current = e.touches[0].clientY; setDrag(0); lastTickedThreshold.current = false; };
  const onTouchMove = e => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      const damped = dy < 200 ? dy : 200 + (dy-200)*0.4;
      setDrag(damped);
      if (!lastTickedThreshold.current && damped > 110) { try { hapticHayahSelection(); } catch {} lastTickedThreshold.current = true; }
      else if (lastTickedThreshold.current && damped <= 110) lastTickedThreshold.current = false;
    }
  };
  const onTouchEnd = () => {
    if (drag > 110) { try { hapticHayahSwipe(); } catch {} onClose(); }
    setDrag(0);
    startY.current = null;
  };

  const dragRatio = Math.min(1, drag/400);
  const transformOpen = drag>0
    ? `translateY(${drag}px) scale(${1 - dragRatio*0.08})`
    : 'translateY(0) scale(1)';
  const transformClosed = reduced ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.96)';
  const transformClosing = reduced ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.94)';

  return createPortal((
    <div onClick={onClose} aria-modal="true" role="dialog" aria-label={config.label} style={{
      position:'fixed', inset:0, zIndex:9999,
      background: isOpen ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0)',
      backdropFilter: isOpen ? 'blur(14px)' : 'blur(0px)',
      WebkitBackdropFilter: isOpen ? 'blur(14px)' : 'blur(0px)',
      transition: reduced ? 'background 0.18s ease' : 'background 0.34s ease, backdrop-filter 0.34s ease',
    }}>
      <div onClick={e=>e.stopPropagation()}
        style={{
          position:'fixed', inset:0,
          background: darkMode ? 'linear-gradient(165deg,#0d141c 0%,#070a0e 100%)' : 'linear-gradient(165deg,#f8f4ed 0%,#fff 100%)',
          borderTop: `0.5px solid ${accent}`,
          boxShadow: `0 -24px 60px rgba(0,0,0,0.45), 0 0 0 0.5px ${border} inset`,
          transform: isOpen ? transformOpen : (phase==='closing' ? transformClosing : transformClosed),
          opacity: isOpen ? 1 : 0,
          transition: reduced ? 'opacity 0.18s ease' : (drag>0 ? 'none' : 'transform 0.42s cubic-bezier(0.22,1,0.36,1), opacity 0.32s ease'),
          display:'flex', flexDirection:'column', overflow:'hidden',
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}>
        {/* Drag-to-dismiss handlers ONLY on the top strip (handle + header).
            Putting them on the entire panel hijacked SwipeRow swipes
            and made tap targets in the body register a brief "drag" reset
            that interfered with onClick — taps would open and immediately
            close items in the calories-breakdown overlay. */}
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'center', padding:'12px 0 6px' }}>
            <div style={{ width:48, height:5, borderRadius:3, background:darkMode?'rgba(255,255,255,0.18)':'rgba(0,0,0,0.18)' }}/>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 22px 14px', borderBottom:`0.5px solid ${border}`, position:'relative' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:accent, boxShadow:`0 0 10px ${accent}`, display:'inline-block' }}/>
            <span style={{ fontSize:13, color:accent, letterSpacing:'0.2em', fontFamily:'monospace', fontWeight:700 }}>{config.label}</span>
            <button onClick={onClose} aria-label="Close" style={{ marginLeft:'auto', background:'transparent', border:`0.5px solid ${border}`, borderRadius:18, width:36, height:36, color:muted, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}>
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
            {isOpen && !reduced && (
              <div style={{ position:'absolute', left:0, right:0, bottom:-1, height:1, overflow:'hidden' }}>
                <div style={{ position:'absolute', left:'-30%', width:'60%', height:'100%', background:`linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`, animation:'vitalsScan 1100ms cubic-bezier(0.22,0.61,0.36,1) forwards' }}/>
              </div>
            )}
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'24px 22px calc(40px + env(safe-area-inset-bottom, 0px))', WebkitOverflowScrolling:'touch' }}>
          {body}
        </div>
      </div>
    </div>
  ), document.body);
}

// Today-screen intelligence strip. Three tappable pills sandwiched between
// the calorie cluster and the macros section: logging streak, weight trend,
// and net energy balance. Quietly hidden when no data is available for a
// pill — the strip itself collapses if all three are empty so first-run
// users don't see a row of zeroes.
function VitalsIntel({ onMetric }) {
  const { darkMode, gold, text, muted, border, ink4, intel, goals } = useContext(VitalsCtx);
  if (!intel) return null;
  const { streak, underCap } = intel;
  const cardBase = {
    flexShrink:0, minWidth:128, padding:'9px 11px', borderRadius:12,
    background: darkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)',
    border: `0.5px solid ${border}`, cursor:'pointer', userSelect:'none',
    WebkitUserSelect:'none', display:'flex', flexDirection:'column', gap:2,
  };
  const labelStyle = { fontSize:8.5, color:ink4, fontFamily:'monospace', letterSpacing:'0.14em', fontWeight:700 };
  const valueStyle = { fontSize:13, color:text, fontWeight:700, fontVariantNumeric:'tabular-nums', display:'flex', alignItems:'baseline', gap:6 };
  const subStyle = { fontSize:9, color:muted, letterSpacing:'0.06em', fontFamily:'monospace' };

  const items = [];
  // Streak moved into the calorie cluster's top-left corner so it isn't
  // covered by the BURNED dial below. Strip kept for future trend pills.
  // Net Energy lives under the REMAINING dial in the calorie cluster — out of
  // the strip to keep this row about streak + (future) trend pills only.

  if (items.length === 0) return null;

  return (
    <div className="ghidha-no-sb"
      style={{ display:'flex', gap:8, overflowX:'auto', padding:'2px 2px 4px', scrollSnapType:'x proximity' }}>
      {items.map(it => (
        <div key={it.key}
          role={it.onTap ? "button" : undefined}
          onClick={it.onTap || undefined}
          style={{ ...cardBase, cursor: it.onTap ? 'pointer' : 'default', scrollSnapAlign:'start' }}>
          {it.content}
        </div>
      ))}
    </div>
  );
}

// Pulls theme tokens from VitalsCtx so MedicationsDueRow doesn't need to be
// rewritten against context; matches the same pattern VitalsIntel uses.
function VitalsMedDue() {
  const { gold, teal, danger, text, muted, border, s1, s2, darkMode } = useContext(VitalsCtx);
  return <MedicationsDueRow theme={{ gold, teal, danger, text, muted, border, s1, s2, darkMode }}/>;
}

function VitalsCard({ fullBleed = false, landscape = false }) {
  const { darkMode, gold, border, totalProtein, totalCarbs, totalFat, goals, compactVitals, breakdownReopenSignal, pendingBreakdownReopenRef, onOpenProteinFocus, onInitialMetricClosed } = useContext(VitalsCtx);
  const revRef = useRef(null);
  // All Today-screen overlays (calories ring, macros, burned, remaining,
  // activity, sleep) share a single full-screen VitalsMetricOverlay shell so
  // they look and feel consistent.
  const [activeMetric, setActiveMetric] = useState(null);
  const [metricPhase, setMetricPhase] = useState(null);
  const metricTimerRef = useRef(null);
  // Set to true when this overlay was opened from HayahToday via initialMetric,
  // so closeMetric knows to fire onInitialMetricClosed.
  const wasInitialRef = useRef(false);
  const openMetric = key => {
    // Protein has its own full-screen overlay (whey calculator + milk mixer).
    // Route it out instead of opening the generic MacroDetailPanel.
    if (key === 'protein' && onOpenProteinFocus) { onOpenProteinFocus(); return; }
    if (metricTimerRef.current) { clearTimeout(metricTimerRef.current); metricTimerRef.current = null; }
    setActiveMetric(key);
    setMetricPhase('mounting');
    metricTimerRef.current = setTimeout(() => { metricTimerRef.current = null; setMetricPhase('open'); }, 20);
  };
  const closeMetric = () => {
    if (metricTimerRef.current) { clearTimeout(metricTimerRef.current); metricTimerRef.current = null; }
    const wasInitial = wasInitialRef.current;
    wasInitialRef.current = false;
    // When the overlay was opened from Hayah Today (initialMetric flow),
    // close instantly so HayahHub's tab swap to "today" happens in the
    // same frame the overlay disappears — no flash of the Calories
    // dashboard underneath.
    if (wasInitial && onInitialMetricClosed) {
      setActiveMetric(null);
      setMetricPhase(null);
      onInitialMetricClosed();
      return;
    }
    setMetricPhase('closing');
    metricTimerRef.current = setTimeout(() => {
      metricTimerRef.current = null;
      setActiveMetric(null);
      setMetricPhase(null);
    }, 420);
  };
  // Re-open the breakdown after a successful add. Two reopen paths:
  //  1. VitalsCard stayed mounted (rare — flow always navigates to search,
  //     unmounting it) — caught by the breakdownReopenSignal effect below.
  //  2. VitalsCard unmounted while in the flow and remounts when view returns
  //     to dashboard — caught by the pendingBreakdownReopenRef on mount.
  useEffect(() => {
    if (pendingBreakdownReopenRef && pendingBreakdownReopenRef.current) {
      pendingBreakdownReopenRef.current = false;
      setTimeout(() => openMetric('calories'), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the parent (HayahHub via HayahToday tile taps) passes an
  // initialMetric through context, auto-open the matching overlay once.
  const { initialMetric, onInitialMetricConsumed } = useContext(VitalsCtx);
  useEffect(() => {
    if (!initialMetric) return;
    const valid = ['calories','burned','remaining','activity','sleep','protein','carbs','fat'];
    if (!valid.includes(initialMetric)) {
      onInitialMetricConsumed && onInitialMetricConsumed();
      return;
    }
    const t = setTimeout(() => {
      wasInitialRef.current = true;
      openMetric(initialMetric);
      onInitialMetricConsumed && onInitialMetricConsumed();
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMetric]);
  const initSignalRef = useRef(breakdownReopenSignal);
  useEffect(() => {
    if (breakdownReopenSignal !== initSignalRef.current) {
      initSignalRef.current = breakdownReopenSignal;
      setTimeout(() => openMetric('calories'), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownReopenSignal]);

  // Distinct colors per macro to match HayahToday's LedMeter palette.
  const macroItems = [
    { label:'Protein', val:totalProtein, goal:goals.protein, color:VH.cream,  macroKey:'protein' },
    { label:'Carbs',   val:totalCarbs,   goal:goals.carbs,   color:VH.amber,  macroKey:'carbs'   },
    { label:'Fat',     val:totalFat,     goal:goals.fat,     color:VH.coral,  macroKey:'fat'     },
  ];

  // Full-bleed: card fills parent; standard: content-driven height.
  // Landscape: GhidhaImmersive handles its own layout.
  const cardBg = darkMode ? '#070b09' : '#f5f1ea';
  const cardBdr = darkMode ? 'rgba(0,230,118,0.1)' : border;
  const cardOuter = landscape
    ? { background:darkMode?'linear-gradient(135deg,#111a23 0%,#0a1218 60%,#0d1117 100%)':'linear-gradient(135deg,#f8f4ed 0%,#fffefb 60%,#fff 100%)', border:`0.5px solid ${border}`, borderRadius:20, overflow:'hidden', position:'relative', display:'flex', flexDirection:'row', height:'100%', flex:1, minHeight:0, boxShadow: darkMode ? '0 10px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)' : '0 10px 40px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)' }
    : fullBleed
      ? { background:cardBg, border:`0.5px solid ${cardBdr}`, borderRadius:18, overflow:'hidden', position:'relative', display:'flex', flexDirection:'column', height:'100%', flex:1, minHeight:0 }
      : { background:cardBg, border:`0.5px solid ${cardBdr}`, borderRadius:18, overflow:'hidden', position:'relative' };

  const sectionFlex = fullBleed
    ? { flex:'1 1 0', minHeight:0, display:'flex', flexDirection:'column' }
    : {};

  const sectionPad = fullBleed
    ? 'clamp(10px, 2vh, 18px) 12px'
    : null;

  const sectionDivider = `0.5px solid ${VH.panelBdr}`;

  const cardBody = landscape ? (
    <GhidhaImmersive
      onOpenMetric={openMetric}
      onRingTap={() => openMetric('calories')}
    />
  ) : (
    <div ref={revRef} style={cardOuter}>
      <VitalsScanline/>
      <VitalsGridLines/>
      <div style={{ padding: sectionPad || '18px 10px 14px', position:'relative', ...sectionFlex }}>
        <VitalsClusterHeader/>
        <VitalsCalorieCluster onRingTap={()=>openMetric("calories")} onMetric={openMetric}/>
      </div>
      <div style={{ padding: sectionPad || '14px 14px 16px', borderTop:sectionDivider, position:'relative', ...sectionFlex }}>
        <VitalsSectionLabel num="02" label="MACROS" right="TAP TO EXPAND"/>
        <div style={{ display:'flex', justifyContent:'space-around', background:VH.panel, borderRadius:14, padding:'12px 8px 8px', border:`1px solid rgba(255,255,255,0.06)`, ...(fullBleed ? { flex:1, minHeight:0 } : {}) }}>
          {macroItems.map((m, i) => (
            <div key={m.macroKey} style={{ display:'contents' }}>
              {i > 0 && <div style={{ width:1, background:VH.panelBdr, margin:'4px 0' }}/>}
              <div onClick={()=>openMetric(m.macroKey)} role="button" aria-label={`${m.label} breakdown`} style={{ cursor:'pointer', touchAction:'manipulation', WebkitTapHighlightColor:'transparent' }}>
                <VitalsMacroDial {...m} delay={120+i*80}/>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: sectionPad || '14px 14px 16px', borderTop:sectionDivider, position:'relative', ...sectionFlex }}>
        <VitalsSectionLabel num="03" label="ACTIVITY · SLEEP" right="TAP TO EXPAND" color={VH.deepBlue}/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, ...(fullBleed ? { flex:1, minHeight:0, alignItems:'stretch' } : {}) }}>
          <div role="button" tabIndex={0} aria-label="Open activity detail" onClick={()=>{try{hapticTap();}catch{}openMetric('activity');}} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();openMetric('activity');}}} style={{ cursor:'pointer', touchAction:'manipulation', WebkitTapHighlightColor:'transparent', display:'flex', flexDirection:'column', minHeight:0 }}>
            <VitalsActivity compact={compactVitals} stretch={fullBleed}/>
          </div>
          <div role="button" tabIndex={0} aria-label="Open sleep detail" onClick={()=>{try{hapticTap();}catch{}openMetric('sleep');}} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();openMetric('sleep');}}} style={{ cursor:'pointer', touchAction:'manipulation', WebkitTapHighlightColor:'transparent', display:'flex', flexDirection:'column', minHeight:0 }}>
            <VitalsSleep compact={compactVitals} stretch={fullBleed}/>
          </div>
        </div>
      </div>
      {fullBleed && (
        <div style={{ padding:'10px 14px 14px', borderTop:sectionDivider, position:'relative', flex:'0 0 auto' }}>
          <VitalsMedDue/>
        </div>
      )}
    </div>
  );

  return (
    <>
      {cardBody}

      <VitalsMetricOverlay metricKey={activeMetric} phase={metricPhase} onClose={closeMetric}/>
    </>
  );
}

// ────────────────────────────── MacroEditor ──────────────────────────────
// Gold-standard macro/calorie editor for the Goals tab. Combines goal-mode
// presets, calorie target, % ↔ g linked split, donut visualisation, preset
// auto-match, live reconciliation, and optional training-day overrides.
function MacroEditor({
  draft, setDraft, wp,
  MACRO_PRESETS, GOAL_MODES,
  calcTDEE, matchedPresetName, macroPercentsFromDraft,
  theme, chipBase,
}) {
  const { text, muted, border, gold, teal, accent, danger, s1, s2 } = theme;
  const tdee = calcTDEE(wp);
  const editMode = draft.editMode || "percent"; // "percent" | "grams"

  const setMacroFromPercent = (pct) => {
    const cals = Number(draft.calories) || 0;
    const protein = Math.round((cals * pct.p) / 4);
    const carbs   = Math.round((cals * pct.c) / 4);
    const fat     = Math.round((cals * pct.f) / 9);
    setDraft({ ...draft, protein, carbs, fat });
  };
  const applyGoalMode = (gm) => {
    const calories = tdee
      ? Math.round((tdee * gm.multiplier) / 5) * 5
      : Number(draft.calories) || 2000;
    const protein = Math.round((calories * gm.split.p) / 4);
    const carbs   = Math.round((calories * gm.split.c) / 4);
    const fat     = Math.round((calories * gm.split.f) / 9);
    setDraft({ ...draft, calories, protein, carbs, fat, goalMode: gm.id });
  };

  const matched = matchedPresetName(draft);
  const pct = macroPercentsFromDraft(draft);
  const splitTotalKcal = Math.round(pct.totalKcal);
  const calorieTarget = Number(draft.calories) || 0;
  const delta = splitTotalKcal - calorieTarget;
  const inSync = Math.abs(delta) <= calorieTarget * 0.02;

  const proteinColor = "#e8e2d2", carbsColor = gold, fatColor = "#ff8c69";

  const setCalories = (v) => {
    if (v === "") { setDraft({ ...draft, calories: "" }); return; }
    const n = parseInt(String(v).replace(/[^0-9]/g, ""), 10);
    if (Number.isNaN(n)) return;
    if (editMode === "percent" && pct.totalKcal > 0) {
      // Keep current % split, recompute grams from new calorie target.
      setDraft({
        ...draft, calories: n,
        protein: Math.round((n * pct.p) / 4),
        carbs:   Math.round((n * pct.c) / 4),
        fat:     Math.round((n * pct.f) / 9),
      });
    } else {
      setDraft({ ...draft, calories: n });
    }
  };

  // Per-macro row: edits %, grams, or kcal depending on mode. Linked so that
  // adjusting one macro adjusts another to keep total = 100% (in % mode) or
  // recomputes the kcal/%-share readouts (in grams mode).
  const MacroRow = ({ k, label, color, kcalPerG }) => {
    const grams = Number(draft[k]) || 0;
    const macroKcal = Math.round(grams * kcalPerG);
    const macroPct = pct.totalKcal > 0 ? Math.round((macroKcal / pct.totalKcal) * 100) : 0;
    const onPercentChange = (newPct) => {
      const cals = Number(draft.calories) || 0;
      // Keep this row at newPct, scale the other two proportionally.
      const remaining = Math.max(0, 1 - newPct / 100);
      const others = ["protein", "carbs", "fat"].filter(o => o !== k);
      const otherKcal = others.reduce((s, o) => s + (Number(draft[o]) || 0) * (o === "fat" ? 9 : 4), 0);
      const next = { ...draft, [k]: Math.round((cals * (newPct / 100)) / kcalPerG) };
      if (otherKcal > 0) {
        for (const o of others) {
          const oKcal = (Number(draft[o]) || 0) * (o === "fat" ? 9 : 4);
          const oShare = oKcal / otherKcal;
          const oGrams = Math.round((cals * remaining * oShare) / (o === "fat" ? 9 : 4));
          next[o] = oGrams;
        }
      }
      setDraft(next);
    };
    const onGramsChange = (newGrams) => {
      setDraft({ ...draft, [k]: newGrams });
    };
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${border}`}}>
        <div style={{width:8,height:8,borderRadius:"50%",background:color,flexShrink:0}}/>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:"12px",color:text,fontWeight:700}}>{label}</div>
          <div style={{fontSize:"10px",color:muted,marginTop:1,fontVariantNumeric:"tabular-nums"}}>
            {editMode === "percent" ? `${grams}g · ${macroKcal} kcal` : `${macroPct}% · ${macroKcal} kcal`}
          </div>
        </div>
        {editMode === "percent" ? (
          <PctInput value={macroPct} onCommit={onPercentChange} theme={theme}/>
        ) : (
          <GramsInput value={grams} onCommit={onGramsChange} theme={theme}/>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Goal mode chips */}
      <div style={{marginBottom:14}}>
        <div style={{fontSize:"10px",color:muted,fontWeight:700,marginBottom:8,letterSpacing:"0.5px"}}>
          GOAL MODE{tdee ? ` · TDEE ${tdee} kcal` : ""}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6}}>
          {GOAL_MODES.map(gm => {
            const active = draft.goalMode === gm.id;
            return (
              <button key={gm.id} onClick={()=>applyGoalMode(gm)}
                style={{...chipBase, padding:"10px 4px",
                  display:"flex",flexDirection:"column",alignItems:"center",gap:2,
                  background: active ? gold : "transparent",
                  color: active ? "#000" : muted,
                  borderColor: active ? gold : border}}>
                <span style={{fontSize:16,lineHeight:1}}>{gm.icon}</span>
                <span style={{fontSize:11}}>{gm.label}</span>
                <span style={{fontSize:9, opacity:0.7}}>×{gm.multiplier.toFixed(2)}</span>
              </button>
            );
          })}
        </div>
        {!tdee && (
          <div style={{fontSize:"9.5px",color:muted,marginTop:6}}>
            Run the BMR/TDEE calculator above to make goal-mode chips set calories automatically.
          </div>
        )}
      </div>

      {/* Calorie target hero input */}
      <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12,
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontSize:"10px",color:muted,fontWeight:700,letterSpacing:"0.5px"}}>DAILY CALORIES</div>
          <div style={{fontSize:"10.5px",color:muted,marginTop:2}}>Macros recompute from this target</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <input
            type="text" inputMode="numeric"
            value={draft.calories === "" || draft.calories == null ? "" : String(draft.calories)}
            onFocus={e=>e.target.select()}
            onChange={e=>setCalories(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={()=>{ if (draft.calories === "" || draft.calories == null) setDraft({...draft, calories:2000}); }}
            style={{width:90,padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,
              background:s2,color:gold,fontSize:"22px",fontWeight:900,textAlign:"right",
              fontFamily:"inherit",outline:"none",touchAction:"manipulation",
              fontVariantNumeric:"tabular-nums"}}/>
          <span style={{fontSize:"12px",color:muted,fontWeight:700}}>kcal</span>
        </div>
      </div>

      {/* Macro split panel */}
      <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <div style={{fontSize:"11px",color:muted,fontWeight:700,letterSpacing:"0.5px"}}>MACRO SPLIT</div>
          <div style={{display:"flex",border:`1px solid ${border}`,borderRadius:8,overflow:"hidden"}}>
            {[{id:"percent",label:"%"},{id:"grams",label:"g"}].map(opt => (
              <button key={opt.id} onClick={()=>setDraft({...draft, editMode: opt.id})}
                style={{padding:"5px 14px",fontSize:"11px",fontWeight:800,
                  background: editMode === opt.id ? teal : "transparent",
                  color: editMode === opt.id ? "#000" : muted,
                  border:0,fontFamily:"inherit",cursor:"pointer"}}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preset chips with active state */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {MACRO_PRESETS.map(sp => {
            const active = matched === sp.name;
            return (
              <button key={sp.name} onClick={()=>setMacroFromPercent(sp)}
                style={{...chipBase,fontSize:"11px",
                  background: active ? gold : "transparent",
                  color: active ? "#000" : teal,
                  borderColor: active ? gold : teal,
                  fontWeight: active ? 800 : 700}}>
                {active ? "✓ " : ""}{sp.name}
              </button>
            );
          })}
          <span style={{...chipBase,fontSize:"11px",
            background: matched === null ? gold : "transparent",
            color: matched === null ? "#000" : muted,
            borderColor: matched === null ? gold : border,
            fontWeight: matched === null ? 800 : 700,
            cursor: "default"}}>
            {matched === null ? "✓ Custom" : "Custom"}
          </span>
        </div>

        {/* Donut + macro rows */}
        <div style={{display:"flex",gap:14,alignItems:"center"}}>
          <MacroDonut p={pct.p} c={pct.c} f={pct.f} size={92}
            colors={{p:proteinColor, c:carbsColor, f:fatColor}} ringBg={s2}/>
          <div style={{flex:1,minWidth:0}}>
            <MacroRow k="protein" label="Protein" color={proteinColor} kcalPerG={4}/>
            <MacroRow k="carbs"   label="Carbs"   color={carbsColor}   kcalPerG={4}/>
            <MacroRow k="fat"     label="Fat"     color={fatColor}     kcalPerG={9}/>
          </div>
        </div>

        {/* Reconciliation strip */}
        <div style={{marginTop:12,padding:"8px 10px",borderRadius:8,
          background: inSync ? "rgba(0,201,167,0.10)" : "rgba(255,107,107,0.10)",
          border: `1px solid ${inSync ? teal : danger}`,
          display:"flex",justifyContent:"space-between",alignItems:"center",
          fontSize:"11px",fontWeight:700,fontVariantNumeric:"tabular-nums"}}>
          <span style={{color:muted}}>Macros total</span>
          <span style={{color: inSync ? teal : danger}}>
            {splitTotalKcal} kcal
            {!inSync && delta !== 0 && ` (${delta > 0 ? "+" : ""}${delta})`}
          </span>
        </div>
      </div>

      {/* Training-day toggle */}
      <TrainingDayPanel draft={draft} setDraft={setDraft} theme={theme} chipBase={chipBase}
        MACRO_PRESETS={MACRO_PRESETS}/>
    </>
  );
}

function PctInput({ value, onCommit, theme }) {
  const { text, muted, border, s2 } = theme;
  const [local, setLocal] = useState(String(value));
  const last = useRef(value);
  if (last.current !== value) { last.current = value; setLocal(String(value)); }
  return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      <input type="text" inputMode="numeric"
        value={local}
        onFocus={e=>e.target.select()}
        onChange={e=>setLocal(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={()=>{ const n = parseInt(local, 10); if (!Number.isNaN(n) && n >= 0 && n <= 100) onCommit(n); else setLocal(String(value)); }}
        onKeyDown={e=>{ if (e.key === "Enter") e.target.blur(); }}
        style={{width:42,padding:"6px 8px",borderRadius:8,border:`1px solid ${border}`,
          background:s2,color:text,fontSize:"13px",fontWeight:800,textAlign:"right",
          fontFamily:"inherit",outline:"none",fontVariantNumeric:"tabular-nums"}}/>
      <span style={{fontSize:"11px",color:muted,fontWeight:700}}>%</span>
    </div>
  );
}

function GramsInput({ value, onCommit, theme }) {
  const { text, muted, border, s2 } = theme;
  const [local, setLocal] = useState(String(value));
  const last = useRef(value);
  if (last.current !== value) { last.current = value; setLocal(String(value)); }
  return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      <input type="text" inputMode="numeric"
        value={local}
        onFocus={e=>e.target.select()}
        onChange={e=>setLocal(e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={()=>{ const n = parseInt(local, 10); if (!Number.isNaN(n) && n >= 0) onCommit(n); else setLocal(String(value)); }}
        onKeyDown={e=>{ if (e.key === "Enter") e.target.blur(); }}
        style={{width:52,padding:"6px 8px",borderRadius:8,border:`1px solid ${border}`,
          background:s2,color:text,fontSize:"13px",fontWeight:800,textAlign:"right",
          fontFamily:"inherit",outline:"none",fontVariantNumeric:"tabular-nums"}}/>
      <span style={{fontSize:"11px",color:muted,fontWeight:700}}>g</span>
    </div>
  );
}

function MacroDonut({ p, c, f, size = 80, colors, ringBg }) {
  const r = size / 2 - 6;
  const C = 2 * Math.PI * r;
  const cx = size / 2, cy = size / 2;
  const segs = [
    { share: p, color: colors.p },
    { share: c, color: colors.c },
    { share: f, color: colors.f },
  ];
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{flexShrink:0}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={ringBg} strokeWidth={10}/>
      {segs.map((s, i) => {
        const len = C * s.share;
        const dasharray = `${len} ${C - len}`;
        const arc = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color}
            strokeWidth={10} strokeDasharray={dasharray}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition:"stroke-dasharray 0.4s, stroke-dashoffset 0.4s" }}/>
        );
        offset += len;
        return arc;
      })}
    </svg>
  );
}

function TrainingDayPanel({ draft, setDraft, theme, chipBase, MACRO_PRESETS }) {
  const { text, muted, border, gold, teal, accent, s1, s2 } = theme;
  const enabled = !!draft.trainingEnabled;
  const t = draft.training || {};
  const setT = (next) => setDraft({ ...draft, training: { ...t, ...next } });
  const enable = () => {
    if (enabled) { try { hapticHayahToggle(); } catch {} setDraft({ ...draft, trainingEnabled: false }); return; }
    // First time: seed training-day macros from current rest-day values + 15%
    // calories with the same protein, common cut/recomp pattern.
    const cals = Number(draft.calories) || 2000;
    const trainCals = Math.round((cals * 1.15) / 5) * 5;
    try { hapticHayahToggle(); } catch {} setDraft({ ...draft, trainingEnabled: true, training: {
      calories: trainCals,
      protein: draft.protein,
      carbs: Math.round(draft.carbs * 1.30),
      fat: draft.fat,
    }});
  };
  const tCals = Number(t.calories) || 0;
  const tKcal = (Number(t.protein) || 0) * 4 + (Number(t.carbs) || 0) * 4 + (Number(t.fat) || 0) * 9;
  const tDelta = Math.round(tKcal - tCals);
  const tInSync = Math.abs(tDelta) <= tCals * 0.02;
  return (
    <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:"12px",fontWeight:700,color:text}}>Training-day macros</div>
          <div style={{fontSize:"10.5px",color:muted,marginTop:2}}>
            Higher targets on Riyadah workout days
          </div>
        </div>
        <button onClick={enable}
          style={{width:46,height:26,borderRadius:13,
            background: enabled ? teal : "rgba(120,120,120,0.3)",
            border: 0, position:"relative", cursor:"pointer", transition:"background 0.2s"}}>
          <span style={{position:"absolute", top:3, left: enabled ? 23 : 3,
            width:20, height:20, borderRadius:"50%", background:"#fff",
            transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
        </button>
      </div>
      {enabled && (
        <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${border}`,
          display:"flex",flexDirection:"column",gap:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:"12px",color:text,fontWeight:700}}>Calories</span>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <input type="text" inputMode="numeric"
                value={t.calories ?? ""}
                onFocus={e=>e.target.select()}
                onChange={e=>{
                  const v = e.target.value.replace(/[^0-9]/g, "");
                  setT({ calories: v === "" ? "" : parseInt(v, 10) });
                }}
                style={{width:80,padding:"6px 10px",borderRadius:9,border:`1px solid ${border}`,
                  background:s2,color:text,fontSize:"13px",fontWeight:700,textAlign:"right",
                  fontFamily:"inherit",outline:"none",fontVariantNumeric:"tabular-nums"}}/>
              <span style={{fontSize:"11px",color:muted,minWidth:32}}>kcal</span>
            </div>
          </div>
          {[{k:"protein",l:"Protein"},{k:"carbs",l:"Carbs"},{k:"fat",l:"Fat"}].map(m => (
            <div key={m.k} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:"12px",color:text,fontWeight:700}}>{m.l}</span>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <input type="text" inputMode="numeric"
                  value={t[m.k] ?? ""}
                  onFocus={e=>e.target.select()}
                  onChange={e=>{
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    setT({ [m.k]: v === "" ? "" : parseInt(v, 10) });
                  }}
                  style={{width:80,padding:"6px 10px",borderRadius:9,border:`1px solid ${border}`,
                    background:s2,color:text,fontSize:"13px",fontWeight:700,textAlign:"right",
                    fontFamily:"inherit",outline:"none",fontVariantNumeric:"tabular-nums"}}/>
                <span style={{fontSize:"11px",color:muted,minWidth:32}}>g</span>
              </div>
            </div>
          ))}
          <div style={{padding:"6px 10px",borderRadius:8,
            background: tInSync ? "rgba(0,201,167,0.10)" : "rgba(255,107,107,0.10)",
            border: `1px solid ${tInSync ? teal : "#ff6b6b"}`,
            display:"flex",justifyContent:"space-between",alignItems:"center",
            fontSize:"11px",fontWeight:700,fontVariantNumeric:"tabular-nums"}}>
            <span style={{color:muted}}>Total</span>
            <span style={{color: tInSync ? teal : "#ff6b6b"}}>
              {Math.round(tKcal)} kcal{!tInSync && tDelta !== 0 ? ` (${tDelta > 0 ? "+" : ""}${tDelta})` : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Junk Today tile (LogView header) ─────────────────────────────────
// Live junk-% gauge for today + clean-day streak + earned-cheat chip.
// Tap routes to Reports → JUNK pill for the trend view.
function JunkTodayTile({ colors, calorieLog, today, goalKcal, onOpenReports, onOpenVitals, onClaimedCheat }) {
  const { text, muted, border, s1, s2, gold, teal, danger, darkMode } = colors;
  const [claimedTick, setClaimedTick] = useState(0);
  const [streakInfoOpen, setStreakInfoOpen] = useState(false);
  const threshold = DEFAULT_JUNK_THRESHOLD;

  const todayJunk = useMemo(() => computeDayJunk(calorieLog[today], threshold), [calorieLog, today, threshold]);
  // Manual freeze list — user owns this via the ❄ badge on this card.
  const [manualFreezes, setManualFreezes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("junkFreezesUsed") || "[]"); } catch { return []; }
  });
  const persistFreezes = useCallback((next) => {
    setManualFreezes(next);
    try { localStorage.setItem("junkFreezesUsed", JSON.stringify(next)); } catch {}
  }, []);
  const streak = useMemo(() => computeJunkStreak(calorieLog, threshold, today, manualFreezes), [calorieLog, today, threshold, manualFreezes]);
  const freezeActiveToday = manualFreezes.includes(today);
  const recentFreezeDate = useMemo(() => {
    const cutoff = Date.now() - 7 * 86400000;
    return [...manualFreezes]
      .filter(d => Date.parse(`${d}T00:00:00`) >= cutoff)
      .sort()
      .pop() || null;
  }, [manualFreezes]);
  const freezeWindowSpent = !!recentFreezeDate && recentFreezeDate !== today;
  // claimedTick is just a re-render trigger — the source of truth is
  // localStorage (read via isCheatClaimedToday). Bumping the tick after
  // a claim forces this memo to re-evaluate and pick up the fresh value.
  const claimedToday = useMemo(() => isCheatClaimedToday(today), [today, claimedTick]);
  const earned = useMemo(
    () => computeTodayEarnedCheat(calorieLog, threshold, today, claimedToday),
    [calorieLog, today, threshold, claimedToday, claimedTick]
  );

  // % is junk kcal as a share of the user's daily calorie goal (not of
  // what they've logged so far) — that way "20% goal" is a fixed daily
  // budget, not a moving target that tightens whenever they log clean food.
  const hasGoal = goalKcal > 0 && todayJunk.hasMeals;
  const pct = hasGoal
    ? Math.round((todayJunk.junkKcal / goalKcal) * 1000) / 10
    : todayJunk.pct;
  const junkBudget = goalKcal > 0 ? (goalKcal * threshold) / 100 : 0;
  const junkLeft = Math.max(0, Math.round(junkBudget - todayJunk.junkKcal));
  const junkOver = Math.max(0, Math.round(todayJunk.junkKcal - junkBudget));

  // Color logic — under threshold green, threshold..1.5x amber, beyond red
  const over = pct >= threshold;
  const wayOver = pct >= threshold * 1.5;
  const barColor = !todayJunk.hasMeals ? muted : wayOver ? danger : over ? gold : teal;
  const fillFrac = todayJunk.hasMeals
    ? Math.min(1, pct / (threshold * 2))   // bar maxes out at 2× threshold
    : 0;

  // Rolling averages — same junk-vs-goal-kcal metric the today badge uses,
  // so today's % and the 7/14/30-day averages sit on one scale. Goal: keep
  // every rolling window ≤ threshold. The 7d "kcal left today" target is
  // the shortest-window lever the user can actually pull on today.
  const rollingAvgs = useMemo(() => {
    if (goalKcal <= 0) return { hasData: false };
    const fmt = (d) => {
      const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0');
      return `${y}-${m}-${dd}`;
    };
    // Walk back 30 days from today, computing each day's pct vs current goal.
    const pcts = []; // index 0 = today, 29 = 30 days ago
    for (let i = 0; i < 30; i++) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const ds = fmt(d);
      const day = computeDayJunk(calorieLog[ds], threshold);
      pcts.push(day.hasMeals ? (day.junkKcal / goalKcal) * 100 : null);
    }
    // Average COMPLETED days only — exclude today (index 0). A half-eaten today
    // would drag the rolling average down all morning; the big ring already
    // shows today live. "7D" = the 7 most recent completed days.
    const windowStats = (n) => {
      const slice = pcts.slice(1, n + 1).filter(p => p != null);
      if (slice.length === 0) return null;
      const avg = slice.reduce((s, x) => s + x, 0) / slice.length;
      return { avgPct: Math.round(avg * 10) / 10, loggedDays: slice.length };
    };
    const a7  = windowStats(7);
    const a14 = windowStats(14);
    const a30 = windowStats(30);
    if (!a7 && !a14 && !a30) return { hasData: false };

    // 7d clean-day count (under threshold) — completed days only (exclude today,
    // which isn't done yet so "clean" is undetermined).
    const last7 = pcts.slice(1, 8).filter(p => p != null);
    const cleanDays7 = last7.filter(p => p < threshold).length;

    // Today's kcal ceiling that keeps the 7d avg ≤ threshold.
    // Past 6 days (indices 1..6), assume today fills index 0.
    const past6 = pcts.slice(1, 7).filter(p => p != null);
    const denom = past6.length + 1;
    const sumPast6 = past6.reduce((s, x) => s + x, 0);
    const todayPctMax = threshold * denom - sumPast6;
    const todayKcalMax = Math.round((todayPctMax / 100) * goalKcal);

    return {
      hasData: true,
      a7, a14, a30,
      cleanDays7,
      loggedDays7: last7.length,
      todayKcalMax,
    };
  }, [calorieLog, goalKcal, threshold]);

  // ─── Freeze dialog (replaces native confirm / alert) ──────────────
  // Mode: 'use' | 'undo' | 'reset' | 'cant' | null. Gate uses the
  // budget-% (pct) the badge displays — not the eaten-% from junkStats.
  const [freezeDialog, setFreezeDialog] = useState(null);
  const handleFreezeBadgeClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    if (freezeActiveToday) { setFreezeDialog('undo'); return; }
    if (freezeWindowSpent) { setFreezeDialog('reset'); return; }
    if (!todayJunk.hasMeals || pct < threshold) { setFreezeDialog('cant'); return; }
    setFreezeDialog('use');
  }, [freezeActiveToday, freezeWindowSpent, todayJunk.hasMeals, pct, threshold]);
  const confirmFreezeAction = useCallback(() => {
    if (freezeDialog === 'use')        persistFreezes([...manualFreezes, today].sort());
    else if (freezeDialog === 'undo')  persistFreezes(manualFreezes.filter(d => d !== today));
    else if (freezeDialog === 'reset') {
      const cutoff = Date.now() - 7 * 86400000;
      persistFreezes(manualFreezes.filter(d => Date.parse(`${d}T00:00:00`) < cutoff));
    }
    setFreezeDialog(null);
  }, [freezeDialog, manualFreezes, today, persistFreezes]);

  const handleClaim = () => {
    try { hapticHayahSuccess(); } catch {}
    claimCheatToday(today);
    setClaimedTick(t => t + 1);
    // Visible confirmation so the user knows the redeem registered —
    // previously the only feedback was the chip disappearing, which
    // wasn't obvious.
    if (onClaimedCheat) onClaimedCheat(earned.suggestedKcal);
  };

  return (
    <div style={{ padding: "8px 14px 0" }}>
      <button
        onClick={onOpenReports}
        aria-label="Open junk trend report"
        style={{
          display: "block", width: "100%", textAlign: "left",
          padding: 14, borderRadius: 14, border: `1px solid ${border}`, background: s1,
          fontFamily: "inherit", color: text, cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", color: muted }}>JUNK TODAY</span>
            {streak.current > 0 && (
              <span
                role="button" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setStreakInfoOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setStreakInfoOpen(true); } }}
                aria-label="What does this streak mean?"
                style={{
                  fontSize: 10, fontWeight: 800, color: gold,
                  padding: "2px 7px", borderRadius: 999, border: `1px solid ${gold}55`,
                  background: darkMode ? "rgba(255,179,0,0.10)" : "rgba(184,112,8,0.08)",
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}
              >🔥 {streak.current}d clean</span>
            )}
            {/* ❄ Manual freeze badge — three states, all opt-in:
                 • Active today  → bright, tap to undo
                 • Used in last 7d (not today) → muted, tap to reset
                 • Otherwise → hidden unless streak ≥ 3 (then a subtle "use" affordance)
                Tap always stopPropagation so the parent card's
                onOpenReports doesn't fire. */}
            {freezeActiveToday ? (
              <span role="button" tabIndex={0}
                onClick={handleFreezeBadgeClick}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFreezeBadgeClick(e); } }}
                style={{
                  fontSize: 9, fontWeight: 800, color: "#7fd1ff",
                  padding: "2px 7px", borderRadius: 999,
                  border: "1px solid rgba(127,209,255,0.45)",
                  background: "rgba(127,209,255,0.12)",
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                  letterSpacing: "0.04em",
                }}>❄ freeze active today</span>
            ) : freezeWindowSpent ? (
              <span role="button" tabIndex={0}
                onClick={handleFreezeBadgeClick}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFreezeBadgeClick(e); } }}
                style={{
                  fontSize: 9, color: muted, fontWeight: 700,
                  padding: "2px 6px", borderRadius: 999,
                  border: `1px solid ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}>❄ freeze used</span>
            ) : streak.current >= 1 ? (
              // Freeze badge surfaces whenever the user could use one
              // today — bright + "use freeze?" if today is dirty (call
              // to action), or muted + "freeze ready" if today is still
              // clean (just an availability cue so the user knows the
              // safety net is there).
              (todayJunk.hasMeals && !todayJunk.isClean) ? (
                <span role="button" tabIndex={0}
                  onClick={handleFreezeBadgeClick}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFreezeBadgeClick(e); } }}
                  aria-label="Use today's freeze to protect your streak"
                  style={{
                    fontSize: 9, color: gold, fontWeight: 800,
                    padding: "2px 7px", borderRadius: 999,
                    border: `1px dashed ${gold}66`,
                    background: darkMode ? "rgba(255,179,0,0.06)" : "rgba(184,112,8,0.05)",
                    cursor: "pointer", WebkitTapHighlightColor: "transparent",
                  }}>❄ use freeze?</span>
              ) : (
                <span role="button" tabIndex={0}
                  onClick={handleFreezeBadgeClick}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFreezeBadgeClick(e); } }}
                  aria-label="Streak freeze available — tap to learn how it works"
                  style={{
                    fontSize: 9, color: "#7fd1ff", fontWeight: 700,
                    padding: "2px 7px", borderRadius: 999,
                    border: "1px solid rgba(127,209,255,0.25)",
                    background: "rgba(127,209,255,0.06)",
                    cursor: "pointer", WebkitTapHighlightColor: "transparent",
                    letterSpacing: "0.04em", opacity: 0.85,
                  }}>❄ freeze ready</span>
              )
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {onOpenVitals && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onOpenVitals(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onOpenVitals(); } }}
                aria-label="Open Vitals dashboard"
                style={{
                  fontSize: 10, color: gold, fontWeight: 800, letterSpacing: "0.06em",
                  padding: "3px 9px", borderRadius: 999,
                  border: `1px solid ${border}`,
                  background: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                  cursor: "pointer", WebkitTapHighlightColor: "transparent",
                }}>◐ VITALS</span>
            )}
            <span style={{ fontSize: 10, color: muted, fontWeight: 700, letterSpacing: "0.08em" }}>TREND →</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 30, fontWeight: 900, color: barColor, lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >{todayJunk.hasMeals ? `${pct}%` : "—"}</span>
          <span style={{ fontSize: 11, color: muted }}>
            {!todayJunk.hasMeals
              ? "log a meal to see today's junk %"
              : hasGoal
                ? (junkOver > 0
                    ? `${Math.round(todayJunk.junkKcal)} kcal · ${junkOver} over ${threshold}% of ${Math.round(goalKcal)}`
                    : `${Math.round(todayJunk.junkKcal)} kcal · ${junkLeft} left of ${Math.round(junkBudget)} (${threshold}% of ${Math.round(goalKcal)})`)
                : `${Math.round(todayJunk.junkKcal)} / ${Math.round(todayJunk.totalKcal)} kcal · goal <${threshold}%`}
          </span>
        </div>
        {/* Progress bar with threshold tick */}
        <div style={{ position: "relative", height: 8, background: s2, borderRadius: 99, overflow: "hidden" }}>
          <div style={{
            width: `${fillFrac * 100}%`, height: "100%",
            background: barColor, transition: "width 0.4s cubic-bezier(0.22,0.61,0.36,1)",
          }} />
          {/* Goal-line tick at the threshold position (=0.5 of the bar since bar maxes at 2× threshold) */}
          <div style={{
            position: "absolute", left: `50%`, top: -2, bottom: -2, width: 2,
            background: muted, opacity: 0.5,
          }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 9, color: muted, fontWeight: 700 }}>0%</span>
          <span style={{ fontSize: 9, color: muted, fontWeight: 700 }}>{threshold}% goal</span>
          <span style={{ fontSize: 9, color: muted, fontWeight: 700 }}>{threshold * 2}%+</span>
        </div>
        {/* Rolling averages row — 7/14/30 day on one line, then headroom */}
        {rollingAvgs.hasData && (() => {
          const colorFor = (avgPct) => (avgPct > threshold ? danger : teal);
          // Headroom messaging — uses the 7d kcal ceiling. If negative,
          // it's already impossible to land at ≤threshold today.
          let headroom;
          if (rollingAvgs.todayKcalMax <= 0) {
            headroom = `7d avg already locked above ${threshold}% — best you can do today is zero junk`;
          } else if (todayJunk.junkKcal >= rollingAvgs.todayKcalMax) {
            const over = Math.round(todayJunk.junkKcal - rollingAvgs.todayKcalMax);
            headroom = `${over} kcal over today's ${rollingAvgs.todayKcalMax} kcal ceiling for a ≤${threshold}% 7d avg`;
          } else {
            const left = Math.round(rollingAvgs.todayKcalMax - todayJunk.junkKcal);
            headroom = `${left} kcal junk left today to keep 7d avg ≤${threshold}%`;
          }
          const Cell = ({ label, stats }) => (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", color: muted }}>{label}</span>
              {stats ? (
                <span style={{ fontSize: 15, fontWeight: 900, color: colorFor(stats.avgPct), fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                  {stats.avgPct}%
                </span>
              ) : (
                <span style={{ fontSize: 15, fontWeight: 900, color: muted, lineHeight: 1 }}>—</span>
              )}
            </div>
          );
          return (
            <div style={{
              marginTop: 10, paddingTop: 10,
              borderTop: `1px dashed ${border}`,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 18 }}>
                <Cell label="7D AVG"  stats={rollingAvgs.a7} />
                <Cell label="14D AVG" stats={rollingAvgs.a14} />
                <Cell label="30D AVG" stats={rollingAvgs.a30} />
                <div style={{ flex: 1, textAlign: "right", fontSize: 10, color: muted, alignSelf: "center" }}>
                  {rollingAvgs.cleanDays7}/{rollingAvgs.loggedDays7} day{rollingAvgs.loggedDays7 === 1 ? '' : 's'} clean (7d)
                </div>
              </div>
              <div style={{ fontSize: 10, color: muted, lineHeight: 1.4 }}>{headroom}</div>
            </div>
          );
        })()}
        {earned.eligible && (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); handleClaim(); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); handleClaim(); } }}
            style={{
              marginTop: 10, padding: "9px 12px", borderRadius: 10,
              background: `linear-gradient(135deg, ${teal}1f, ${teal}0a)`,
              border: `1px solid ${teal}55`, cursor: "pointer",
            }}
          >
            <div style={{ fontSize: 11, color: teal, fontWeight: 800, letterSpacing: "0.04em", marginBottom: 2 }}>
              ✓ CHEAT MEAL EARNED · TAP TO CLAIM
            </div>
            <div style={{ fontSize: 11, color: muted, lineHeight: 1.4 }}>
              {earned.reason}. Up to <strong style={{ color: text }}>~{earned.suggestedKcal} kcal</strong> of junk keeps the day under {threshold}%.
            </div>
          </div>
        )}
      </button>

      {/* ❄ Freeze dialog — on-brand replacement for window.confirm/alert */}
      {freezeDialog && createPortal((
        <div
          onClick={() => setFreezeDialog(null)}
          aria-modal="true" role="dialog" aria-label="Streak freeze"
          style={{
            position:'fixed', inset:0, zIndex:9999,
            background:'rgba(0,0,0,0.55)',
            backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)',
            display:'flex', alignItems:'flex-end', justifyContent:'center',
            padding:'0 0 max(20px, env(safe-area-inset-bottom))',
            animation:'freezeFadeIn 0.18s ease-out both',
          }}
        >
          <style>{`
            @keyframes freezeFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes freezeSlideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          `}</style>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:380, margin:'0 16px',
              background: darkMode ? 'linear-gradient(165deg, #0f161e 0%, #0a0d12 100%)' : 'linear-gradient(165deg, #faf6ef 0%, #fff 100%)',
              border:`1px solid ${darkMode ? 'rgba(127,209,255,0.35)' : 'rgba(70,150,200,0.30)'}`,
              borderRadius:22,
              padding:'22px 22px 18px',
              boxShadow:`0 24px 60px rgba(0,0,0,0.5), 0 0 0 0.5px ${border} inset`,
              color:text,
              animation:'freezeSlideUp 0.28s cubic-bezier(0.22,1,0.36,1) both',
            }}
          >
            {/* Header icon + title */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <div style={{
                width:36, height:36, borderRadius:'50%',
                background: freezeDialog === 'cant' ? `${muted}22` : 'rgba(127,209,255,0.18)',
                border:`1px solid ${freezeDialog === 'cant' ? `${muted}55` : 'rgba(127,209,255,0.5)'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:18,
              }}>❄</div>
              <div style={{ fontFamily:'monospace', fontSize:11, fontWeight:800, letterSpacing:'0.16em', color: freezeDialog === 'cant' ? muted : '#7fd1ff', textTransform:'uppercase' }}>
                {freezeDialog === 'undo'  && 'Undo Freeze'}
                {freezeDialog === 'reset' && 'Reset Freezes'}
                {freezeDialog === 'cant'  && 'No Freeze Needed'}
                {freezeDialog === 'use'   && 'Use Today’s Freeze'}
              </div>
            </div>

            {/* Body copy */}
            <div style={{ fontSize:14, lineHeight:1.55, color:text, marginBottom:18 }}>
              {freezeDialog === 'use' && (<>
                You’re at <strong style={{ color: pct >= threshold * 1.5 ? danger : gold }}>{pct}%</strong> junk today — over the <strong>{threshold}%</strong> threshold. Use your freeze to keep your <strong>{streak.current}‑day streak</strong> alive?
                <div style={{ fontSize:12, color:muted, marginTop:8 }}>You only get one freeze every 7 days.</div>
              </>)}
              {freezeDialog === 'undo' && (<>
                Undo today’s freeze? Your streak will lose its protection and a dirty day will break it.
              </>)}
              {freezeDialog === 'reset' && (<>
                Your weekly freeze was used <strong>{recentFreezeDate}</strong>. Reset all freezes from the last 7 days so you can use one again today?
              </>)}
              {freezeDialog === 'cant' && (<>
                You’re at <strong style={{ color: teal }}>{Math.max(0, pct)}%</strong> junk — still under the <strong>{threshold}%</strong> threshold. Your day is already clean, no freeze needed.
              </>)}
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:8 }}>
              {freezeDialog === 'cant' ? (
                <button onClick={() => setFreezeDialog(null)} style={{
                  flex:1, padding:'13px', borderRadius:12,
                  background: teal, border:'none',
                  color:'#001a14', fontFamily:'inherit', fontSize:13, fontWeight:800,
                  letterSpacing:'0.04em', cursor:'pointer',
                }}>Got it</button>
              ) : (
                <>
                  <button onClick={() => setFreezeDialog(null)} style={{
                    flex:1, padding:'12px', borderRadius:12,
                    background:'transparent', border:`1px solid ${border}`,
                    color:muted, fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer',
                  }}>Cancel</button>
                  <button onClick={confirmFreezeAction} style={{
                    flex:1.4, padding:'12px', borderRadius:12,
                    background: freezeDialog === 'undo' || freezeDialog === 'reset' ? danger : '#7fd1ff',
                    border:'none', color:'#001520',
                    fontFamily:'inherit', fontSize:13, fontWeight:800, letterSpacing:'0.04em', cursor:'pointer',
                  }}>
                    {freezeDialog === 'use'   && '❄ Use Freeze'}
                    {freezeDialog === 'undo'  && 'Undo'}
                    {freezeDialog === 'reset' && 'Reset Freezes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ), document.body)}
      <StreakInfoSheet
        open={streakInfoOpen}
        onClose={() => setStreakInfoOpen(false)}
        title="Clean streak"
        accent={gold}
        lines={[
          `Counts your consecutive past days where junk food stayed under ${threshold}% of your daily calorie goal.`,
          `🔥 ${streak.current}d clean = your last ${streak.current} day${streak.current === 1 ? '' : 's'} all came in under the line.`,
          { label: 'Current', value: `${streak.current} day${streak.current === 1 ? '' : 's'}` },
          { label: 'Longest', value: `${streak.best} day${streak.best === 1 ? '' : 's'}` },
          `Today doesn't count yet — it's still in progress.`,
          `Days with no meals logged are skipped (they don't break the streak).`,
          `Tap the ❄ badge once per 7 days to freeze a single dirty day and save the streak.`,
        ]}
      />
    </div>
  );
}

export default function CalorieCounter({ darkMode, onBack, prayerTimes, onLogoClick, hostedInHayah = false, isLandscape = false, initialMetric = null, onInitialMetricConsumed, onInitialMetricClosed, onSwitchToHayahToday, onOpenCoach }) {
  // Schedule the daily supplement reminder (iOS local notification) +
  // request permission once. Safe on web — initSupplementReminders no-ops.
  useEffect(() => { initSupplementReminders(); }, []);
  useEffect(() => { initWeeklyChecksReminders(); }, []);
  const [supplementBannerTick, setSupplementBannerTick] = useState(0);
  const [weeklyChecksTick,    setWeeklyChecksTick]    = useState(0);
  const [logStreakInfoOpen,   setLogStreakInfoOpen]   = useState(false);
  const [calorieLog, setCalorieLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { return {}; }
  });
  const [foodFrequency, setFoodFrequency] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calorieFrequency") || "{}"); } catch { return {}; }
  });
  const [goalsRaw, setGoals] = useState(() => {
    try { return { ...DEFAULT_GOALS, ...JSON.parse(localStorage.getItem("calorieGoals") || "{}") }; } catch { return DEFAULT_GOALS; }
  });
  // True if Riyadah has a finished workout dated today. Used to swap macro
  // targets to the training-day overrides when the user has them enabled.
  function isTrainingDayToday() {
    try {
      const log = JSON.parse(localStorage.getItem("sirat-workouts") || "[]");
      const todayStr = new Date().toLocaleDateString();
      for (const w of log) {
        if (!w?.timestamp) continue;
        if (new Date(w.timestamp).toLocaleDateString() === todayStr) return true;
      }
    } catch {}
    return false;
  }
  // Resolves training-day overrides on top of the raw goals object so the rest
  // of the UI consumes a single `goals` shape regardless of mode.
  const goals = useMemo(() => {
    if (!goalsRaw?.trainingEnabled || !goalsRaw?.training) return goalsRaw;
    if (!isTrainingDayToday()) return goalsRaw;
    const t = goalsRaw.training;
    return {
      ...goalsRaw,
      calories: t.calories ?? goalsRaw.calories,
      protein:  t.protein  ?? goalsRaw.protein,
      carbs:    t.carbs    ?? goalsRaw.carbs,
      fat:      t.fat      ?? goalsRaw.fat,
    };
  }, [goalsRaw]);
  // True when the training-day override is actually in effect right now (enabled
  // + a workout logged today). Drives the "Training day" badge on the ring/Log.
  const trainingActive = useMemo(() =>
    !!(goalsRaw?.trainingEnabled && goalsRaw?.training && isTrainingDayToday()), [goalsRaw]);
  const [weightLog, setWeightLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calorieWeightLog") || "[]"); } catch { return []; }
  });
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem("caloriesFavorites") || "[]"); } catch { return []; }
  });
  // Auto-grown user food library. Every food the user logs (especially
  // AI-search results) lands here so the next search can resolve it
  // locally without re-hitting the model. Deduped by lowercase name +
  // brand. Backfilled once from calorieLog history on first mount —
  // see the useEffect below — so the user's whole logging history is
  // immediately searchable.
  const [userFoods, setUserFoods] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ghidhaUserFoods") || "[]"); } catch { return []; }
  });

  const [view, setView] = useState(() => {
    // When opened from a Hayah Today metric tile (initialMetric is set),
    // force the dashboard view so VitalsCard mounts and can dispatch the
    // overlay. Closing it returns the user to Hayah Today (no flash, see
    // closeMetric below).
    if (initialMetric) return "dashboard";
    // Persist Ghidha's view (log / favorites / history / weekly / goals)
    // so an iOS WKWebView reap on resume lands the user back where they
    // were. Dashboard is intentionally NOT auto-restored — it's now
    // hidden behind the explicit ◐ Vitals chip.
    try {
      const saved = localStorage.getItem("ghidhaLastView");
      const valid = ["log", "favorites", "history", "weekly", "goals", "search"];
      if (saved && valid.includes(saved)) return saved;
    } catch {}
    return "log";
  });
  useEffect(() => {
    try { localStorage.setItem("ghidhaLastView", view); } catch {}
  }, [view]);
  // Weekly AI Calorie Target auto-recompute. Fires lazily on mount,
  // gated to Sun/Mon/Tue and at most once every 6 days. Silent unless
  // target shifts > 50 kcal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        maybeAutoRecompute({
          calorieLog, today, weightLog, wizardProfile, goalsRaw,
          saveGoals: (g) => { setGoals(g); try { localStorage.setItem('calorieGoals', JSON.stringify(g)); } catch {} },
          showToast,
        });
      } catch {}
    }, 3000);
    return () => clearTimeout(t);
  }, []);
  // ── Flow layer ────────────────────────────────────────────────────────────
  // Tracks transient navigation into search/food-detail/barcode so the header
  // chevron pops through the in-flow stack instead of exiting Ghidha, and so
  // past-day adds (History +) write to flowCtx.date instead of today.
  // Invariant:
  //   active === true  ⇒ view ∈ {search, food-detail, barcode}
  //                     AND view === flowStack[last]
  //   active === false ⇒ view ∈ {dashboard, log, favorites, history, weekly, goals}
  //                     AND flowStack.length === 0
  const [flowCtx, setFlowCtx] = useState({ active:false, slot:null, date:null, flowStack:[], prevView:null, prevExpandedSlot:null, prevScrollTop:null });
  const containerRef = useRef(null);
  const [historyAddPicker, setHistoryAddPicker] = useState(false);
  // Bottom-sheet slot picker for the Search header (replaces an iOS-flaky
  // absolute <select> overlay).
  const [searchSlotPickerOpen, setSearchSlotPickerOpen] = useState(false);
  // Remembers the previous tab-root when a tab-root → tab-root link (e.g. Today's
  // "See all saved →") navigates to favorites. The header chevron returns there
  // before falling through to the host's onBack.
  const [prevTabRoot, setPrevTabRoot] = useState(null);
  // FatSecret-style multi-select. Map keyed by food id (or food.name for foods
  // without ids). Each entry: { food, servingG, servingLabel }. Cleared when
  // the flow ends.
  const [searchSelections, setSearchSelections] = useState({});
  // Per-row inline serving overrides for the expanded SelectableFoodRow.
  // Map: foodKey → { qty, servingG, servingLabel }. qty is multiplier of the
  // food's standard serving (1× = default). Used both for the expanded row's
  // displayed numbers and as the serving captured when the row is ticked.
  const [searchInlineServings, setSearchInlineServings] = useState({});
  // The single inline-expanded row in any list tab.
  const [expandedFoodId, setExpandedFoodId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  // searchLoading was used while we hit Open Food Facts / USDA over the
  // network. Those sources were removed in 2026-05-22; search is now a
  // synchronous filter over local arrays so the spinner state is dead.
  const searchLoading = false;
  const [searchTab, setSearchTab] = useState("search");

  // Track keyboard open/close via Visual Viewport API so the food-search
  // view can collapse non-essential chrome (slot title row + tab strip) and
  // re-dock the "items selected" pill against the keyboard. Same baseline-
  // tracking pattern as TabBar.js — under Capacitor KeyboardResize.Native
  // both innerHeight and vv.height shrink with the keyboard, so a static
  // baseline-on-mount alone wouldn't detect anything.
  const [kbOpen, setKbOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    let baseline = vv.height;
    const onResize = () => {
      if (vv.height > baseline) baseline = vv.height;
      setKbOpen(baseline - vv.height > 150);
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  // Optimistically collapse the top chrome the instant we enter a view
  // that auto-focuses an input. Without this, banners render for one
  // frame and then disappear when visualViewport reports the keyboard
  // resize ~300ms later. The real listener above keeps kbOpen accurate
  // if the keyboard never actually opens.
  useEffect(() => {
    if (view === "search" || view === "barcode") setKbOpen(true);
  }, [view]);

  // Kill .scroll-content's 84px tab-bar gutter while the keyboard is open.
  // That gutter exists so the floating tab bar pill doesn't overlap content
  // — but the tab bar HIDES itself when the keyboard is up (TabBar.js
  // checks `keyboardVisible`), so the reserved space becomes dead air above
  // the keyboard. Override via inline style (wins against the App.css rule),
  // restore on close / unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const sc = document.querySelector('.scroll-content');
    if (!sc) return;
    if (kbOpen) {
      sc.style.paddingBottom = '8px';
    } else {
      sc.style.paddingBottom = '';
    }
    return () => { sc.style.paddingBottom = ''; };
  }, [kbOpen]);

  // Toggle a body class while the keyboard is open INSIDE the food-search
  // flow specifically. Drives a CSS rule (injected below) that hides the
  // Hayah tab strip + Calories sub-tabs row so just the search input +
  // results occupy the available vertical room above the keyboard.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const active = kbOpen && flowCtx.active && view === "search";
    document.body.classList.toggle('haic-kb-search', active);
    return () => { document.body.classList.remove('haic-kb-search'); };
  }, [kbOpen, flowCtx.active, view]);

  // Inject the haic-kb-search stylesheet once at mount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById('haic-kb-search-styles')) return;
    const tag = document.createElement('style');
    tag.id = 'haic-kb-search-styles';
    tag.textContent = `
      /* Title row keeps its safe-area-inset-top so the search input doesn't
         slide under the iOS status bar / Dynamic Island. We zero everything
         else and hide the logo+title children. */
      body.haic-kb-search [data-haic-collapsible="hayah-title"] {
        padding: env(safe-area-inset-top) 0 0 0 !important;
        min-height: env(safe-area-inset-top) !important;
        gap: 0 !important;
      }
      body.haic-kb-search [data-haic-collapsible="hayah-title"] > * { display: none !important; }
      body.haic-kb-search [data-haic-collapsible="hayah-tabs"] { display: none !important; }
      body.haic-kb-search [data-haic-collapsible="calories-subtabs"] { display: none !important; }
    `;
    document.head.appendChild(tag);
  }, []);
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedServingG, setSelectedServingG] = useState(0);
  const [selectedServingLabel, setSelectedServingLabel] = useState("");
  const [servingQtyStr, setServingQtyStr] = useState("1");
  const [selectedMealSlot, setSelectedMealSlot] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem("hayahPrefs") || "null");
      const v = p?.calories?.defaultMealSlot;
      if (typeof v === "string" && v) return v;
    } catch {}
    return "meal";
  });
  const [aiMealText, setAiMealText] = useState("");
  const [aiMealLoading, setAiMealLoading] = useState(false);
  const [aiMealError, setAiMealError] = useState(null);
  const [aiMealItems, setAiMealItems] = useState([]);
  // Which AI-parsed item is currently expanded for inline editing. The user
  // taps an item row to expand it, edits name + macros in-place (state
  // updates aiMealItems live so the macro summary stays accurate), then
  // either taps Add to log or × to skip.
  const [editingAiItemIdx, setEditingAiItemIdx] = useState(null);
  const updateAiItem = (idx, patch) => {
    setAiMealItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  // aiListening + aiSpeechRef are now provided by the useDictation hook
  // below; references to them in the JSX continue to work via the hook'''s
  // returned listening / toggle / error fields.
  const [aiPhotoPreview, setAiPhotoPreview] = useState(null);
  // (aiSpeechRef removed — useDictation manages the session internally)
  // aiMealText base is captured in a ref so partials append to the text the
  // user had typed before tapping the mic, rather than overwriting it.
  const aiBaseTextRef = useRef("");
  const aiDict = useDictation({
    onPartial: (live) => {
      const next = aiBaseTextRef.current ? (aiBaseTextRef.current + " " + live).trim() : live;
      setAiMealText(next);
    },
    onFinal: (final) => {
      const next = aiBaseTextRef.current ? (aiBaseTextRef.current + " " + final).trim() : final;
      setAiMealText(next);
      aiBaseTextRef.current = next;
    },
    onError: (friendly) => { setAiMealError(friendly); },
  });
  // Compatibility shim: aiListening was used widely in the JSX below.
  const aiListening = aiDict.listening;
  const aiPhotoInputRef = useRef(null);

  // Bridge from the search-bar inline icons (camera / library / AI) into the
  // AI-tab IIFE's handlers. The IIFE only mounts when searchTab === "ai", so
  // the search-tab caller can't invoke them directly. Pattern:
  //   1) Search-bar handler: setSearchTab("ai"); setPendingAiPicker("camera")
  //   2) IIFE renders, assigns aiPickerCameraRef.current = addFromCamera
  //   3) Effect below sees pendingAiPicker + searchTab === "ai", fires the
  //      ref-bound handler, then clears the pending flag.
  const [pendingAiPicker, setPendingAiPicker] = useState(null);
  const aiPickerCameraRef = useRef(null);
  const aiPickerLibraryRef = useRef(null);
  const aiPickerParseRef = useRef(null);

  // Inline AI fallback for the Search tab — when local search has zero
  // results for a meaningful query, we silently call Gemini and render the
  // estimated food items in the SAME results list. The user never leaves
  // Search; tapping the sparkle icon is the only path to the full AI
  // describe-it tab now.
  const [aiSearchResults, setAiSearchResults] = useState([]);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState(null);
  const aiSearchAbortRef = useRef(null);

  async function runInlineAiSearch(query) {
    const q = (query || '').trim();
    if (!q) return;
    if (aiSearchAbortRef.current) aiSearchAbortRef.current.cancelled = true;
    const mine = { cancelled: false };
    aiSearchAbortRef.current = mine;
    setAiSearchLoading(true);
    setAiSearchError(null);
    setAiSearchResults([]);
    try {
      if (!getGeminiKey()) {
        if (mine.cancelled) return;
        setAiSearchError("Add a Gemini key in Settings to use AI search.");
        return;
      }
      // v2: slim region hint + halal filter from user prefs + schema-enforced
      // output. The central callGemini handles model fallback; the v1
      // per-site retry loop is gone. Truncation-repair parsing is gone too —
      // responseSchema guarantees valid JSON.
      const userProfile = getUserProfile();
      const sys = composeSystem([
        "You are a nutrition expert. Given a user food query, return a JSON array of 1 to 5 best-guess matches. NEVER return an empty array — even low-confidence guesses are useful. Estimate from typical portions; for generic queries (e.g. \"rice\") return 2 or 3 common variants. For branded restaurant items, estimate from the chain's typical menu portions.",
        regionFoodHint(userProfile.identity.region),
        halalFilterInstruction(userProfile.nutrition.halalOnly),
        OUTPUT_JSON_STRICT,
      ]);
      const FOOD_ITEM_SCHEMA = {
        type: "object",
        properties: {
          name: { type: "string" },
          brand: { type: "string" },
          calories: { type: "number" },
          protein: { type: "number" },
          carbs: { type: "number" },
          fat: { type: "number" },
          fiber: { type: "number" },
          sugar: { type: "number" },
          sodium: { type: "number" },
          servingG: { type: "number" },
          servingLabel: { type: "string" },
          halal: { type: "boolean", nullable: true },
          halalNote: { type: "string" },
        },
        required: ["name", "calories", "protein", "carbs", "fat", "servingG", "servingLabel"],
      };
      const FOOD_ARRAY_SCHEMA = { type: "array", minItems: 1, maxItems: 5, items: FOOD_ITEM_SCHEMA };
      const raw = await callGemini(q, {
        systemInstruction: sys,
        responseSchema: FOOD_ARRAY_SCHEMA,
        model: MODELS.foodSearch,
        maxOutputTokens: 2048,
        feature: "foodSearch",
      });
      if (mine.cancelled) return;
      let items;
      try { items = JSON.parse(raw); }
      catch { items = []; }
      if (!Array.isArray(items)) items = [items];
      // Numeric coercion — Gemini sometimes returns "250" (string) or null.
      const num = (v, d = 0) => {
        const n = typeof v === "number" ? v : parseFloat(v);
        return Number.isFinite(n) ? n : d;
      };
      const normalized = items
        .filter(i => i && typeof i.name === "string" && i.name.trim())
        .map((it, idx) => {
          const calories = num(it.calories);
          return {
            id: `ai_search_${Date.now()}_${idx}`,
            source: "ai",
            halal: null,
            name: String(it.name).trim().slice(0, 60),
            brand: it.brand && String(it.brand).trim() ? String(it.brand).trim() : null,
            calories,
            protein: num(it.protein),
            carbs: num(it.carbs),
            fat: num(it.fat),
            fiber: num(it.fiber),
            sugar: num(it.sugar),
            sodium: num(it.sodium),
            servingG: num(it.servingG, 100),
            servingLabel: it.servingLabel && String(it.servingLabel).trim() ? String(it.servingLabel).trim() : "1 serving",
          };
        })
        // Items with calories <= 0 are usually unfit-for-logging entries
        // (Gemini falling back to "I do not know"); drop them rather than
        // letting users log a 0-kcal item by accident.
        .filter(it => it.calories > 0);
      if (mine.cancelled) return;
      if (normalized.length === 0) {
        setAiSearchError("AI couldn\'t pin that down. Try a more specific name, brand + item, or use Quick Add.");
      } else {
        setAiSearchResults(normalized);
      }
    } catch (err) {
      if (mine.cancelled) return;
      const msg = err?.message || "AI search failed — try again";
      if (/API key not valid/i.test(msg)) {
        setAiSearchError("Gemini key invalid — update it in Hayah Settings → AI Assistant.");
      } else if (/quota|429/i.test(msg)) {
        setAiSearchError("Gemini quota exhausted. Try again later, or use Quick Add.");
      } else {
        setAiSearchError(msg);
      }
    } finally {
      if (!mine.cancelled) setAiSearchLoading(false);
    }
  }

  // Dispatch pending AI picker request once searchTab transitions to "ai".
  // Waits one microtask so the IIFE has populated the refs during its render.
  // Placed AFTER pendingAiPicker + picker refs are declared (their const TDZ
  // would otherwise throw "Cannot access X before initialization" when the
  // dep array is evaluated). 
  useEffect(() => {
    if (!pendingAiPicker) return;
    if (searchTab !== "ai") return;
    const fn =
      pendingAiPicker === "camera" ? aiPickerCameraRef.current
      : pendingAiPicker === "library" ? aiPickerLibraryRef.current
      : pendingAiPicker === "parse" ? aiPickerParseRef.current
      : null;
    setPendingAiPicker(null);
    if (typeof fn === "function") {
      Promise.resolve().then(() => { try { fn(); } catch {} });
    }
  }, [pendingAiPicker, searchTab]);

  // Auto-AI fallback: when the local search returns nothing for ~1.2s, run
  // Gemini inline and surface the estimated items in the SAME search results
  // list. User stays on the Search tab — tapping the sparkle icon is the
  // only way to the full AI describe-it tab. 3-char floor keeps single-letter
  // typos from burning tokens.
  useEffect(() => {
    if (searchTab !== "search") return;
    const q = searchQuery.trim();
    if (q.length < 3) return;
    if (searchResults.length > 0) return;
    if (aiSearchLoading) return;
    if (aiSearchResults.length > 0) return;
    // 5s pause feels right — gives the user time to finish typing a long
    // dish name (e.g. "Nando's spicy mushroom burger") before AI fires.
    const t = setTimeout(() => { runInlineAiSearch(q); }, 5000);
    return () => clearTimeout(t);
  }, [searchQuery, searchResults.length, searchTab, aiSearchLoading, aiSearchResults.length]);
  // ── AI photo log redesign state ────────────────────────────────────
  const AI_ITEMS_KEY = 'aiPhotoItemsV2';
  const AI_PENDING_KEY = 'aiPendingPhotosV2';
  const [aiItems, setAiItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AI_ITEMS_KEY) || '[]').map(it => ({...it, fresh:false})); }
    catch { return []; }
  });
  const [pendingPhotos, setPendingPhotos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(AI_PENDING_KEY) || '[]');
      return saved.map(p => ({ ...p, status: p.status === 'error' ? 'error' : 'pending' }));
    } catch { return []; }
  });
  const [loggedItemId, setLoggedItemId] = useState(null);
  const [allLoggedFlash, setAllLoggedFlash] = useState(null);
  const [aiHint, setAiHint] = useState('');
  const aiAnalysingRef = useRef(null);
  useEffect(() => { try { localStorage.setItem(AI_ITEMS_KEY, JSON.stringify(aiItems.map(({fresh, ...r}) => r))); } catch {} }, [aiItems]);
  useEffect(() => { try { localStorage.setItem(AI_PENDING_KEY, JSON.stringify(pendingPhotos)); } catch {} }, [pendingPhotos]);

  // Auto-analyse pending photos one at a time
  useEffect(() => {
    if (aiAnalysingRef.current) return;
    const toAnalyse = pendingPhotos.find(p => p.status === 'pending');
    if (!toAnalyse) return;
    aiAnalysingRef.current = toAnalyse.id;
    setPendingPhotos(prev => prev.map(p => p.id === toAnalyse.id ? { ...p, status: 'analysing' } : p));
    (async () => {
      try {
        // v2: persona + region + halal filter + minimal user context
        // (today's remaining macros + profile basics). Truncation-repair
        // parser deleted — responseSchema enforces shape.
        const userProfile = getUserProfile();
        const allData = readAllData();
        const photoCtx = buildPhotoContext(allData);
        try {
          const _bytes = (toAnalyse.dataUrl && toAnalyse.dataUrl.length) ? Math.round(toAnalyse.dataUrl.length * 0.75) : null;
          publishSources("photo", photoSources({
            photoBytes: _bytes,
            hint: toAnalyse.hint,
            region: userProfile?.identity?.region,
            halalOnly: userProfile?.nutrition?.halalOnly,
          }));
        } catch {}
        const sys = composeSystem([
          "You are a nutrition expert analysing a food photo. Identify every distinct food item in the image. Estimate calories and macros per serving based on what's visible. Surface SA brands if recognisable from packaging.",
          regionFoodHint(userProfile.identity.region),
          halalFilterInstruction(userProfile.nutrition.halalOnly),
          OUTPUT_JSON_STRICT,
        ]);
        const PHOTO_ITEM_SCHEMA = {
          type: "object",
          properties: {
            name: { type: "string" },
            brand: { type: "string" },
            calories: { type: "number" },
            protein: { type: "number" },
            carbs: { type: "number" },
            fat: { type: "number" },
            fiber: { type: "number" },
            sugar: { type: "number" },
            sodium: { type: "number" },
            servingG: { type: "number" },
            servingLabel: { type: "string" },
            halal: { type: "boolean", nullable: true },
            halalNote: { type: "string" },
          },
          required: ["name", "calories", "protein", "carbs", "fat", "servingG", "servingLabel"],
        };
        const PHOTO_ARRAY_SCHEMA = { type: "array", minItems: 1, maxItems: 10, items: PHOTO_ITEM_SCHEMA };
        const userHint = toAnalyse.hint
          ? `\n\nThe user says about this photo: "${toAnalyse.hint}" — use this to disambiguate items.`
          : "";
        const prompt = `User's macros remaining today: ${JSON.stringify(photoCtx.today.remaining)}.${userHint}\n\nIdentify every food item in the photo.`;
        const raw = await callGeminiVision(prompt, toAnalyse.dataUrl, {
          systemInstruction: sys,
          responseSchema: PHOTO_ARRAY_SCHEMA,
          model: MODELS.foodPhoto,
          // 2048 truncated multi-item plates mid-string ("Unterminated string").
          // A full plate of ~8-10 items × ~12 fields needs much more headroom.
          maxOutputTokens: 8192,
          feature: "foodPhoto",
        });
        // Robust parse: responseSchema should give valid JSON, but a truncated
        // response still breaks JSON.parse. Salvage every COMPLETE item rather
        // than losing the whole analysis to one cut-off trailing object.
        let items;
        try {
          items = JSON.parse(raw);
        } catch {
          const s = String(raw || "");
          const start = s.indexOf("[");
          const lastObj = s.lastIndexOf("}");
          if (start >= 0 && lastObj > start) {
            try { items = JSON.parse(s.slice(start, lastObj + 1) + "]"); } catch {}
          }
          if (!items) throw new Error("Couldn't read the photo — try again or describe it");
        }
        if (!Array.isArray(items)) items = [items];
        items = items.filter(i => i && i.name && Number.isFinite(i.calories));
        if (items.length === 0) throw new Error('No food items detected');
        const newItems = items.map((it, idx) => ({
          ...it,
          id: `item_${Date.now()}_${idx}_${Math.random().toString(36).slice(2,6)}`,
          sourcePhotoId: toAnalyse.id,
          photoDataUrl: toAnalyse.dataUrl,
          addedAt: Date.now(),
          fresh: true,
        }));
        setAiItems(prev => [...prev, ...newItems]);
        setPendingPhotos(prev => prev.filter(p => p.id !== toAnalyse.id));
        setTimeout(() => { setAiItems(prev => prev.map(it => ({ ...it, fresh: false }))); }, 800);
      } catch (err) {
        setPendingPhotos(prev => prev.map(p => p.id === toAnalyse.id ? { ...p, status: 'error', error: err.message || 'Analysis failed' } : p));
      } finally {
        aiAnalysingRef.current = null;
      }
    })();
  }, [pendingPhotos]);
  // Clear legacy v1 photo queue on first load (replaced by aiItems + pendingPhotos)
  useEffect(() => { try { localStorage.removeItem('aiPhotoQueue'); } catch {} }, []);
  // Re-read state from localStorage when the backend delivers a fresh sync
  // (e.g. first login after reinstall — data arrives after this component mounts)
  useEffect(() => {
    const onSync = () => {
      try { setCalorieLog(JSON.parse(localStorage.getItem("calorieLog")||"{}")); } catch {}
      try { setGoals({...DEFAULT_GOALS,...JSON.parse(localStorage.getItem("calorieGoals")||"{}")}); } catch {}
      try { setFavorites(JSON.parse(localStorage.getItem("caloriesFavorites")||"[]")); } catch {}
      try { setFoodFrequency(JSON.parse(localStorage.getItem("calorieFrequency")||"{}")); } catch {}
      try { setWeightLog(JSON.parse(localStorage.getItem("calorieWeightLog")||"[]")); } catch {}
      try { setWizardProfile(JSON.parse(localStorage.getItem("ghidhaProfile")||"{}")); } catch {}
    };
    window.addEventListener("hayah-server-sync", onSync);
    return () => window.removeEventListener("hayah-server-sync", onSync);
  }, []);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingLoggedAt, setEditingLoggedAt] = useState(null);
  const [aiCoachCache, setAiCoachCache] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("aiCoachReport_v1") || "null");
      const currentRange = parseInt(localStorage.getItem("ghidhaReportDays") || "7", 10);
      if (c?.report && c?.rangeDays === currentRange) return c;
    } catch {}
    return null;
  });
  const [aiCoachLoading, setAiCoachLoading] = useState(false);
  const [aiCoachError, setAiCoachError] = useState(null);
  const [patternInsights, setPatternInsights] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("hayahPatternInsights") || "null");
      const currentRange = parseInt(localStorage.getItem("ghidhaReportDays") || "7", 10);
      if (c?.insights?.length && c?.generatedAt && (Date.now() - c.generatedAt) < 86400000 && c?.rangeDays === currentRange) return c;
    } catch {}
    return null;
  });
  const [patternInsightsLoading, setPatternInsightsLoading] = useState(false);
  const [patternInsightsError, setPatternInsightsError] = useState(null);
  // Tracks the date we're editing (or deleting from) so commit/undo write to
  // the correct day. Defaults to today; History edit/delete sets it to the
  // selected past day.
  const [editingEntryDate, setEditingEntryDate] = useState(null);
  // Captured at the moment openEditEntry runs so closeEdit can return the
  // user to the same place they tapped from (Log ledger / Log meal slot,
  // Dashboard meal slot, or History day). Without this, every save/cancel
  // dropped them onto Today regardless of where they started.
  const [editReturnView, setEditReturnView] = useState(null);
  // Full-screen protein focus overlay (whey scoop calculator + milk mixer).
  // Triggered from 3 places: HayahToday protein tile (via initialMetric),
  // VitalsCard protein macro tile (via openMetric), and the cyan hero bar
  // in GhidhaLogView (direct callback). All converge on this state.
  const [proteinFocusOpen, setProteinFocusOpen] = useState(false);
  const [cappuccinoFocusOpen, setCappuccinoFocusOpen] = useState(false);
  // Tracks whether ProteinFocusOverlay was opened via initialMetric so the
  // close callback knows to return to HayahToday.
  const wasInitialProteinRef = useRef(false);
  // Top-level interception of initialMetric === 'protein'. Routing through
  // VitalsCard's effect would only fire when the user lands on the dashboard
  // sub-view; this handler makes it work regardless of saved Ghidha view.
  useEffect(() => {
    if (initialMetric === 'protein') {
      wasInitialProteinRef.current = true;
      setProteinFocusOpen(true);
      if (onInitialMetricConsumed) onInitialMetricConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMetric]);
  const [breakdownReopenSignal, setBreakdownReopenSignal] = useState(0);
  const [undoToast, setUndoToast] = useState(null); // { slot, entry, key }
  const pendingBreakdownReopenRef = useRef(false);
  const [expandedSlot, setExpandedSlot] = useState(null);
  // Slot that just received a new entry — drives the brief teal pulse on
  // the slot row so the user sees their log "land". Cleared by a timer.
  const [recentlyLoggedSlot, setRecentlyLoggedSlot] = useState(null);
  const recentlyLoggedTimerRef = useRef(null);
  const flashSlot = (slot) => {
    if (!slot) return;
    if (recentlyLoggedTimerRef.current) clearTimeout(recentlyLoggedTimerRef.current);
    setRecentlyLoggedSlot(slot);
    recentlyLoggedTimerRef.current = setTimeout(() => setRecentlyLoggedSlot(null), 1400);
  };
  useEffect(() => () => { if (recentlyLoggedTimerRef.current) clearTimeout(recentlyLoggedTimerRef.current); }, []);
  const [historyDate, setHistoryDate] = useState(localDateStr());
  const [scannerActive, setScannerActive] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [barcodeSearchFallback, setBarcodeSearchFallback] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(false);
  const [quickAddDraft, setQuickAddDraft] = useState({ name:"", calories:"", protein:"", carbs:"", fat:"" });
  const [weightInput, setWeightInput] = useState("");
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [editingMacro, setEditingMacro] = useState(null);
  const [macroInput, setMacroInput] = useState("");
  const [favSlotPicker, setFavSlotPicker] = useState(null);
  // Variant picker state for foods with `pickerKey` (e.g., Jacobs Cappuccino
  // → which milk?). Holds the deferred addFoodEntry arguments — when the
  // user picks an option we re-enter addFoodEntry with the resolved food.
  const [variantPicker, setVariantPicker] = useState(null);
  const [editingFav, setEditingFav] = useState(null);
  const [editFavDraft, setEditFavDraft] = useState(null);
  const [savePresetMode, setSavePresetMode] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetSlot, setPresetSlot] = useState(null);
  // "Save as meal" from the search-selection set. When non-null, opens the
  // SaveSelectionsAsMealSheet with a name input and Save / Save & Log actions.
  const [saveMealSheet, setSaveMealSheet] = useState(null); // null | { name: string }
  // Last 8 distinct search queries, newest first. Surfaced as tap chips
  // when the search input is empty so the user can re-run a previous query
  // without retyping. Persists across reloads.
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calorieSearchRecents") || "[]").slice(0, 8); }
    catch { return []; }
  });
  const pushRecentSearch = (q) => {
    const v = String(q || "").trim();
    if (!v || v.length < 2) return;
    setRecentSearches(prev => {
      const next = [v, ...prev.filter(p => p.toLowerCase() !== v.toLowerCase())].slice(0, 8);
      try { localStorage.setItem("calorieSearchRecents", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [reportTab, setReportTab] = useState("calories");
  const REPORT_DAY_OPTIONS = [1, 3, 7, 14, 30, 45, 60, 90];
  const [reportDays, setReportDaysState] = useState(() => {
    try { const v = parseInt(localStorage.getItem("ghidhaReportDays") || "7", 10); return REPORT_DAY_OPTIONS.includes(v) ? v : 7; } catch { return 7; }
  });
  const setReportDays = (n) => {
    setReportDaysState(n);
    try { localStorage.setItem("ghidhaReportDays", String(n)); } catch {}
    setTappedDay(null);
  };
  // Invalidate AI Patterns + AI Coach caches when the user switches the
  // Reports range. Both were generated against a specific rangeDays —
  // showing them under a different range would be misleading, and the
  // "AI PATTERNS · N DAYS" label would no longer match the underlying
  // analysis. Clearing the state surfaces the "Discover patterns" button
  // again so the user can opt into a fresh analysis for the new range.
  useEffect(() => {
    setPatternInsights(prev => (prev && prev.rangeDays !== reportDays) ? null : prev);
    setAiCoachCache(prev => (prev && prev.rangeDays !== reportDays) ? null : prev);
    setPatternInsightsError(null);
    setAiCoachError(null);
  }, [reportDays]);
  // tappedDay lives on the parent (not inside WeeklyReport) because WeeklyReport
  // is invoked as a function rather than rendered as a component, which would
  // otherwise put a conditional useState into HayahHub's hook list and crash
  // React's rules-of-hooks check the moment the user opens Reports.
  const [tappedDay, setTappedDay] = useState(null);
  const [toast, setToast] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [goalsDraft, setGoalsDraft] = useState(DEFAULT_GOALS);
  // Seed the editor draft from the actual saved goals every time the user
  // navigates to the Goals tab. Without this the editor opens on
  // DEFAULT_GOALS, hiding the user's existing targets.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === "goals") setGoalsDraft(goalsRaw); }, [view]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardProfile, setWizardProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ghidhaProfile") || "{}"); } catch { return {}; }
  });
  const [healthAvailable, setHealthAvailable] = useState(false);
  const [aiTargetForceExpand, setAiTargetForceExpand] = useState(false);
  const scannerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const searchInputRef = useRef(null);
  const autoSyncRef = useRef(null);
  const pushDebounceRef = useRef(null);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(86);
  const [viewportH, setViewportH] = useState(typeof window !== "undefined" ? window.innerHeight : 800);

  useEffect(() => {
    let lastH = window.innerHeight;

    // Reset ALL scroll containers when the keyboard closes. iOS WKWebView
    // scrolls the nearest scrollable ancestor (often .scroll-content, the
    // parent div above this component) to bring the focused input into view,
    // and does not reset it on keyboard dismiss — leaving a black gap above.
    const resetScroll = () => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        // Walk up and reset any scrolled parent div (catches .scroll-content)
        let el = containerRef.current?.parentElement;
        while (el && el !== document.body) {
          if (el.scrollTop > 0) el.scrollTop = 0;
          el = el.parentElement;
        }
      });
    };

    const onResize = () => {
      const h = window.innerHeight;
      setViewportH(h);
      if (h > lastH) resetScroll(); // viewport grew back = keyboard closed
      lastH = h;
    };
    window.addEventListener("resize", onResize);

    // Capacitor Keyboard plugin fires reliably on iOS even when the resize
    // event lags or doesn't fire at all.
    let removeKbListener = () => {};
    (async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        const handle = await Keyboard.addListener('keyboardDidHide', resetScroll);
        removeKbListener = () => { try { handle.remove(); } catch {} };
        // Re-apply accessory-bar hide. Done in index.js but the plugin
        // bridge isn't always ready at app init; calling again here cures
        // the lingering "previous / next / Done" toolbar above the keyboard.
        try { await Keyboard.setAccessoryBarVisible({ isVisible: false }); } catch {}
      } catch {}
    })();

    return () => {
      window.removeEventListener("resize", onResize);
      removeKbListener();
    };
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setHeaderHeight(h);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hostedInHayah]);

  const compactVitals = viewportH < 700;
  const [ghidhaShareOpen, setGhidhaShareOpen] = useState(false);
  const [ghidhaShareImg, setGhidhaShareImg] = useState(null);
  const [ghidhaShareBlob, setGhidhaShareBlob] = useState(null);
  const [healthPulling, setHealthPulling] = useState(false);
  const [healthPushing, setHealthPushing] = useState(false);
  const [lastHealthSyncAt, setLastHealthSyncAt] = useState(() => {
    const v = parseInt(localStorage.getItem("ghidhaLastHealthSyncAt") || "0");
    return v > 0 ? v : null;
  });
  const [syncTick, setSyncTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSyncTick(k => k + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const gold   = darkMode ? "#ffb300" : "#b87008";
  const teal   = darkMode ? "#00c9a7" : "#009e87";
  const accent = darkMode ? "#82c83c" : "#508c0a";
  const danger = "#ff6b6b";
  const border = darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)";
  const s1     = darkMode ? "#0d1117" : "#ffffff";
  const s2     = darkMode ? "#111a23" : "#f0f5fa";
  // muted is the workhorse secondary text colour used across the
  // Calories card layout (date labels, goal sub-text, percentages).
  // #8a9fae was ~64% luminance on dark — fine in daylight but
  // washed out under bedside dim screen. Bumped to #a8b8c5 (~73%)
  // so it stays readable at minimum brightness.
  const muted  = darkMode ? "#a8b8c5" : "#5e7a93";
  const text   = darkMode ? "#e8f0f5" : "#18262f";
  const ink2   = darkMode ? "rgba(232,240,245,0.86)" : "rgba(24,38,47,0.72)";
  const ink4   = darkMode ? "rgba(232,240,245,0.55)" : "rgba(0,0,0,0.32)";
  const lineSoft = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  const today = localDateStr();
  const todayData = calorieLog[today] || { meals:{ meal:[] }, water:0, weight:null, caloriesBurned:0, steps:0, sleepMinutes:0, deepMinutes:0, remMinutes:0, basalBurned:0 };
  const allTodayEntries = Object.values(todayData.meals || {}).flat();
  const totalCalories  = allTodayEntries.reduce((s,e)=>s+(e.calories||0),0);
  const totalProtein   = allTodayEntries.reduce((s,e)=>s+(e.protein||0),0);
  const totalCarbs     = allTodayEntries.reduce((s,e)=>s+(e.carbs||0),0);
  const totalFat       = allTodayEntries.reduce((s,e)=>s+(e.fat||0),0);
  const totalFiber     = allTodayEntries.reduce((s,e)=>s+(e.fiber||0),0);
  const totalSugar     = allTodayEntries.reduce((s,e)=>s+(e.sugar||0),0);
  const totalSodium    = allTodayEntries.reduce((s,e)=>s+(e.sodium||0),0);
  const caloriesBurned = todayData.caloriesBurned || 0;
  const totalBurned    = caloriesBurned + (todayData.basalBurned || 0);
  const netCalories    = Math.round(totalCalories - totalBurned);
  const water          = todayData.water || 0;

  // ── Today-screen intelligence strip ─────────────────────────────────────
  // Cheap reductions over the existing logs — feed the streak / weight /
  // net-energy pills the dashboard surfaces above the macros.
  const intel = useMemo(() => {
    // Streak: consecutive days back from today logged AND under the calorie
    // cap. Today only counts once at least one entry exists, so an empty
    // morning doesn't drop yesterday's run. A logged day that exceeds the
    // cap breaks the streak.
    const cap = goals.calories || 0;
    let streak = 0;
    for (let i = 0; i < 366; i++) {
      const dt = localDateStr(-i);
      const day = calorieLog[dt];
      const items = day ? Object.values(day.meals || {}).flat() : [];
      const has = items.length > 0;
      if (i === 0 && !has) continue;
      if (!has) break;
      const kcal = items.reduce((s, e) => s + (e.calories || 0), 0);
      if (cap > 0 && kcal > cap) break;
      streak++;
    }
    // Last 7 days: how many were logged AND under the calorie cap.
    let underCap = 0;
    for (let i = 0; i < 7; i++) {
      const dt = localDateStr(-i);
      const day = calorieLog[dt];
      if (!day) continue;
      const kcal = Object.values(day.meals || {}).flat().reduce((s, e) => s + (e.calories || 0), 0);
      if (kcal > 0 && kcal <= (goals.calories || 0)) underCap++;
    }
    // Weight: latest sample, 7d moving avg, week-over-week delta of MAs.
    let weightStats = null;
    if (Array.isArray(weightLog) && weightLog.length > 0) {
      const last = weightLog[weightLog.length - 1].kg;
      const last7  = weightLog.slice(-7);
      const prev7  = weightLog.slice(-14, -7);
      const avg = (xs) => xs.reduce((s, w) => s + (w.kg || 0), 0) / Math.max(1, xs.length);
      const ma7  = avg(last7);
      const wkDelta = prev7.length > 0 ? ma7 - avg(prev7) : 0;
      const goalKg = parseFloat(goals.weight) || null;
      weightStats = { last, ma7, wkDelta, goal: goalKg };
    }
    // Net energy: today and rolling 7d cumulative. Negative = deficit.
    let netWeek = 0;
    for (let i = 0; i < 7; i++) {
      const dt = localDateStr(-i);
      const day = calorieLog[dt];
      if (!day) continue;
      const kcal = Object.values(day.meals || {}).flat().reduce((s, e) => s + (e.calories || 0), 0);
      const burned = (day.caloriesBurned || 0) + (day.basalBurned || 0);
      netWeek += (kcal - burned);
    }
    return {
      streak,
      underCap,
      weight: weightStats,
      netToday: netCalories,
      netWeek: Math.round(netWeek),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calorieLog, weightLog, goals.calories, goals.weight, netCalories, today]);

  const isFastingToday = (() => {
    try {
      const log = JSON.parse(localStorage.getItem("fastingLog") || "{}");
      const v = log[today];
      return v !== undefined && v !== false && v !== null;
    } catch { return false; }
  })();
  const isSunnahFastDay = (() => { const d = new Date().getDay(); return d === 1 || d === 4; })();

  const saveLog = (newLog) => { setCalorieLog(newLog); localStorage.setItem("calorieLog", JSON.stringify(newLog)); };
  const saveGoals = (g) => { setGoals(g); localStorage.setItem("calorieGoals", JSON.stringify(g)); };
  const saveFavs = (f) => { setFavorites(f); localStorage.setItem("caloriesFavorites", JSON.stringify(f)); };
  const saveWeights = (w) => { setWeightLog(w); localStorage.setItem("calorieWeightLog", JSON.stringify(w)); };

  // Write today's totals to App Group so the Ghidha widget stays current.
  function syncToWidget(log, currentGoals) {
    const t = localDateStr();
    const entries = Object.values((log[t] || {}).meals || {}).flat();
    writeCalorieData({
      date: t,
      consumed:    Math.round(entries.reduce((s, e) => s + (e.calories || 0), 0)),
      goal:        (currentGoals || goals).calories    || 2000,
      protein:     Math.round(entries.reduce((s, e) => s + (e.protein  || 0), 0)),
      proteinGoal: (currentGoals || goals).protein     || 150,
      carbs:       Math.round(entries.reduce((s, e) => s + (e.carbs    || 0), 0)),
      carbsGoal:   (currentGoals || goals).carbs       || 225,
      fat:         Math.round(entries.reduce((s, e) => s + (e.fat      || 0), 0)),
      fatGoal:     (currentGoals || goals).fat         || 65,
    });
  }

  // Sync on mount so widget reflects current state immediately.
  useEffect(() => { // eslint-disable-line
    syncToWidget(calorieLog);
    syncVitalsToWidget(calorieLog);
    syncFrequentMealsToWidget(calorieLog, foodFrequency);
  }, []); // eslint-disable-line

  // Pick up quick-adds written by App.js (from widget QuickAddCaloriesIntent or LogFrequentMealIntent).
  useEffect(() => {
    const onSync = () => {
      try {
        const updated = JSON.parse(localStorage.getItem("calorieLog") || "{}");
        setCalorieLog(updated);
        syncFrequentMealsToWidget(updated, foodFrequency);
      } catch {}
    };
    window.addEventListener("sirat:calorieSync", onSync);
    return () => window.removeEventListener("sirat:calorieSync", onSync);
  }, []); // eslint-disable-line

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null), 1800); };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ghs = params.get("ghidhaHealth");
      if (ghs) {
        const data = JSON.parse(decodeURIComponent(ghs));
        if (data && data.date && data.burned != null) {
          const log = JSON.parse(localStorage.getItem("calorieLog") || "{}");
          const dt = data.date;
          log[dt] = { ...(log[dt] || { meals:{ breakfast:[],lunch:[],dinner:[] }, water:0 }), caloriesBurned: data.burned, steps: data.steps || 0 };
          localStorage.setItem("calorieLog", JSON.stringify(log));
          setCalorieLog(log);
          showToast(`Synced ${data.burned} kcal burned from Apple Health`);
          window.history.replaceState({}, "", window.location.pathname);
        }
      }
    } catch {}
    // isAvailable() resolves on iOS native, rejects on web — no extra guards needed
    CapacitorHealthkit.isAvailable().then(() => setHealthAvailable(true)).catch(() => {});
  }, []);

  // Auto-sync: every 15 min + on app resume. Uses ref so closure is always fresh.
  useEffect(() => {
    const INTERVAL_MS = 15 * 60 * 1000;
    const tick = () => autoSyncRef.current?.();
    const id = setInterval(tick, INTERVAL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(pushDebounceRef.current);
    };
  }, []);

  function normalizeOff(p) {
    const n = p.nutriments || {};
    const kcal = n["energy-kcal_100g"] != null ? n["energy-kcal_100g"]
      : (n["energy_100g"] ? n["energy_100g"] / 4.184 : null);
    if (kcal == null) return null;
    const labels = (p.labels || "").toLowerCase();
    return {
      id: p.id || p.code || genId(),
      name: p.product_name || p.product_name_en || "Unknown",
      brand: p.brands || null,
      calories: Math.round(kcal*10)/10,
      protein: Math.round((n["proteins_100g"]||0)*10)/10,
      carbs:   Math.round((n["carbohydrates_100g"]||0)*10)/10,
      fat:     Math.round((n["fat_100g"]||0)*10)/10,
      fiber:   Math.round((n["fiber_100g"]||0)*10)/10,
      sugar:   Math.round((n["sugars_100g"]||0)*10)/10,
      sodium:  Math.round((n["sodium_100g"]||0)*1000),
      servingG: 100,
      servingLabel: "100g",
      source: "openfoodfacts",
      halal: labels.includes("halal") ? true : null,
      rawServing: p.serving_size || null,
    };
  }
  const SA_ALIASES = {
    "nandos":"nando's", "nando":"nando's",
    "kfc":"kfc sa",
    "mcdonalds":"mcdonald's sa", "macdonalds":"mcdonald's sa", "maccas":"mcdonald's sa",
    "pnp":"pick n pay", "pick and pay":"pick n pay",
    "woolies":"woolworths",
    "rocco":"rocomamas", "roccomamas":"rocomamas", "roco":"rocomamas",
    "bk":"burger king sa", "burger king":"burger king sa",
    "licken":"chicken licken",
    "debo":"debonairs",
    "ocb":"ocean basket", "ocean basket":"ocean basket",
    "fishy":"fishaways",
    // Drink synonyms — make sure typing the colloquial term still hits the
    // catalogued Coca-Cola / Sprite / Fanta etc. entries instead of falling
    // through to external API noise.
    "coke":"coca-cola", "cocacola":"coca-cola", "coca cola":"coca-cola",
    "diet coke":"coca-cola zero", "coke zero":"coca-cola zero",
    "soda":"cola", "fizzy":"cola", "fizzy drink":"cola",
    "cooldrink":"cola", "cool drink":"cola", "cold drink":"cola", "soft drink":"cola",
    "energy drink":"red bull", "sports drink":"powerade",
    "gums":"wine gums", "gum":"wine gums", "wine gum":"wine gums",
    "sweets":"maynard", "candy":"maynard",
    "chocolate bar":"cadbury", "choc":"cadbury",
    "chippies":"chips", "chippy":"chips", "crisps":"chips",
    "romans":"romans pizza", "roman":"romans pizza",
    "mugg":"mugg & bean", "mugg and bean":"mugg & bean",
    "panarotti":"panarotti's",
    "hungry lion":"hungry lion",
    "mocha":"mochachos",
    "pizza hut":"pizza hut sa",
    "almond milk":"almond breeze",
  };

  function localToFood(item, source) {
    return {
      id: item.id,
      name: item.name,
      brand: item.restaurant || item.brand || item.category || null,
      calories: item.calories,
      protein: item.protein,
      carbs: item.carbs,
      fat: item.fat,
      fiber: item.fiber || 0,
      sugar: item.sugar || 0,
      sodium: item.sodium || 0,
      servingG: item.servingG,
      servingLabel: item.servingLabel,
      source,
      halal: true,
    };
  }
  function searchLocal(q) {
    if (!q.trim()) return [];
    let lq = q.toLowerCase().trim();
    lq = SA_ALIASES[lq] || lq;
    // Strip punctuation/apostrophes so "nandos" matches "nando's", "kfc"
    // matches "K.F.C", etc. Run on both query tokens and the haystack so
    // they meet on a normalised plane.
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
    const lqN  = norm(lq);
    const tokens = lqN.split(/\s+/).filter(Boolean);

    const scoreItem = (item) => {
      const name  = norm(item.name);
      const rest  = norm(item.restaurant || "");
      const brand = norm(item.brand || "");
      const cat   = norm(item.category || "");
      const all   = `${name} ${rest} ${brand} ${cat}`;
      if (!tokens.every(t => all.includes(t))) return 0;
      let s = 1;
      if (name.includes(lqN))              s += 3;
      if (rest.includes(lqN) || brand.includes(lqN)) s += 4;
      if (name === lqN || rest === lqN)    s += 6;
      const freq = foodFrequency[item.id] || foodFrequency[item.name] || 0;
      if (freq > 0) s += Math.min(Math.ceil(Math.log2(freq + 1)) * 3, 12);
      return s;
    };

    const rank = (arr, source) =>
      arr.map(item => ({ item, s: scoreItem(item), source }))
         .filter(x => x.s > 0)
         .sort((a, b) => b.s - a.s)
         .map(x => localToFood(x.item, x.source));

    // User food library ranked FIRST so personal history beats generic
    // matches on every query. Each personal hit also gets a small
    // boost from its lastUsedAt so the most-recent things float up.
    const userRanked = userFoods
      .map(item => {
        let s = scoreItem(item);
        if (s <= 0) return null;
        const recency = item.lastUsedAt ? Math.max(0, 1 - (Date.now() - item.lastUsedAt) / (60 * 86400000)) : 0;
        s += 5 + Math.round(recency * 6); // up to +11 for fresh recents
        return { item, s };
      })
      .filter(Boolean)
      .sort((a, b) => b.s - a.s)
      .map(x => ({ ...x.item, source: "user" }));

    return [
      ...userRanked,
      ...rank(GENERIC_FOODS, "generic"),
      ...rank(SA_FOODS, "sa_food"),
      ...rank(SA_SUPPLEMENTS, "sa_supplement"),
      ...rank(SA_RESTAURANTS, "sa_restaurant"),
    ];
  }
  async function lookupBarcode(code) {
    try {
      const res = await fetch(`https://world.openfoodfacts.org/api/v0/product/${code}.json`);
      const data = await res.json();
      if (data.status === 1 && data.product) {
        const food = normalizeOff(data.product);
        if (food) {
          setSelectedFood(food);
          setSelectedServingG(food.servingG || 100);
          setSelectedServingLabel(food.servingLabel || "100g");
          // Replace top of stack: barcode → food-detail (so Back returns to search, not the camera)
          if (flowCtx.active) {
            const stack = [...flowCtx.flowStack];
            if (stack[stack.length - 1] === "barcode") stack[stack.length - 1] = "food-detail";
            else stack.push("food-detail");
            setFlowCtx({ ...flowCtx, flowStack: stack });
          }
          setView("food-detail");
          return;
        }
      }
      setScanError("Product not found. Search manually below:");
      setBarcodeSearchFallback(true);
    } catch {
      setScanError("Network error. Search manually below:");
      setBarcodeSearchFallback(true);
    }
  }
  function getRecentFoods() {
    const seen = new Set();
    const all = [];
    Object.values(calorieLog).forEach(day => {
      Object.values(day.meals || {}).forEach(entries => (entries || []).forEach(e => all.push(e)));
    });
    all.sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt));
    const out = [];
    for (const e of all) {
      if (!seen.has(e.name)) { seen.add(e.name); out.push(e); }
      if (out.length >= 12) break;
    }
    return out;
  }
  function autoMealSlot() {
    return "meal";
  }
  function getFrequentFoods(n = 6) {
    const byId = {}, byName = {};
    Object.values(calorieLog).forEach(day =>
      Object.values(day.meals || {}).forEach(entries =>
        (entries || []).forEach(e => {
          if (e.id && !byId[e.id]) byId[e.id] = e;
          if (!byName[e.name]) byName[e.name] = e;
        })));
    return Object.entries(foodFrequency)
      .sort((a, b) => b[1] - a[1]).slice(0, n)
      .map(([key]) => byId[key] || byName[key]).filter(Boolean);
  }
  function syncVitalsToWidget(log) {
    const t = localDateStr();
    const day = log[t] || {};
    writeVitalsData({
      date: t,
      steps:          day.steps          || 0,
      caloriesBurned: day.caloriesBurned || 0,
      basalBurned:    day.basalBurned    || 0,
      sleepMinutes:   day.sleepMinutes   || 0,
      deepMinutes:    day.deepMinutes    || 0,
      remMinutes:     day.remMinutes     || 0,
    });
  }
  function syncFrequentMealsToWidget(log, freq) {
    const byId = {}, byName = {};
    Object.values(log).forEach(day =>
      Object.values(day.meals || {}).forEach(entries =>
        (entries || []).forEach(e => {
          if (e.id && !byId[e.id]) byId[e.id] = e;
          if (!byName[e.name]) byName[e.name] = e;
        })));
    const top3 = Object.entries(freq || {})
      .sort((a, b) => b[1] - a[1]).slice(0, 3)
      .map(([key]) => byId[key] || byName[key]).filter(Boolean)
      .map(e => ({
        name: e.name,
        calories: Math.round(e.calories || 0),
        protein: Math.round((e.protein || 0) * 10) / 10,
        carbs:   Math.round((e.carbs   || 0) * 10) / 10,
        fat:     Math.round((e.fat     || 0) * 10) / 10,
        servingG: e.servingG || 100,
        servingLabel: e.servingLabel || `${e.servingG || 100}g`,
      }));
    if (top3.length) writeFrequentMeals(top3);
  }
  function copyYesterdayDiary() {
    const yesterday = localDateStr(-1);
    const yData = calorieLog[yesterday];
    if (!yData?.meals || Object.values(yData.meals).every(m => !m?.length)) { showToast("No meals logged yesterday."); return; }
    const prev = calorieLog[today] || { meals:{ breakfast:[],lunch:[],dinner:[] }, water:0, weight:null };
    const merged = { ...prev };
    const newMeals = { ...prev.meals };
    MEAL_SLOTS.forEach(slot => {
      const yMeals = yData.meals?.[slot.key] || [];
      newMeals[slot.key] = [...(prev.meals?.[slot.key] || []), ...yMeals.map(e => ({ ...e, id: genId(), loggedAt: new Date().toISOString() }))];
    });
    merged.meals = newMeals;
    saveLog({ ...calorieLog, [today]: merged });
    showToast("Yesterday's meals copied ✓");
  }
  function commitQuickAdd() {
    const cal = parseFloat(quickAddDraft.calories);
    if (!cal || cal <= 0) { showToast("Enter calories to continue."); return; }
    const food = {
      id: null, name: quickAddDraft.name.trim() || "Quick Add",
      brand: null, calories: cal,
      protein: parseFloat(quickAddDraft.protein) || 0,
      carbs:   parseFloat(quickAddDraft.carbs)   || 0,
      fat:     parseFloat(quickAddDraft.fat)      || 0,
      fiber: 0, sugar: 0, sodium: 0,
      servingG: 1, servingLabel: "1 serving", source: "quick_add", halal: true,
    };
    // Force a valid MEAL_SLOTS key — selectedMealSlot can hold a stale legacy
    // value (breakfast / lunch / dinner) carried over from an old log or from
    // having previously edited a legacy-slot entry. Without this guard the
    // new item lands under a key the History view doesn't recognise and shows
    // up uncategorised at the top of the day card.
    const validKeys = MEAL_SLOTS.map(m => m.key);
    const slot = validKeys.includes(selectedMealSlot) ? selectedMealSlot : "meal";
    addFoodEntry(food, 1, "1 serving", slot);
    if (slot !== selectedMealSlot) setSelectedMealSlot(slot);
    setQuickAddMode(false);
    setQuickAddDraft({ name:"", calories:"", protein:"", carbs:"", fat:"" });
    setView("log");
    setExpandedSlot(slot);
  }
  function handleSearchInput(v) {
    // Curated food DB (GENERIC + SA + supplements + restaurants) + favorites
    // + recents is the only source we hit now. Open Food Facts and USDA were
    // pulled out 2026-05-22 — they were producing too much noise alongside
    // the AI helper. Barcode lookup still uses OFF (see lookupBarcode).
    const local = searchLocal(v);
    setSearchQuery(v);
    setSearchResults(local);
    // Cancel any in-flight inline AI search and clear stale results — the
    // auto-AI useEffect will re-fire after 1.2s if results stay empty.
    if (aiSearchAbortRef.current) aiSearchAbortRef.current.cancelled = true;
    setAiSearchResults([]);
    setAiSearchError(null);
    setAiSearchLoading(false);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }

  useEffect(() => {
    if (view !== "barcode") {
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(()=>{}); } catch {}
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
      setScannerActive(false);
      return;
    }
    setScanError(null);
    setBarcodeSearchFallback(false);
    let stopped = false;
    import("html5-qrcode").then(({ Html5Qrcode }) => {
      if (stopped) return;
      const scanner = new Html5Qrcode("ghidha-scanner");
      scannerRef.current = scanner;
      scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 150 } },
        (decoded) => {
          stopped = true;
          try { hapticHayahSuccess(); } catch {}
          scanner.stop().then(() => { setScannerActive(false); lookupBarcode(decoded); }).catch(()=>lookupBarcode(decoded));
        },
        () => {}
      ).then(() => { if (!stopped) setScannerActive(true); })
       .catch(err => setScanError("Camera: " + (err.message || err)));
    }).catch(() => setScanError("Scanner library failed to load."));
    return () => {
      stopped = true;
      if (scannerRef.current) {
        try { scannerRef.current.stop().catch(()=>{}); } catch {}
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Detects whey / protein-powder / mass-gainer foods. Matches the curated
  // SA supplement IDs plus a name-based regex so OFF / USDA imports of
  // protein products also classify correctly (their chocolate flavour
  // names would otherwise trip the junk classifier).
  function isWheyProtein(foodBase) {
    if (!foodBase) return false;
    if (WHEY_FOCUS_IDS.includes(foodBase.id)) return true;
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    // Supplement-first carve-out — pre-workout / creatine / omega-3 sometimes
    // ride a brand name (ON, Biogen, USN) that overlaps the whey allowlist
    // below. Always classify those as supplements, never whey.
    if (isSupplement(foodBase)) return false;
    return /\b(whey|isolate|hydrolysate|hydrolyzed|casein|protein powder|protein blend|protein shake|mass gainer|usn iso|gold standard|on ?gold|on ?whey|premier protein|optimum nutrition|nutritech|biogen|evox|muscle ?tech|dymatize|myprotein|protein ?world)\b/i.test(hay);
  }
  // Supplements — capsules / powders / ergogenic aids that aren't protein.
  // Pre-workouts, creatine, omega-3, multivitamin, vitamin D, magnesium,
  // BCAA, glutamine, collagen, ZMA, ashwagandha, melatonin etc. Routes
  // into the dedicated "supplements" slot ahead of whey/junk so the user
  // can see ergogenic intake separately from food calories. The match is
  // conservative — a product must be self-identified as a supplement
  // (name carries one of the keywords), not just sold by a supplement
  // brand (those still flow to whey/meal as before).
  function isSupplement(foodBase) {
    if (!foodBase) return false;
    if (foodBase.category === 'Supplements') return true;
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''}`.toLowerCase();
    if (!hay.trim()) return false;
    // Whey-protein hits are NOT supplements (they go to the whey slot).
    if (/\b(whey|isolate|hydrolysate|hydrolyzed|casein|protein powder|protein blend|protein shake|mass gainer)\b/i.test(hay)) return false;
    return /\b(creatine|monohydrate|kre-?alkalyn|pre[- ]?workout|pre[- ]?wo|nitric oxide|n\.o\.|beta[- ]?alanine|caffeine pill|caffeine capsule|bcaa|eaa|essential amino|glutamine|leucine|hmb|carnitine|l[- ]carnitine|omega[- ]?3|fish oil|cod liver oil|krill oil|epa[- ]?dha|multi[- ]?vitamin|multivitamin|vitamin [a-k][0-9]?|magnesium|zinc|iron supplement|calcium supplement|biotin|collagen|hyaluronic|zma|tribulus|ashwagandha|melatonin|probiotic|prebiotic|electrolyte capsule|electrolyte tablet|electrolyte powder|greens powder|spirulina|chlorella|maca|turmeric supplement|curcumin)\b/i.test(hay);
  }
  // Detects milks. Matches curated milk IDs plus name-based, but excludes
  // milkshake / chocolate-milk-drink which are junk.
  function isMilk(foodBase) {
    if (!foodBase) return false;
    if (MILK_FOCUS_IDS.includes(foodBase.id)) return true;
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    if (/\bmilkshake|stumpie|flav-?o|chocolate ?milk(?!\s+powder)/i.test(hay)) return false;
    return /\b(milk|almond breeze|oat milk|soy milk|coconut milk|skim milk|full[- ]cream milk|low[- ]fat milk)\b/i.test(hay);
  }
  // Sugary-drink catch-all (user policy: all sugary drinks → junk).
  // Picks up juice, smoothies, iced tea, kombucha, lemonade, cordial,
  // squash, frappes, sweetened waters, bubble tea, hot chocolate,
  // chocolate milk, etc. — independently of the brand list. Genuinely
  // sugar-free / unsweetened / diet variants are excluded by name. Water,
  // black coffee, plain milk and unsweetened tea aren't sugary drinks.
  function drinkLooksSugary(foodBase) {
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''}`.toLowerCase();
    if (!hay.trim()) return false;
    if (/\b(unsweetened|sugar[- ]?free|sugarfree|zero ?sugar|no ?sugar|sugarless|diet)\b/.test(hay)) return false;
    return /\b(juice|smoothie|milkshake|frappe|frappuccino|frappucino|iced tea|sweet tea|sweetened tea|kombucha|cordial|squash|lemonade|slush|slushie|slush puppy|freezo|vitamin water|flavou?red water|sweetened water|sweetened|sugared|punch|cocktail|mocktail|cool ?drink|soft ?drink|fizzy|soda|cola|energy drink|sports drink|hot chocolate|chocolate milk|bubble tea|boba|matcha latte|chai latte|caramel latte|mocha|hot cocoa|ginger beer|root beer|cream soda|iced coffee|frozen coffee|coffee freezo|coffee frappe|coffee shake)\b/.test(hay);
  }

  // Detects "junk food" — sweets, chips, doughnuts, milkshakes, chocolate,
  // ice cream, candy, sodas, energy drinks, etc. Anything matched is
  // auto-routed to the snacks (Junk Food) slot regardless of the slot the
  // caller chose, unless the caller forced a non-auto-routed slot like
  // supplements. Whey-protein and milk products are explicitly excluded.
  // Plain coffee drinks (black coffee, cappuccino, americano, espresso, latte,
  // unsweetened iced coffee) should land in the Meal slot, not Junk Food.
  // Only the dessert-style coffees with sugar/syrup keywords (mocha, frappe,
  // freezo, caramel latte) stay junk-classified via drinkLooksSugary.
  function isPlainCoffee(foodBase) {
    if (!foodBase) return false;
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    const isCoffee = /\b(coffee|cappuccino|americano|espresso|latte|macchiato|flat white|cortado|piccolo)\b/.test(hay);
    if (!isCoffee) return false;
    // Disqualifiers — sweet/dessert markers that take it out of "plain coffee".
    const dessertMarkers = /\b(mocha|frappe|frappucc?ino|freezo|shake|caramel|vanilla|hazelnut|syrup|sweet(en|ened)?|sugared|hot chocolate|chocolate milk|cookie|brownie|cake|frozen|iced sweet|iced (.*)?sugar|cream(y)? topping|whipped|with sugar)\b/;
    return !dessertMarkers.test(hay);
  }

  // isDrink — items that route to the dedicated Drinks meal slot (separate
  // from Meal so users can see "where their calories are going"). Conservative
  // match-set: plain coffee, plain tea, water, dairy milk. Smoothies stay in
  // Meal (food-shaped). Sugary drinks stay in Junk via drinkLooksSugary.
  // Whey shakes stay in Whey via the whey classifier (higher priority).
  function isDrink(foodBase) {
    if (!foodBase) return false;
    if (isWheyProtein(foodBase)) return false;
    if (isSupplement(foodBase)) return false;
    const hay0 = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    // Chocolate beats milk — "Chuckles Peanuts in Milk Chocolate", "Milky
    // Way", "milk chocolate bar" are confectionery, not drinks. If both
    // words appear AND there's a confectionery noun, defer to junk.
    if (/\bchocolate\b/.test(hay0) && /(bar|peanuts? in milk chocolate|milky way|button|drop|ball|truffle|praline|kit ?kat|cadbury|nestle|hershey|aero|lindt|toblerone|mars\b|snickers|bounty|smarties|m&m|chuckles)/.test(hay0)) return false;
    // Cereal with milk beats milk — "All-Bran with Milk", "Weet-Bix with
    // Milk", "Oats with Milk" is a breakfast meal, not a drink.
    if (/(cereal|all[- ]?bran|weet[- ]?bix|cornflakes|cheerios|coco ?pops|rice krispies|frosties|muesli|granola|oatmeal|oats|porridge|maltabella)\b.*\bwith\b.*\bmilk\b/.test(hay0)) return false;
    // Curated-DB Beverages/Drinks category wins over the sugary-drink check.
    // A coffee shop's freezo / iced mocha is still a beverage from the user's
    // POV — they want it in the Drinks slot, not Junk. Sugary drinks that
    // arrive WITHOUT a Beverages category (e.g. raw text matches in unknown
    // foods) still fall through to junk-classification below.
    if (foodBase.category === 'Beverages' || foodBase.category === 'Drinks') return true;
    // SA-foods "soft drinks" ID prefix (saf_sd…) also routes to drinks.
    if (typeof foodBase.id === 'string' && /^saf_sd/i.test(foodBase.id)) return true;
    if (drinkLooksSugary(foodBase)) return false;
    if (isPlainCoffee(foodBase)) return true;
    if (isMilk(foodBase)) return true;
    return /\b(tea|chai tea|matcha tea|rooibos|chamomile|herbal infusion|water|sparkling water|mineral water|still water|seltzer|club soda)\b/.test(hay0);
  }

  function isJunkFood(foodBase) {
    if (!foodBase) return false;
    // Whey-protein products and milks are never junk, even if their flavour
    // names contain "chocolate" / "vanilla" / "strawberry" etc.
    if (isWheyProtein(foodBase) || isMilk(foodBase)) return false;
    // User policy: anything with chicken or prawn is real food and routes
    // to Meal regardless of the rest of the name. Covers fast-food chicken
    // (KFC, Roco Mamas, Nando's wings), prawn-with-chips/rice combos, and
    // chicken burgers. Edge case "chicken-flavoured chips" is rare enough
    // that the user can move it manually if it ever shows up.
    const chickenPrawnHay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    if (/\b(chicken|prawn|prawns|shrimp)\b/.test(chickenPrawnHay)) return false;
    // Plain coffee drinks (no sugar/syrup markers) are never junk — keep
    // cappuccinos / lattes / iced coffee in the Meal slot where the user
    // expects them.
    if (isPlainCoffee(foodBase)) return false;
    // Combo meals — e.g. "Chicken Wrap Meal with Regular Chips",
    // "1/4 Chicken & Chips", "Burger Combo + Fries". The chips/fries token
    // would otherwise trigger junk-classification even though the dish is a
    // proper sit-down meal. Standalone "Peri-Peri Chips" (no connector word)
    // is not affected and still routes to Snacks.
    const comboHay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    const looksLikeComboMeal =
      /\b(?:&|\+|and|with|w\/)\s+(?:regular\s+|small\s+|large\s+|peri\s*[- ]?peri\s+)?(?:chips|fries|wedges|rice|salad|slaw|coleslaw|mash)\b/i.test(comboHay)
      || /\b(?:combo|meal\s+deal|set\s+meal|platter|wrap\s+meal|burger\s+meal|chicken\s+meal)\b/i.test(comboHay);
    if (looksLikeComboMeal) return false;
    if (foodBase.category === 'Snacks' || foodBase.category === 'Sweets' || foodBase.category === 'Junk') return true;
    // SA-foods sweets ("saf_sw…") and soft drinks ("saf_sd…") are always junk.
    if (typeof foodBase.id === 'string' && /^saf_(sw|sd)/i.test(foodBase.id)) return true;
    // All sugary drinks → junk.
    if (drinkLooksSugary(foodBase)) return true;
    const hay = `${foodBase.name || ''} ${foodBase.brand || ''} ${foodBase.restaurant || ''}`.toLowerCase();
    // Pure sweeteners — sugar/honey/syrup/agave/molasses standalone are
    // not "meals". They live in junk so the user sees the cost of straight
    // sweetener pours.
    if (/^(?:white |brown |raw |caster |icing |golden )?(?:granulated |refined )?(sugar|honey|syrup|agave nectar|maple syrup|molasses|treacle|jaggery)\b/i.test(hay) ||
        /^\s*(?:sugar|honey|syrup|maple syrup|agave|molasses)\b/i.test(hay)) {
      return true;
    }
    // Fast-food chain rule — Steers / Wimpy / McDonald's / Burger King /
    // KFC / Hungry Lion / Roco Mamas / Chicken Licken / Pedros / Nando's
    // combined with a fast-food product (burger, fries, wings, milkshake,
    // sundae, nugget). "Steers Salad" stays a meal; "Steers Cheese Burger"
    // is junk.
    if (/\b(steers|wimpy|burger king|mcdonald'?s|mickey ?d'?s|kfc|hungry lion|roco ?mamas?|chicken licken|pedros|nando'?s|hudson'?s|hudsons|debonair'?s|dominos|domino'?s|pizza hut|kotas|spur)\b/i.test(hay) &&
        /\b(burger|cheese ?burger|hamburger|fries|chips|wings|drumsticks?|nuggets?|milkshakes?|float|sundae|fried chicken|kentucky|big mac|whopper|spicy ?wings|hot ?wings|cheese ?griller|toasted ?sandwich|pizza)\b/i.test(hay)) {
      return true;
    }
    // Woolworths confectionery — Woolworths sells everything from chicken
    // to chocolate, so we can't blanket-flag the brand. But any Woolworths
    // product whose name carries a confectionery descriptor is junk:
    //   "Woolworths Sour Beans" / "Fizz Pops" / "Gummy Bears" /
    //   "Chocolate Buttons" / "Jelly Babies" / "Marshmallow Eggs", etc.
    // The first capture group is the brand/restaurant; the second is the
    // sweet-product descriptor. Both must be present on the same product
    // for the rule to fire, so "Woolworths Salmon Fillet" stays as Meal
    // and "Maynards Wine Gums" still routes via the brand allowlist below.
    if (/\bwoolworths\b.*?\b(sour|fizz(?:ers?|pops?)?|jell(?:y|ies)|gumm(?:y|ies)|chocolat(?:e)?|sweet|sweets|candy|candies|fudge|toffee|liquorice|licorice|marshmallow|nougat|pastilles?|lolli?(?:pop)?|drops?|buttons?|balls?|beans?|bears?|worms?|snakes?|rings?|tots?|stars?|hearts?|hundreds and thousands|love hearts)\b/i.test(hay)) return true;
    // Brand allowlist — common SA + global confectionery, chips, soft-drink and ice-cream brands.
    if (/(maynard|cadbury|nestle|hershey|mars|snickers|bounty|kit ?kat|aero|lindt|lindor|toblerone|ferrero|rocher|smarties|m&m|skittles|haribo|trolli|jelly belly|sour patch|beacon|tex|wilson|sparkles|chappies|stimorol|mentos|tic tac|pringles|doritos|lay'?s|simba|niknaks|fritos|cheetos|messaris|bakers|romany|tennis|eet ?sum ?mor|ouma|krackel|willards|big ?korn ?bites|big ?corn ?bites|cheas ?curls|coca[- ]?cola|\bcoke\b|pepsi|fanta|sprite|krest|stoney|schweppes|appletiser|grapetiser|iron[- ]?br[ue]w?|irn[- ]?bru|powerade|gatorade|lucozade|red bull|monster|rockstar|7[- ]?up|dr ?pepper|mountain dew|mtn ?dew|cream soda|oros|halls|minute maid|liqui[- ]?fruit|tropika|caprisun|capri ?sun|bundaberg|slo ?jo|sally williams|woolworths strawberry hearts|magnum|cornetto|h(ä|a)agen[- ]?dazs|ben (&|and) jerry'?s|country fresh|dairymaid|wall'?s|walls|breyers|halo top|twister|calippo|paul'?s)/i.test(hay)) return true;
    // Keyword catch-all. `pastilles?` (broadened from `fruit pastille`) so
    // store-brand pastilles still classify even when "fruit" isn't in the
    // name. Added jelly babies, jelly beans, chocolate buttons, marshmallow
    // eggs and a handful of common confectionery shapes that previously
    // slipped through.
    return /\b(chips?|crisps?|doughnuts?|donuts?|chocolate|chocolat|sweet|sweets|candy|candies|milkshakes?|ice ?cream|cookies?|biscuits?|cupcakes?|brownies?|wafers?|toffee|fudge|gummy|gummies|jelly|jelly bab(?:y|ies)|jelly beans?|lolly|lollies|lollipops?|sundae|gulab jamun|jalebi|barfi|halwa|ladoo|wine gum|sour worm|sour bean|sour snake|sour ring|jelly tot|pastilles?|liquorice|licorice|marshmallows?|marshmallow eggs?|chocolate buttons?|chocolate drops?|chocolate balls?|popcorn|pretzels?|pastry|pastries|muffins?|cakes?|cinnamon roll|rusks?|drumstick|cool ?drink|soft ?drink|fizzy|soda|cola|energy drink|sports drink|slushies?|slush puppy|sherbet|nougat|strawberry heart|gelato|sorbet|frozen yog(h)?urt|frozen dessert|soft serve|ice lolly|ice pop|choc ?ice|popsicles?|ginger beer|pancakes?|flapjacks?|crepes?|waffles?|fries|french fries|chips? \(fries\)|hash ?browns?|tater tots?|onion rings?)\b/i.test(hay);
  }

  // Retroactive junk-routing migration. Entries logged before the junk
  // classifier covered something (e.g. soft drinks, newly added brands)
  // stay in their original slot. Walk every day's log and move any
  // junk-classified entries from non-junk slots into 'snacks', and whey
  // entries into the 'whey' slot.
  //
  // Version-stamped so the O(n·m·regex) loop only runs once per migration
  // version — subsequent mounts skip it entirely, preventing a JS-thread
  // block that made the UI unresponsive on first open after a new build.
  // One-time migration: pull items under legacy / unknown meal-slot keys
  // (breakfast/lunch/dinner or stale Quick Add slots) into the canonical
  // "meal" slot. Without this, entries are visible in the ledger but
  // missing from the "Meals" timeline, which only walks MEAL_SLOTS. The
  // earlier fix wedged this effect inside useState's initializer, after
  // the return — it never ran. Idempotent.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("calorieLog");
      if (!raw) return;
      const log = JSON.parse(raw);
      const valid = new Set(MEAL_SLOTS.map(m => m.key));
      let dirty = false;
      for (const dt of Object.keys(log)) {
        const day = log[dt];
        if (!day || typeof day !== "object" || !day.meals) continue;
        const fixed = {};
        let dayDirty = false;
        for (const key of Object.keys(day.meals)) {
          const items = Array.isArray(day.meals[key]) ? day.meals[key] : [];
          const target = valid.has(key) ? key : "meal";
          if (target !== key && items.length) { dayDirty = true; dirty = true; }
          fixed[target] = [...(fixed[target] || []), ...items];
        }
        if (dayDirty) log[dt] = { ...day, meals: fixed };
      }
      if (dirty) {
        localStorage.setItem("calorieLog", JSON.stringify(log));
        setCalorieLog(log);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const JUNK_MIGRATION_V = 11; // bump when classifier rules change (v11: chicken / prawn / shrimp always route to Meal regardless of other junk markers)
  useEffect(() => {
    const stored = parseInt(localStorage.getItem('ghidhaMigrationV') || '0', 10);
    if (stored >= JUNK_MIGRATION_V) return; // already migrated — fast path
    // Walk every day, route every entry to its correct slot:
    //   - whey/protein-classified  -> 'whey'
    //   - junk-classified          -> 'snacks'  (supplements stay put)
    //   - already in a valid slot  -> keep
    //   - legacy/unknown slot      -> 'meal'
    // v3 skipped slot === 'meal' entirely, leaving junk entries that had
    // been merged into 'meal' (e.g. Lays Chips logged via Quick Add) stuck
    // there. v4 routes from every slot, including 'meal'.
    const valid = new Set(['meal', 'drinks', 'supplements', 'whey', 'snacks']);
    let needsSave = false;
    const next = {};
    for (const [date, day] of Object.entries(calorieLog || {})) {
      if (!day?.meals) { next[date] = day; continue; }
      const buckets = { meal: [], drinks: [], supplements: [], whey: [], snacks: [] };
      let dayChanged = false;
      for (const [slot, items] of Object.entries(day.meals || {})) {
        const list = Array.isArray(items) ? items : [];
        for (const e of list) {
          let target;
          if (e && e.userSlot) target = e.userSlot; // user moved it deliberately — never auto-reclassify
          else if (isSupplement(e)) target = 'supplements';
          else if (isWheyProtein(e)) target = 'whey';
          else if (isDrink(e)) target = 'drinks';
          else if (isJunkFood(e)) target = 'snacks';
          // Items currently in 'snacks' that no longer classify as junk
          // (e.g. combo meals like "Chicken Wrap with Chips" once the
          // combo-meal heuristic landed) get pulled back to 'meal'.
          else if (slot === 'snacks') target = 'meal';
          else if (valid.has(slot)) target = slot;
          else target = 'meal'; // legacy (breakfast/lunch/dinner) or unknown
          if (target !== slot) dayChanged = true;
          buckets[target].push(e);
        }
      }
      if (dayChanged) {
        next[date] = { ...day, meals: buckets };
        needsSave = true;
      } else {
        next[date] = day;
      }
    }
    if (needsSave) {
      setCalorieLog(next);
      try { localStorage.setItem("calorieLog", JSON.stringify(next)); } catch {}
    }
    try { localStorage.setItem('ghidhaMigrationV', String(JUNK_MIGRATION_V)); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addFoodEntry(foodBase, servingG, servingLabel, mealSlot, dateKey) {
    // Variant picker shortcut: foods carrying `pickerKey` defer to a sheet
    // so the user can choose a variant (e.g., milk type for Jacobs
    // Cappuccino) before the entry is committed. The sheet re-calls
    // addFoodEntry with the resolved food (pickerKey stripped).
    if (foodBase && foodBase.pickerKey && Array.isArray(foodBase.milkOptions) && foodBase.milkOptions.length) {
      setVariantPicker({ foodBase, servingG, servingLabel, mealSlot, dateKey });
      return;
    }
    const dt = dateKey || flowCtx.date || today;
    const baseG = foodBase.servingG || 100;
    const scale = servingG / baseG;
    // Normalize the incoming slot first — callers may still pass legacy
    // values like 'breakfast' / 'lunch' / 'dinner' (e.g. from a stale
    // selectedMealSlot, or from GhidhaLogView's old time-of-day autoSlot).
    // Anything not in MEAL_SLOTS becomes 'meal' so the item shows up in
    // the Meals timeline instead of being orphaned in an invisible slot.
    const VALID_SLOT_KEYS = MEAL_SLOTS.map(m => m.key);
    let routedSlot = VALID_SLOT_KEYS.includes(mealSlot) ? mealSlot : 'meal';
    // Auto-route classified foods to their dedicated slot. Whey protein
    // always wins over junk classification (its chocolate flavour name
    // would otherwise be junk-classified — isJunkFood handles this, but
    // we still need to send it to "whey" rather than the caller's slot).
    // Supplements first — pre-workout / creatine / omega-3 etc carry
    // brand names (ON, Biogen, USN) that overlap whey's allowlist; if we
    // checked whey first, a "Biogen Omega-3" would land in the whey slot.
    if (isSupplement(foodBase)) {
      routedSlot = 'supplements';
    } else if (isWheyProtein(foodBase)) {
      routedSlot = 'whey';
    } else if (isDrink(foodBase)) {
      routedSlot = 'drinks';
    } else if (isJunkFood(foodBase) && routedSlot !== 'supplements') {
      routedSlot = 'snacks';
    }
    // Stamp loggedAt with a slot-appropriate time on the bucket's date if
    // the user is back-filling. Logging into today still uses the current
    // moment so realtime entries stay accurate. Without this, a back-fill
    // anchored loggedAt at "now" — which let a Tuesday-morning back-fill
    // live in Monday's bucket pull the fasting-window start to the wrong
    // time. Defaults: breakfast 07:30, lunch 12:30, dinner 19:00,
    // whey/supplements 08:00, snacks 15:00.
    const SLOT_DEFAULT_TIME = {
      meal: "12:00",
      drinks: "10:00",
      supplements: "08:00",
      whey: "08:00",
      snacks: "15:00",
    };
    const isPastDate = dt !== today;
    const slotTime = SLOT_DEFAULT_TIME[routedSlot] || "12:00";
    const loggedAt = isPastDate
      ? new Date(`${dt}T${slotTime}:00`).toISOString()
      : new Date().toISOString();
    const entry = {
      id: genId(),
      name: foodBase.name,
      brand: foodBase.brand || foodBase.restaurant || null,
      calories: Math.round((foodBase.calories || 0) * scale * 10) / 10,
      protein:  Math.round((foodBase.protein  || 0) * scale * 10) / 10,
      carbs:    Math.round((foodBase.carbs    || 0) * scale * 10) / 10,
      fat:      Math.round((foodBase.fat      || 0) * scale * 10) / 10,
      fiber:    Math.round((foodBase.fiber    || 0) * scale * 10) / 10,
      sugar:    Math.round((foodBase.sugar    || 0) * scale * 10) / 10,
      sodium:   Math.round((foodBase.sodium   || 0) * scale),
      servingG, servingLabel,
      source: foodBase.source || (foodBase.restaurant ? "sa_restaurant" : "sunnah"),
      halal: foodBase.halal != null ? foodBase.halal : null,
      loggedAt,
    };
    // Functional state updater so back-to-back addFoodEntry calls (e.g.
    // protein-focus overlay logging whey + milk in the same tick) each
    // see the most recent state instead of two stale closures racing.
    let committedLog = null;
    setCalorieLog(prevLog => {
      const prev = prevLog[dt] || { meals:{ meal:[] }, water:0, weight:null };
      const next = {
        ...prevLog,
        [dt]: {
          ...prev,
          meals: { ...(prev.meals || {}), [routedSlot]: [...((prev.meals || {})[routedSlot] || []), entry] },
        },
      };
      committedLog = next;
      localStorage.setItem("calorieLog", JSON.stringify(next));
      return next;
    });
    if (dt === today && committedLog) syncToWidget(committedLog);
    const freqKey = foodBase.id || foodBase.name;
    const newFreq = { ...foodFrequency, [freqKey]: (foodFrequency[freqKey] || 0) + 1 };
    setFoodFrequency(newFreq);
    localStorage.setItem("calorieFrequency", JSON.stringify(newFreq));
    if (committedLog) syncFrequentMealsToWidget(committedLog, newFreq);
    const slotLabel = MEAL_SLOTS.find(m=>m.key===routedSlot)?.label || routedSlot;
    const entryKcal = Math.round((foodBase.calories || 0) * scale);
    const toastBody = entryKcal > 0
      ? `+${entryKcal.toLocaleString()} kcal · ${slotLabel}${dt !== today ? ` · ${dt}` : ""}`
      : `Added to ${slotLabel}${dt !== today ? ` · ${dt}` : ""}`;
    showToast(toastBody);
    if (dt === today) {
      flashSlot(routedSlot);
      try { hapticHayahSuccess(); } catch {}
      if (committedLog) evaluateAndScheduleFastToast(committedLog, entry, routedSlot, foodBase);
    } else {
      // Backfill: lighter ack so the past-date case doesn't feel like a goal-hit.
      try { hapticTap(); } catch {}
    }
    schedulePush();
    // Grow the user food library so this item shows up in the next
    // local search without an AI round-trip. Curated DB items are
    // skipped — they're already searchable from the hardcoded sets.
    upsertUserFood(foodBase);
  }

  // ── User food library (auto-grown searchable index) ────────────
  const userFoodKey = (e) => {
    const n = (e?.name  || "").toLowerCase().trim();
    const b = (e?.brand || "").toLowerCase().trim();
    return `${n}|${b}`;
  };
  const isCuratedSource = (src) => !!src && /^(generic|sa_|sunnah)/i.test(String(src));

  function upsertUserFood(food) {
    if (!food || !food.name) return;
    if (isCuratedSource(food.source)) return; // already in hardcoded DBs
    const key = userFoodKey(food);
    setUserFoods(prev => {
      const idx = prev.findIndex(uf => userFoodKey(uf) === key);
      const baseG = food.servingG && food.servingG > 0 ? food.servingG : 100;
      const next = [...prev];
      const existing = idx >= 0 ? prev[idx] : null;
      const merged = {
        id: existing?.id || `user_${genId()}`,
        name: food.name,
        brand: food.brand || food.restaurant || null,
        // Keep per-serving macros from the food spec (the addFoodEntry
        // caller already scales these to the chosen serving — what we
        // store is the BASE per-servingG, matching the GENERIC_FOODS
        // contract so the search detail/log path stays consistent).
        calories: +food.calories || 0,
        protein:  +food.protein  || 0,
        carbs:    +food.carbs    || 0,
        fat:      +food.fat      || 0,
        fiber:    +food.fiber    || 0,
        sugar:    +food.sugar    || 0,
        sodium:   +food.sodium   || 0,
        servingG: baseG,
        servingLabel: food.servingLabel || `${baseG}g`,
        category: food.category || "Your foods",
        source: "user",
        halal: food.halal != null ? food.halal : null,
        addedAt: existing?.addedAt || Date.now(),
        lastUsedAt: Date.now(),
        usageCount: (existing?.usageCount || 0) + 1,
      };
      if (idx >= 0) next[idx] = merged;
      else next.unshift(merged);
      try { localStorage.setItem("ghidhaUserFoods", JSON.stringify(next)); } catch {}
      return next;
    });
  }

  // One-time backfill — scan calorieLog history and seed the library
  // with every unique food the user has logged. After this runs, every
  // historic AI-search result is available offline. Idempotent via a
  // sentinel key.
  useEffect(() => {
    if (localStorage.getItem("ghidhaUserFoodsBackfilledAt")) return;
    try {
      const log = JSON.parse(localStorage.getItem("calorieLog") || "{}");
      const found = new Map();
      for (const day of Object.values(log)) {
        const meals = day?.meals || {};
        for (const arr of Object.values(meals)) {
          if (!Array.isArray(arr)) continue;
          for (const e of arr) {
            if (!e?.name) continue;
            if (isCuratedSource(e.source)) continue;
            const k = userFoodKey(e);
            const eTs = Date.parse(e.loggedAt) || Date.now();
            const baseG = e.servingG && e.servingG > 0 ? e.servingG : 100;
            if (!found.has(k)) {
              found.set(k, {
                id: `user_${genId()}`,
                name: e.name,
                brand: e.brand || null,
                calories: +e.calories || 0,
                protein:  +e.protein  || 0,
                carbs:    +e.carbs    || 0,
                fat:      +e.fat      || 0,
                fiber:    +e.fiber    || 0,
                sugar:    +e.sugar    || 0,
                sodium:   +e.sodium   || 0,
                servingG: baseG,
                servingLabel: e.servingLabel || `${baseG}g`,
                category: "Your foods",
                source: "user",
                halal: e.halal != null ? e.halal : null,
                addedAt: eTs,
                lastUsedAt: eTs,
                usageCount: 1,
              });
            } else {
              const cur = found.get(k);
              cur.usageCount += 1;
              if (eTs > cur.lastUsedAt) cur.lastUsedAt = eTs;
            }
          }
        }
      }
      const list = Array.from(found.values()).sort((a, b) => b.lastUsedAt - a.lastUsedAt);
      // Merge against anything already in storage (in case state was
      // hydrated mid-flight).
      const existing = (() => { try { return JSON.parse(localStorage.getItem("ghidhaUserFoods") || "[]"); } catch { return []; } })();
      const merged = [...existing];
      let added = 0;
      for (const f of list) {
        const k = userFoodKey(f);
        if (!merged.some(uf => userFoodKey(uf) === k)) { merged.push(f); added += 1; }
      }
      localStorage.setItem("ghidhaUserFoods", JSON.stringify(merged));
      localStorage.setItem("ghidhaUserFoodsBackfilledAt", new Date().toISOString());
      setUserFoods(merged);
      if (added > 0) showToast(`✓ Indexed ${added} previously logged food${added === 1 ? '' : 's'} for offline search`);
    } catch (e) {
      // Don't block the UI — try again next session.
      console.warn("[Ghidha] user-food backfill failed", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removeEntry(mealSlot, entryId, dateKey) {
    const dt = dateKey || today;
    const day = calorieLog[dt];
    if (!day) return;
    const newLog = { ...calorieLog, [dt]: { ...day, meals: { ...(day.meals || {}), [mealSlot]: (day.meals?.[mealSlot] || []).filter(e => e.id !== entryId) } } };
    saveLog(newLog);
    if (dt === today) syncToWidget(newLog);
  }
  function removeEntryWithUndo(mealSlot, entry, dateKey) {
    if (!entry) return;
    const dt = dateKey || today;
    removeEntry(mealSlot, entry.id, dt);
    setUndoToast({ slot: mealSlot, entry, key: Date.now(), date: dt });
  }
  // Re-log a meal entry: append a fresh copy (new id, new loggedAt) to the same
  // slot for today. The entry's stored calories/macros already reflect the
  // chosen serving, so we treat servingG as the baseline (scale = 1).
  function relogEntry(mealSlot, entry) {
    if (!entry) return;
    const food = {
      name: entry.name,
      brand: entry.brand || null,
      calories: entry.calories || 0,
      protein:  entry.protein  || 0,
      carbs:    entry.carbs    || 0,
      fat:      entry.fat      || 0,
      fiber:    entry.fiber    || 0,
      sugar:    entry.sugar    || 0,
      sodium:   entry.sodium   || 0,
      servingG: entry.servingG || 100,
      servingLabel: entry.servingLabel || `${entry.servingG||100}g`,
      source: entry.source || null,
      halal: entry.halal != null ? entry.halal : null,
    };
    addFoodEntry(food, food.servingG, food.servingLabel, mealSlot, today);
  }
  // Save a meal entry into Quick Add (favorites) so it appears in the
  // breakdown overlay's ⚡ QUICK ADD strip for one-tap re-logging later.
  function addEntryToQuickAdd(entry) {
    if (!entry) return;
    const fullName = entry.name + (entry.brand ? ` (${entry.brand})` : "");
    const dup = favorites.find(f => f.type === "food" && (f.name === fullName || f.name === entry.name));
    if (dup) { showToast("Already in Quick Add"); return; }
    const baseG = entry.servingG || 100;
    const fav = {
      id: "fav_" + genId(),
      type: "food",
      name: fullName,
      food: {
        name: entry.name,
        brand: entry.brand || null,
        calories: entry.calories || 0,
        protein:  entry.protein  || 0,
        carbs:    entry.carbs    || 0,
        fat:      entry.fat      || 0,
        fiber:    entry.fiber    || 0,
        sugar:    entry.sugar    || 0,
        sodium:   entry.sodium   || 0,
        servingG: baseG,
        servingLabel: entry.servingLabel || `${baseG}g`,
        source: entry.source || null,
        halal: entry.halal != null ? entry.halal : null,
      },
      defaultServingG: baseG,
      defaultServingLabel: entry.servingLabel || `${baseG}g`,
      usageCount: 0,
      lastUsed: today,
    };
    saveFavs([fav, ...favorites]);
    showToast("⚡ Added to Quick Add");
  }
  function restoreEntry(mealSlot, entry, dateKey) {
    if (!entry) return;
    const dt = dateKey || today;
    const day = calorieLog[dt] || { meals:{ breakfast:[], lunch:[], dinner:[] }, water:0 };
    const list = [...(day.meals?.[mealSlot] || []), entry];
    const newLog = { ...calorieLog, [dt]: { ...day, meals: { ...(day.meals || {}), [mealSlot]: list } } };
    saveLog(newLog);
    if (dt === today) syncToWidget(newLog);
  }
  // Treat the entry's logged values as the per-(servingG) baseline so the FoodDetail
  // serving editor can rescale them. We don't keep a link back to the source food
  // record so we have to derive it from the entry.
  function openEditEntry(mealSlot, entry, dateKey) {
    if (!entry) return;
    const baseG = entry.servingG || 100;
    const synthFood = {
      name: entry.name,
      brand: entry.brand || "",
      calories: entry.calories || 0,
      protein:  entry.protein  || 0,
      carbs:    entry.carbs    || 0,
      fat:      entry.fat      || 0,
      fiber:    entry.fiber    || 0,
      sugar:    entry.sugar    || 0,
      sodium:   entry.sodium   || 0,
      servingG: baseG,
      servingLabel: entry.servingLabel || `${baseG}g`,
      source: entry.source || "edit",
    };
    setSelectedFood(synthFood);
    setSelectedServingG(baseG);
    setSelectedServingLabel(entry.servingLabel || `${baseG}g`);
    setSelectedMealSlot(mealSlot);
    setEditingEntryId(entry.id);
    setEditingLoggedAt(entry.loggedAt || null);
    setEditingEntryDate(dateKey || today);
    setEditReturnView(view);
    setView("food-detail");
  }
  function commitEditEntry(food, newServingG, newServingLabel, mealSlot, entryId, dateKey, newLoggedAt) {
    try { hapticConfirm(); } catch {}
    const dt = dateKey || editingEntryDate || today;
    const day = calorieLog[dt];
    if (!day) return;
    const baseG = food.servingG || 100;
    const scale = newServingG / (baseG || 1);
    const original = Object.values(day.meals || {}).flat().find(e => e?.id === entryId);
    const SLOT_DEFAULT_TIME = { meal: "12:00", drinks: "10:00", supplements: "08:00", whey: "08:00", snacks: "15:00" };
    const fallbackTime = SLOT_DEFAULT_TIME[mealSlot] || "12:00";
    const loggedAt = newLoggedAt
      ? newLoggedAt
      : (original?.loggedAt
          ? original.loggedAt
          : (dt === today ? new Date().toISOString() : new Date(`${dt}T${fallbackTime}:00`).toISOString()));
    const updated = {
      id: entryId,
      name: food.name,
      brand: food.brand || "",
      servingG: newServingG,
      servingLabel: newServingLabel,
      calories: (food.calories || 0) * scale,
      protein:  (food.protein  || 0) * scale,
      carbs:    (food.carbs    || 0) * scale,
      fat:      (food.fat      || 0) * scale,
      fiber:    (food.fiber    || 0) * scale,
      sugar:    (food.sugar    || 0) * scale,
      sodium:   (food.sodium   || 0) * scale,
      source:   food.source || "edit",
      loggedAt,
      userSlot: mealSlot,
    };
    // Honor the user's explicit slot pick from the edit picker — do NOT
    // re-run the junk/whey/drinks classifier here. Junk is purely snacks-slot
    // membership, so forcing a re-route would undo a deliberate move out of
    // Junk. userSlot (stamped above) keeps the migration from re-junking it.
    const targetSlot = mealSlot;
    const meals = { ...(day.meals || {}) };
    // Remove the entry from whichever slot currently holds it (its slot may
    // differ from the picker value), then add the updated copy to targetSlot.
    for (const k of Object.keys(meals)) {
      meals[k] = (meals[k] || []).filter(e => e.id !== entryId);
    }
    meals[targetSlot] = [...(meals[targetSlot] || []), updated];
    const newLog = { ...calorieLog, [dt]: { ...day, meals } };
    saveLog(newLog);
    if (dt === today) syncToWidget(newLog);
    showToast(dt === today ? "Updated ✓" : `Updated · ${dt} ✓`);
    schedulePush();
  }
  function setWater(cups) {
    const prev = calorieLog[today] || { meals:{ breakfast:[],lunch:[],dinner:[] }, water:0 };
    saveLog({ ...calorieLog, [today]: { ...prev, water: cups } });
  }
  function logWeight() {
    const kg = parseFloat(weightInput);
    if (!kg || kg <= 0) return;
    const updated = [...weightLog.filter(w => w.date !== today), { date: today, kg }].sort((a,b)=>a.date.localeCompare(b.date));
    saveWeights(updated);
    setWeightInput("");
    showToast(`Weight logged: ${kg} kg`);
  }
  function favoriteFoodName(food) {
    return food.name + (food.brand ? ` (${food.brand})` : "");
  }
  function isFoodFavorited(food) {
    if (!food) return false;
    const fullName = favoriteFoodName(food);
    return favorites.some(f => f.type === "food" && (f.name === fullName || f.name === food.name));
  }
  function toggleFavorite(food) {
    try { hapticHayahToggle(); } catch {}
    const fullName = favoriteFoodName(food);
    const existing = favorites.find(f => f.type === "food" && (f.name === fullName || f.name === food.name));
    if (existing) {
      saveFavs(favorites.filter(f => f.id !== existing.id));
      showToast("Removed from favorites");
      return;
    }
    const fav = {
      id: "fav_" + genId(), type: "food",
      name: fullName,
      food: { ...food },
      defaultServingG: food.servingG || 100,
      defaultServingLabel: food.servingLabel || "100g",
      usageCount: 0, lastUsed: today,
    };
    saveFavs([fav, ...favorites]);
    showToast("⭐ Added to favorites");
  }
  function quickAddFavorite(fav, slot) {
    if (fav.type === "food") {
      addFoodEntry(fav.food, fav.defaultServingG, fav.defaultServingLabel, slot);
    } else if (fav.type === "preset") {
      fav.foods.forEach(f => addFoodEntry(f.food, f.servingG, f.servingLabel, slot));
      showToast(`${fav.name} added ✓`);
    }
    saveFavs(favorites.map(f => f.id === fav.id ? { ...f, usageCount: (f.usageCount||0)+1, lastUsed: today } : f));
    setFavSlotPicker(null);
  }
  function deleteFavorite(id) { saveFavs(favorites.filter(f => f.id !== id)); }
  function openEditFavorite(fav) {
    setEditingFav(fav);
    if (fav.type === "food") {
      setEditFavDraft({
        name: fav.name || "",
        defaultServingG: String(fav.defaultServingG || fav.food?.servingG || 100),
        defaultServingLabel: fav.defaultServingLabel || "",
        calories: String(Math.round(fav.food?.calories || 0)),
        protein: String(fav.food?.protein || 0),
        carbs: String(fav.food?.carbs || 0),
        fat: String(fav.food?.fat || 0),
      });
    } else {
      setEditFavDraft({ name: fav.name || "" });
    }
  }
  function commitEditFavorite() {
    if (!editingFav || !editFavDraft) return;
    const id = editingFav.id;
    const trimmedName = (editFavDraft.name || "").trim();
    if (!trimmedName) { showToast("Name can't be empty"); return; }
    if (editingFav.type === "food") {
      const sg = parseFloat(editFavDraft.defaultServingG) || editingFav.food?.servingG || 100;
      const sl = (editFavDraft.defaultServingLabel || "").trim() || `${sg}g`;
      const baseSg = editingFav.food?.servingG || 100;
      const cal = parseFloat(editFavDraft.calories);
      const p   = parseFloat(editFavDraft.protein);
      const c   = parseFloat(editFavDraft.carbs);
      const fa  = parseFloat(editFavDraft.fat);
      const newFood = { ...editingFav.food };
      if (!isNaN(cal)) newFood.calories = cal * (baseSg / sg);
      if (!isNaN(p))   newFood.protein  = p   * (baseSg / sg);
      if (!isNaN(c))   newFood.carbs    = c   * (baseSg / sg);
      if (!isNaN(fa))  newFood.fat      = fa  * (baseSg / sg);
      newFood.name = trimmedName;
      saveFavs(favorites.map(f => f.id === id
        ? { ...f, name: trimmedName, defaultServingG: sg, defaultServingLabel: sl, food: newFood }
        : f));
    } else {
      saveFavs(favorites.map(f => f.id === id ? { ...f, name: trimmedName } : f));
    }
    setEditingFav(null); setEditFavDraft(null);
    showToast("Saved ✓");
  }
  function saveFrequentAsFavorite(food, openEdit = true) {
    const fullName = food.name + (food.brand ? ` (${food.brand})` : "");
    const dup = favorites.find(f => f.type === "food" && (f.name === fullName || f.name === food.name));
    if (dup) {
      if (openEdit) openEditFavorite(dup);
      return;
    }
    const fav = {
      id: "fav_" + genId(), type: "food",
      name: fullName,
      food: { ...food },
      defaultServingG: food.servingG || 100,
      defaultServingLabel: food.servingLabel || `${food.servingG || 100}g`,
      usageCount: 0, lastUsed: today,
    };
    saveFavs([fav, ...favorites]);
    showToast("⭐ Saved");
    if (openEdit) setTimeout(()=>openEditFavorite(fav), 80);
  }
  function saveSlotAsPreset(slot, name) {
    try { hapticHayahSuccess(); } catch {}
    const items = (todayData.meals?.[slot] || []);
    if (!items.length) return;
    const fav = {
      id: "fav_" + genId(), type: "preset",
      name: name || `${MEAL_SLOTS.find(m=>m.key===slot)?.label} Preset`,
      foods: items.map(it => ({ food: { ...it }, servingG: it.servingG, servingLabel: it.servingLabel })),
      usageCount: 0, lastUsed: today,
    };
    saveFavs([fav, ...favorites]);
    showToast("⭐ Preset saved");
    setSavePresetMode(false); setPresetName(""); setPresetSlot(null);
  }
  // Saves the current search-selection set as a named meal preset. When
  // alsoLog is true, follows up with the existing saveAllSelections() so
  // the user gets both the log and the reusable preset in one go.
  function saveSelectionsAsPreset(name, alsoLog) {
    try { hapticHayahSuccess(); } catch {}
    const items = Object.values(searchSelections);
    if (!items.length) return;
    const fav = {
      id: "fav_" + genId(), type: "preset",
      name: (name || "").trim() || "My meal",
      foods: items.map(it => ({ food: { ...it.food }, servingG: it.servingG, servingLabel: it.servingLabel })),
      usageCount: 0, lastUsed: today,
    };
    saveFavs([fav, ...favorites]);
    setSaveMealSheet(null);
    if (alsoLog) {
      saveAllSelections();
      showToast("⭐ Meal saved & logged");
    } else {
      setSearchSelections({});
      showToast("⭐ Meal saved to Quick Add");
    }
  }
  function copyDayToToday(srcDate) {
    const src = calorieLog[srcDate];
    if (!src) return;
    const prev = calorieLog[today] || { meals:{ breakfast:[],lunch:[],dinner:[] }, water:0 };
    const merged = { ...prev.meals };
    Object.keys(src.meals || {}).forEach(slot => {
      const fresh = (src.meals[slot] || []).map(e => ({ ...e, id: genId(), loggedAt: new Date().toISOString() }));
      merged[slot] = [...(merged[slot] || []), ...fresh];
    });
    saveLog({ ...calorieLog, [today]: { ...prev, meals: merged } });
    showToast(`Copied ${srcDate} to today ✓`);
    setView("log");
  }
  async function readAppleHealth(silent = false) {
    try {
      await CapacitorHealthkit.requestAuthorization({
        all: [], write: [],
        read: ["calories", "steps", "activity", "sleepAnalysis"],
      });
      const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
      const now = new Date();
      const yesterday6pm = new Date(startOfDay); yesterday6pm.setHours(-6);
      const opts = { startDate: startOfDay.toISOString(), endDate: now.toISOString(), limit: 0 };
      const sleepOpts = { startDate: yesterday6pm.toISOString(), endDate: now.toISOString(), limit: 0 };
      const [activeRes, stepsRes, basalRes, sleepRes] = await Promise.all([
        CapacitorHealthkit.queryHKitSampleType({ ...opts,      sampleName: SampleNames.ACTIVE_ENERGY_BURNED }),
        CapacitorHealthkit.queryHKitSampleType({ ...opts,      sampleName: SampleNames.STEP_COUNT }),
        CapacitorHealthkit.queryHKitSampleType({ ...opts,      sampleName: SampleNames.BASAL_ENERGY_BURNED }),
        CapacitorHealthkit.queryHKitSampleType({ ...sleepOpts, sampleName: SampleNames.SLEEP_ANALYSIS }),
      ]);
      const burned = Math.round(activeRes.resultData.reduce((s, r) => s + (r.value || 0), 0));
      const basalBySource = {};
      for (const r of basalRes.resultData) {
        const src = r.sourceBundleId || r.source || "unknown";
        basalBySource[src] = (basalBySource[src] || 0) + (r.value || 0);
      }
      const basalBurned = Math.round(Math.max(0, ...Object.values(basalBySource)));
      const stepsBySource = {};
      for (const r of stepsRes.resultData) {
        const src = r.sourceBundleId || r.source || "unknown";
        stepsBySource[src] = (stepsBySource[src] || 0) + (r.value || 0);
      }
      const steps = Math.round(Math.max(0, ...Object.values(stepsBySource)));

      const isDeep  = s => s === 4 || s === "4" || /deep/i.test(String(s));
      const isREM   = s => s === 5 || s === "5" || /rem/i.test(String(s));
      const isSleep = s => [1,3,"1","3"].includes(s) || /asleep|core|unspecified/i.test(String(s));
      let sleepMinutes = 0, deepMinutes = 0, remMinutes = 0, awakeMinutes = 0;
      const sleepSegments = []; // raw stage timeline for the real hypnogram

      // Try native readSleepData first for proper Deep/REM stage granularity.
      // Falls back to capacitor-healthkit results if not available.
      let usedNativeSleep = false;
      try {
        const nativeSleep = await GhidhaHealth.readSleepData();
        const samples = nativeSleep?.data || [];
        // Apple Watch (iOS 16+) writes both a summary ASLEEP(1) sample AND detailed
        // ASLEEPCORE/DEEP/REM samples for the same period. Summing all of them
        // doubles the total. If detailed stages exist, skip the summary ASLEEP(1).
        const hasDetailedStages = samples.some(r =>
          r.sleepState === "ASLEEPCORE" || r.sleepState === "ASLEEPDEEP" || r.sleepState === "ASLEEPREM"
        );
        for (const r of samples) {
          const st = r.sleepState;
          if (hasDetailedStages && st === "ASLEEP") continue;
          if (st === "INBED" || st === "UNKNOWN") continue;
          const startMs = new Date(r.startDate).getTime();
          const endMs   = new Date(r.endDate).getTime();
          const mins = Math.round((endMs - startMs) / 60000);
          if (mins <= 0) continue;
          sleepSegments.push({ st, s: startMs, e: endMs });
          if (st === "AWAKE")           { awakeMinutes += mins; }
          else if (isDeep(st))          { deepMinutes  += mins; sleepMinutes += mins; }
          else if (isREM(st))           { remMinutes   += mins; sleepMinutes += mins; }
          else if (st === "ASLEEPCORE") { sleepMinutes += mins; }
          else if (!hasDetailedStages && isSleep(st)) { sleepMinutes += mins; }
        }
        usedNativeSleep = true;
      } catch {}
      if (!usedNativeSleep) {
        for (const r of (sleepRes.resultData || [])) {
          const st = r.sleepState;
          if (st === "INBED" || st === "UNKNOWN") continue;
          const startMs = new Date(r.startDate).getTime();
          const endMs   = new Date(r.endDate).getTime();
          const mins = Math.round((endMs - startMs) / 60000);
          if (mins <= 0) continue;
          sleepSegments.push({ st, s: startMs, e: endMs });
          if (st === "AWAKE")     { awakeMinutes += mins; }
          else if (isDeep(st))    { deepMinutes  += mins; sleepMinutes += mins; }
          else if (isREM(st))     { remMinutes   += mins; sleepMinutes += mins; }
          else if (isSleep(st))   { sleepMinutes += mins; }
        }
      }

      // Real activity + vitals from HealthKit native plugin.
      let distanceMeters = 0, exerciseMinutes = 0;
      let restingHeartRate = 0, heartRateVariability = 0, oxygenSaturation = 0, respiratoryRate = 0, standHours = 0;
      // Sample timestamps + freshness flags — the Swift plugin falls back to
      // 72h-old samples when today's overnight reading hasn't landed yet, so
      // we MUST avoid persisting those as if they were today's value.
      let restingHeartRateAt = null, heartRateVariabilityAt = null;
      let rhrFresh = false, hrvFresh = false;
      try {
        const actData = await GhidhaHealth.readActivityData();
        distanceMeters      = actData?.distanceMeters      || 0;
        exerciseMinutes     = Math.round(actData?.exerciseMinutes     || 0);
        restingHeartRate    = Math.round(actData?.restingHeartRate    || 0);
        heartRateVariability= Math.round(actData?.heartRateVariability|| 0);
        oxygenSaturation    = actData?.oxygenSaturation ? parseFloat(actData.oxygenSaturation.toFixed(1)) : 0;
        respiratoryRate     = actData?.respiratoryRate   ? parseFloat(actData.respiratoryRate.toFixed(1))  : 0;
        standHours          = actData?.standHours        || 0;
        restingHeartRateAt     = actData?.restingHeartRateAt     || null;
        heartRateVariabilityAt = actData?.heartRateVariabilityAt || null;
        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        rhrFresh = !!(restingHeartRateAt     && Date.parse(restingHeartRateAt)     >= todayStart.getTime());
        hrvFresh = !!(heartRateVariabilityAt && Date.parse(heartRateVariabilityAt) >= todayStart.getTime());
      } catch {}
      let bodyMassKg = 0, bodyFatPct = 0, bodyHistory = { weight: [], bodyFat: [] };
      try {
        const bc = await GhidhaHealth.readBodyComposition();
        bodyHistory.weight  = (bc?.weightSamples  || []).map(s => ({ date: s.date, value: s.value }));
        bodyHistory.bodyFat = (bc?.bodyFatSamples || []).map(s => ({ date: s.date, value: s.value }));
        if (bodyHistory.weight.length)  bodyMassKg = bodyHistory.weight[bodyHistory.weight.length - 1].value;
        if (bodyHistory.bodyFat.length) bodyFatPct = bodyHistory.bodyFat[bodyHistory.bodyFat.length - 1].value;
      } catch {}
      // Slow-moving / early-warning signals (vo2Max, wrist temp delta,
      // walking HR avg, flights, mindful minutes). None are critical paths
      // so we treat the call as best-effort and zero-default on failure.
      let vo2Max = 0, vo2MaxDelta30d = 0, wristTempDeltaC = 0, walkingHrAvg = 0, flightsClimbed = 0, mindfulMinutes = 0;
      let vo2MaxAt = null, wristTempDeltaAt = null, walkingHrAvgAt = null;
      try {
        const adv = await GhidhaHealth.readAdvancedSignals();
        vo2Max          = adv?.vo2Max          ? parseFloat(Number(adv.vo2Max).toFixed(1))          : 0;
        vo2MaxDelta30d  = adv?.vo2MaxDelta30d  ? parseFloat(Number(adv.vo2MaxDelta30d).toFixed(2))  : 0;
        wristTempDeltaC = adv?.wristTempDeltaC ? parseFloat(Number(adv.wristTempDeltaC).toFixed(2)) : 0;
        walkingHrAvg    = adv?.walkingHrAvg    ? Math.round(adv.walkingHrAvg)                       : 0;
        flightsClimbed  = adv?.flightsClimbed  ? Math.round(adv.flightsClimbed)                     : 0;
        mindfulMinutes  = adv?.mindfulMinutes  ? Math.round(adv.mindfulMinutes)                     : 0;
        vo2MaxAt         = adv?.vo2MaxAt         || null;
        wristTempDeltaAt = adv?.wristTempDeltaAt || null;
        walkingHrAvgAt   = adv?.walkingHrAvgAt   || null;
      } catch {}
      // Dietary backfill from other tracker apps (MyFitnessPal etc).
      // Captured here so the AI context can see "user logged 1850 kcal in
      // MFP today" even if Sirat itself has zero entries.
      let externalDietary = null;
      try {
        const d = await GhidhaHealth.readDietaryToday();
        if (d && Object.values(d).some(v => typeof v === "number" && v > 0)) {
          externalDietary = {
            kcal:     Math.round(d.kcal || 0),
            proteinG: Math.round(d.proteinG || 0),
            carbsG:   Math.round(d.carbsG || 0),
            fatG:     Math.round(d.fatG || 0),
            fiberG:   Math.round(d.fiberG || 0),
            waterMl:  Math.round(d.waterMl || 0),
            sources:  Array.isArray(d.sources) ? d.sources : [],
          };
        }
      } catch {}
      const log = JSON.parse(localStorage.getItem("calorieLog") || "{}");
      // Only persist HRV/RHR/etc into today's log when the sample is actually
      // from today. Otherwise the Swift fallback (most recent in last 72h)
      // would write yesterday's value into today's slot and pollute the
      // rolling baselines used by the Readiness deltas.
      const prev = log[today] || { meals:{ breakfast:[],lunch:[],dinner:[] }, water:0 };
      const next = { ...prev,
        caloriesBurned: burned, steps, basalBurned,
        distanceMeters, exerciseMinutes,
        oxygenSaturation, respiratoryRate, standHours,
        bodyMassKg, bodyFatPct, bodyHistory,
        vo2MaxDelta30d, flightsClimbed, mindfulMinutes, externalDietary,
      };
      // Sleep is only meaningful when HealthKit has actually processed
      // last night. A 0 from an empty query at 06:35 would otherwise
      // overwrite a legitimate earlier sync.
      if (sleepMinutes > 0) {
        next.sleepMinutes  = sleepMinutes;
        next.deepMinutes   = deepMinutes;
        next.remMinutes    = remMinutes;
        next.awakeMinutes  = awakeMinutes;
        next.sleepSegments = sleepSegments;
      }
      // Conditional fields — only overwrite when the sample is from today,
      // otherwise leave whatever's there (or absent) untouched.
      if (rhrFresh) { next.restingHeartRate     = restingHeartRate;     next.restingHeartRateAt     = restingHeartRateAt; }
      if (hrvFresh) { next.heartRateVariability = heartRateVariability; next.heartRateVariabilityAt = heartRateVariabilityAt; }
      // Non-fresh fallback → separate slot (never feeds the rolling baseline).
      // Lets the Readiness card show "stale · Nd ago" before today's
      // overnight RHR/HRV sample has landed, instead of a blank "—".
      if (restingHeartRate     > 0 && !rhrFresh) { next.restingHeartRateRecent     = restingHeartRate;     next.restingHeartRateRecentAt     = restingHeartRateAt; }
      if (heartRateVariability > 0 && !hrvFresh) { next.heartRateVariabilityRecent = heartRateVariability; next.heartRateVariabilityRecentAt = heartRateVariabilityAt; }
      // VO₂max / wrist temp / walking HR: Apple Health updates these slowly
      // (VO₂max weeks; walking HR rolling). Store the value + timestamp so
      // the UI can show "as of <date>" rather than imply it's today's read.
      if (vo2Max > 0)         { next.vo2Max          = vo2Max;          if (vo2MaxAt) next.vo2MaxAt = vo2MaxAt; }
      if (wristTempDeltaC !== 0) { next.wristTempDeltaC = wristTempDeltaC; if (wristTempDeltaAt) next.wristTempDeltaAt = wristTempDeltaAt; }
      if (walkingHrAvg > 0)   { next.walkingHrAvg    = walkingHrAvg;    if (walkingHrAvgAt) next.walkingHrAvgAt = walkingHrAvgAt; }
      log[today] = next;
      localStorage.setItem("calorieLog", JSON.stringify(log));
      setCalorieLog(log);
      syncVitalsToWidget(log);
      const syncedAt = Date.now();
      localStorage.setItem("ghidhaLastHealthSyncAt", String(syncedAt));
      setLastHealthSyncAt(syncedAt);
      if (!silent) {
        if (burned === 0 && steps === 0 && sleepMinutes === 0) {
          showToast("No data — check Settings → Health → Sirat");
        } else {
          showToast(`Synced · ${steps.toLocaleString()} steps · ${sleepMinutes ? Math.floor(sleepMinutes/60)+"h sleep" : "no sleep data"}`);
        }
      }
    } catch (e) {
      if (!silent) showToast("Health sync failed");
    }
  }
  async function pushFoodToHealth(silent = false) {
    if (totalCalories <= 0) return;
    const hash = `${today}_${Math.round(totalCalories)}_${Math.round(totalProtein)}_${Math.round(totalCarbs)}_${Math.round(totalFat)}_${Math.round(totalFiber)}_${water}`;
    if (hash === localStorage.getItem("ghidhaLastPushHash")) {
      if (!silent) showToast("Already up to date in Apple Health ✓");
      return;
    }
    try {
      // Only request write auth on an explicit user push, and only once per
      // install. On silent auto-syncs we skip the request entirely so iOS
      // never re-presents the dietary-write sheet on app foreground. If the
      // user never authorized, writeDietaryData will just no-op silently.
      if (!silent && localStorage.getItem("ghidhaFoodHealthAuthAsked") !== "1") {
        await GhidhaHealth.requestAuthorization();
        localStorage.setItem("ghidhaFoodHealthAuthAsked", "1");
      }
      await GhidhaHealth.writeDietaryData({
        calories: Math.round(totalCalories),
        protein:  Math.round(totalProtein),
        carbs:    Math.round(totalCarbs),
        fat:      Math.round(totalFat),
        fiber:    Math.round(totalFiber),
        waterMl:  water * 250,
      });
      localStorage.setItem("ghidhaLastPushHash", hash);
      if (!silent) showToast("Food data saved to Apple Health ✓");
    } catch (e) {
      if (!silent) showToast("Write to Health failed — check Settings → Health → Sirat");
    }
  }
  // Updated every render so interval/visibilitychange always call fresh closures
  autoSyncRef.current = () => {
    if (!healthAvailable) return;
    readAppleHealth(true);
    pushFoodToHealth(true);
  };
  function schedulePush() {
    if (!healthAvailable) return;
    clearTimeout(pushDebounceRef.current);
    pushDebounceRef.current = setTimeout(() => pushFoodToHealth(true), 30000);
  }

  // ── Flow layer helpers ────────────────────────────────────────────────────
  function _assertFlowOK(nextView, nextCtx) {
    const flowViews = ["search", "food-detail", "barcode"];
    const rootViews = ["dashboard", "log", "favorites", "history", "weekly", "goals"];
    const ok = nextCtx.active
      ? flowViews.includes(nextView) && nextCtx.flowStack.length > 0 && nextCtx.flowStack[nextCtx.flowStack.length - 1] === nextView
      : rootViews.includes(nextView) && nextCtx.flowStack.length === 0;
    if (!ok && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[Ghidha] flow invariant violated", { nextView, nextCtx });
    }
  }
  // Begin a flow from a tab root (Today / Log / History). Snapshots view,
  // expandedSlot, and scroll so cancelFlow/endFlow can restore them.
  function startFlow({ slot, date }) {
    const next = {
      active: true,
      slot: slot || null,
      date: date || today,
      flowStack: ["search"],
      prevView: view,
      prevExpandedSlot: expandedSlot,
      prevScrollTop: containerRef.current ? containerRef.current.scrollTop : 0,
    };
    _assertFlowOK("search", next);
    setFlowCtx(next);
    if (slot) setSelectedMealSlot(slot);
    setSearchTab("search");
    setView("search");
  }
  function pushFlowView(nextView) {
    if (!flowCtx.active) { setView(nextView); return; }
    const stack = [...flowCtx.flowStack, nextView];
    const next = { ...flowCtx, flowStack: stack };
    _assertFlowOK(nextView, next);
    setFlowCtx(next);
    setView(nextView);
  }
  function popFlowView() {
    if (!flowCtx.active) return;
    if (flowCtx.flowStack.length <= 1) { cancelFlow(); return; }
    const stack = flowCtx.flowStack.slice(0, -1);
    const nextView = stack[stack.length - 1];
    const next = { ...flowCtx, flowStack: stack };
    _assertFlowOK(nextView, next);
    setFlowCtx(next);
    setView(nextView);
  }
  function _restoreScroll(top) {
    if (top == null) return;
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = top;
    });
  }
  // Successful commit — restore source tab and (if entered from Today) expand
  // the slot just logged into so the user sees their entry.
  function endFlow() {
    if (!flowCtx.active) return;
    const prevView = flowCtx.prevView || "dashboard";
    const slotJustLogged = flowCtx.slot;
    // Expand the slot we just logged to whenever returning to a meal-list view
    // (Today / Log) so the user sees their entry land. History also shows meals
    // but the day-detail card always lists everything for the selected day.
    const showsMealList = prevView === "dashboard" || prevView === "log" || prevView === "history";
    const prevScroll = flowCtx.prevScrollTop;
    const nextCtx = { active:false, slot:null, date:null, flowStack:[], prevView:null, prevExpandedSlot:null, prevScrollTop:null };
    _assertFlowOK(prevView, nextCtx);
    setFlowCtx(nextCtx);
    setView(prevView);
    if (showsMealList && slotJustLogged) setExpandedSlot(slotJustLogged);
    else setExpandedSlot(flowCtx.prevExpandedSlot);
    setSearchSelections({}); setSearchInlineServings({});
    setExpandedFoodId(null);
    _restoreScroll(prevScroll);
  }
  // Back-out without commit.
  function cancelFlow() {
    if (!flowCtx.active) return;
    const prevView = flowCtx.prevView || "dashboard";
    const prevExp = flowCtx.prevExpandedSlot;
    const prevScroll = flowCtx.prevScrollTop;
    const nextCtx = { active:false, slot:null, date:null, flowStack:[], prevView:null, prevExpandedSlot:null, prevScrollTop:null };
    _assertFlowOK(prevView, nextCtx);
    setFlowCtx(nextCtx);
    setView(prevView);
    setExpandedSlot(prevExp);
    setSearchSelections({}); setSearchInlineServings({});
    setExpandedFoodId(null);
    _restoreScroll(prevScroll);
  }
  function onHeaderBack() {
    if (flowCtx.active) {
      if (flowCtx.flowStack.length > 1) popFlowView();
      else cancelFlow();
    } else if (editingEntryId) {
      // Entry-edit isn't a flow but reuses food-detail; back cancels the edit.
      const editDt = editingEntryDate;
      setEditingEntryId(null);
      setEditingEntryDate(null);
      setView(editDt && editDt !== today ? "history" : "dashboard");
    } else if (prevTabRoot) {
      // Came from another tab root via a "See all" link — return there.
      const target = prevTabRoot;
      setPrevTabRoot(null);
      setView(target);
    } else if (onBack) {
      onBack();
    }
  }
  // ── Multi-select helpers ──────────────────────────────────────────────────
  function _foodKey(food) { return food && (food.id || food.name); }
  function _inlineServingFor(food) {
    const key = _foodKey(food);
    const override = searchInlineServings[key];
    if (override) return { qty: override.qty, servingG: override.servingG, servingLabel: override.servingLabel };
    const baseG = food.servingG || 100;
    return { qty: 1, servingG: baseG, servingLabel: food.servingLabel || `${baseG}g` };
  }
  function setInlineServingQty(food, qty) {
    if (!food || !Number.isFinite(qty) || qty <= 0) return;
    const key = _foodKey(food);
    const baseG = food.servingG || 100;
    const baseLabel = food.servingLabel || `${baseG}g`;
    const fmtLabel = (q) => q === 1 ? baseLabel : q === 0.5 ? `½× ${baseLabel}` : `${Number.isInteger(q) ? q : Math.round(q*100)/100}× ${baseLabel}`;
    const nextServingG = Math.round(baseG * qty * 100) / 100;
    const nextLabel = fmtLabel(qty);
    setSearchInlineServings(prev => ({ ...prev, [key]: { qty, servingG: nextServingG, servingLabel: nextLabel } }));
    // Keep an active selection's serving in sync with the row stepper.
    setSearchSelections(prev => prev[key] ? { ...prev, [key]: { ...prev[key], servingG: nextServingG, servingLabel: nextLabel } } : prev);
  }
  function toggleFoodSelection(food) {
    if (!food) return;
    const key = _foodKey(food);
    const inline = _inlineServingFor(food);
    setSearchSelections(prev => {
      if (prev[key]) {
        const { [key]:_, ...rest } = prev;
        return rest;
      }
      // First tick after a query → remember the query so the next time
      // the user opens search with an empty box, this query is offered
      // as a chip below the input.
      try { pushRecentSearch(searchQuery); } catch {}
      return { ...prev, [key]: { food, servingG: inline.servingG, servingLabel: inline.servingLabel } };
    });
  }
  function isFoodSelected(food) { return !!searchSelections[_foodKey(food)]; }
  function clearAllSelections() { setSearchSelections({}); setSearchInlineServings({}); }
  // Commit every selected item, then exit the flow.
  function saveAllSelections() {
    const allItems = Object.values(searchSelections);
    if (!allItems.length) return;
    const slot = selectedMealSlot;
    if (!slot) return;
    const dt = flowCtx.date || today;
    // Items with `pickerKey` (e.g. Jacobs Cappuccino → which milk?) defer
    // to the variant sheet AFTER the regular bulk save. We pull them out
    // here, save the rest in one batch, then hand the queue to the picker.
    const items = allItems.filter(it => !(it.food && it.food.pickerKey && Array.isArray(it.food.milkOptions) && it.food.milkOptions.length));
    const pickerItems = allItems.filter(it => it.food && it.food.pickerKey && Array.isArray(it.food.milkOptions) && it.food.milkOptions.length);
    // Slot-appropriate fallback time for back-fill timestamps (see also
    // addFoodEntry). Avoids stamping a back-filled meal with "now" which
    // would distort the fasting-window math.
    const SLOT_DEFAULT_TIME = {
      breakfast: "07:30", lunch: "12:30", dinner: "19:00",
      drinks: "10:00", supplements: "08:00", whey: "08:00", snacks: "15:00",
    };
    const isPastDate = dt !== today;
    const slotTime = SLOT_DEFAULT_TIME[slot] || "12:00";
    const bulkLoggedAt = isPastDate
      ? new Date(`${dt}T${slotTime}:00`).toISOString()
      : new Date().toISOString();
    // Fold every selection into a single new log + frequency map. Calling
    // addFoodEntry in a loop hits a state-closure bug where each iteration
    // reads the same calorieLog snapshot, so the final saveLog wipes out
    // every prior add. Build the merged result up-front instead.
    // Skip the whole batch if the only selections were picker items —
    // those add later via the variant sheet.
    if (items.length === 0) { /* fall through to picker block below */ }
    else {
    const prevDay = calorieLog[dt] || { meals:{ breakfast:[], lunch:[], dinner:[] }, water:0, weight:null };
    const newFreq = { ...foodFrequency };
    // Per-item slot routing — same rules as addFoodEntry. Without this the
    // bulk save dumps every selection into the user-picked slot (e.g. MEAL)
    // even when items should auto-route (whey → whey, beverages → drinks,
    // junk → snacks). See addFoodEntry for the canonical routing block.
    const routeSlot = (foodBase) => {
      if (isSupplement(foodBase))  return 'supplements';
      if (isWheyProtein(foodBase)) return 'whey';
      if (isDrink(foodBase))       return 'drinks';
      if (isJunkFood(foodBase) && slot !== 'supplements') return 'snacks';
      return slot;
    };
    const slotBuckets = {};   // routedSlot → [newEntry, …]
    items.forEach(it => {
      const foodBase = it.food;
      const baseG = foodBase.servingG || 100;
      const scale = it.servingG / baseG;
      const freqKey = foodBase.id || foodBase.name;
      newFreq[freqKey] = (newFreq[freqKey] || 0) + 1;
      const entry = {
        id: genId(),
        name: foodBase.name,
        brand: foodBase.brand || foodBase.restaurant || null,
        calories: Math.round((foodBase.calories || 0) * scale * 10) / 10,
        protein:  Math.round((foodBase.protein  || 0) * scale * 10) / 10,
        carbs:    Math.round((foodBase.carbs    || 0) * scale * 10) / 10,
        fat:      Math.round((foodBase.fat      || 0) * scale * 10) / 10,
        fiber:    Math.round((foodBase.fiber    || 0) * scale * 10) / 10,
        sugar:    Math.round((foodBase.sugar    || 0) * scale * 10) / 10,
        sodium:   Math.round((foodBase.sodium   || 0) * scale),
        servingG: it.servingG,
        servingLabel: it.servingLabel,
        source: foodBase.source || (foodBase.restaurant ? "sa_restaurant" : "sunnah"),
        halal: foodBase.halal != null ? foodBase.halal : null,
        loggedAt: bulkLoggedAt,
      };
      const routed = routeSlot(foodBase);
      (slotBuckets[routed] = slotBuckets[routed] || []).push(entry);
    });
    const mergedMeals = { ...(prevDay.meals || {}) };
    for (const [routed, entries] of Object.entries(slotBuckets)) {
      mergedMeals[routed] = [...(mergedMeals[routed] || []), ...entries];
    }
    const newLog = {
      ...calorieLog,
      [dt]: { ...prevDay, meals: mergedMeals },
    };
    saveLog(newLog);
    setFoodFrequency(newFreq);
    localStorage.setItem("calorieFrequency", JSON.stringify(newFreq));
    if (dt === today) syncToWidget(newLog);
    syncFrequentMealsToWidget(newLog, newFreq);
    schedulePush();
    const slotLabel = MEAL_SLOTS.find(m=>m.key===slot)?.label || slot;
    const dateSuffix = dt !== today ? ` · ${dt}` : "";
    showToast(`${items.length} item${items.length===1?"":"s"} → ${slotLabel}${dateSuffix} ✓`);
    }
    setSearchSelections({});
    setSearchInlineServings({});
    // Navigate back unless a variant picker is about to take over — in that
    // case the picker's onPick handler runs `afterAllDone` once the queue
    // empties so the user lands on the Log view AFTER all milk choices.
    const afterAllDone = () => {
      if (flowCtx.active) endFlow();
      else { setView("log"); setExpandedSlot(slot); }
      if (pendingBreakdownReopenRef.current) setBreakdownReopenSignal(s => s + 1);
    };
    if (pickerItems.length) {
      const first = pickerItems[0];
      const rest = pickerItems.slice(1).map(it => ({ foodBase: it.food, servingG: it.servingG, servingLabel: it.servingLabel, mealSlot: slot, dateKey: dt }));
      setVariantPicker({ foodBase: first.food, servingG: first.servingG, servingLabel: first.servingLabel, mealSlot: slot, dateKey: dt, queue: rest, afterAllDone });
    } else {
      afterAllDone();
    }
  }

  // Reset the flow stack to Search and clear the food selection so the user can
  // log another item to the same slot/date without leaving the flow.
  function continueFlowAfterAdd() {
    if (!flowCtx.active) return;
    const next = { ...flowCtx, flowStack: ["search"] };
    _assertFlowOK("search", next);
    setFlowCtx(next);
    setView("search");
    setSelectedFood(null);
    setSelectedServingG(0);
    setSelectedServingLabel("");
    setSearchQuery("");
    setSearchResults([]);
    setSearchTab("search");
  }

  // Open the search flow targeting a specific meal slot, and remember to re-open
  // the breakdown overlay after a successful add so users can keep editing without
  // re-tapping the calorie ring.
  function startLogForSlot(slot) {
    const chosen = slot || autoMealSlot();
    pendingBreakdownReopenRef.current = true;
    startFlow({ slot: chosen, date: today });
  }
  // Auto-dismiss the undo toast after 5s.
  useEffect(() => {
    if (!undoToast) return;
    const t = setTimeout(() => setUndoToast(null), 5000);
    return () => clearTimeout(t);
  }, [undoToast]);
  // Sync the serving-qty input string when a new food gets selected (search,
  // recent, sunnah, supps, barcode, edit). Does NOT sync on every selectedServingG
  // change — that would clobber partial typing like "1." while user is still typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedFood) return;
    const baseG = selectedFood.servingG || 100;
    const q = baseG > 0 ? selectedServingG / baseG : 1;
    if (!Number.isFinite(q) || q <= 0) { setServingQtyStr("1"); return; }
    setServingQtyStr(Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100));
  }, [selectedFood && selectedFood.id]);
  function startEditGoal() { setGoalInput(String(goals.calories)); setEditingGoal(true); }
  function commitGoal() {
    const n = parseInt(goalInput);
    if (n > 0 && n < 20000) saveGoals({ ...goals, calories: n });
    setEditingGoal(false);
  }
  function startEditMacro(key) { setMacroInput(String(goals[key])); setEditingMacro(key); }
  function commitMacro() {
    const n = parseInt(macroInput);
    if (n > 0 && n < 2000 && editingMacro) saveGoals({ ...goals, [editingMacro]: n });
    setEditingMacro(null);
  }

  const MACRO_PRESETS = [
    { name:"Balanced",     p:0.30, c:0.40, f:0.30 },
    { name:"High-Protein", p:0.40, c:0.30, f:0.30 },
    { name:"Low-Carb",     p:0.35, c:0.20, f:0.45 },
    { name:"Keto",         p:0.25, c:0.05, f:0.70 },
  ];
  // Goal modes drive the calorie target (relative to TDEE) and a sensible
  // default macro split. Tapping a chip prefills both — user can still
  // override the macro split or calorie target after.
  const GOAL_MODES = [
    { id:"cut",      icon:"🔻", label:"Cut",      multiplier:0.80, split:{p:0.40, c:0.30, f:0.30} },
    { id:"maintain", icon:"⚖", label:"Maintain", multiplier:1.00, split:{p:0.30, c:0.40, f:0.30} },
    { id:"bulk",     icon:"📈", label:"Bulk",     multiplier:1.10, split:{p:0.30, c:0.45, f:0.25} },
    { id:"recomp",   icon:"♺",  label:"Recomp",   multiplier:1.00, split:{p:0.40, c:0.35, f:0.25} },
  ];
  function applyPreset(split, draft, setDraft) {
    const cals = draft.calories || 2000;
    setDraft({ ...draft,
      protein: Math.round((cals * split.p) / 4),
      carbs:   Math.round((cals * split.c) / 4),
      fat:     Math.round((cals * split.f) / 9),
    });
  }
  // Active percentages from grams (4 kcal/g protein+carbs, 9 kcal/g fat).
  function macroPercentsFromDraft(d) {
    const pkc = (d.protein || 0) * 4;
    const ckc = (d.carbs   || 0) * 4;
    const fkc = (d.fat     || 0) * 9;
    const total = pkc + ckc + fkc;
    if (total <= 0) return { p:0, c:0, f:0, totalKcal:0 };
    return { p: pkc/total, c: ckc/total, f: fkc/total, totalKcal: total };
  }
  // Find the named preset whose ratios match the draft within 2pp, else null
  // (=> "Custom"). Used to mark the active preset chip.
  function matchedPresetName(d) {
    const pct = macroPercentsFromDraft(d);
    for (const sp of MACRO_PRESETS) {
      if (Math.abs(pct.p - sp.p) <= 0.02
       && Math.abs(pct.c - sp.c) <= 0.02
       && Math.abs(pct.f - sp.f) <= 0.02) return sp.name;
    }
    return null;
  }
  function calcBMR(p) {
    const age = parseInt(p.age), h = parseInt(p.height), w = parseInt(p.weight);
    if (!age || !h || !w) return null;
    return Math.round(p.sex === "F" ? 10*w + 6.25*h - 5*age - 161 : 10*w + 6.25*h - 5*age + 5);
  }
  function calcTDEE(p) {
    const age = parseInt(p.age), height = parseInt(p.height), weight = parseInt(p.weight);
    if (!age || !height || !weight) return null;
    const bmr = p.sex === "F"
      ? 10 * weight + 6.25 * height - 5 * age - 161
      : 10 * weight + 6.25 * height - 5 * age + 5;
    return Math.round(bmr * (parseFloat(p.activity) || 1.55) + (parseInt(p.goal) || 0));
  }

  async function generateAiCoachReport() {
    if (aiCoachLoading) return;
    setAiCoachLoading(true);
    setAiCoachError(null);
    try {
      const rangeN = reportDays;
      // v2: full coach-report context — sleep/HR/steps per day, workouts
      // in range, weights, plus a structured profile (bio replaces the
      // hardcoded "Muslim, creatine 5g/day, ..." string from v1).
      const allData = readAllData();
      const ctx = buildCoachReportContext(allData, rangeN);
      const sys = composeSystem([
        COACH_VOICE,
        formatProfileForPrompt(getUserProfile()),
        EVIDENCE_STANDARD,
        OUTPUT_JSON_STRICT,
        `Task: Return a JSON coaching report with exactly 6 sections in order: 1) Energy Balance, 2) Protein & Muscle, 3) Food Quality, 4) Weight Trend, 5) Fasting & Meal Timing, 6) This Week's Top 2 Priorities. headline must include a specific number. Each section body is MAX 2 sentences referencing actual numbers from the context. Each actions array has exactly 2 items, each a concrete tip with a number where possible.`,
      ]);
      const COACH_REPORT_SCHEMA = {
        type: "object",
        properties: {
          headline: { type: "string" },
          statusColor: { type: "string", enum: ["green", "amber", "red"] },
          sections: {
            type: "array",
            minItems: 6,
            maxItems: 6,
            items: {
              type: "object",
              properties: {
                title: { type: "string" },
                icon: { type: "string" },
                status: { type: "string", enum: ["excellent", "good", "warning", "critical"] },
                body: { type: "string" },
                actions: { type: "array", minItems: 2, maxItems: 2, items: { type: "string" } },
              },
              required: ["title", "icon", "status", "body", "actions"],
            },
          },
        },
        required: ["headline", "statusColor", "sections"],
      };
      const raw = await callGemini(JSON.stringify(ctx), {
        systemInstruction: sys,
        responseSchema: COACH_REPORT_SCHEMA,
        model: MODELS.coachReport,
        maxOutputTokens: 4096,
        feature: "coachReport",
      });
      let report;
      try { report = JSON.parse(raw); }
      catch { throw new Error("Coach report JSON parse failed"); }
      if (!report.headline || !Array.isArray(report.sections) || report.sections.length === 0) throw new Error("Invalid report");
      const cached = { report, generatedAt: Date.now(), rangeDays: rangeN };
      try { localStorage.setItem("aiCoachReport_v1", JSON.stringify(cached)); } catch {}
      setAiCoachCache(cached);
    } catch(err) {
      setAiCoachError(err.message || "Generation failed — try again");
    } finally {
      setAiCoachLoading(false);
    }
  }

  async function generatePatternInsights() {
    if (patternInsightsLoading) return;
    setPatternInsightsLoading(true);
    setPatternInsightsError(null);
    try {
      const rangeN = reportDays;
      // v2: per-day rows (not just aggregates), persona, sleep/steps/weight,
      // workout-day flags, weekend/weekday split. Lets the model spot
      // patterns the aggregates-only payload couldn't surface.
      const allData = readAllData();
      const ctx = buildPatternInsightsContext(allData, rangeN);
      if ((ctx.dailyLog || []).length < 2) {
        setPatternInsightsError(`Need at least 2 logged days — you have ${(ctx.dailyLog || []).length} in the last ${rangeN} days.`);
        return;
      }
      const sys = composeSystem([
        COACH_VOICE,
        formatProfileForPrompt(getUserProfile()),
        EVIDENCE_STANDARD,
        OUTPUT_JSON_STRICT,
        "Task: Generate exactly 3 insight cards spotting real patterns in the per-day data. Each insight: { icon (emoji), title (short), body (2 sentences with specific numbers), type ('positive'|'warning'|'neutral') }. Cite actual numbers from the dailyLog rows.",
      ]);
      const PATTERN_INSIGHTS_SCHEMA = {
        type: "object",
        properties: {
          insights: {
            type: "array",
            minItems: 3,
            maxItems: 3,
            items: {
              type: "object",
              properties: {
                icon: { type: "string" },
                title: { type: "string" },
                body: { type: "string" },
                type: { type: "string", enum: ["positive", "warning", "neutral"] },
              },
              required: ["icon", "title", "body", "type"],
            },
          },
        },
        required: ["insights"],
      };
      let raw;
      try {
        raw = await callGemini(JSON.stringify(ctx), {
          systemInstruction: sys,
          responseSchema: PATTERN_INSIGHTS_SCHEMA,
          model: MODELS.patternInsights,
          maxOutputTokens: 3000,
          feature: "patternInsights",
        });
      } catch (err) {
        if (/high demand|overload|unavailable|503|429|quota/i.test(err.message || '')) {
          throw new Error("Gemini is overloaded right now — try again in a moment.");
        }
        throw err;
      }
      if (!raw || raw.trim().length < 2) {
        throw new Error("Gemini returned an empty response — try again.");
      }
      let parsed;
      try { parsed = JSON.parse(raw); }
      catch (e) { throw new Error("Couldn't parse AI response — try again."); }
      if (!parsed?.insights?.length) throw new Error("AI returned no insights");
      const cached = { insights: parsed.insights.slice(0,4), generatedAt: Date.now(), rangeDays: rangeN };
      try { localStorage.setItem("hayahPatternInsights", JSON.stringify(cached)); } catch {}
      setPatternInsights(cached);
    } catch (err) {
      setPatternInsightsError(err.message || "Generation failed — try again");
    } finally {
      setPatternInsightsLoading(false);
    }
  }

  const fmtMins = m => {
    if (!m) return "—";
    const h = Math.floor(m / 60), min = m % 60;
    if (h > 0 && min > 0) return `${h}h ${min}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const MetricBar = ({ label, value, pct, color, align="left" }) => (
    <div style={{width:"100%",display:"flex",flexDirection:"column",gap:2,alignItems:align==="right"?"flex-end":"flex-start"}}>
      <div style={{fontSize:"14px",fontWeight:800,color:text,lineHeight:1}}>{value || "—"}</div>
      <div style={{fontSize:"9px",color:muted,fontWeight:600,letterSpacing:"0.3px"}}>{label}</div>
      {pct !== null && (
        <div style={{width:"100%",height:4,borderRadius:2,background:darkMode?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.08)"}}>
          <div style={{width:`${Math.min(100,Math.round((pct||0)*100))}%`,height:"100%",borderRadius:2,background:color,transition:"width 0.6s ease"}}/>
        </div>
      )}
    </div>
  );



  const navItems = [
    { v:"dashboard", icon:"🏠", label:"Today" },
    { v:"plan",      icon:"🍽️", label:"Plan" },
    { v:"log",       icon:"➕", label:"Log" },
    { v:"history",   icon:"📅", label:"History" },
    { v:"weekly",    icon:"📊", label:"Reports" },
    { v:"goals",     icon:"🎯", label:"Goals" },
  ];

  const subTabsRowRef = useRef(null);
  // Drive the row's horizontal scroll entirely from JS. iOS PWA's native
  // overflow:auto kept rubber-banding scrollLeft back toward 0 on release,
  // even with scroll-snap. Taking control via touch events guarantees the
  // position holds wherever the user releases.
  useEffect(() => {
    let el = subTabsRowRef.current;
    if (!el) {
      // Ref not populated yet on first effect run — schedule a retry on the
      // next paint frame so listeners always attach to the live DOM node.
      const raf = requestAnimationFrame(() => {
        el = subTabsRowRef.current;
        if (el) wireUp(el);
      });
      return () => cancelAnimationFrame(raf);
    }
    return wireUp(el);
    function wireUp(el) {
    let dragging = false, startX = 0, startSL = 0, lastT = 0, vx = 0, momentumRaf = 0;
    const cancelMomentum = () => { if (momentumRaf) { cancelAnimationFrame(momentumRaf); momentumRaf = 0; } };
    const clamp = (v) => Math.max(0, Math.min(el.scrollWidth - el.clientWidth, v));
    const onTouchStart = (e) => {
      cancelMomentum();
      const t = e.touches[0]; if (!t) return;
      startX = t.clientX; startSL = el.scrollLeft;
      lastT = Date.now(); vx = 0; dragging = true;
    };
    const onTouchMove = (e) => {
      if (!dragging) return;
      const t = e.touches[0]; if (!t) return;
      const dx = t.clientX - startX;
      // Allow vertical scroll if the user is mostly moving up/down and we
      // haven't yet started horizontal scroll.
      if (Math.abs(dx) <= 4 && el.scrollLeft === startSL) return;
      const target = clamp(startSL - dx);
      const now = Date.now();
      const dt = Math.max(1, now - lastT);
      vx = (target - el.scrollLeft) / dt;
      el.scrollLeft = target;
      lastT = now;
      // Block iOS from running its own scroll on top of ours.
      if (e.cancelable) e.preventDefault();
    };
    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      // Momentum: keep gliding for a short tail after release.
      if (Math.abs(vx) > 0.05) {
        let v = vx * 16; // px per frame
        const friction = 0.93;
        const tick = () => {
          v *= friction;
          el.scrollLeft = clamp(el.scrollLeft + v);
          if (Math.abs(v) > 0.4) momentumRaf = requestAnimationFrame(tick);
          else momentumRaf = 0;
        };
        momentumRaf = requestAnimationFrame(tick);
      }
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    el.addEventListener('touchcancel',onTouchEnd,   { passive: true });
    return () => {
      cancelMomentum();
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
      el.removeEventListener('touchcancel',onTouchEnd);
    };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const SubTabsRow = () => (
    <div
      ref={subTabsRowRef}
      className="ghidha-no-sb"
      data-haic-collapsible="calories-subtabs"
      style={{
        display:"flex",
        alignItems:"stretch",
        gap:0,
        padding:0,
      }}
    >
      {navItems.map(ni => {
        const active = view === ni.v;
        return (
          <button key={ni.v} onClick={()=>{setPrevTabRoot(null);setView(ni.v);}} style={{
            flex:1, minWidth:0,
            padding:"11px 6px 10px", border:"none", background:"transparent", cursor:"pointer",
            color: active ? gold : muted,
            fontSize:12, fontWeight:500, letterSpacing:"-0.01em",
            fontFamily:"inherit", whiteSpace:"nowrap", textAlign:"center",
            borderBottom:`1.5px solid ${active ? gold : "transparent"}`,
            marginBottom:-0.5,
          }}>{ni.label}</button>
        );
      })}
    </div>
  );

  const Header = () => {
    const stickyWrap = {
      flexShrink:0, position:"sticky", top:0, zIndex:30,
      background: darkMode ? "rgba(7,9,12,0.94)" : "rgba(245,241,234,0.94)",
      backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
      borderBottom:`2px solid ${darkMode?"rgba(255,179,0,0.18)":"rgba(184,112,8,0.16)"}`,
    };
    const inFlow = flowCtx.active;
    const slotMeta = inFlow ? MEAL_SLOTS.find(m => m.key === flowCtx.slot) : null;
    const slotLabel = slotMeta ? (isFastingToday ? slotMeta.labelFasting : slotMeta.label) : "";
    const flowTitle = inFlow
      ? `Add to ${slotLabel || "meal"}${flowCtx.date && flowCtx.date !== today ? ` · ${flowCtx.date}` : ""}`
      : null;
    if (hostedInHayah) {
      // Inside Hayah: switcher sits above; render only the sub-tabs row
      // (or a flow-title bar when a flow is active so the user sees what
      // they're adding to and the host's chevron pops the flow).
      return (
        <div ref={headerRef} style={stickyWrap}>
          {inFlow ? (
            <div className="ghidha-no-sb" style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px"}}>
              <button onClick={onHeaderBack} style={{background:"transparent",border:"none",color:text,cursor:"pointer",padding:"4px 6px",display:"flex",flexShrink:0}}>
                <svg width="11" height="18" viewBox="0 0 11 18" fill="none"><path d="M9.5 1L1.5 9L9.5 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <div style={{fontSize:"15px",fontWeight:800,color:text,letterSpacing:"-0.2px",flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{flowTitle}</div>
            </div>
          ) : SubTabsRow()}
        </div>
      );
    }
    // Standalone: full title row + sub-tabs row (sub-tabs hidden during flow)
    return (
      <div ref={headerRef} style={stickyWrap}>
        <div className="ghidha-no-sb" style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px 6px"}}>
          <button onClick={onHeaderBack} style={{background:"transparent",border:"none",color:text,cursor:"pointer",padding:"4px 6px",display:"flex",flexShrink:0}}>
            <svg width="11" height="18" viewBox="0 0 11 18" fill="none"><path d="M9.5 1L1.5 9L9.5 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          {onLogoClick && !inFlow && (
            <div onClick={(e) => onLogoClick(e.currentTarget)} style={{width:"30px",height:"30px",borderRadius:"9px",flexShrink:0,background:darkMode?"rgba(4,18,16,0.5)":"rgba(224,247,243,0.5)",backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",border:darkMode?"1px solid rgba(0,201,167,0.22)":"1px solid rgba(0,158,135,0.18)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
              <svg viewBox="0 0 44 44" width="18" height="18" fill="none"><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9" transform="rotate(60 22 22)"/><ellipse cx="22" cy="22" rx="18.5" ry="6.5" stroke="#00c9a7" strokeWidth="1.8" opacity="0.9" transform="rotate(120 22 22)"/></svg>
            </div>
          )}
          <div style={{fontSize: inFlow ? "16px" : "22px",fontWeight:900,color:text,letterSpacing:"-0.3px",flex:1,minWidth:0,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{inFlow ? flowTitle : "Calories"}</div>
        </div>
        {!inFlow && SubTabsRow()}
      </div>
    );
  };

  // ── Today: just the Vital card, full-bleed, non-scrollable ──
  const Dashboard = () => {
    const headerInLandscape = isLandscape && hostedInHayah; // header hidden, floating pill instead
    const effectiveHeader = headerInLandscape ? 0 : headerHeight;
    const reservedBelow = headerInLandscape
      ? 84  // just app tab bar; floating switcher overlays content
      : (hostedInHayah ? 84 + 56 : 84); // app tab bar (~84) + Hayah switcher (~56) when hosted
    const cardHeight = `calc(100dvh - ${effectiveHeader}px - ${reservedBelow}px - var(--nav-inset, 0px))`;
    // Same merged Quick Add list the LogView uses, exposed through VitalsCtx so the
    // calorie-breakdown overlay can let users one-tap-log without leaving the modal.
    // Quick Add (dashboard breakdown overlay) — saved favourites only,
    // de-duped by case-insensitive name. Frequent meals removed per user
    // request: Quick Add is a curated shortcut, not auto-suggested.
    const dashTopFavs = [...favorites].sort((a,b) => (b.usageCount||0)-(a.usageCount||0));
    const dashSeenNames = new Set();
    const quickAddItems = dashTopFavs
      .filter(f => {
        const k = (f.name || "").toLowerCase().trim();
        if (!k || dashSeenNames.has(k)) return false;
        dashSeenNames.add(k);
        return true;
      })
      .slice(0, 10)
      .map(f => ({
        id: f.id,
        name: f.name,
        tag: f.type === "preset" ? "PRESET" : "SAVED",
        kcal: f.type === "food" && f.food
          ? Math.round(f.food.calories * (f.defaultServingG / (f.food.servingG || 100)))
          : null,
        onTap: () => quickAddFavorite(f, autoMealSlot()),
      }));
    return (
      <div style={{
        display:"flex", flexDirection:"column",
        height: cardHeight,
        minHeight: 0,
        padding: headerInLandscape ? "calc(env(safe-area-inset-top) + 50px) 12px 6px" : "8px 8px 4px",
        boxSizing:"border-box",
      }}>
        <AICalorieTargetBanner
          colors={{ text, muted, border, s1, s2, gold, teal, accent, danger, darkMode }}
          onOpen={() => { setAiTargetForceExpand(true); setView('goals'); }}
        />
        <VitalsCtx.Provider value={{ darkMode, accent, gold, teal, text, muted, border, ink2, ink4, lineSoft, s2, danger, s1, totalCalories, goals, trainingActive, totalProtein, totalCarbs, totalFat, totalFiber, totalSugar, totalSodium, caloriesBurned, totalBurned, todayData, calorieLog, prayerTimes, isSunnahFastDay, editingGoal, goalInput, setGoalInput, startEditGoal, commitGoal, editingMacro, macroInput, setMacroInput, startEditMacro, commitMacro, compactVitals, openShareCard, healthAvailable, healthPulling, lastHealthSyncAt, syncTick, triggerHealthSync: async()=>{ if(healthPulling) return; setHealthPulling(true); await readAppleHealth(false); setHealthPulling(false); }, removeEntry, removeEntryWithUndo, relogEntry, addEntryToQuickAdd, openEditEntry, commitEditEntry, startLogMealNow: ()=>{ pendingBreakdownReopenRef.current = true; startFlow({ slot: autoMealSlot(), date: today }); }, startLogForSlot, breakdownReopenSignal, pendingBreakdownReopenRef, quickAddItems, intel, initialMetric, onInitialMetricConsumed, onInitialMetricClosed, onOpenProteinFocus: () => setProteinFocusOpen(true) }}>
          <VitalsCard fullBleed landscape={headerInLandscape} />
        </VitalsCtx.Provider>
      </div>
    );
  };

  // ── Log: where logging actions live (Quick Add + Meals + Water + Weight + Apple Health + Hadith) ──
  const LogView = () => {
    const topFavs = [...favorites].sort((a,b) => (b.usageCount||0)-(a.usageCount||0)).slice(0, 6);
    const yesterday = localDateStr(-1);
    const yesterdayMeals = calorieLog[yesterday]?.meals || {};
    const hasYesterday = Object.values(yesterdayMeals).some(m => m?.length > 0);
    // Pre-compute yesterday's kcal + item count so the "Copy yesterday"
    // affordance can preview what the user is about to bring in.
    const yesterdayItems = hasYesterday ? Object.values(yesterdayMeals).flat() : [];
    const yesterdayKcal = yesterdayItems.reduce((s, e) => s + (e.calories || 0), 0);
    const yesterdayItemCount = yesterdayItems.length;
    const totalMealItems = MEAL_SLOTS.reduce((s, slot) => s + ((todayData.meals?.[slot.key]) || []).length, 0);
    const totalMealKcal = MEAL_SLOTS.reduce((s, slot) => s + ((todayData.meals?.[slot.key]) || []).reduce((ss, e) => ss + (e.calories || 0), 0), 0);
    // First-run detection — has the user ever logged a meal? Drives the
    // welcoming hero card the first time they land here.
    const hasAnyLogHistory = Object.values(calorieLog || {}).some(day =>
      day?.meals && Object.values(day.meals).some(m => m?.length > 0)
    );
    const showOnboardingHero = !hasAnyLogHistory && totalMealItems === 0;

    // Quick Add = ONLY items the user explicitly saved as favourites/presets.
    // Frequent meals were dropped per user request — Quick Add is a curated
    // shortcut, not an auto-suggested list. De-duped by case-insensitive
    // name so two saves with the same label don't both render.
    const seenNames = new Set();
    const mergedQuickAdd = topFavs
      .filter(f => {
        const k = (f.name || "").toLowerCase().trim();
        if (!k || seenNames.has(k)) return false;
        seenNames.add(k);
        return true;
      })
      .map(f => ({
        kind: "fav",
        id: f.id,
        name: f.name,
        tag: f.type === "preset" ? "PRESET" : "SAVED",
        kcal: f.type === "food" && f.food ? Math.round(f.food.calories * (f.defaultServingG / (f.food.servingG || 100))) : null,
        onTap: () => quickAddFavorite(f, autoMealSlot()),
      }))
      .slice(0, 10);

    return (
      <div style={{padding:"0 0 100px"}}>
        {/* Streak pill removed — the same info lives in the Junk tile
            and the Vitals/Today screen; a separate pill above the
            tile was wasted height. The StreakInfoSheet is still
            reachable from the 🔥 chip inside the Junk tile. */}
        {/* Junk tracker tile — harm-reduction view. Today's junk %, clean
            streak, earned-cheat chip. Tap → Reports JUNK pill. */}
        <JunkTodayTile
          colors={{ text, muted, border, s1, s2, gold, teal, accent, danger, darkMode }}
          calorieLog={calorieLog}
          today={today}
          goalKcal={goals.calories}
          onOpenReports={() => { setView('weekly'); setReportTab('junk'); try { localStorage.setItem('ghidhaLastView', 'weekly'); } catch {} }}
          onOpenVitals={onSwitchToHayahToday
            ? () => { try { hapticTap(); } catch {} onSwitchToHayahToday(); }
            : () => { setView('dashboard'); try { localStorage.setItem("ghidhaLastView", "dashboard"); } catch {} }}
          onClaimedCheat={(kcal) => {
            try { showToast && showToast(kcal > 0 ? `✓ Cheat meal claimed — ~${kcal} kcal of junk keeps the day under 20%` : `✓ Cheat meal claimed`); } catch {}
          }}
        />
        {(() => {
          void supplementBannerTick;
          if (!isPastReminderTime()) return null;
          if (isDismissedToday(today)) return null;
          const missing = getMissingToday(calorieLog, today);
          if (!missing.length) return null;
          return (
            <SupplementReminderBanner
              missing={missing}
              time={getReminderTime()}
              colors={{ text, muted, border, s1, darkMode }}
              onLog={(d) => {
                try { hapticConfirm(); } catch {}
                const base = { id: `qa_${d.id}`, ...d.base };
                addFoodEntry(base, d.servingG, d.servingLabel, "supplements", today);
                try { showToast && showToast(d.toast); } catch {}
              }}
              onDismiss={() => {
                try { hapticTap(); } catch {}
                dismissToday(today);
                setSupplementBannerTick(t => t + 1);
              }}
            />
          );
        })()}
        {(() => {
          // Weekly checks banner — GLP-1 dose + weigh-in. Unlike the
          // supplement nudge these aren't tied to a time of day (the
          // dose-day question is binary, not "have you done it by now")
          // so we surface them whenever the app's open and something's
          // due. Per-day dismissal still applies.
          void weeklyChecksTick;
          if (isWeeklyChecksDismissedToday(today)) return null;
          const checks = getDueWeeklyChecks(calorieLog, weightLog, today);
          if (!checks.length) return null;
          return (
            <WeeklyChecksBanner
              checks={checks}
              colors={{ text, muted, border, s1, darkMode }}
              onAction={(d) => {
                if (d.action === 'logEntry') {
                  try { hapticConfirm(); } catch {}
                  const base = { id: `qa_${d.id}`, ...d.base };
                  addFoodEntry(base, d.servingG || 0, d.servingLabel || "1 dose", "supplements", today);
                  try { showToast && showToast(typeof d.toast === 'function' ? d.toast() : d.toast); } catch {}
                  try { window.dispatchEvent(new CustomEvent("weeklyChecks-updated")); } catch {}
                  setWeeklyChecksTick(t => t + 1);
                } else if (d.action === 'weighIn') {
                  // Native prompt is the lowest-friction path on iOS and
                  // matches the existing weight-input UX in Goals.
                  const raw = window.prompt("Weigh-in — enter today's weight (kg)");
                  const kg = parseFloat(String(raw || "").replace(',', '.'));
                  if (!Number.isFinite(kg) || kg <= 0) return;
                  const updated = [...weightLog.filter(w => w.date !== today), { date: today, kg }]
                    .sort((a, b) => a.date.localeCompare(b.date));
                  saveWeights(updated);
                  try { hapticConfirm(); } catch {}
                  try { showToast && showToast(typeof d.toast === 'function' ? d.toast(kg) : `⚖️ Weight logged: ${kg} kg`); } catch {}
                  try { window.dispatchEvent(new CustomEvent("weeklyChecks-updated")); } catch {}
                  setWeeklyChecksTick(t => t + 1);
                }
              }}
              onDismiss={() => {
                try { hapticTap(); } catch {}
                dismissWeeklyChecksToday(today);
                setWeeklyChecksTick(t => t + 1);
              }}
            />
          );
        })()}
        {/* ── New Ledger view (Sirat Log v3) ── */}
        <GhidhaLogView
          totalCalories={totalCalories}
          totalProtein={totalProtein}
          totalCarbs={totalCarbs}
          totalFat={totalFat}
          goals={goals}
          trainingActive={trainingActive}
          todayData={todayData}
          startLogForSlot={startLogForSlot}
          openEditEntry={openEditEntry}
          removeEntryWithUndo={removeEntryWithUndo}
          onOpenProteinFocus={() => setProteinFocusOpen(true)}
          onOpenQuickAdd={() => { setQuickAddMode(false); setView("search"); setSearchTab("search"); }}
          onOpenCoach={onOpenCoach}
          kcalAvgs={(() => {
            // Rolling kcal averages over COMPLETED days only — same windows as
            // the junk strip. index 0 = today is EXCLUDED (a half-eaten today
            // would drag the average down all day; the ring shows today live).
            // "7D" = the 7 most recent completed days; empty days are null/skipped.
            const fmt = (d) => {
              const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
              return `${y}-${m}-${dd}`;
            };
            const kcals = []; // index 0 = today (excluded from windows below), 1..29 = prior days
            for (let i = 0; i < 30; i++) {
              const d = new Date(); d.setDate(d.getDate() - i);
              let kcal;
              if (i === 0) kcal = totalCalories || 0;
              else {
                const day = calorieLog[fmt(d)];
                kcal = day ? Object.values(day.meals || {}).flat().reduce((s, e) => s + (+e?.calories || 0), 0) : 0;
              }
              kcals.push(kcal > 0 ? Math.round(kcal) : null);
            }
            const windowAvg = (n) => {
              const slice = kcals.slice(1, n + 1).filter(v => v != null);
              if (slice.length === 0) return null;
              return Math.round(slice.reduce((s, v) => s + v, 0) / slice.length);
            };
            return {
              a7:  windowAvg(7),
              a14: windowAvg(14),
              a30: windowAvg(30),
              goal: goals.calories || null,
            };
          })()}
        />

        {/* ── Merged Quick Add strip ── */}
        <div style={{padding:"14px 16px 4px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:11,color:muted,fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase"}}>⚡ Quick Add</div>
            {mergedQuickAdd.length > 0 && (
              <button onClick={()=>{setPrevTabRoot(view);setView("favorites");}} style={{background:"transparent",border:"none",color:gold,fontSize:"11px",fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:3}}>See all saved →</button>
            )}
          </div>
          <div className="ghidha-no-sb" style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:4,marginBottom:12}}>
              {/* ☕ Quick Cappuccino — always-present tile, distinct coffee colour.
                  Tap anywhere on the tile body to open the picker modal.
                  Tap the + chip to instantly re-log the last-used combo. */}
              <div
                className="ghidha-tile"
                onClick={() => { try { hapticTap(); } catch {} setCappuccinoFocusOpen(true); }}
                role="button"
                tabIndex={0}
                aria-label="Open cappuccino picker"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCappuccinoFocusOpen(true); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(201,160,122,0.10)' : 'rgba(201,160,122,0.08)',
                  border: `0.5px solid #c9a07a66`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#c9a07a",fontWeight:700,letterSpacing:"0.14em"}}>☕ CAPPUCCINO</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      try { hapticConfirm(); } catch {}
                      const logged = quickLogLastCappuccino({
                        milkProducts: CAPPUCCINO_MILK_PRODUCTS,
                        addFoodEntry,
                        today,
                      });
                      if (logged) {
                        try { showToast && showToast(`☕ Logged · ${logged.calories} kcal`); } catch {}
                      }
                    }}
                    aria-label="Quick-log last cappuccino combo"
                    title="Re-log last combo"
                    style={{
                      width:22, height:22, borderRadius:999, background:"#c9a07a",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      border:"none", padding:0, cursor:"pointer", touchAction:"manipulation",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#1a0f06" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Quick log</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>tap + to re-log</div>
              </div>
              {/* 💪 Quick Creatine — Foundation Creapure 5g, one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_creatine", brand: "Foundation", name: "Creapure Creatine Monohydrate", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 5, servingLabel: "1 scoop (5g)", halal: true };
                  addFoodEntry(base, 5, "1 scoop (5g)", "supplements", today);
                  try { showToast && showToast("💪 Creatine logged · 5g"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Creatine 5g"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(107,154,110,0.10)' : 'rgba(107,154,110,0.08)',
                  border: `0.5px solid #6b9a6e66`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#6b9a6e",fontWeight:700,letterSpacing:"0.14em"}}>💪 CREATINE</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#6b9a6e",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#0c1a0e" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Creapure 5g</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>Foundation</div>
              </div>
              {/* 🐟 Quick Omega-3 — Biogen Supreme, 2 softgels, one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_omega3", brand: "Biogen", name: "Biogen Supreme Omega-3", category: "Supplements", calories: 18, protein: 0, carbs: 0, fat: 2, fiber: 0, sugar: 0, sodium: 0, servingG: 2, servingLabel: "2 softgels", halal: true };
                  addFoodEntry(base, 2, "2 softgels", "supplements", today);
                  try { showToast && showToast("🐟 Omega-3 logged · 18 kcal"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Biogen Supreme Omega-3"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(74,144,217,0.10)' : 'rgba(74,144,217,0.08)',
                  border: `0.5px solid #4a90d966`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#4a90d9",fontWeight:700,letterSpacing:"0.14em"}}>🐟 OMEGA-3</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#4a90d9",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#06121e" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Supreme Omega-3</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>2 softgels · 18 kcal</div>
              </div>
              {/* 🦴 Quick Collagen — Biogen, 3 capsules (recommended), one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_collagen", brand: "Biogen", name: "Biogen Collagen Capsules", category: "Supplements", calories: 6, protein: 1.5, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 1.5, servingLabel: "3 capsules", halal: true };
                  addFoodEntry(base, 1.5, "3 capsules", "supplements", today);
                  try { showToast && showToast("🦴 Collagen logged · 3 caps"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Biogen Collagen 3 capsules"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(211,107,138,0.10)' : 'rgba(211,107,138,0.08)',
                  border: `0.5px solid #d36b8a66`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#d36b8a",fontWeight:700,letterSpacing:"0.14em"}}>🦴 COLLAGEN</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#d36b8a",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#1f0e13" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Collagen</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>3 caps · 6 kcal</div>
              </div>
              {/* ✨ Quick Magnesium — Biogen Glycinate, 1 tablet (recommended), one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_magnesium", brand: "Biogen", name: "Biogen Magnesium Glycinate", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 1, servingLabel: "1 tablet", halal: true };
                  addFoodEntry(base, 1, "1 tablet", "supplements", today);
                  try { showToast && showToast("✨ Magnesium logged · 1 tab"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Biogen Magnesium Glycinate 1 tablet"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(95,184,168,0.10)' : 'rgba(95,184,168,0.08)',
                  border: `0.5px solid #5fb8a866`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#5fb8a8",fontWeight:700,letterSpacing:"0.14em"}}>✨ MAGNESIUM</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#5fb8a8",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#06181a" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Magnesium</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>1 tablet · 0 kcal</div>
              </div>
              {/* 🌾 Quick Psyllium Husk — Lifestyle Food, 2 heaped tsp (~12g), one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_psyllium", brand: "Lifestyle Food", name: "Psyllium Husk", category: "Supplements", calories: 40, protein: 0, carbs: 10, fat: 0, fiber: 10, sugar: 0, sodium: 0, servingG: 12, servingLabel: "2 heaped tsp (12g)", halal: true };
                  addFoodEntry(base, 12, "2 heaped tsp (12g)", "supplements", today);
                  try { showToast && showToast("🌾 Psyllium logged · 10g fibre"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Psyllium Husk 2 heaped tsp"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(124,154,74,0.10)' : 'rgba(124,154,74,0.08)',
                  border: `0.5px solid #7c9a4a66`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#7c9a4a",fontWeight:700,letterSpacing:"0.14em"}}>🌾 PSYLLIUM</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#7c9a4a",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#0e160a" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Psyllium Husk</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>2 hp tsp · 10g fibre</div>
              </div>
              {/* ⚡ Quick Electrolytes — Revive sachet (Raspberry/Raw), one-tap log. */}
              <div
                className="ghidha-tile"
                onClick={() => {
                  try { hapticConfirm(); } catch {}
                  const base = { id: "qa_revive", brand: "Revive", name: "Revive Raspberry", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 1000, servingG: 6, servingLabel: "1 sachet", halal: true };
                  addFoodEntry(base, 6, "1 sachet", "supplements", today);
                  try { showToast && showToast("⚡ Revive logged"); } catch {}
                }}
                role="button"
                tabIndex={0}
                aria-label="Quick-log Revive Raspberry sachet"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
                style={{
                  flexShrink:0, minWidth:148, padding:12,
                  background: darkMode ? 'rgba(224,86,122,0.10)' : 'rgba(224,86,122,0.08)',
                  border: `0.5px solid #e0567a66`, borderRadius:12,
                  cursor:"pointer", touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
                  position:"relative",
                }}
              >
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                  <span style={{fontSize:9,color:"#e0567a",fontWeight:700,letterSpacing:"0.14em"}}>⚡ REVIVE</span>
                  <div style={{width:22,height:22,borderRadius:999,background:"#e0567a",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2v8M2 6h8" stroke="#1f060d" strokeWidth="2" strokeLinecap="round"/></svg>
                  </div>
                </div>
                <div style={{fontSize:13,color:text,fontWeight:600,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Revive Raspberry</div>
                <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>1 sachet</div>
              </div>
              {mergedQuickAdd.map(item => (
                <div key={item.id} className="ghidha-tile" onClick={item.onTap} role="button" tabIndex={0} aria-label={`Log ${item.name}${item.kcal != null ? `, ${item.kcal} kcal` : ''}`} onKeyDown={(e)=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); item.onTap && item.onTap(); }}} style={{flexShrink:0,minWidth:148,padding:12,background:s1,border:`0.5px solid ${border}`,borderRadius:12,cursor:"pointer",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,minHeight:14}}>
                    {item.tag ? (
                      <span style={{fontSize:9,color:gold,fontWeight:700,letterSpacing:"0.14em"}}>{item.tag}</span>
                    ) : <span/>}
                    <div style={{width:22,height:22,borderRadius:999,background:gold,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2v8M2 6h8" stroke="#000" strokeWidth="2" strokeLinecap="round"/></svg>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:text,fontWeight:500,lineHeight:1.3,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                  {item.kcal != null && <div style={{fontSize:11,color:muted,fontFamily:"monospace"}}>{item.kcal} <span style={{color:muted,fontSize:9}}>kcal</span></div>}
                </div>
              ))}
          </div>
        </div>

        {/* GLP-1 / Medications card lives at the bottom of the Log view —
            see below, after Apple Health. */}

        {/* Sunnah fast-day banner removed per user request. */}

        {/* ── Meal timeline ── */}
        {/* First-run onboarding hero — only shown when the user has never
            logged a meal and today is empty. A single welcoming card with a
            clear CTA. Hidden forever after the first log. */}
        {showOnboardingHero && (
          <div style={{margin:"0 16px 12px",padding:"18px 16px",borderRadius:16,background:`linear-gradient(135deg, ${gold}1f, ${teal}1a)`,border:`1px solid ${gold}55`,position:"relative",overflow:"hidden"}}>
            <div aria-hidden style={{position:"absolute",inset:0,background:darkMode?"radial-gradient(ellipse at top right, rgba(255,179,0,0.15), transparent 60%)":"radial-gradient(ellipse at top right, rgba(184,112,8,0.12), transparent 60%)",pointerEvents:"none"}}/>
            <div style={{position:"relative",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,color:gold,fontFamily:"monospace",letterSpacing:"0.2em",textTransform:"uppercase",fontWeight:800}}>Welcome</div>
              <div style={{fontSize:18,color:text,fontWeight:800,letterSpacing:"-0.4px",lineHeight:1.25}}>Log your first meal</div>
              <div style={{fontSize:12,color:muted,lineHeight:1.55}}>Search a food, describe what you ate to AI, or scan a barcode. Your daily totals build up here as you go.</div>
              <button onClick={()=>{ try { hapticConfirm(); } catch {} pendingBreakdownReopenRef.current = false; startFlow({ slot: autoMealSlot(), date: today }); }} style={{marginTop:8,alignSelf:"flex-start",padding:"10px 16px",borderRadius:12,border:"none",background:gold,color:"#000",fontSize:13,fontWeight:800,fontFamily:"inherit",cursor:"pointer",letterSpacing:"-0.01em",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>+ Log a meal</button>
            </div>
          </div>
        )}

        {/* Copy yesterday — replaces the tiny inline "Paste yesterday's"
            button with a full-width card previewing what'll be copied. Only
            shown when yesterday has data; gone when today already has 5+
            items (the user has a fresh diary going, no need for the nudge). */}
        {hasYesterday && totalMealItems < 5 && (
          <button onClick={()=>{ try { hapticHayahSelection(); } catch {} copyYesterdayDiary(); }} style={{
            display:"flex",alignItems:"center",gap:12,
            width:"calc(100% - 32px)",margin:"0 16px 12px",
            padding:"12px 14px",borderRadius:14,
            background:darkMode?"rgba(95,209,185,0.08)":"rgba(0,158,135,0.07)",
            border:`1px solid ${teal}55`,
            color:text,cursor:"pointer",fontFamily:"inherit",textAlign:"left",
            touchAction:"manipulation",WebkitTapHighlightColor:"transparent",
          }}>
            <div style={{width:34,height:34,borderRadius:10,background:`${teal}22`,border:`1px solid ${teal}55`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M4 10a6 6 0 1 1 6 6" stroke={teal} strokeWidth="1.6" strokeLinecap="round"/>
                <path d="M3.5 6v4h4" stroke={teal} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:text,letterSpacing:"-0.01em"}}>Copy yesterday's diary</div>
              <div style={{fontSize:11,color:muted,marginTop:2,fontFamily:"monospace",letterSpacing:"0.04em"}}>{yesterdayItemCount} ITEM{yesterdayItemCount===1?"":"S"} · {Math.round(yesterdayKcal).toLocaleString()} KCAL</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{flexShrink:0,color:teal}}>
              <path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        <div style={{padding:"4px 16px 0"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:11,color:muted,fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase"}}>Categories</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{fontSize:10,color:muted,fontFamily:"monospace",letterSpacing:"0.1em"}}>
                {Math.round(totalMealKcal).toLocaleString()} KCAL · {totalMealItems} ITEMS
              </div>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
            {(() => {
              const slotKcalMap = {};
              MEAL_SLOTS.forEach(s => {
                slotKcalMap[s.key] = (todayData.meals?.[s.key] || [])
                  .reduce((sum, e) => sum + (e.calories || 0), 0);
              });
              const grandTotal = Object.values(slotKcalMap).reduce((s, v) => s + v, 0);
              return [...MEAL_SLOTS]
                .sort((a, b) => slotKcalMap[b.key] - slotKcalMap[a.key])
                .map(slot => {
              const isSupplements = slot.key === 'supplements';
              const rawItems = todayData.meals?.[slot.key] || [];
              const items = slot.key === 'meal'
                ? [...rawItems].sort((a,b)=>(a.loggedAt||'')>(b.loggedAt||'')?1:-1)
                : rawItems;
              const slotKcal = slotKcalMap[slot.key];
              const slotPct = grandTotal > 0 ? Math.round((slotKcal / grandTotal) * 100) : 0;
              const expanded = expandedSlot === slot.key;
              const label = isFastingToday ? slot.labelFasting : slot.label;
              const empty = items.length === 0;
              const fmtTime = iso => { try { const d=new Date(iso); return d.toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit',hour12:false}); } catch { return ''; } };
              const slotTimeDisplay = slot.key === 'meal' && !empty
                ? (() => {
                    const ts = items.filter(e=>e.loggedAt).map(e=>new Date(e.loggedAt).getTime()).filter(t=>!isNaN(t));
                    if (!ts.length) return '—';
                    const lo = fmtTime(Math.min(...ts)), hi = fmtTime(Math.max(...ts));
                    return lo === hi ? lo : `${lo} – ${hi}`;
                  })()
                : slot.typicalTime;
              const flashing = recentlyLoggedSlot === slot.key;
              return (
                <div key={slot.key} className={flashing ? "ghidha-slot-flash" : undefined} style={{borderRadius:14,border:`0.5px solid ${isSupplements?`${slot.color}44`:border}`,background:s1,overflow:"hidden"}}>
                  <div
                    className="ghidha-press"
                    onClick={()=>{
                      try { hapticHayahSelection(); } catch {}
                      setExpandedSlot(expanded?null:slot.key);
                    }}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"13px 14px",cursor:"pointer",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                    <div style={{width:36,height:36,borderRadius:10,background:`${slot.color}18`,border:`0.5px solid ${slot.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                      {slot.glyph}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14.5,color:text,fontWeight:500,letterSpacing:"-0.005em",marginBottom:2}}>{label}</div>
                      <div style={{fontSize:10.5,color:muted,fontFamily:"monospace",letterSpacing:"0.06em"}}>{slotTimeDisplay} · {empty ? "Empty" : `${items.length} item${items.length!==1?"s":""}`}</div>
                    </div>
                    <div style={{textAlign:"right",marginRight:4}}>
                      {empty ? (
                        <div style={{fontSize:10.5,color:muted,fontFamily:"monospace",letterSpacing:"0.1em"}}>EMPTY</div>
                      ) : (
                        <>
                          <div style={{fontSize:16,color:text,fontWeight:500}}>{Math.round(slotKcal).toLocaleString()}</div>
                          <div style={{fontSize:9,color:muted,fontFamily:"monospace",letterSpacing:"0.1em"}}>{slotPct}% · KCAL</div>
                        </>
                      )}
                    </div>
                    <button
                      className="ghidha-press"
                      onClick={e=>{e.stopPropagation();try{hapticTap();}catch{}startFlow({slot:slot.key,date:today});}}
                      style={{width:30,height:30,borderRadius:999,border:`0.5px solid ${border}`,background:"transparent",color:text,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,touchAction:"manipulation"}}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                  <div className={`ghidha-slot-expand ${expanded ? "open" : ""}`}>
                    <div style={{padding: expanded ? "0 14px 10px" : "0 14px",borderTop: expanded ? `0.5px solid ${border}` : "none"}}>
                      {items.length === 0 ? (
                        <div style={{padding:"12px 0",textAlign:"center",fontSize:"12px",color:muted}}>No items logged. Tap + to add.</div>
                      ) : (
                        <>
                          {items.map(e => (
                            <div key={e.id}
                              className="ghidha-press"
                              onClick={()=>openEditEntry(slot.key, e, today)}
                              role="button"
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:`0.5px solid ${border}`,gap:8,cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:"13px",color:text,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.name}</div>
                                <div style={{fontSize:"10.5px",color:muted}}>{slot.key === 'meal' && e.loggedAt ? `${fmtTime(e.loggedAt)} · ` : ''}{e.servingLabel} · P{Math.round(e.protein||0)} C{Math.round(e.carbs||0)} F{Math.round(e.fat||0)}</div>
                              </div>
                              <div style={{fontSize:"13px",fontWeight:700,color:text}}>{Math.round(e.calories)}</div>
                              <button onClick={ev=>{ev.stopPropagation();try{hapticConfirm();}catch{}removeEntry(slot.key,e.id);}} style={{background:"transparent",border:"none",color:muted,cursor:"pointer",padding:4,fontSize:"16px"}}>×</button>
                            </div>
                          ))}
                          <button onClick={()=>{setPresetSlot(slot.key);setSavePresetMode(true);setPresetName(`My ${slot.label}`);}} style={{marginTop:8,padding:"6px 10px",background:"transparent",border:`1px dashed ${border}`,borderRadius:10,color:gold,fontSize:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⭐ Save this {slot.label} as preset</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </div>

        {/* ── Weight (full-width) ── */}
        {/* Weight section moved to Goals → Body Composition (read-only,
            Apple Health is the source of truth). */}

        {/* ── Apple Health (only when available) ── */}
        {healthAvailable && (
          <div style={{padding:"0 16px"}}>
            <div style={{padding:"14px",background:s1,border:`0.5px solid ${border}`,borderRadius:12,marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:10,color:muted,fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase"}}>Apple Health</div>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{width:5,height:5,borderRadius:"50%",background:teal}}/>
                  <span style={{fontSize:8,color:teal,fontFamily:"monospace",letterSpacing:"0.12em"}}>LIVE</span>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <button disabled={healthPulling||healthPushing} onClick={async()=>{setHealthPulling(true);await readAppleHealth(false);setHealthPulling(false);}} style={{padding:"10px",background:healthPulling?gold:s2,border:`0.5px solid ${healthPulling?gold:border}`,borderRadius:8,color:healthPulling?"#000":text,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600,transition:"all 0.2s"}}>{healthPulling?"Pulling…":"↓ Pull"}</button>
                <button disabled={healthPulling||healthPushing} onClick={async()=>{setHealthPushing(true);await pushFoodToHealth();setHealthPushing(false);}} style={{padding:"10px",background:healthPushing?teal:"transparent",border:`0.5px solid ${healthPushing?teal:border}`,borderRadius:8,color:healthPushing?"#000":muted,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}>{healthPushing?"Pushing…":"↑ Push"}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Medications / GLP-1 (moved to bottom of Log per user request) ── */}
        <MedicationsCard theme={{ gold, teal, text, muted, border, s1, s2, darkMode }}
          showToast={showToast}/>

        {/* ── Hadith footer ── */}
        <div style={{margin:"4px 16px 12px",padding:"14px 4px",borderTop:`0.5px solid ${border}`}}>
          <div style={{fontSize:13,color:muted,fontStyle:"italic",lineHeight:1.5,marginBottom:4}}>
            "A third for food, a third for drink, a third for breath."
          </div>
          <div style={{fontSize:9.5,color:muted,fontFamily:"monospace",letterSpacing:"0.16em",opacity:0.7}}>TIRMIDHI · 2380</div>
        </div>
      </div>
    );
  };

  const Modals = () => (
    <>
      {favSlotPicker && (
        <div onClick={()=>setFavSlotPicker(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:50,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",borderTop:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:14,textAlign:"center"}}>Add to which meal?</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
              {MEAL_SLOTS.map(slot => (
                <button key={slot.key} onClick={()=>{const fav = favorites.find(f => f.id === favSlotPicker.favId); if (fav) quickAddFavorite(fav, slot.key);}} style={{padding:"14px 12px",background:s2,border:`1px solid ${border}`,borderRadius:12,color:text,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:"18px"}}>{slot.glyph}</span>
                  <span>{isFastingToday ? slot.labelFasting : slot.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {variantPicker && (
        <div onClick={()=>{ const vp = variantPicker; setVariantPicker(null); if (typeof vp?.afterAllDone === "function") vp.afterAllDone(); }} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:50,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",borderTop:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:4,textAlign:"center"}}>{variantPicker.foodBase.name}</div>
            <div style={{fontSize:"12px",color:muted,marginBottom:14,textAlign:"center"}}>Which milk?</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {variantPicker.foodBase.milkOptions.map(opt => (
                <button key={opt.key} onClick={()=>{
                  const vp = variantPicker;
                  const resolved = {
                    ...vp.foodBase,
                    name: `${vp.foodBase.name} (${opt.label})`,
                    calories: opt.calories,
                    protein:  opt.protein,
                    carbs:    opt.carbs,
                    fat:      opt.fat,
                    fiber:    opt.fiber,
                    sugar:    opt.sugar,
                    sodium:   opt.sodium,
                    pickerKey: undefined,
                    milkOptions: undefined,
                  };
                  addFoodEntry(resolved, vp.servingG, vp.servingLabel, vp.mealSlot, vp.dateKey);
                  if (vp.queue && vp.queue.length) {
                    const next = vp.queue[0];
                    setVariantPicker({ ...next, queue: vp.queue.slice(1), afterAllDone: vp.afterAllDone });
                  } else {
                    setVariantPicker(null);
                    if (typeof vp.afterAllDone === "function") vp.afterAllDone();
                  }
                }} style={{padding:"14px 14px",background:s2,border:`1px solid ${border}`,borderRadius:12,color:text,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:12,justifyContent:"space-between"}}>
                  <span style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:"18px"}}>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </span>
                  <span style={{fontSize:"11px",color:muted,fontFamily:"monospace",fontVariantNumeric:"tabular-nums"}}>{opt.calories} kcal · P{opt.protein}g · F{opt.fat}g</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {savePresetMode && (
        <div onClick={()=>setSavePresetMode(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:340,background:s1,borderRadius:16,padding:18,border:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:10}}>⭐ Save as Preset</div>
            <input value={presetName} onChange={e=>setPresetName(e.target.value)} placeholder="e.g. Morning Stack" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none",marginBottom:12}}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setSavePresetMode(false)} style={{flex:1,padding:10,background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:muted,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={()=>{try{hapticConfirm();}catch{}saveSlotAsPreset(presetSlot, presetName);}} style={{flex:1,padding:10,background:gold,border:"none",borderRadius:10,color:"#000",fontSize:"13px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
            </div>
          </div>
        </div>
      )}
      {editingFav && editFavDraft && (
        <div onClick={()=>{setEditingFav(null);setEditFavDraft(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:60,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxHeight:"85vh",overflowY:"auto",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 28px",borderTop:`1px solid ${border}`}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div style={{fontSize:"15px",fontWeight:800,color:text}}>{editingFav.type === "preset" ? "Edit Preset" : "Edit Favorite"}</div>
              <button onClick={()=>{setEditingFav(null);setEditFavDraft(null);}} style={{background:"transparent",border:"none",color:muted,fontSize:"20px",cursor:"pointer",padding:"2px 6px",lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:"10.5px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.4px"}}>NAME (SHOWN IN QUICK ADD)</div>
            <input value={editFavDraft.name} onChange={e=>setEditFavDraft(d=>({...d,name:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none",marginBottom:14}} placeholder="e.g. Morning Smoothie"/>
            {editingFav.type === "food" && (
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1.5fr",gap:8,marginBottom:14}}>
                  <div>
                    <div style={{fontSize:"10.5px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.4px"}}>SERVING (g)</div>
                    <input type="number" inputMode="decimal" value={editFavDraft.defaultServingG} onChange={e=>setEditFavDraft(d=>({...d,defaultServingG:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:"10.5px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.4px"}}>SERVING LABEL</div>
                    <input value={editFavDraft.defaultServingLabel} onChange={e=>setEditFavDraft(d=>({...d,defaultServingLabel:e.target.value}))} placeholder="e.g. 1 plate" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none"}}/>
                  </div>
                </div>
                <div style={{fontSize:"10.5px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.4px"}}>PER SERVING</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:14}}>
                  {[{k:"calories",l:"kcal"},{k:"protein",l:"P (g)"},{k:"carbs",l:"C (g)"},{k:"fat",l:"F (g)"}].map(f=>(
                    <div key={f.k}>
                      <div style={{fontSize:"9.5px",color:muted,fontWeight:700,marginBottom:4,letterSpacing:"0.4px",textAlign:"center"}}>{f.l}</div>
                      <input type="number" inputMode="decimal" value={editFavDraft[f.k]} onChange={e=>setEditFavDraft(d=>({...d,[f.k]:e.target.value}))} style={{width:"100%",padding:"9px 8px",borderRadius:9,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontFamily:"inherit",boxSizing:"border-box",outline:"none",textAlign:"center"}}/>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setEditingFav(null);setEditFavDraft(null);}} style={{flex:1,padding:12,background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:muted,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={commitEditFavorite} style={{flex:1.4,padding:12,background:gold,border:"none",borderRadius:10,color:"#000",fontSize:"13px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
            </div>
          </div>
        </div>
      )}
      {quickAddMode && (
        <div onClick={()=>setQuickAddMode(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:50,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:340,background:s1,borderRadius:16,padding:18,border:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:4}}>⚡ Quick Add</div>
            <div style={{fontSize:"11px",color:muted,marginBottom:14}}>Log macros directly — no food search needed.</div>
            {[{k:"name",pl:"Name (optional)",type:"text",im:undefined},{k:"calories",pl:"Calories (kcal) *",type:"number",im:"numeric"},{k:"protein",pl:"Protein (g)",type:"number",im:"numeric"},{k:"carbs",pl:"Carbs (g)",type:"number",im:"numeric"},{k:"fat",pl:"Fat (g)",type:"number",im:"numeric"}].map(f => (
              <input key={f.k} type={f.type} inputMode={f.im} placeholder={f.pl} value={quickAddDraft[f.k]} onChange={e=>setQuickAddDraft(d=>({...d,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none",marginBottom:8}}/>
            ))}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:"10.5px",color:muted,fontWeight:700,marginBottom:6}}>ADD TO MEAL:</div>
              <select value={selectedMealSlot} onChange={e=>setSelectedMealSlot(e.target.value)} style={{width:"100%",padding:"9px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontWeight:700,fontFamily:"inherit",outline:"none"}}>
                {MEAL_SLOTS.map(s => <option key={s.key} value={s.key}>{s.glyph} {isFastingToday?s.labelFasting:s.label}</option>)}
              </select>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setQuickAddMode(false)} style={{flex:1,padding:10,background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:muted,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={commitQuickAdd} style={{flex:1,padding:10,background:teal,border:"none",borderRadius:10,color:"#000",fontSize:"13px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Add ✓</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Inline-expanding row used by every list tab (Recent / Search results /
  // SA / Supplements). The expanded panel exposes a +/- stepper, RDI %, and
  // scaled macros so users can adjust serving before ticking. The chevron
  // beside the stepper pushes to full Food Detail for finer control.
  // Highlight the user's search query inside a food name. Case-insensitive,
  // first-occurrence only (the result was returned because it matched, so the
  // first occurrence is the "why"). Returns a React fragment; safe with empty
  // query (returns plain text).
  const highlightMatch = (text, query) => {
    if (!text) return text;
    const q = (query || '').trim();
    if (!q) return text;
    const idx = String(text).toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: gold, fontWeight: 800 }}>{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const SelectableFoodRow = ({ food, sourceTag }) => {
    const key = _foodKey(food);
    const expanded = expandedFoodId === key;
    const selected = !!searchSelections[key];
    const decorated = sourceTag ? { ...food, source: sourceTag } : food;
    const inline = _inlineServingFor(decorated);
    const qty = inline.qty;
    const scaled = (n) => Math.round(((n || 0) * qty) * 10) / 10;
    const scaledKcal = Math.round((food.calories || 0) * qty);
    const goalCal = goals.calories || 2000;
    const rdi = Math.max(0, Math.round((scaledKcal / goalCal) * 100));
    const baseLabel = food.servingLabel || `${food.servingG || 100}g`;
    const fmtQty = (q) => Number.isInteger(q) ? String(q) : String(Math.round(q*100)/100);
    const stepLabel = qty === 1 ? baseLabel : `${fmtQty(qty)}× ${baseLabel}`;
    const openDetail = (e) => {
      if (e) e.stopPropagation();
      setSelectedFood(decorated);
      setSelectedServingG(inline.servingG);
      setSelectedServingLabel(inline.servingLabel);
      pushFlowView("food-detail");
    };
    const stepBtn = (label, onClick, ariaLabel) => (
      <button onClick={(e)=>{e.stopPropagation();onClick();}} aria-label={ariaLabel} style={{flexShrink:0,width:32,height:32,borderRadius:8,border:`1px solid ${border}`,background:s1,color:text,fontSize:18,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>{label}</button>
    );
    return (
      <div style={{borderBottom:`1px solid ${border}`,background: expanded ? s2 : "transparent"}}>
        <div style={{display:"flex",alignItems:"center",padding:"12px 16px",gap:10,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
          <div onClick={()=>setExpandedFoodId(expanded ? null : key)} style={{flex:1,minWidth:0,cursor:"pointer"}}>
            <div style={{fontSize:"13.5px",fontWeight:700,color:text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{highlightMatch(food.name, searchQuery)}</div>
            <div style={{fontSize:"11px",color:muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {food.brand || food.restaurant || stepLabel} · RDI {rdi}% · {scaledKcal} kcal
            </div>
          </div>
          <button
            onClick={(e)=>{ e.stopPropagation(); toggleFoodSelection(decorated); }}
            aria-label={selected ? `Unselect ${food.name}` : `Select ${food.name}`}
            style={{width:30,height:30,borderRadius:999,border:`2px solid ${selected?accent:border}`,background:selected?accent:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,padding:0}}>
            {selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
          </button>
        </div>
        {expanded && (
          <div style={{padding:"4px 16px 14px",display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {stepBtn("−", ()=>setInlineServingQty(decorated, Math.max(0.5, Math.round((qty - 0.5) * 100) / 100) || 0.5), "Decrease serving")}
              <div style={{flex:1,minWidth:0,padding:"6px 10px",borderRadius:8,background:s1,border:`1px solid ${border}`,textAlign:"center"}}>
                <div style={{fontSize:"15px",fontWeight:800,color:text,fontVariantNumeric:"tabular-nums"}}>{fmtQty(qty)}</div>
                <div style={{fontSize:"10px",color:muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>× {baseLabel}</div>
              </div>
              {stepBtn("+", ()=>setInlineServingQty(decorated, Math.round((qty + 0.5) * 100) / 100), "Increase serving")}
              <button onClick={openDetail} aria-label="Open full details" style={{flexShrink:0,width:32,height:32,borderRadius:8,border:`1px solid ${border}`,background:s1,color:gold,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
              </button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
              <div style={{padding:"6px 4px",borderRadius:8,background:s1,border:`1px solid ${border}`,textAlign:"center"}}>
                <div style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.4px"}}>KCAL</div>
                <div style={{fontSize:"13px",fontWeight:800,color:text,fontVariantNumeric:"tabular-nums"}}>{scaledKcal}</div>
              </div>
              <div style={{padding:"6px 4px",borderRadius:8,background:s1,border:`1px solid ${border}`,textAlign:"center"}}>
                <div style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.4px"}}>PROT</div>
                <div style={{fontSize:"13px",fontWeight:800,color:gold,fontVariantNumeric:"tabular-nums"}}>{scaled(food.protein)}</div>
              </div>
              <div style={{padding:"6px 4px",borderRadius:8,background:s1,border:`1px solid ${border}`,textAlign:"center"}}>
                <div style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.4px"}}>CARB</div>
                <div style={{fontSize:"13px",fontWeight:800,color:teal,fontVariantNumeric:"tabular-nums"}}>{scaled(food.carbs)}</div>
              </div>
              <div style={{padding:"6px 4px",borderRadius:8,background:s1,border:`1px solid ${border}`,textAlign:"center"}}>
                <div style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.4px"}}>FAT</div>
                <div style={{fontSize:"13px",fontWeight:800,color:accent,fontVariantNumeric:"tabular-nums"}}>{scaled(food.fat)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const SearchView = () => {
    const selectionCount = Object.keys(searchSelections).length;
    const slotMeta = MEAL_SLOTS.find(m=>m.key===selectedMealSlot);
    const slotTitle = slotMeta ? (isFastingToday ? slotMeta.labelFasting : slotMeta.label) : "Pick a meal";
    const dateLabel = (() => {
      const dt = flowCtx.date || today;
      try {
        const d = new Date(dt + "T00:00:00");
        return d.toLocaleDateString(undefined, { weekday:"long", month:"long", day:"numeric" });
      } catch { return dt; }
    })();
    const stickyBg = darkMode ? "rgba(7,9,12,0.96)" : "rgba(245,241,234,0.96)";
    return (
    <div style={{padding: kbOpen ? "0 0 12px" : "0 0 80px", transition:"padding-bottom 0.2s ease"}}>
      {/* Slot title + tabs + search input freeze together below the Ghidha
          header so the input stays visible while the user scrolls results. */}
      <div style={{position:"sticky",top: headerHeight, zIndex:25, background: stickyBg, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", paddingTop:4, borderBottom:`1px solid ${border}`}}>
        {/* Title + slot row — hidden while the keyboard is up to recover
            ~80px of vertical space for results. Comes back as soon as the
            user dismisses the keyboard. */}
        <div style={{padding: kbOpen && searchTab === "search" ? "0" : "10px 16px 8px", display:"flex", alignItems:"flex-start", gap:8, height: kbOpen && searchTab === "search" ? 0 : "auto", overflow: kbOpen && searchTab === "search" ? "hidden" : "visible", transition:"height 0.2s ease, padding 0.2s ease"}}>
          {(() => {
            // On the today flow the slot is auto-routed via autoMealSlot — show
            // a static "LOGGING TO · LUNCH" pill instead of the chevron picker
            // so the user never has to choose a meal. Past-day flows from the
            // History tab keep the dropdown so the slot can still be retargeted.
            const isToday = (flowCtx.date || today) === today;
            if (isToday) {
              return (
                <div style={{flex:1,minWidth:0,textAlign:"left"}}>
                  <span style={{fontSize:"22px",fontWeight:900,color:text,letterSpacing:"-0.3px"}}>Log food</span>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:2,flexWrap:"wrap"}}>
                    <span style={{fontSize:"9.5px",fontFamily:"monospace",letterSpacing:"0.14em",fontWeight:700,color:muted,padding:"2px 8px",borderRadius:999,border:`0.5px solid ${border}`,background:s1,whiteSpace:"nowrap"}}>
                      LOGGING TO · {slotTitle?.toUpperCase()}
                    </span>
                    <span style={{fontSize:"11.5px",color:muted,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flex:1,minWidth:0}}>{dateLabel}</span>
                  </div>
                </div>
              );
            }
            return (
              <button
                onClick={()=>setSearchSlotPickerOpen(true)}
                aria-label={`Change meal slot — currently ${slotTitle}`}
                style={{flex:1,minWidth:0,textAlign:"left",background:"transparent",border:"none",padding:0,cursor:"pointer",fontFamily:"inherit",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:"22px",fontWeight:900,color:text,letterSpacing:"-0.3px"}}>{slotTitle}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{color:muted,marginTop:2}}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                <div style={{fontSize:"11.5px",color:muted,fontWeight:600,marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{dateLabel}</div>
              </button>
            );
          })()}
          {selectionCount > 0 ? (
            <button onClick={saveAllSelections} style={{padding:"9px 16px",borderRadius:10,border:"none",background:accent,color:"#000",fontSize:"13px",fontWeight:900,fontFamily:"inherit",cursor:"pointer",letterSpacing:"0.3px",flexShrink:0}}>
              {selectionCount === 1 ? "Save · or pick more" : `Save (${selectionCount})`}
            </button>
          ) : (
            <button onClick={()=>pushFlowView("barcode")} aria-label="Scan barcode" style={{padding:"9px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"12px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",gap:4}}>📷 <span>Scan</span></button>
          )}
        </div>
        {/* Tab strip (Recent / Search / + AI / Quick Add) — also hidden when
            the keyboard is open on Search. The user is committed to Search;
            tab switching is rare while typing. Saves another ~50px. */}
        <div style={{display: kbOpen && searchTab === "search" ? "none" : "flex", padding:"4px 16px 8px", gap:6, overflowX:"auto", WebkitOverflowScrolling:"touch"}}>
          {[{v:"recent",l:"🕐 Recent"},{v:"search",l:"🔍 Search"}].map(t=>(
            <button key={t.v} onClick={()=>setSearchTab(t.v)} style={{padding:"7px 12px",borderRadius:14,border:"none",background:searchTab===t.v?(darkMode?"rgba(255,179,0,0.18)":"rgba(184,112,8,0.13)"):"transparent",color:searchTab===t.v?gold:muted,fontSize:"11.5px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>{t.l}</button>
          ))}
          <button onClick={()=>{setSearchTab("ai");setAiMealItems([]);setAiMealError(null);}} style={{padding:"7px 12px",borderRadius:14,border:"none",background:searchTab==="ai"?(darkMode?"rgba(95,209,185,0.18)":"rgba(0,158,135,0.12)"):"transparent",color:searchTab==="ai"?teal:muted,fontSize:"11.5px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✦ AI</button>
          <button onClick={()=>setQuickAddMode(true)} style={{marginLeft:"auto",padding:"7px 12px",borderRadius:14,border:`1px solid ${border}`,background:s2,color:teal,fontSize:"11.5px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>⚡ Quick Add</button>
        </div>
        {searchTab === "search" && (
          <div style={{padding:"6px 16px 10px"}}>
            {/* Search input + inline action icons. Flex layout so the right-
                side AI / library / camera icons stay reachable even when the
                keyboard is up and the rest of the chrome has collapsed. */}
            <div style={{display:"flex",alignItems:"center",gap:4,background:s2,borderRadius:12,border:`1px solid ${border}`,padding:"0 4px 0 12px"}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0}}>
                <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={searchInputRef}
                autoFocus
                value={searchQuery}
                onChange={e=>handleSearchInput(e.target.value)}
                onFocus={e=>e.target.select()}
                onKeyDown={e=>{
                  if (e.key === "Escape") { handleSearchInput(""); }
                  else if (e.key === "Enter") { pushRecentSearch(searchQuery); e.currentTarget.blur(); }
                }}
                placeholder="Search foods (chicken, banana, rice…)"
                enterKeyHint="search"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                style={{flex:1,minWidth:0,padding:"12px 6px",border:"none",background:"transparent",color:text,fontSize:"14px",fontFamily:"inherit",outline:"none",touchAction:"manipulation"}}
              />
              {searchQuery && (
                <button
                  onClick={()=>{ handleSearchInput(""); searchInputRef.current && searchInputRef.current.focus(); }}
                  aria-label="Clear search"
                  style={{flexShrink:0,width:26,height:26,borderRadius:999,border:"none",background:darkMode?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)",color:muted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
              {/* AI describe-it shortcut. Carries any typed query into aiMealText. */}
              <button
                onClick={()=>{
                  try { hapticHayahSelection(); } catch {}
                  // Read straight from the DOM input — React state lags by
                  // one render after a fast keystroke or programmatic
                  // dispatch, and we want the latest value the moment the
                  // user taps sparkle.
                  const liveValue = searchInputRef.current?.value || '';
                  const q = (liveValue || searchQuery).trim();
                  if (q) {
                    // Trigger inline AI search RIGHT NOW — skip the 5s
                    // debounce. User stays on Search tab; AI items render
                    // inline below the search bar.
                    if (aiSearchAbortRef.current) aiSearchAbortRef.current.cancelled = true;
                    setAiSearchResults([]);
                    setAiSearchError(null);
                    runInlineAiSearch(q);
                  } else {
                    // Empty query — open the full AI tab for describe / photo.
                    setSearchTab("ai");
                  }
                }}
                aria-label="Estimate with AI"
                style={{flexShrink:0,width:32,height:32,borderRadius:8,border:"none",background:"transparent",color:teal,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  {/* Sparkle cluster — Gemini / ChatGPT-style 4-pointed AI star
                      plus a smaller accent sparkle. Distinctly NOT a 5-point
                      favorite star. */}
                  <path d="M8 3.2C8.5 5.8 9.3 7.4 11.2 8.3C9.3 9.2 8.5 10.8 8 13.4C7.5 10.8 6.7 9.2 4.8 8.3C6.7 7.4 7.5 5.8 8 3.2Z" fill="currentColor"/>
                  <path d="M14.6 11.2C14.8 12.3 15.2 12.9 16.2 13.3C15.2 13.7 14.8 14.3 14.6 15.4C14.4 14.3 14 13.7 13 13.3C14 12.9 14.4 12.3 14.6 11.2Z" fill="currentColor"/>
                </svg>
              </button>
              {/* Photo library AI */}
              <button
                onClick={()=>{ try { hapticHayahSelection(); } catch {} setSearchTab("ai"); setPendingAiPicker("library"); }}
                aria-label="Pick a meal photo from library"
                style={{flexShrink:0,width:32,height:32,borderRadius:8,border:"none",background:"transparent",color:teal,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <rect x="2.5" y="3.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                  <circle cx="7" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M3 14L7.5 10.5L11 13L14.5 9.5L17 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {/* Camera AI */}
              <button
                onClick={()=>{ try { hapticHayahSelection(); } catch {} setSearchTab("ai"); setPendingAiPicker("camera"); }}
                aria-label="Take a meal photo with the camera"
                style={{flexShrink:0,width:32,height:32,borderRadius:8,border:"none",background:"transparent",color:teal,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M7 4h6l1.5 2H17a1.5 1.5 0 0 1 1.5 1.5V15A1.5 1.5 0 0 1 17 16.5H3A1.5 1.5 0 0 1 1.5 15V7.5A1.5 1.5 0 0 1 3 6h2.5L7 4Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                  <circle cx="10" cy="11" r="3" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      {searchTab === "recent" && (() => {
        const recents = getRecentFoods();
        return recents.length === 0 ? (
          <div style={{textAlign:"center",padding:32,color:muted,fontSize:"13px"}}>No foods logged yet.<br/>Add something to see your recent foods here.</div>
        ) : (
          <div>
            <div style={{padding:"10px 16px 4px",fontSize:"10.5px",color:muted,fontWeight:700,letterSpacing:"0.5px"}}>RECENTLY EATEN</div>
            {recents.map(r => (
              <SelectableFoodRow key={r.id} food={{...r, servingG:r.servingG||100, servingLabel:r.servingLabel||"serving"}} />
            ))}
          </div>
        );
      })()}
      {searchTab === "search" && (
        <>
          {/* The search <input> lives inside the sticky header above. A second
              copy used to render here too, sharing the same ref + autoFocus,
              which made the two inputs fight for focus and caused the mobile
              keyboard to pop up and immediately collapse. */}
          {selectionCount > 0 && (
            <div className="ghidha-no-sb" style={{display:"flex",gap:6,overflowX:"auto",padding:"4px 16px 8px",alignItems:"center"}}>
              {Object.entries(searchSelections).map(([key, sel]) => (
                <div key={key} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 6px 5px 10px",borderRadius:999,background:darkMode?"rgba(0,201,167,0.12)":"rgba(0,158,135,0.10)",border:`1px solid ${darkMode?"rgba(0,201,167,0.35)":"rgba(0,158,135,0.30)"}`,whiteSpace:"nowrap",flexShrink:0,fontSize:"11.5px",color:text,fontWeight:600}}>
                  <span style={{maxWidth:140,overflow:"hidden",textOverflow:"ellipsis"}}>{sel.food?.name || "—"}</span>
                  <span style={{fontFamily:"monospace",fontSize:"10px",color:muted}}>{sel.servingLabel}</span>
                  <button onClick={()=>toggleFoodSelection(sel.food)} aria-label={`Remove ${sel.food?.name}`} style={{width:18,height:18,borderRadius:999,border:"none",background:darkMode?"rgba(255,255,255,0.12)":"rgba(0,0,0,0.10)",color:muted,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,fontFamily:"inherit"}}>
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Empty-state CTAs — only render the "no matches yet" hint when
              the AI block below is idle. When AI is loading / has results /
              errored, the merged AI block (further down) is the single
              source of truth, preventing duplicate AI ESTIMATED headers
              and double "Estimating…" pills. */}
          {!searchLoading && searchQuery && searchResults.length === 0 && searchQuery.trim().length >= 3 && !aiSearchLoading && aiSearchResults.length === 0 && !aiSearchError && (
            <div style={{padding:"20px 16px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
              <div style={{fontSize:"13px",color:text,fontWeight:700,letterSpacing:"-0.01em",textAlign:"center"}}>No matches for "{searchQuery}"</div>
              <div style={{fontSize:"11px",color:muted,lineHeight:1.5,textAlign:"center",maxWidth:300}}>
                AI will try in ~1 second. Or skip ahead:
              </div>
              <div style={{display:"flex",gap:8,width:"100%",maxWidth:320,marginTop:2}}>
                <button onClick={()=>setQuickAddMode(true)} style={{flex:1,padding:"11px 12px",borderRadius:12,border:`1px solid ${gold}`,background:darkMode?"rgba(255,179,0,0.10)":"rgba(184,112,8,0.08)",color:gold,fontSize:"12.5px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",letterSpacing:"-0.01em",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>⚡ Quick Add</button>
                <button onClick={()=>pushFlowView("barcode")} style={{flex:1,padding:"11px 12px",borderRadius:12,border:`1px solid ${border}`,background:s2,color:text,fontSize:"12.5px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",letterSpacing:"-0.01em",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>📷 Scan</button>
              </div>
            </div>
          )}
          {!searchLoading && !searchQuery && recentSearches.length > 0 && (
            <div style={{padding:"6px 16px 10px"}}>
              <div style={{fontSize:"10px",color:muted,fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>Recent searches</div>
              <div className="ghidha-no-sb" style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
                {recentSearches.map(q => (
                  <button key={q} onClick={()=>{ handleSearchInput(q); if (searchInputRef.current) searchInputRef.current.focus(); }} style={{padding:"6px 12px",borderRadius:14,border:`1px solid ${border}`,background:s2,color:text,fontSize:"11.5px",fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>{q}</button>
                ))}
              </div>
            </div>
          )}
          {!searchLoading && !searchQuery && (
            <div style={{textAlign:"center",padding:"24px 32px",color:muted,fontSize:"12px",lineHeight:1.6}}>
              Start typing to search foods.<br/>
              <span style={{opacity:0.7,fontSize:"11px"}}>Pro tip: tap <b style={{color:teal}}>🕐 Recent</b> for foods you've logged before.</span>
            </div>
          )}
          {/* Inline AI block — renders ABOVE local results whenever AI is
              active (loading / has results / errored). Triggered by either
              the 5s auto-fallback (when local is empty) or the sparkle icon
              (explicit user tap). User picks from AI items or local items
              just like one merged search panel. */}
          {searchQuery && searchQuery.trim().length >= 3 && (aiSearchLoading || aiSearchResults.length > 0 || aiSearchError) && (
            aiSearchResults.length > 0 ? (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 16px 6px"}}>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{color:teal,flexShrink:0}}>
                    <path d="M8 3.2C8.5 5.8 9.3 7.4 11.2 8.3C9.3 9.2 8.5 10.8 8 13.4C7.5 10.8 6.7 9.2 4.8 8.3C6.7 7.4 7.5 5.8 8 3.2Z" fill="currentColor"/>
                    <path d="M14.6 11.2C14.8 12.3 15.2 12.9 16.2 13.3C15.2 13.7 14.8 14.3 14.6 15.4C14.4 14.3 14 13.7 13 13.3C14 12.9 14.4 12.3 14.6 11.2Z" fill="currentColor"/>
                  </svg>
                  <span style={{fontFamily:"monospace",fontSize:"9.5px",letterSpacing:"0.18em",color:teal,fontWeight:800,textTransform:"uppercase"}}>AI Estimated · {aiSearchResults.length} item{aiSearchResults.length===1?"":"s"}</span>
                  <button onClick={()=>{ if (aiSearchAbortRef.current) aiSearchAbortRef.current.cancelled = true; setAiSearchResults([]); runInlineAiSearch(searchQuery); }} style={{marginLeft:"auto",background:"transparent",border:"none",color:muted,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",padding:"4px 6px",letterSpacing:"-0.01em"}}>↻ Refresh</button>
                </div>
                {aiSearchResults.map(r => (
                  <SelectableFoodRow key={r.id} food={r} sourceTag="ai"/>
                ))}
              </div>
            ) : aiSearchLoading ? (
              <div style={{padding:"14px 16px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 14px",borderRadius:999,background:`${teal}1f`,border:`0.5px solid ${teal}66`}}>
                  <span aria-hidden style={{width:8,height:8,borderRadius:"50%",background:teal,animation:"ghidhaPulseFade 1.1s ease-in-out infinite"}}/>
                  <span style={{fontSize:"11.5px",color:teal,fontWeight:700,letterSpacing:"0.04em"}}>Estimating "{searchQuery}" with AI…</span>
                </div>
              </div>
            ) : aiSearchError ? (
              <div style={{padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,background:`rgba(184,64,53,0.10)`,borderTop:`0.5px solid rgba(184,64,53,0.20)`,borderBottom:`0.5px solid rgba(184,64,53,0.20)`}}>
                <div style={{flex:1,minWidth:0,fontSize:"11.5px",color:darkMode?"#ff8a73":"#a64536"}}>{aiSearchError}</div>
                <button onClick={()=>runInlineAiSearch(searchQuery)} style={{flexShrink:0,padding:"6px 10px",borderRadius:8,border:`1px solid ${teal}`,background:"transparent",color:teal,fontSize:"11px",fontWeight:700,fontFamily:"inherit",cursor:"pointer"}}>↻ Retry</button>
              </div>
            ) : null
          )}
          {searchResults.map((r, i) => r.__divider ? (
            <div key={`div-${i}`} style={{padding:"10px 16px 4px",marginTop:6,
              borderTop:`1px solid ${border}`,
              fontSize:"10.5px",color:muted,fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase"}}>
              ↓ {r.label}
            </div>
          ) : (
            <SelectableFoodRow key={r.id} food={{...r, servingG:r.servingG||100, servingLabel:r.servingLabel||"100g"}} />
          ))}
        </>
      )}
      {searchTab === "sunnah" && (
        <div>
          <div style={{padding:"10px 16px 4px",fontSize:"10.5px",color:gold,fontWeight:700,letterSpacing:"0.5px"}}>☽ SUNNAH FOODS</div>
          {SUNNAH_FOODS.map(f => (
            <SelectableFoodRow key={f.id} food={{...f, brand: f.nameAr}} sourceTag="sunnah" />
          ))}
        </div>
      )}
      {searchTab === "sa" && (
        <div style={{padding:"6px 0 24px"}}>
          {Array.from(new Set(SA_FOODS.map(f=>f.category))).map(cat => (
            <div key={cat}>
              <div style={{padding:"10px 16px 4px",fontSize:"11px",fontWeight:800,color:teal,letterSpacing:"0.6px",textTransform:"uppercase"}}>🇿🇦 {cat}</div>
              {SA_FOODS.filter(f=>f.category===cat).map(f=>(
                <SelectableFoodRow key={f.id} food={f} sourceTag="sa_food" />
              ))}
            </div>
          ))}
          <div style={{padding:"10px 16px 4px",fontSize:"11px",fontWeight:800,color:gold,letterSpacing:"0.6px",textTransform:"uppercase"}}>🍔 Restaurants</div>
          {Array.from(new Set(SA_RESTAURANTS.map(r=>r.restaurant))).map(rest => (
            <div key={rest}>
              <div style={{padding:"8px 16px 2px",fontSize:"11px",fontWeight:700,color:muted}}>{rest}</div>
              {SA_RESTAURANTS.filter(r=>r.restaurant===rest).map(r=>(
                <SelectableFoodRow key={r.id} food={r} sourceTag="sa_restaurant" />
              ))}
            </div>
          ))}
        </div>
      )}
      {searchTab === "supplements" && (
        <div style={{padding:"6px 0 24px"}}>
          {Array.from(new Set(SA_SUPPLEMENTS.map(s=>s.brand))).map(brand => (
            <div key={brand}>
              <div style={{padding:"10px 16px 4px",fontSize:"11px",fontWeight:800,color:accent,letterSpacing:"0.6px",textTransform:"uppercase"}}>💊 {brand}</div>
              {SA_SUPPLEMENTS.filter(s=>s.brand===brand).map(s=>(
                <SelectableFoodRow key={s.id} food={{...s, name:`${s.brand} ${s.name}`}} sourceTag="sa_supplement" />
              ))}
            </div>
          ))}
        </div>
      )}
      {searchTab === "ai" && (() => {
        const T = AI_T;
        const hasKey = !!getGeminiKey();
        const isNativeApp = !!(window.Capacitor?.isNativePlatform?.() || window.Capacitor?.isNative);
        const speechSupported = isDictationAvailable();

        // Compress data URL to ~1024px JPEG 0.75 (raw iPhone photos crash WKWebView)
        const compressDataUrl = (dataUrl) => new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const MAX = 1024;
            let { width: w, height: h } = img;
            if (w > MAX || h > MAX) {
              if (w >= h) { h = Math.round(h * MAX / w); w = MAX; }
              else { w = Math.round(w * MAX / h); h = MAX; }
            }
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.75));
          };
          img.onerror = () => resolve(dataUrl);
          img.src = dataUrl;
        });

        const addPending = async (dataUrl, hint = '') => {
          const compressed = await compressDataUrl(dataUrl);
          const hint_ = hint || aiHint;
          if (hint_) setAiHint('');
          setPendingPhotos(prev => [...prev, { id: `photo_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, dataUrl: compressed, hint: hint_, status: 'pending', addedAt: Date.now() }]);
        };

        const addFromCamera = async () => {
          if (!hasKey) return;
          if (isNativeApp) {
            try {
              const photo = await CapacitorCamera.getPhoto({ quality: 75, allowEditing: false, resultType: 'uri', source: 'CAMERA' });
              const src = photo?.webPath || photo?.dataUrl || photo?.path;
              if (src) {
                if (src.startsWith('blob:') || src.startsWith('http') || src.startsWith('capacitor://')) {
                  const blob = await fetch(src).then(r => r.blob());
                  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
                  await addPending(dataUrl);
                } else {
                  await addPending(src);
                }
                return;
              }
            } catch {}
            // native failed — fall through to file input
          }
          if (aiPhotoInputRef.current) {
            aiPhotoInputRef.current.setAttribute('capture', 'environment');
            aiPhotoInputRef.current.removeAttribute('multiple');
            aiPhotoInputRef.current.click();
          }
        };

        const addFromLibrary = async () => {
          if (!hasKey) return;
          // Helper: did the plugin error come from the user cancelling
          // the native picker? iOS Camera throws "User cancelled photos
          // app" / "User cancelled photos selection" on cancel; Android
          // surfaces "User cancelled". When that happens we must NOT
          // fall through to the next picker — the cascade is what made
          // the picker re-open multiple times before showing the iOS
          // 3-option sheet.
          const isCancelError = (err) => {
            const msg = (err?.message || err?.errorMessage || String(err || '')).toLowerCase();
            return /cancel/.test(msg);
          };
          if (isNativeApp) {
            // Try multi-select first (requires updated Camera pod).
            try {
              const result = await CapacitorCamera.pickImages({ quality: 75, limit: 0 });
              for (const p of (result?.photos || [])) {
                const src = p.dataUrl || p.webPath || p.path;
                if (!src) continue;
                if (src.startsWith('blob:') || src.startsWith('http')) {
                  try {
                    const blob = await fetch(src).then(r => r.blob());
                    const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
                    await addPending(dataUrl);
                  } catch {}
                } else {
                  await addPending(src);
                }
              }
              // Whether the user picked photos or cancelled (empty
              // array), the picker has already done its job — stop here.
              return;
            } catch (err) {
              // User cancelled — done, do not open another picker.
              if (isCancelError(err)) return;
              // Otherwise pickImages may genuinely be unavailable on
              // this build (older Capacitor Camera pod). Fall through
              // to the single-photo picker.
            }
            try {
              const photo = await CapacitorCamera.getPhoto({ quality: 75, allowEditing: false, resultType: 'dataUrl', source: 'PHOTOS' });
              const url = photo?.dataUrl || photo?.path || photo?.webPath;
              if (url) await addPending(url);
              return;
            } catch (err) {
              if (isCancelError(err)) return;
              // Native plugin truly failed — fall through to file input.
            }
          }
          if (aiPhotoInputRef.current) {
            aiPhotoInputRef.current.removeAttribute('capture');
            aiPhotoInputRef.current.setAttribute('multiple', '');
            aiPhotoInputRef.current.click();
          }
        };

        const handlePhotoFile = async (e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) return;
          e.target.value = "";
          for (const file of files) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => {
              const reader = new FileReader();
              reader.onload = async (ev) => {
                const compressed = await compressDataUrl(ev.target.result);
                setPendingPhotos(prev => [...prev, { id: `photo_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, dataUrl: compressed, status: 'pending', addedAt: Date.now() }]);
                res();
              };
              reader.onerror = () => res();
              reader.readAsDataURL(file);
            });
          }
        };

        const removeItem = (id) => setAiItems(prev => prev.filter(i => i.id !== id));
        const updateItem = (id, updates) => setAiItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
        const removePending = (id) => setPendingPhotos(prev => prev.filter(p => p.id !== id));
        const retryPending = (id) => setPendingPhotos(prev => prev.map(p => p.id === id ? { ...p, status: 'pending', error: null } : p));

        const logSingleItem = (id) => {
          const item = aiItems.find(i => i.id === id);
          if (!item) return;
          addFoodEntry({ ...item, id: `ai_${Math.random().toString(36).slice(2)}`, source: 'ai', halal: null }, item.servingG || 100, item.servingLabel || `${item.servingG || 100}g`, selectedMealSlot);
          setLoggedItemId(id);
          setTimeout(() => { setLoggedItemId(null); setAiItems(prev => prev.filter(i => i.id !== id)); }, 1100);
        };

        const logAll = () => {
          const total = aiItems.reduce((s,i)=>s+(i.calories||0), 0);
          aiItems.forEach(item => addFoodEntry({ ...item, id: `ai_${Math.random().toString(36).slice(2)}`, source: 'ai', halal: null }, item.servingG || 100, item.servingLabel || `${item.servingG || 100}g`, selectedMealSlot));
          setAllLoggedFlash({ total: Math.round(total), slot: selectedMealSlot });
          setTimeout(() => { setAllLoggedFlash(null); setAiItems([]); }, 1700);
        };

        // Expose the photo handlers via refs so the search-bar inline icons
        // can invoke them after switching searchTab to "ai".
        aiPickerCameraRef.current = addFromCamera;
        aiPickerLibraryRef.current = addFromLibrary;

        const handleAiParse = async () => {
          if (!aiMealText.trim()) return;
          setAiMealLoading(true);
          setAiMealError(null);
          setAiMealItems([]);
          try {
            // v2: persona + region + halal filter + schema. Truncation-repair
            // parser deleted — responseSchema enforces shape.
            const userProfile = getUserProfile();
            try {
              publishSources("meal-parse", mealParseSources({
                text: aiMealText.trim(),
                region: userProfile?.identity?.region,
                halalOnly: userProfile?.nutrition?.halalOnly,
              }));
            } catch {}
            const sys = composeSystem([
              "You are a nutrition expert. The user describes a meal in plain language. Return a JSON array of food items, splitting compound meals (e.g. \"chicken and rice\") into separate entries.",
              regionFoodHint(userProfile.identity.region),
              halalFilterInstruction(userProfile.nutrition.halalOnly),
              OUTPUT_JSON_STRICT,
            ]);
            const MEAL_ITEM_SCHEMA = {
              type: "object",
              properties: {
                name: { type: "string" },
                brand: { type: "string" },
                calories: { type: "number" },
                protein: { type: "number" },
                carbs: { type: "number" },
                fat: { type: "number" },
                fiber: { type: "number" },
                sugar: { type: "number" },
                sodium: { type: "number" },
                servingG: { type: "number" },
                servingLabel: { type: "string" },
                halal: { type: "boolean", nullable: true },
                halalNote: { type: "string" },
              },
              required: ["name", "calories", "protein", "carbs", "fat", "servingG", "servingLabel"],
            };
            const MEAL_ARRAY_SCHEMA = { type: "array", minItems: 1, maxItems: 10, items: MEAL_ITEM_SCHEMA };
            const raw = await callGemini(aiMealText.trim(), {
              systemInstruction: sys,
              responseSchema: MEAL_ARRAY_SCHEMA,
              model: MODELS.mealParser,
              maxOutputTokens: 2048,
              feature: "mealParser",
            });
            let items = JSON.parse(raw);
            if (!Array.isArray(items)) items = [items];
            setAiMealItems(items.filter(i => i && i.name && Number.isFinite(i.calories)));
          } catch {
            setAiMealError("AI couldn't parse that — try rephrasing or add more detail.");
          } finally {
            setAiMealLoading(false);
          }
        };
        // Expose parse trigger for the search-bar AI shortcut + auto-fallback.
        aiPickerParseRef.current = handleAiParse;

        const handleAddAllText = () => {
          aiMealItems.forEach(item => addFoodEntry({ ...item, id: `ai_${Math.random().toString(36).slice(2)}`, source: "ai", halal: null }, item.servingG || 100, item.servingLabel || `${item.servingG || 100}g`, selectedMealSlot));
          setAiMealItems([]);
          setAiMealText("");
          setSearchTab("recent");
        };

        const handleDictate = async () => {
          // Snapshot the base text the user already had typed so the partial
          // results append rather than overwrite. The hook handles haptic +
          // sanitization + session lifecycle.
          if (!aiDict.listening) {
            aiBaseTextRef.current = aiMealText;
            setAiMealError(null);
          }
          await aiDict.toggle();
        };

        const totalKcal = aiItems.reduce((s,i)=>s+(i.calories||0), 0);
        const hasContent = aiItems.length > 0 || pendingPhotos.length > 0;

        // "All logged" celebration
        if (allLoggedFlash) {
          return (
            <div style={{padding:"60px 16px",display:"flex",flexDirection:"column",alignItems:"center",gap:14,animation:"aiFadeUp 0.4s ease"}}>
              <style>{AI_KEYFRAMES}</style>
              <div style={{width:68,height:68,borderRadius:'50%',background:T.sageD,border:`1px solid rgba(124,196,160,0.3)`,display:'flex',alignItems:'center',justifyContent:'center',animation:'aiScaleIn 0.5s cubic-bezier(0.2,0.8,0.3,1)'}}>
                <svg width="30" height="22" viewBox="0 0 30 22" fill="none">
                  <path d="M2 11L11 20L28 2" stroke={T.sage} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{textAlign:'center'}}>
                <div style={{fontFamily:'inherit',fontSize:17,fontWeight:700,color:T.ink1,marginBottom:5,letterSpacing:'-0.02em'}}>All logged!</div>
                <div style={{fontFamily:T.mono,fontSize:12,color:T.ink3,fontVariantNumeric:'tabular-nums'}}>{allLoggedFlash.total} kcal added to {allLoggedFlash.slot.charAt(0).toUpperCase()+allLoggedFlash.slot.slice(1)}</div>
              </div>
            </div>
          );
        }

        return (
          <div style={{padding:"12px 16px 24px"}}>
            <style>{AI_KEYFRAMES}</style>
            <input ref={aiPhotoInputRef} type="file" accept="image/*" onChange={handlePhotoFile} style={{display:"none"}} />

            {!hasKey && (
              <div style={{padding:"14px 16px",borderRadius:12,background:T.amberD,border:`1px solid rgba(224,160,48,0.3)`,marginBottom:12,fontSize:"12.5px",color:T.ink2,lineHeight:1.55}}>
                Add a Gemini API key in <b style={{color:T.amber}}>Settings → AI Assistant</b> to use this feature. Free keys at aistudio.google.com.
              </div>
            )}

            {hasContent ? (
              <>
                {/* Queue header */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontFamily:T.mono,fontSize:10,letterSpacing:'0.12em',textTransform:'uppercase'}}>
                    {aiItems.length > 0 ? (
                      <>
                        <span style={{color:T.ink2}}>{aiItems.length} item{aiItems.length!==1?'s':''}</span>
                        <span style={{color:T.amber}}> · {Math.round(totalKcal)} kcal</span>
                      </>
                    ) : (
                      <span style={{color:T.ink4}}>Analysing…</span>
                    )}
                  </div>
                  <div style={{display:'flex',gap:6}}>
                    <button onClick={addFromCamera} disabled={!hasKey} aria-label="Take photo" style={{height:30,padding:'0 11px',background:T.bg4,border:`1px solid ${T.line}`,borderRadius:9,color:T.ink2,fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5,letterSpacing:'-0.01em',cursor:'pointer',fontFamily:'inherit',opacity:!hasKey?0.45:1}}>
                      <svg width="13" height="11" viewBox="0 0 14 12" fill="none">
                        <path d="M5 1H9L11.5 4H13C13.6 4 14 4.4 14 5V10.5C14 11.1 13.6 11.5 13 11.5H1C0.4 11.5 0 11.1 0 10.5V5C0 4.4 0.4 4 1 4H2.5L5 1Z" stroke={T.ink3} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="7" cy="7.5" r="2" stroke={T.ink3} strokeWidth="1.1"/>
                      </svg>
                      Snap
                    </button>
                    <button onClick={addFromLibrary} disabled={!hasKey} aria-label="Choose from library" style={{height:30,padding:'0 11px',background:T.bg4,border:`1px solid ${T.line}`,borderRadius:9,color:T.ink2,fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:5,letterSpacing:'-0.01em',cursor:'pointer',fontFamily:'inherit',opacity:!hasKey?0.45:1}}>
                      <svg width="13" height="11" viewBox="0 0 14 12" fill="none">
                        <rect x="0.6" y="2.4" width="12.8" height="9.3" rx="2" stroke={T.ink3} strokeWidth="1.1" fill="none"/>
                        <path d="M2.2 9.6L5.2 6.8L7.2 8.8L9.4 6.4L12 9.6" stroke={T.ink3} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                      Library
                    </button>
                  </div>
                </div>

                {/* Photo hint — optional context for next photo */}
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                  <input
                    value={aiHint}
                    onChange={e => setAiHint(e.target.value)}
                    placeholder="Add a hint for the next photo (optional)…"
                    style={{flex:1,fontFamily:'inherit',fontSize:12,color:T.ink2,background:T.bg3,border:`1px solid ${aiHint?T.amber+'66':T.line}`,borderRadius:9,padding:'7px 11px',outline:'none',minWidth:0,transition:'border-color 0.15s'}}
                  />
                  {aiHint && (
                    <button onClick={() => setAiHint('')} style={{flexShrink:0,width:24,height:24,borderRadius:'50%',background:T.bg4,border:`1px solid ${T.line}`,color:T.ink4,fontSize:14,lineHeight:'22px',textAlign:'center',cursor:'pointer',padding:0,fontFamily:'inherit'}}>×</button>
                  )}
                </div>

                {/* Result + analysing + error cards */}
                <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
                  {aiItems.map(item => (
                    <AiFoodCard key={item.id} item={item}
                      logged={item.id === loggedItemId}
                      onRemove={() => removeItem(item.id)}
                      onLog={() => logSingleItem(item.id)}
                      onUpdate={(u) => updateItem(item.id, u)}
                      onRedescribe={(id, desc, photoDataUrl) => {
                        // v2: persona + halal filter + schema. The v1 parseRobust
                        // is gone — responseSchema enforces shape.
                        updateItem(id, { name: 'Re-analysing…', calories: 0, protein: 0, carbs: 0, fat: 0, fresh: true });
                        (async () => {
                          try {
                            const userProfile = getUserProfile();
                            const sys = composeSystem([
                              "You are a nutrition expert. The user is correcting an AI's earlier guess on a food item. Re-identify based on the user's description (and the photo if attached). Return a JSON array with one entry.",
                              regionFoodHint(userProfile.identity.region),
                              halalFilterInstruction(userProfile.nutrition.halalOnly),
                              OUTPUT_JSON_STRICT,
                            ]);
                            const REDESCRIBE_ITEM_SCHEMA = {
                              type: "object",
                              properties: {
                                name: { type: "string" },
                                brand: { type: "string" },
                                calories: { type: "number" },
                                protein: { type: "number" },
                                carbs: { type: "number" },
                                fat: { type: "number" },
                                servingG: { type: "number" },
                                servingLabel: { type: "string" },
                                halal: { type: "boolean", nullable: true },
                                halalNote: { type: "string" },
                              },
                              required: ["name", "calories", "protein", "carbs", "fat", "servingG", "servingLabel"],
                            };
                            const REDESCRIBE_ARRAY_SCHEMA = { type: "array", minItems: 1, maxItems: 1, items: REDESCRIBE_ITEM_SCHEMA };
                            const userPrompt = `The user says this food is: "${desc}". ${photoDataUrl ? "Use the photo and this description together to" : "Based on this description,"} identify the item accurately.`;
                            const opts = {
                              systemInstruction: sys,
                              responseSchema: REDESCRIBE_ARRAY_SCHEMA,
                              model: MODELS.redescribe,
                              // 2048 — Gemini 2.5 Flash with thinking
                              // routinely burns ~500 tokens before output.
                              // 600 was risking MAX_TOKENS failures on a
                              // single-item JSON redescribe.
                              maxOutputTokens: 2048,
                              feature: "redescribe",
                            };
                            const raw = photoDataUrl
                              ? await callGeminiVision(userPrompt, photoDataUrl, opts)
                              : await callGemini(userPrompt, opts);
                            const parsed = JSON.parse(raw);
                            const items = (Array.isArray(parsed) ? parsed : [parsed]).filter(i => i && i.name && Number.isFinite(i.calories));
                            if (items.length > 0) updateItem(id, { ...items[0], fresh: true });
                          } catch { updateItem(id, { name: desc, fresh: true }); }
                        })();
                      }}
                    />
                  ))}
                  {pendingPhotos.map(p => (
                    p.status === 'error'
                      ? <AiErrorCard key={p.id} photo={p} onRetry={() => retryPending(p.id)} onRemove={() => removePending(p.id)} />
                      : <AiAnalysingCard key={p.id} thumbDataUrl={p.dataUrl} />
                  ))}
                </div>

                {/* Log All button */}
                {aiItems.length >= 2 && (
                  <button onClick={logAll} style={{
                    width:'100%',height:52,background:T.amber,border:'none',borderRadius:15,
                    color:'#180C00',fontFamily:'inherit',fontSize:15,fontWeight:700,
                    display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                    letterSpacing:'-0.01em',marginBottom:6,cursor:'pointer',
                    animation:'aiAmberPulse 2.8s ease infinite',
                  }}>
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <path d="M7.5 1L9.2 6H14.5L10.1 9.1L11.8 14L7.5 10.9L3.2 14L4.9 9.1L0.5 6H5.8L7.5 1Z" fill="#180C00"/>
                    </svg>
                    Log All · {Math.round(totalKcal)} kcal
                  </button>
                )}

                {/* OR describe — compact */}
                <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:12}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,color:T.ink4,fontFamily:T.mono,fontSize:9,letterSpacing:'0.14em',textTransform:'uppercase'}}>
                    <div style={{flex:1,height:1,background:T.line}}/>
                    or describe it
                    <div style={{flex:1,height:1,background:T.line}}/>
                  </div>
                  {speechSupported && (
                    <div style={{display:'flex',justifyContent:'flex-end',marginBottom:-2}}>
                                          {/* aria-live partial transcription for VoiceOver users. */}
                    <div aria-live="polite" aria-atomic="true" style={{ position:'absolute', width:1, height:1, padding:0, margin:-1, overflow:'hidden', clip:'rect(0 0 0 0)', whiteSpace:'nowrap', border:0 }}>
                      {aiListening ? ('Listening: ' + (aiMealText || '')) : ''}
                    </div>
                    <button onClick={handleDictate} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,border:`1px solid ${aiListening?"rgba(240,138,107,0.5)":T.amber+"44"}`,background:aiListening?"rgba(240,138,107,0.12)":T.bg3,color:aiListening?"#f08a6b":T.amber,fontSize:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px"}}>
                        <span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:aiListening?"#f08a6b":T.amber}}/>
                        {aiListening ? "Stop" : "Dictate"}
                      </button>
                    </div>
                  )}
                  <textarea value={aiMealText} onChange={e=>setAiMealText(e.target.value)}
                    placeholder="e.g. bowl of oats with banana, honey…"
                    style={{width:'100%',height:60,background:T.bg3,border:`1px solid ${T.line}`,borderRadius:12,padding:'10px 13px',fontFamily:'inherit',fontSize:12,color:T.ink2,resize:'none',outline:'none',lineHeight:1.6,boxSizing:'border-box'}}
                  />
                  <button onClick={handleAiParse} disabled={!aiMealText.trim() || aiMealLoading || !hasKey}
                    style={{width:'100%',height:38,background:'transparent',border:`1px solid ${T.line}`,borderRadius:10,fontFamily:'inherit',fontSize:12,fontWeight:500,color:T.ink3,cursor:'pointer',opacity:!aiMealText.trim()||!hasKey?0.45:1}}>
                    {aiMealLoading ? "Analysing…" : "✦ Parse with AI"}
                  </button>
                  {aiMealError && (<>
                    <div style={{padding:"10px 14px",borderRadius:10,background:`rgba(184,64,53,0.12)`,border:`1px solid rgba(184,64,53,0.35)`,color:T.coral,fontSize:"12px"}}>{aiMealError}</div>
                  {isPermissionError(aiMealError) && (<button onClick={openIOSSettings} style={{marginTop:6,padding:'5px 12px',borderRadius:12,border:'1px solid rgba(240,138,107,0.45)',background:'rgba(240,138,107,0.10)',color:'#f08a6b',fontFamily:'inherit',fontSize:11,fontWeight:600,cursor:'pointer'}}>Open Settings →</button>)}</>)}
                  {aiMealItems.length > 0 && (() => {
                    // Confidence heuristic — flag items that are likely
                    // wrong (missing name, kcal == 0, suspiciously round
                    // macros that scream "fallback default"). Drives the
                    // header chip color + label so the user knows whether
                    // to trust the parse or eyeball each row first.
                    const flagged = aiMealItems.filter(it => !it?.name || !it.calories || (Math.abs(it.protein - Math.round(it.protein)) < 0.01 && it.protein > 0 && it.calories === Math.round(it.protein) * 4)).length;
                    const allGood = flagged === 0;
                    const chipBg = allGood ? `${T.sage || '#7CC4A0'}22` : 'rgba(224,160,48,0.13)';
                    const chipFg = allGood ? (T.sage || '#7CC4A0') : T.amber;
                    const chipLabel = allGood ? `READY · ${aiMealItems.length} ITEM${aiMealItems.length!==1?"S":""}` : `REVIEW · ${aiMealItems.length} ITEM${aiMealItems.length!==1?"S":""}`;
                    return (
                    <div style={{marginTop:6}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:8}}>
                        <span style={{fontSize:"10px",color:chipFg,fontWeight:800,letterSpacing:"0.18em",padding:"4px 8px",borderRadius:999,background:chipBg,border:`0.5px solid ${chipFg}44`}}>{chipLabel}</span>
                        <span style={{fontSize:"10px",color:T.ink4,letterSpacing:"0.05em"}}>Tap a row to edit</span>
                      </div>
                      {aiMealItems.map((item,i) => {
                        const isEditing = editingAiItemIdx === i;
                        return (
                          <div key={i} style={{borderRadius:12,background:T.bg3,border:`1px solid ${isEditing ? T.amber+'66' : T.line}`,marginBottom:6,overflow:'hidden',transition:'border-color 0.15s'}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px"}}>
                              <button onClick={() => setEditingAiItemIdx(isEditing ? null : i)} aria-label={isEditing ? "Collapse" : "Edit item"} style={{flex:1,minWidth:0,background:'transparent',border:0,padding:0,textAlign:'left',cursor:'pointer',fontFamily:'inherit'}}>
                                <div style={{fontSize:"13px",fontWeight:700,color:T.ink1,marginBottom:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.name || 'Unnamed item'}</div>
                                <div style={{fontSize:"10.5px",color:T.ink3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.servingLabel || `${item.servingG||100}g`} · {Math.round(item.calories || 0)} kcal · {Math.round(item.protein || 0)}g P · {Math.round(item.carbs || 0)}g C · {Math.round(item.fat || 0)}g F</div>
                              </button>
                              <button onClick={()=>setAiMealItems(prev=>prev.filter((_,j)=>j!==i))} aria-label={`Skip ${item.name || 'item'}`} style={{width:28,height:28,borderRadius:'50%',border:0,background:'transparent',color:T.ink4,fontSize:18,lineHeight:1,cursor:'pointer',padding:0,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',touchAction:'manipulation',WebkitTapHighlightColor:'transparent'}}>×</button>
                              <button onClick={() => { addFoodEntry({ ...item, id: `ai_${Math.random().toString(36).slice(2)}`, source: "ai", halal: null }, item.servingG || 100, item.servingLabel || `${item.servingG || 100}g`, selectedMealSlot); setAiMealItems(prev => prev.filter((_,j)=>j!==i)); }}
                                style={{padding:"7px 12px",borderRadius:10,border:"none",background:T.amberD,color:T.amber,fontSize:"11px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",flexShrink:0}}>
                                Add
                              </button>
                            </div>
                            {isEditing && (
                              <div style={{padding:'8px 12px 12px',borderTop:`1px solid ${T.line}`,display:'flex',flexDirection:'column',gap:8,animation:'aiFadeUp 0.18s ease both'}}>
                                <input value={item.name || ''} onChange={e=>updateAiItem(i, { name: e.target.value })} placeholder="Item name"
                                  style={{width:'100%',background:T.bg2,border:`1px solid ${T.line}`,borderRadius:9,padding:'9px 11px',fontFamily:'inherit',fontSize:13,color:T.ink1,outline:'none',boxSizing:'border-box'}}/>
                                <input value={item.servingLabel || ''} onChange={e=>updateAiItem(i, { servingLabel: e.target.value })} placeholder="Serving (e.g. 1 cup, 150g)"
                                  style={{width:'100%',background:T.bg2,border:`1px solid ${T.line}`,borderRadius:9,padding:'9px 11px',fontFamily:'inherit',fontSize:12,color:T.ink2,outline:'none',boxSizing:'border-box'}}/>
                                <div style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:6}}>
                                  {[{k:'calories',l:'KCAL'},{k:'protein',l:'P (g)'},{k:'carbs',l:'C (g)'},{k:'fat',l:'F (g)'}].map(f => (
                                    <div key={f.k} style={{display:'flex',flexDirection:'column',gap:3}}>
                                      <span style={{fontSize:9,color:T.ink4,letterSpacing:'0.12em',fontFamily:'monospace'}}>{f.l}</span>
                                      <input type="number" inputMode="decimal" value={item[f.k] != null ? item[f.k] : ''} onChange={e=>{const v=e.target.value; updateAiItem(i, { [f.k]: v === '' ? 0 : parseFloat(v) || 0 });}}
                                        style={{width:'100%',background:T.bg2,border:`1px solid ${T.line}`,borderRadius:9,padding:'8px 9px',fontFamily:'inherit',fontSize:13,fontWeight:600,color:T.ink1,outline:'none',boxSizing:'border-box',textAlign:'center'}}/>
                                    </div>
                                  ))}
                                </div>
                                <button onClick={()=>setEditingAiItemIdx(null)} style={{width:'100%',padding:'8px',borderRadius:9,border:`1px solid ${T.line}`,background:'transparent',color:T.ink3,fontSize:11,fontWeight:600,fontFamily:'inherit',cursor:'pointer',letterSpacing:'-0.01em'}}>Done editing</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button onClick={handleAddAllText} style={{marginTop:4,width:"100%",padding:"12px",borderRadius:12,border:"none",background:T.amber,color:"#180C00",fontSize:"13px",fontWeight:800,fontFamily:"inherit",cursor:"pointer",letterSpacing:"0.3px"}}>
                        Add All to {selectedMealSlot.charAt(0).toUpperCase()+selectedMealSlot.slice(1)}
                      </button>
                    </div>
                    );
                  })()}
                </div>
              </>
            ) : (
              /* Empty state hero */
              <div style={{display:'flex',flexDirection:'column',gap:16,animation:'aiFadeIn 0.3s ease'}}>
                <div style={{
                  width:'100%',background:T.bg3,border:`1.5px dashed ${T.line}`,borderRadius:18,
                  padding:'30px 20px 22px',display:'flex',flexDirection:'column',alignItems:'center',gap:15,
                  opacity:!hasKey?0.45:1,
                }}>
                  {/* Top zone — opens camera */}
                  <button onClick={addFromCamera} disabled={!hasKey} aria-label="Open camera"
                    style={{display:'flex',flexDirection:'column',alignItems:'center',gap:15,background:'transparent',border:'none',padding:0,cursor:!hasKey?'not-allowed':'pointer',fontFamily:'inherit'}}>
                    <div style={{width:62,height:62,borderRadius:18,background:T.amberD,border:`1px solid rgba(224,160,48,0.2)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <svg width="30" height="26" viewBox="0 0 30 26" fill="none">
                        <path d="M11 2H19L22.5 6.5H27C28.1 6.5 29 7.4 29 8.5V22C29 23.1 28.1 24 27 24H3C1.9 24 1 23.1 1 22V8.5C1 7.4 1.9 6.5 3 6.5H7.5L11 2Z" stroke={T.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="15" cy="15.5" r="5" stroke={T.amber} strokeWidth="1.6"/>
                        <circle cx="15" cy="15.5" r="1.8" fill={T.amber} fillOpacity="0.35"/>
                      </svg>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontFamily:'inherit',fontSize:16,fontWeight:700,color:T.ink1,marginBottom:6,letterSpacing:'-0.01em'}}>Snap your meal</div>
                      <div style={{fontFamily:'inherit',fontSize:12,color:T.ink3,lineHeight:1.65}}>
                        AI identifies every item & its nutrition<br/>instantly — no typing required
                      </div>
                    </div>
                  </button>
                  {/* Bottom pill — opens photo library */}
                  <button onClick={addFromLibrary} disabled={!hasKey} aria-label="Choose from photo library"
                    style={{height:40,padding:'0 24px',background:T.amber,border:'none',borderRadius:12,color:'#180C00',fontFamily:'inherit',fontSize:14,fontWeight:700,display:'flex',alignItems:'center',gap:7,letterSpacing:'-0.01em',cursor:!hasKey?'not-allowed':'pointer'}}>
                    <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
                      <rect x="0.7" y="2.7" width="14.6" height="10.6" rx="2.3" stroke="#180C00" strokeWidth="1.4" fill="none"/>
                      <path d="M2.5 11L6 7.5L8.5 10L11 7L14 11" stroke="#180C00" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      <circle cx="11.5" cy="5.5" r="1.1" fill="#180C00"/>
                    </svg>
                    Choose from Library
                  </button>
                </div>

                {/* Photo hint input — optional context before snapping */}
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <input
                    value={aiHint}
                    onChange={e => setAiHint(e.target.value)}
                    placeholder="Hint for AI (e.g. lamb biryani) — optional"
                    style={{flex:1,fontFamily:'inherit',fontSize:13,color:T.ink2,background:T.bg3,border:`1px solid ${aiHint?T.amber+'66':T.line}`,borderRadius:11,padding:'10px 13px',outline:'none',minWidth:0,transition:'border-color 0.15s'}}
                  />
                  {aiHint && (
                    <button onClick={() => setAiHint('')} style={{flexShrink:0,width:28,height:28,borderRadius:'50%',background:T.bg4,border:`1px solid ${T.line}`,color:T.ink4,fontSize:15,lineHeight:'26px',textAlign:'center',cursor:'pointer',padding:0,fontFamily:'inherit'}}>×</button>
                  )}
                </div>

                <div style={{display:'flex',alignItems:'center',gap:10,color:T.ink4,fontFamily:T.mono,fontSize:9,letterSpacing:'0.14em',textTransform:'uppercase'}}>
                  <div style={{flex:1,height:1,background:T.line}}/>
                  or describe your meal
                  <div style={{flex:1,height:1,background:T.line}}/>
                </div>

                {speechSupported && (
                  <div style={{display:'flex',justifyContent:'flex-end',marginBottom:-8}}>
                                        {/* aria-live partial transcription for VoiceOver users. */}
                    <div aria-live="polite" aria-atomic="true" style={{ position:'absolute', width:1, height:1, padding:0, margin:-1, overflow:'hidden', clip:'rect(0 0 0 0)', whiteSpace:'nowrap', border:0 }}>
                      {aiListening ? ('Listening: ' + (aiMealText || '')) : ''}
                    </div>
                    <button onClick={handleDictate} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:20,border:`1px solid ${aiListening?"rgba(240,138,107,0.5)":T.amber+"44"}`,background:aiListening?"rgba(240,138,107,0.12)":T.bg3,color:aiListening?"#f08a6b":T.amber,fontSize:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px"}}>
                      <span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:aiListening?"#f08a6b":T.amber}}/>
                      {aiListening ? "Stop" : "Dictate"}
                    </button>
                  </div>
                )}

                <textarea value={aiMealText} onChange={e=>setAiMealText(e.target.value)}
                  placeholder="e.g. a bowl of oats with banana, honey and full-fat milk"
                  rows={3}
                  style={{width:'100%',minHeight:78,background:T.bg3,border:`1px solid ${T.line}`,borderRadius:13,padding:'12px 14px',fontFamily:'inherit',fontSize:13,color:T.ink2,resize:'none',outline:'none',lineHeight:1.6,boxSizing:'border-box'}}
                />
                <button onClick={handleAiParse} disabled={!aiMealText.trim() || aiMealLoading || !hasKey}
                  style={{width:'100%',height:40,background:'transparent',border:`1px solid ${T.line}`,borderRadius:11,fontFamily:'inherit',fontSize:13,fontWeight:500,color:T.ink3,letterSpacing:'-0.01em',cursor:'pointer',opacity:!aiMealText.trim()||!hasKey?0.45:1}}>
                  {aiMealLoading ? "Analysing…" : "✦ Parse with AI"}
                </button>
                {aiMealError && (<>
                  <div style={{padding:"10px 14px",borderRadius:10,background:`rgba(184,64,53,0.12)`,border:`1px solid rgba(184,64,53,0.35)`,color:T.coral,fontSize:"12px"}}>{aiMealError}</div>
                {isPermissionError(aiMealError) && (<button onClick={openIOSSettings} style={{marginTop:6,padding:'5px 12px',borderRadius:12,border:'1px solid rgba(240,138,107,0.45)',background:'rgba(240,138,107,0.10)',color:'#f08a6b',fontFamily:'inherit',fontSize:11,fontWeight:600,cursor:'pointer'}}>Open Settings →</button>)}</>)}
                {aiMealItems.length > 0 && (
                  <div>
                    <div style={{fontSize:"11px",color:T.ink3,fontWeight:600,letterSpacing:"0.4px",marginBottom:8}}>AI DETECTED · {aiMealItems.length} ITEM{aiMealItems.length!==1?"S":""}</div>
                    {aiMealItems.map((item,i) => (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,background:T.bg3,border:`1px solid ${T.line}`,marginBottom:6}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:"13px",fontWeight:700,color:T.ink1,marginBottom:2}}>{item.name}</div>
                          <div style={{fontSize:"10.5px",color:T.ink3}}>{item.servingLabel || `${item.servingG||100}g`} · {Math.round(item.calories)} kcal · {Math.round(item.protein)}g P · {Math.round(item.carbs)}g C · {Math.round(item.fat)}g F</div>
                        </div>
                        <button onClick={() => { addFoodEntry({ ...item, id: `ai_${Math.random().toString(36).slice(2)}`, source: "ai", halal: null }, item.servingG || 100, item.servingLabel || `${item.servingG || 100}g`, selectedMealSlot); setAiMealItems(prev => prev.filter((_,j)=>j!==i)); }}
                          style={{padding:"7px 12px",borderRadius:10,border:"none",background:T.amberD,color:T.amber,fontSize:"11px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",flexShrink:0}}>
                          Add
                        </button>
                      </div>
                    ))}
                    <button onClick={handleAddAllText} style={{marginTop:4,width:"100%",padding:"12px",borderRadius:12,border:"none",background:T.amber,color:"#180C00",fontSize:"13px",fontWeight:800,fontFamily:"inherit",cursor:"pointer",letterSpacing:"0.3px"}}>
                      Add All to {selectedMealSlot.charAt(0).toUpperCase()+selectedMealSlot.slice(1)}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
  };

  const BarcodeView = () => (
    <div style={{padding:"24px 16px",display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
      <div id="ghidha-scanner" style={{width:"100%",maxWidth:380,minHeight:260,borderRadius:16,overflow:"hidden",background:"#000"}}/>
      {scanError && (
        <div style={{width:"100%",maxWidth:380,display:"flex",flexDirection:"column",gap:8,boxSizing:"border-box"}}>
          <div role="alert" style={{color:danger,fontSize:"13px",textAlign:"center",padding:"8px 16px",background:`${danger}15`,borderRadius:10,border:`1px solid ${danger}40`}}>{scanError}</div>
          <button onClick={()=>{ try { hapticTap(); } catch {} setScanError(null); setBarcodeSearchFallback(false); setView("search"); setTimeout(()=>setView("barcode"), 50); }} style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1px solid ${gold}`,background:darkMode?"rgba(255,179,0,0.10)":"rgba(184,112,8,0.08)",color:gold,fontSize:"12.5px",fontWeight:700,fontFamily:"inherit",cursor:"pointer",letterSpacing:"-0.01em",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>↻ Try scanning again</button>
        </div>
      )}
      {barcodeSearchFallback && (
        <div style={{width:"100%",maxWidth:380}}>
          <input autoFocus placeholder="Search manually..." onChange={e=>handleSearchInput(e.target.value)} style={{width:"100%",padding:"12px 14px",borderRadius:12,border:`1px solid ${border}`,background:s2,color:text,fontSize:"14px",fontFamily:"inherit",boxSizing:"border-box",outline:"none"}}/>
          {searchResults.length > 0 && (
            <div style={{borderRadius:12,border:`1px solid ${border}`,overflow:"hidden",marginTop:6}}>
              {searchResults.filter(r => !r.__divider).slice(0,5).map(r => (
                <div key={r.id} onClick={()=>{setSelectedFood(r);setSelectedServingG(r.servingG||100);setSelectedServingLabel(r.servingLabel||"100g");pushFlowView("food-detail");}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",borderBottom:`1px solid ${border}`,cursor:"pointer",background:s1,gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"13px",fontWeight:700,color:text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</div>
                    <div style={{fontSize:"10.5px",color:muted}}>{r.brand||"—"}</div>
                  </div>
                  <div style={{fontSize:"13px",fontWeight:800,color:text}}>{Math.round(r.calories)} <span style={{fontSize:"9px",color:muted}}>kcal</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{fontSize:"12px",color:muted,textAlign:"center"}}>{scannerActive ? "📷 Point camera at barcode" : "Starting camera…"}</div>
      <button onClick={()=>popFlowView()} style={{padding:"10px 18px",background:s2,border:`1px solid ${border}`,borderRadius:10,color:text,fontSize:"12px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Back to Search</button>
    </div>
  );

  const FoodDetail = () => {
    if (!selectedFood) return null;
    const baseG = selectedFood.servingG || 100;
    const scale = selectedServingG / baseG;
    const v = (n) => Math.round((n||0) * scale * 10) / 10;
    return (
      <div style={{padding:"16px 16px 80px"}}>
        <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"16px",fontWeight:800,color:text}}>{selectedFood.name}</div>
              <div style={{fontSize:"11px",color:muted,marginTop:2}}>{selectedFood.brand || selectedFood.restaurant || ""}</div>
            </div>
            
            {(() => {
              const fav = isFoodFavorited(selectedFood);
              return (
                <button
                  onClick={()=>toggleFavorite(selectedFood)}
                  aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                  aria-pressed={fav}
                  title={fav ? "Remove from favorites" : "Add to favorites"}
                  style={{background:"transparent",border:"none",cursor:"pointer",padding:4,display:"flex",alignItems:"center",justifyContent:"center"}}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill={fav?gold:"none"} stroke={fav?gold:muted} strokeWidth="1.8" strokeLinejoin="round" style={{transition:"fill 0.18s ease, stroke 0.18s ease", filter: fav ? `drop-shadow(0 0 6px ${gold}80)` : "none"}}>
                    <path d="M12 2.5l2.95 6.18 6.55.95-4.75 4.78 1.12 6.79L12 17.95l-5.87 3.25 1.12-6.79L2.5 9.63l6.55-.95L12 2.5z"/>
                  </svg>
                </button>
              );
            })()}
          </div>
        </div>
        <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
          <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>SERVING SIZE</div>
          {(() => {
            const baseG = selectedFood.servingG || 100;
            const baseLabel = selectedFood.servingLabel || `${baseG}g`;
            const currentQty = baseG > 0 ? selectedServingG / baseG : (selectedServingG > 0 ? selectedServingG : 1);
            const fmtQty = (q) => {
              if (!Number.isFinite(q) || q <= 0) return "1";
              if (Number.isInteger(q)) return String(q);
              return String(Math.round(q * 100) / 100);
            };
            const fmtLabel = (q) => {
              if (q === 1) return baseLabel;
              if (q === 0.5) return `½× ${baseLabel}`;
              if (Number.isInteger(q)) return `${q}× ${baseLabel}`;
              return `${fmtQty(q)}× ${baseLabel}`;
            };
            const applyQty = (q) => {
              if (!Number.isFinite(q) || q <= 0) return;
              const newG = baseG > 0 ? Math.round(baseG * q * 100) / 100 : q;
              setSelectedServingG(newG);
              setSelectedServingLabel(fmtLabel(q));
              setServingQtyStr(fmtQty(q));
            };
            const onTyped = (raw) => {
              const cleaned = raw.replace(',', '.').replace(/[^0-9.]/g, '');
              setServingQtyStr(cleaned);
              const n = parseFloat(cleaned);
              if (Number.isFinite(n) && n > 0) {
                const newG = baseG > 0 ? Math.round(baseG * n * 100) / 100 : n;
                setSelectedServingG(newG);
                setSelectedServingLabel(fmtLabel(n));
              }
            };
            const onBlurInput = () => {
              const n = parseFloat(servingQtyStr.replace(',', '.'));
              if (!Number.isFinite(n) || n <= 0) applyQty(currentQty || 1);
              else setServingQtyStr(fmtQty(n));
            };
            const stepBtn = (label, onClick, ariaLabel) => (
              <button onClick={onClick} aria-label={ariaLabel} style={{flexShrink:0,width:42,height:42,borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:20,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",padding:0,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>{label}</button>
            );
            // +/− always step by exactly 0.5 from the current value.
            // No grid-snap — saved quantities like 0.25 step cleanly to 0.75,
            // not jump to the nearest half-multiple of (q + 0.5).
            const snapDown = (q) => Math.max(0.5, Math.round((q - 0.5) * 100) / 100);
            const snapUp   = (q) => Math.round((q + 0.5) * 100) / 100;
            // Strip any leading "N× " prefix that some API labels bake in (e.g. "2× 2× 1 cup")
            const cleanBaseLabel = baseLabel.replace(/^[\d.]+\s*[×x]\s*/i, '').replace(/^[\d.]+\s*[×x]\s*/i, '').trim() || baseLabel;
            const labelHasUnit = /\d+\s*(g|ml|kg|L)\b/i.test(cleanBaseLabel);
            const showGramHint = baseG > 0 && !labelHasUnit;
            return (
              <>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  {stepBtn("−", ()=>{ try { hapticSelectionChange(); } catch {} applyQty(snapDown(currentQty)); }, "Decrease serving")}
                  <input
                    type="text"
                    inputMode="decimal"
                    enterKeyHint="done"
                    value={servingQtyStr}
                    onChange={e=>onTyped(e.target.value)}
                    onFocus={e=>e.target.select()}
                    onBlur={onBlurInput}
                    onKeyDown={e=>{ if(e.key==='Enter') e.currentTarget.blur(); }}
                    placeholder="1"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    style={{flex:1,minWidth:0,padding:"10px 8px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"22px",fontWeight:800,fontFamily:"inherit",outline:"none",textAlign:"center",fontVariantNumeric:"tabular-nums",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}
                  />
                  {stepBtn("+", ()=>{ try { hapticSelectionChange(); } catch {} applyQty(snapUp(currentQty)); }, "Increase serving")}
                </div>
                <div style={{textAlign:"center",fontSize:"12.5px",color:muted,fontWeight:600,marginBottom:12,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  {currentQty === 1 ? cleanBaseLabel : `${fmtQty(currentQty)}× ${cleanBaseLabel}`}{showGramHint ? ` · ≈ ${Math.round(baseG * currentQty)}g` : ''}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
                  {[0.5, 1, 1.5, 2, 2.5, 3].map(mult => {
                    const active = Math.abs(currentQty - mult) < 0.01;
                    return (
                      <button key={mult} onClick={()=>{try{hapticHayahSelection();}catch{}applyQty(mult);}} style={{padding:"6px 14px",background:active?gold:s2,border:`1px solid ${active?gold:border}`,borderRadius:8,color:active?"#000":text,fontSize:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>{mult}</button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
        <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
          <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>NUTRITION (this serving)</div>
          {(() => {
            const k = Math.round(v(selectedFood.calories));
            const rdi = Math.max(0, Math.round((k / (goals.calories || 2000)) * 100));
            return (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14}}>
                <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                  <div style={{fontSize:"36px",fontWeight:900,color:text,lineHeight:1}}>{k}</div>
                  <div style={{fontSize:"12px",color:gold,fontWeight:700}}>RDI {rdi}%</div>
                </div>
                <div style={{fontSize:"13px",color:muted,fontWeight:600}}>kcal</div>
              </div>
            );
          })()}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
            {[{l:"Protein",val:v(selectedFood.protein),c:gold,unit:"g"},{l:"Carbs",val:v(selectedFood.carbs),c:teal,unit:"g"},{l:"Fat",val:v(selectedFood.fat),c:accent,unit:"g"},{l:"Fiber",val:v(selectedFood.fiber),c:text,unit:"g"},{l:"Sugar",val:v(selectedFood.sugar),c:text,unit:"g"},{l:"Sodium",val:Math.round(v(selectedFood.sodium)),c:text,unit:"mg"}].map(m => (
              <div key={m.l} style={{padding:10,borderRadius:10,background:s2,textAlign:"center"}}>
                <div style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.4px",textTransform:"uppercase"}}>{m.l}</div>
                <div style={{fontSize:"15px",fontWeight:800,color:m.c,marginTop:3}}>{m.val}<span style={{fontSize:"9px",color:muted,fontWeight:600}}>{m.unit}</span></div>
              </div>
            ))}
          </div>
        </div>
        <div style={{padding:"10px 14px",borderRadius:12,background:`${gold}12`,border:`1px solid ${gold}30`,marginBottom:12,textAlign:"center"}}>
          <div style={{fontSize:"15px",color:gold,direction:"rtl",fontWeight:700}}>بِسْمِ اللَّهِ</div>
          <div style={{fontSize:"10.5px",color:muted,fontStyle:"italic",marginTop:2}}>Say Bismillah before eating</div>
        </div>
        {editingEntryId ? (() => {
          const editDt = editingEntryDate || today;
          const returnView = editReturnView || (editDt !== today ? "history" : "log");
          const closeEdit = () => { setEditingEntryId(null); setEditingLoggedAt(null); setEditingEntryDate(null); setEditReturnView(null); setView(returnView); };
          const editTimeVal = editingLoggedAt ? (() => { try { const d=new Date(editingLoggedAt); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; } catch{return '';} })() : '';
          const buildEditLoggedAt = (tv) => {
            if (!tv) return editingLoggedAt || new Date().toISOString();
            try { return new Date(`${editDt}T${tv}:00`).toISOString(); } catch { return editingLoggedAt || new Date().toISOString(); }
          };
          return (
            <>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,padding:'10px 14px',background:s2,borderRadius:12,border:`1px solid ${border}`}}>
                <span style={{fontSize:'9px',color:muted,fontWeight:700,letterSpacing:'0.12em',fontFamily:'monospace',flexShrink:0}}>EATEN AT</span>
                <input type="time" defaultValue={editTimeVal} onChange={e=>setEditingLoggedAt(buildEditLoggedAt(e.target.value))}
                  style={{flex:1,background:'transparent',border:'none',color:text,fontSize:15,fontWeight:700,fontFamily:'inherit',outline:'none',textAlign:'right'}}/>
              </div>
              {/* Meal-slot picker — lets users move a logged item between
                  slots (e.g. accidentally logged a snack as a meal) without
                  having to delete + re-log. Updates selectedMealSlot which
                  commitEditEntry honors. */}
              <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:10}}>
                <span style={{fontSize:'9px',color:muted,fontWeight:700,letterSpacing:'0.12em',fontFamily:'monospace'}}>MEAL SLOT</span>
                <div style={{display:'grid',gridTemplateColumns:`repeat(${MEAL_SLOTS.length}, 1fr)`,gap:6}}>
                  {MEAL_SLOTS.map(slot => {
                    const active = selectedMealSlot === slot.key;
                    return (
                      <button key={slot.key} onClick={()=>{ try { hapticHayahSelection(); } catch {} setSelectedMealSlot(slot.key); }} aria-pressed={active} style={{
                        padding:'10px 6px',borderRadius:10,
                        border:`1px solid ${active ? gold : border}`,
                        background: active ? `${gold}1f` : s2,
                        color: active ? gold : muted,
                        fontSize:11,fontWeight:700,fontFamily:'inherit',cursor:'pointer',
                        display:'flex',flexDirection:'column',alignItems:'center',gap:4,
                        touchAction:'manipulation',WebkitTapHighlightColor:'transparent',
                        transition:'background 0.15s, border-color 0.15s',
                      }}>
                        <span style={{fontSize:14}}>{slot.icon}</span>
                        <span style={{letterSpacing:'-0.01em'}}>{slot.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={()=>{ commitEditEntry(selectedFood, selectedServingG, selectedServingLabel, selectedMealSlot, editingEntryId, editDt, editingLoggedAt); closeEdit(); }}
                style={{width:"100%",padding:14,background:gold,border:"none",borderRadius:14,color:"#000",fontSize:"15px",fontWeight:900,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px"}}
              >
                Save changes
              </button>
              <button
                onClick={closeEdit}
                style={{width:"100%",padding:12,marginTop:8,background:"transparent",border:`1px solid ${border}`,borderRadius:14,color:muted,fontSize:"12.5px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
              >
                Cancel
              </button>
            </>
          );
        })() : (
          <button onClick={()=>{
            const slot = selectedMealSlot;
            addFoodEntry(selectedFood,selectedServingG,selectedServingLabel,slot);
            // Single-item add with custom portion — exit the flow back to the
            // source tab. Batch adds happen via Save (N) on Search.
            if (flowCtx.active) {
              endFlow();
            } else {
              setView("log");
              setExpandedSlot(slot);
            }
            // Don't clear pendingBreakdownReopenRef — VitalsCard's mount
            // effect consumes it. Bump the signal too so a still-mounted
            // VitalsCard catches it on this render.
            if (pendingBreakdownReopenRef.current) setBreakdownReopenSignal(s => s + 1);
          }} style={{width:"100%",padding:14,background:gold,border:"none",borderRadius:14,color:"#000",fontSize:"15px",fontWeight:900,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px"}}>Add to {isFastingToday?MEAL_SLOTS.find(m=>m.key===selectedMealSlot)?.labelFasting:MEAL_SLOTS.find(m=>m.key===selectedMealSlot)?.label}</button>
        )}
      </div>
    );
  };

  const FavoritesView = () => {
    const sorted = [...favorites].sort((a,b)=>(b.usageCount||0)-(a.usageCount||0));
    const presets   = sorted.filter(f=>f.type==="preset");
    const favFoods  = sorted.filter(f=>f.type==="food");
    const savedNames = new Set(favFoods.map(f => (f.name || "").toLowerCase()));
    const frequents = getFrequentFoods(12).filter(food => {
      const fullName = (food.name + (food.brand ? ` (${food.brand})` : "")).toLowerCase();
      return !savedNames.has(fullName) && !savedNames.has((food.name || "").toLowerCase());
    });
    const editBtn = (onClick) => (
      <button onClick={onClick} title="Edit" aria-label="Edit" style={{padding:"7px 9px",background:"transparent",border:`1px solid ${border}`,borderRadius:9,color:gold,fontSize:"13px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
      </button>
    );
    const isEmpty = sorted.length === 0 && frequents.length === 0;
    return (
      <div style={{padding:"12px 0 80px"}}>
        {isEmpty ? (
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{fontSize:"40px",marginBottom:8}}>⭐</div>
            <div style={{fontSize:"14px",color:text,fontWeight:700,marginBottom:4}}>No favorites yet</div>
            <div style={{fontSize:"12px",color:muted}}>Tap the ⭐ on any food to save it here for one-tap logging.</div>
          </div>
        ) : (
          <>
            {presets.length > 0 && (
              <>
                <div style={{padding:"8px 16px 4px",fontSize:"11px",fontWeight:800,color:gold,letterSpacing:"0.6px",textTransform:"uppercase"}}>📋 Meal Presets</div>
                {presets.map(f => (
                  <div key={f.id} style={{margin:"0 16px 8px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:"14px",fontWeight:800,color:text}}>{f.name}</div>
                        <div style={{fontSize:"11px",color:text,marginTop:2}}>{f.foods.length} item{f.foods.length!==1?"s":""} · {Math.round(f.foods.reduce((s,x)=>s+(x.food.calories*(x.servingG/(x.food.servingG||100))),0))} kcal · used {f.usageCount||0}×</div>
                      </div>
                      <button onClick={()=>quickAddFavorite(f, autoMealSlot())} style={{padding:"8px 14px",background:gold,border:"none",borderRadius:10,color:"#000",fontSize:"12px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+ Add</button>
                      {editBtn(()=>openEditFavorite(f))}
                      <button onClick={()=>deleteFavorite(f.id)} style={{padding:"8px 10px",background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:muted,fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>×</button>
                    </div>
                    <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${border}`,fontSize:"11px",color:muted}}>
                      {f.foods.map((x,i)=>(<span key={i}>{x.food.name} ({x.servingLabel}){i<f.foods.length-1?" · ":""}</span>))}
                    </div>
                  </div>
                ))}
              </>
            )}
            {favFoods.length > 0 && (
              <>
                <div style={{padding:"12px 16px 4px",fontSize:"11px",fontWeight:800,color:gold,letterSpacing:"0.6px",textTransform:"uppercase"}}>⭐ Favorite Foods</div>
                {favFoods.map(f => (
                  <div key={f.id} style={{margin:"0 16px 6px",padding:"12px 14px",borderRadius:12,border:`1px solid ${border}`,background:s1,display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"13px",fontWeight:700,color:text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{f.name}</div>
                      <div style={{fontSize:"10.5px",color:text,marginTop:1}}>{f.defaultServingLabel} · {Math.round(f.food.calories*(f.defaultServingG/(f.food.servingG||100)))} kcal · used {f.usageCount||0}×</div>
                    </div>
                    <button onClick={()=>quickAddFavorite(f, autoMealSlot())} style={{padding:"7px 12px",background:gold,border:"none",borderRadius:9,color:"#000",fontSize:"12px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+</button>
                    {editBtn(()=>openEditFavorite(f))}
                    <button onClick={()=>deleteFavorite(f.id)} style={{padding:"7px 9px",background:"transparent",border:`1px solid ${border}`,borderRadius:9,color:muted,fontSize:"12px",cursor:"pointer",fontFamily:"inherit"}}>×</button>
                  </div>
                ))}
              </>
            )}
            {frequents.length > 0 && (
              <>
                <div style={{padding:"12px 16px 4px",fontSize:"11px",fontWeight:800,color:teal,letterSpacing:"0.6px",textTransform:"uppercase"}}>🕒 From Recent Meals</div>
                <div style={{padding:"0 16px 6px",fontSize:"10.5px",color:muted}}>Foods you log often. Tap the pencil to save & rename, or + to log straight to the current meal slot.</div>
                {frequents.map((food, i) => (
                  <div key={`freq-${i}-${food.name}`} style={{margin:"0 16px 6px",padding:"12px 14px",borderRadius:12,border:`1px solid ${border}`,background:s1,display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"13px",fontWeight:700,color:text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{food.name}{food.brand ? ` (${food.brand})` : ""}</div>
                      <div style={{fontSize:"10.5px",color:muted,marginTop:1}}>{food.servingLabel || `${food.servingG||100}g`} · {Math.round(food.calories||0)} kcal</div>
                    </div>
                    <button onClick={()=>addFoodEntry(food, food.servingG||100, food.servingLabel||`${food.servingG||100}g`, autoMealSlot())} style={{padding:"7px 12px",background:teal,border:"none",borderRadius:9,color:"#000",fontSize:"12px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+</button>
                    {editBtn(()=>saveFrequentAsFavorite(food, true))}
                  </div>
                ))}
              </>
            )}
          </>
        )}
        {favSlotPicker && (
          <div onClick={()=>setFavSlotPicker(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:50,display:"flex",alignItems:"flex-end"}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",borderTop:`1px solid ${border}`}}>
              <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:14,textAlign:"center"}}>Add to which meal?</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
                {MEAL_SLOTS.map(slot => (
                  <button key={slot.key} onClick={()=>{const fav = favorites.find(f => f.id === favSlotPicker.favId); if (fav) quickAddFavorite(fav, slot.key);}} style={{padding:"14px 12px",background:s2,border:`1px solid ${border}`,borderRadius:12,color:text,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:"18px"}}>{slot.icon}</span>
                    <span>{isFastingToday?slot.labelFasting:slot.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const HistoryView = () => {
    const monthFirst = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
    const startDow = monthFirst.getDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const dayKcal = (dt) => { const log = calorieLog[dt]; if (!log) return 0; return Object.values(log.meals||{}).flat().reduce((s,e)=>s+(e.calories||0),0); };
    const cellColor = (kcal) => {
      const g = goals.calories || 2000;
      // Minimum floor = BMR from the AI target (Mifflin, no activity multiplier).
      // Falls back to 70% of goal when no AI target has been computed yet.
      const aiRec = loadAITarget();
      const floor = aiRec?._computed?.bmr || Math.round(g * 0.70);
      if (kcal === 0) return "transparent";
      // Under minimum — faint amber (ate too little)
      if (kcal < floor) return darkMode ? "rgba(255,179,0,0.18)" : "rgba(184,112,8,0.13)";
      // Sweet spot: above floor AND under goal — green
      if (kcal < g) return darkMode ? "rgba(0,230,118,0.30)" : "rgba(0,160,80,0.22)";
      // Slightly over goal — amber
      if (kcal < g * 1.15) return darkMode ? "rgba(255,179,0,0.50)" : "rgba(184,112,8,0.38)";
      // Significantly over goal — red
      return darkMode ? "rgba(255,80,80,0.42)" : "rgba(200,30,30,0.24)";
    };
    const sel = calorieLog[historyDate];
    const selKcal = sel ? Object.values(sel.meals||{}).flat().reduce((s,e)=>s+(e.calories||0),0) : 0;
    const monthName = monthFirst.toLocaleString("en-US",{month:"long",year:"numeric"});
    return (
      <div style={{padding:"12px 0 80px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px 12px"}}>
          <button onClick={()=>{ try { hapticHayahSelection(); } catch {} const m=viewMonth-1; if(m<0){setViewMonth(11);setViewYear(viewYear-1);}else setViewMonth(m); }} style={{background:"transparent",border:"none",color:gold,fontSize:"18px",cursor:"pointer",padding:6,fontWeight:700}}>‹</button>
          <div style={{fontSize:"14px",fontWeight:800,color:text}}>{monthName}</div>
          <button onClick={()=>{ try { hapticHayahSelection(); } catch {} const m=viewMonth+1; if(m>11){setViewMonth(0);setViewYear(viewYear+1);}else setViewMonth(m); }} style={{background:"transparent",border:"none",color:gold,fontSize:"18px",cursor:"pointer",padding:6,fontWeight:700}}>›</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,padding:"0 16px 6px"}}>
          {["S","M","T","W","T","F","S"].map((d,i)=>(<div key={i} style={{textAlign:"center",fontSize:"10px",color:muted,fontWeight:700,padding:"4px 0"}}>{d}</div>))}
          {cells.map((d,i) => {
            if (d === null) return <div key={i}/>;
            const dateStr = `${viewYear}-${String(viewMonth+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
            const k = dayKcal(dateStr);
            const isSel = dateStr === historyDate;
            const isToday = dateStr === today;
            return (
              <div key={i} onClick={()=>{try{hapticHayahSelection();}catch{}setHistoryDate(dateStr);}} style={{aspectRatio:"1",borderRadius:8,padding:"4px 2px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",background:cellColor(k),border:isSel?`2px solid ${gold}`:isToday?`1.5px solid ${teal}`:`1px solid transparent`,fontSize:"11px",fontWeight:700,color:text}}>
                <div>{d}</div>
                {k > 0 && <div style={{fontSize:"8px",color:text,fontWeight:600}}>{Math.round(k)}</div>}
              </div>
            );
          })}
        </div>
        {/* Colour legend */}
        <div style={{display:"flex",gap:10,padding:"4px 16px 10px",flexWrap:"wrap"}}>
          {[
            { bg: darkMode?"rgba(0,230,118,0.30)":"rgba(0,160,80,0.22)", label:"Under goal" },
            { bg: darkMode?"rgba(255,179,0,0.50)":"rgba(184,112,8,0.38)", label:"Over goal" },
            { bg: darkMode?"rgba(255,80,80,0.42)":"rgba(200,30,30,0.24)", label:"Significantly over" },
            { bg: darkMode?"rgba(255,179,0,0.18)":"rgba(184,112,8,0.13)", label:"Under minimum" },
          ].map(l => (
            <div key={l.label} style={{display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:10,height:10,borderRadius:3,background:l.bg,flexShrink:0}}/>
              <span style={{fontSize:9.5,color:muted,fontWeight:600}}>{l.label}</span>
            </div>
          ))}
        </div>
        <div style={{margin:"12px 16px 0",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:"13px",fontWeight:800,color:text}}>{historyDate}</div>
              <div style={{fontSize:"11px",color:text}}>{Math.round(selKcal)} kcal · {sel?Object.values(sel.meals||{}).flat().length:0} items</div>
            </div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setHistoryAddPicker(true)} style={{padding:"7px 12px",background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:text,fontSize:"11.5px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>+ Add</button>
              {sel && historyDate !== today && <button onClick={()=>copyDayToToday(historyDate)} style={{padding:"7px 12px",background:gold,border:"none",borderRadius:10,color:"#000",fontSize:"11.5px",fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>📋 Copy to Today</button>}
            </div>
          </div>
          {/* Per-category breakdown: stacked bar + legend. Shows at a glance
              how much proper food / drinks / whey / junk made up the day's
              calories — and gives each section its own subtotal below. */}
          {sel && (() => {
            const slotTotals = MEAL_SLOTS.map(s => {
              const items = sel.meals?.[s.key] || [];
              const total = items.reduce((sum, e) => sum + (e.calories || 0), 0);
              return { ...s, total: Math.round(total) };
            }).filter(s => s.total > 0);
            if (!slotTotals.length) return null;
            const grand = slotTotals.reduce((s, x) => s + x.total, 0) || 1;
            return (
              <div style={{marginBottom:10,paddingTop:8,borderTop:`1px solid ${border}`,marginTop:6}}>
                <div style={{
                  display:'flex',height:12,borderRadius:6,overflow:'hidden',
                  background:'rgba(255,255,255,0.05)',marginBottom:8,
                }}>
                  {slotTotals.map(s => (
                    <div key={s.key} style={{
                      flex:s.total/grand,
                      background:s.color,
                    }} title={`${s.label}: ${s.total} kcal`} />
                  ))}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px 12px',fontSize:'10.5px'}}>
                  {slotTotals.map(s => (
                    <div key={s.key} style={{display:'flex',alignItems:'center',gap:5}}>
                      <div style={{width:8,height:8,borderRadius:8,background:s.color,boxShadow:`0 0 4px ${s.color}66`}} />
                      <span style={{color:text,fontWeight:700}}>{s.label}</span>
                      <span style={{color:muted,fontFamily:'monospace'}}>{s.total}</span>
                      <span style={{color:muted,fontFamily:'monospace',opacity:0.7}}>· {Math.round(100*s.total/grand)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {sel && (() => {
            const dayTotal = Object.values(sel.meals || {}).flat().reduce((s, e) => s + (e.calories || 0), 0) || 1;
            return Object.keys(sel.meals||{}).map(slot => {
            const rawItems = sel.meals?.[slot] || [];
            if (!rawItems.length) return null;
            const meta = MEAL_SLOTS.find(m=>m.key===slot);
            // Sort by loggedAt ascending so the last item is visually at the bottom — makes it easy
            // to see which was the last meal logged (i.e. the fast-start anchor).
            const items = [...rawItems].sort((a,b) => {
              const ta = a.loggedAt ? Date.parse(a.loggedAt) : 0;
              const tb = b.loggedAt ? Date.parse(b.loggedAt) : 0;
              return ta - tb;
            });
            const slotTotal = Math.round(items.reduce((s, e) => s + (e.calories || 0), 0));
            const slotPct = Math.round(100 * slotTotal / dayTotal);
            return (
              <div key={slot} style={{paddingTop:8,borderTop:`1px solid ${border}`,marginTop:8}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                  <div style={{fontSize:"11px",fontWeight:700,color:meta?.color || gold}}>{meta?.icon} {meta?.label}</div>
                  <div style={{fontSize:"10.5px",fontWeight:800,color:meta?.color || gold,fontFamily:'monospace'}}>{slotTotal} kcal <span style={{fontWeight:500,opacity:0.65}}>· {slotPct}%</span></div>
                </div>
                {items.map((e,idx)=>{
                  const logTime = (() => {
                    if (!e.loggedAt) return null;
                    try {
                      const d = new Date(e.loggedAt);
                      const h = d.getHours(), m = d.getMinutes();
                      const ap = h >= 12 ? "PM" : "AM";
                      return `${h % 12 || 12}:${String(m).padStart(2,"0")} ${ap}`;
                    } catch { return null; }
                  })();
                  // Highlight the last item in each slot — likely the fast-anchor meal.
                  const isLast = idx === items.length - 1;
                  return (
                    <div key={e.id} style={{display:"flex",alignItems:"center",fontSize:"12px",color:text,padding:"4px 0",gap:8,touchAction:"manipulation"}}>
                      <div onClick={()=>openEditEntry(slot, e, historyDate)} style={{flex:1,minWidth:0,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                        <span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.name}</span>
                        {logTime && (
                          <span style={{
                            fontSize:"9px",fontFamily:"monospace",whiteSpace:"nowrap",
                            background: isLast ? `${gold}22` : "transparent",
                            color: isLast ? gold : muted,
                            border: isLast ? `1px solid ${gold}55` : "none",
                            borderRadius:4, padding: isLast ? "1px 4px" : "0",
                          }}>
                            {logTime}
                          </span>
                        )}
                        <span style={{fontSize:"10px",color:muted,fontFamily:"monospace",whiteSpace:"nowrap"}}>{e.servingLabel || ""}</span>
                      </div>
                      <span style={{color:text,fontWeight:700,minWidth:38,textAlign:"right"}}>{Math.round(e.calories)}</span>
                      <button onClick={(ev)=>{ev.stopPropagation();removeEntryWithUndo(slot, e, historyDate);}} aria-label={`Delete ${e.name}`} className="ghidha-trash-btn" style={{background:"transparent",border:`1px solid ${border}`,borderRadius:6,color:muted,cursor:"pointer",padding:"3px 7px",fontSize:"13px",lineHeight:1}}>×</button>
                    </div>
                  );
                })}
              </div>
            );
          });
          })()}
          {!sel && <div style={{textAlign:"center",fontSize:"12px",color:muted,padding:"16px 0"}}>No data for this day</div>}
        </div>
      </div>
    );
  };

  const WeeklyReport = () => {
    const days = Array.from({length:reportDays}, (_,i) => localDateStr(-(reportDays-1-i)));
    const dayLabel = (dt) => { const d = new Date(dt + "T00:00:00"); return reportDays <= 7 ? (["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()] + " " + d.getDate()) : String(d.getDate()); };
    const fullDayLabel = (dt) => { const d = new Date(dt + "T00:00:00"); return d.toLocaleDateString(undefined, { weekday:"long", month:"short", day:"numeric" }); };
    // Chart-bar geometry that scales with the chosen day range. The SVG viewBox
    // stays 0..320 wide so we slot every bar inside that fixed canvas — bars
    // get thinner and labels skip more aggressively as the range grows.
    const chartPadX = 20;
    const chartW = 320 - chartPadX * 2;
    const stepW = chartW / reportDays;
    const barW = Math.max(2, stepW * 0.7);
    const xOf = (i) => chartPadX + stepW * i + (stepW - barW) / 2;
    const labelEvery = reportDays <= 7 ? 1 : reportDays <= 14 ? 2 : reportDays <= 30 ? 3 : reportDays <= 60 ? 7 : 10;
    const showLabelAt = (i) => i === 0 || i === reportDays - 1 || i % labelEvery === 0;
    const showValueAt = reportDays <= 7;
    const dayKcal = (dt) => { const log = calorieLog[dt]; if (!log) return 0; return Math.round(Object.values(log.meals||{}).flat().reduce((s,e)=>s+(e.calories||0),0)); };
    const dayMacro = (dt, key) => { const log = calorieLog[dt]; if (!log) return 0; return Math.round(Object.values(log.meals||{}).flat().reduce((s,e)=>s+(e[key]||0),0)); };
    const weekKcal = days.map(dayKcal);
    const weekTotal = weekKcal.reduce((s,v)=>s+v,0);
    const loggedDays = weekKcal.filter(v => v > 0).length;
    const dailyAvg = loggedDays > 0 ? Math.round(weekTotal / loggedDays) : 0;
    const maxBar = Math.max(...weekKcal, goals.calories || 2000) * 1.1;
    const slotKcalWeek = MEAL_SLOTS.map(slot => {
      const total = days.reduce((s,dt)=>{ const log = calorieLog[dt]; if (!log) return s; const items = log.meals?.[slot.key] || []; return s + items.reduce((ss,e)=>ss+(e.calories||0),0); },0);
      return { ...slot, total: Math.round(total) };
    });
    // Foods list scopes to the highlighted day if any, otherwise the full week.
    const scopeDays = tappedDay != null ? [days[tappedDay]] : days;
    const slotFoodCount = {};
    MEAL_SLOTS.forEach(s => { slotFoodCount[s.key] = {}; });
    scopeDays.forEach(dt => {
      const log = calorieLog[dt]; if (!log) return;
      MEAL_SLOTS.forEach(s => {
        (log.meals?.[s.key] || []).forEach(e => {
          const k = e.name;
          if (!slotFoodCount[s.key][k]) slotFoodCount[s.key][k] = { name: e.name, times: 0, kcal: 0 };
          slotFoodCount[s.key][k].times++;
          slotFoodCount[s.key][k].kcal += e.calories || 0;
        });
      });
    });
    const foodsBySlot = MEAL_SLOTS.map(s => ({
      ...s,
      foods: Object.values(slotFoodCount[s.key]).sort((a,b) => b.times - a.times),
    })).filter(s => s.foods.length > 0);
    const foodsTotalKcal = tappedDay != null ? weekKcal[tappedDay] : weekTotal;
    const wkP = days.reduce((s,dt)=>s+dayMacro(dt,"protein"),0);
    const wkC = days.reduce((s,dt)=>s+dayMacro(dt,"carbs"),0);
    const wkF = days.reduce((s,dt)=>s+dayMacro(dt,"fat"),0);
    const wkFi= days.reduce((s,dt)=>s+dayMacro(dt,"fiber"),0);
    const wkS = days.reduce((s,dt)=>s+dayMacro(dt,"sugar"),0);
    const wkSo= days.reduce((s,dt)=>s+dayMacro(dt,"sodium"),0);
    const wkW = days.reduce((s,dt)=>{const l=calorieLog[dt];return s+(l?.water||0);},0);
    const waterDays = days.filter(dt => (calorieLog[dt]?.water || 0) > 0).length;
    const avg = (total) => loggedDays > 0 ? Math.round(total / loggedDays) : 0;
    const avgWater = waterDays > 0 ? Math.round(wkW / waterDays) : 0;
    return (
      <div style={{padding:"4px 0 80px"}}>
        <div style={{display:"flex",alignItems:"center",padding:"6px 16px 8px",gap:6,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
          <span style={{fontSize:"9px",color:muted,fontWeight:800,letterSpacing:"0.6px",marginRight:2,flexShrink:0}}>RANGE</span>
          {REPORT_DAY_OPTIONS.map(n => (
            <button key={n} onClick={()=>{try{hapticHayahSelection();}catch{}setReportDays(n);}} style={{padding:"7px 11px",borderRadius:99,border:`1px solid ${reportDays===n?gold:border}`,background: reportDays===n ? gold : "transparent",color: reportDays===n ? "#000" : muted,fontSize:"11px",fontWeight:800,letterSpacing:"0.3px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>{n}D</button>
          ))}
        </div>
        <div style={{display:"flex",padding:"4px 16px 12px",gap:6}}>
          {[{v:"calories",l:"CALORIES"},{v:"macros",l:"MACROS"},{v:"nutrients",l:"NUTRIENTS"},{v:"junk",l:"JUNK"},{v:"fasts",l:"FASTS"}].map(t=>(
            <button key={t.v} onClick={()=>{try{hapticHayahSelection();}catch{}setReportTab(t.v);}} style={{flex:1,padding:"9px 6px",borderRadius:10,border:"none",background: reportTab===t.v ? gold : s2,color: reportTab===t.v ? "#000" : muted,fontSize:"10px",fontWeight:800,letterSpacing:"0.4px",cursor:"pointer",fontFamily:"inherit"}}>{t.l}</button>
          ))}
        </div>
        {reportTab === "calories" && (
          <>
            <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{fontSize:"13px",fontWeight:700,color:text,marginBottom:2}}>Calories · {reportDays} days</div>
              <div style={{fontSize:"32px",fontWeight:900,color:text,lineHeight:1,fontVariantNumeric:"tabular-nums"}}>
                <AnimatedNumber value={weekTotal} dur={1100} delay={80}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:"11px",color:muted,marginTop:6}}>
                <span>Daily Average: <AnimatedNumber value={dailyAvg} dur={1100} delay={160}/></span>
                <span>Goal: {goals.calories} kcal</span>
              </div>
              {tappedDay != null && (
                <div style={{marginTop:10,padding:"8px 12px",background:s2,border:`0.5px solid ${border}`,borderRadius:8,fontFamily:"monospace",fontSize:11,color:text,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{color:muted,letterSpacing:"0.1em",fontWeight:700}}>{fullDayLabel(days[tappedDay])}</span>
                  <span><span style={{color:gold,fontWeight:800}}>{weekKcal[tappedDay].toLocaleString()}</span> <span style={{color:muted}}>kcal · vs goal {Math.round((weekKcal[tappedDay]/(goals.calories||1))*100)}%</span></span>
                </div>
              )}
              <svg width="100%" height="160" viewBox="0 0 320 160" style={{marginTop:14,display:"block"}}>
                {[0.5,1].map((p,i)=>(<line key={i} x1={chartPadX} y1={130-(p*100)} x2={320-chartPadX} y2={130-(p*100)} stroke={border} strokeWidth="0.5"/>))}
                {(() => { const goalY = 130 - ((goals.calories / maxBar) * 100); return <line x1={chartPadX} y1={goalY} x2={320-chartPadX} y2={goalY} stroke={gold} strokeWidth="1.5" strokeDasharray="5 4"/>; })()}
                {weekKcal.map((k,i)=>(
                  <AnimatedKcalBar
                    key={i}
                    kcal={k}
                    x={xOf(i)}
                    width={barW}
                    hitWidth={stepW}
                    maxBar={maxBar}
                    label={dayLabel(days[i])}
                    goalKcal={goals.calories}
                    colors={{ gold, danger, border, text, muted }}
                    delay={180 + Math.min(i, 14) * 50}
                    tapped={tappedDay === i}
                    showLabel={showLabelAt(i)}
                    showValue={showValueAt}
                    onTap={()=>setTappedDay(prev => prev === i ? null : i)}
                  />
                ))}
              </svg>
            </div>
            <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>MEAL BREAKDOWN</div>
              {slotKcalWeek.map((slot, i) => {
                const pct = weekTotal > 0 ? Math.round((slot.total/weekTotal)*100) : 0;
                const delay = 220 + i * 70;
                return (
                  <div key={slot.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
                      <div style={{width:10,height:10,background:slot.color,borderRadius:2}}/>
                      <div style={{fontSize:"13px",color:slot.color,fontWeight:700}}>{slot.label}</div>
                    </div>
                    <div style={{fontSize:"11px",color:text,fontVariantNumeric:"tabular-nums"}}>(<AnimatedNumber value={pct} delay={delay}/>%)</div>
                    <div style={{fontSize:"13px",color:slot.total>0?slot.color:text,fontWeight:700,minWidth:50,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{slot.total > 0 ? <AnimatedNumber value={slot.total} delay={delay}/> : "—"}</div>
                  </div>
                );
              })}
            </div>
            <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                <div style={{fontSize:"15px",fontWeight:800,color:text}}>Foods Eaten</div>
                <div style={{fontSize:"10px",color:muted,fontFamily:"monospace",letterSpacing:"0.08em",fontWeight:700}}>
                  {tappedDay != null ? fullDayLabel(days[tappedDay]).toUpperCase() : `ALL ${reportDays} DAYS`}
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:"10px",color:muted,fontWeight:700,letterSpacing:"0.4px",padding:"6px 0",borderBottom:`1px solid ${border}`}}>
                <span>FOODS</span>
                <span style={{display:"flex",gap:24}}><span>TIMES</span><span>CALS</span></span>
              </div>
              {foodsBySlot.length === 0 ? (
                <div style={{textAlign:"center",fontSize:"12px",color:muted,padding:"16px 0"}}>—</div>
              ) : foodsBySlot.map((slot, si) => (
                <div key={slot.key}>
                  <div style={{display:"flex",alignItems:"center",gap:5,padding:"9px 0 4px",borderTop: si > 0 ? `1px solid ${border}` : "none"}}>
                    <span style={{fontSize:"12px"}}>{slot.icon}</span>
                    <span style={{fontSize:"10px",fontWeight:700,color:slot.color,letterSpacing:"0.5px"}}>{slot.label.toUpperCase()}</span>
                  </div>
                  {slot.foods.map((f,i) => (
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${border}`,fontSize:"12px"}}>
                      <span style={{flex:1,color:text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",paddingRight:8}}>{f.name}</span>
                      <span style={{display:"flex",gap:24,color:text,fontWeight:700,fontVariantNumeric:"tabular-nums"}}>
                        <span style={{minWidth:30,textAlign:"right"}}><AnimatedNumber value={f.times} delay={200 + i * 40}/>×</span>
                        <span style={{minWidth:40,textAlign:"right"}}><AnimatedNumber value={Math.round(f.kcal)} delay={200 + i * 40}/></span>
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0 0",fontSize:"13px",fontWeight:800,color:text}}>
                <span>Total</span>
                <span style={{fontVariantNumeric:"tabular-nums"}}><AnimatedNumber value={foodsTotalKcal} delay={120}/> kcal</span>
              </div>
            </div>

            {/* ── Day-of-week breakdown + Top junk items (analytics tiles) ── */}
            {(() => {
              // Compute day-of-week averages + top junk items across the
              // currently-selected reportDays window. Pure derivation from
              // calorieLog — no AI call, no token spend, just facts.
              const dowLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
              const days = Array.from({ length: reportDays }, (_, i) => localDateStr(-(reportDays - 1 - i)));
              const dowAgg = {};
              const junkAgg = new Map();
              let _grandJunk = 0;
              for (const d of days) {
                const l = calorieLog[d];
                if (!l || !l.meals) continue;
                const meals = l.meals;
                let dayCal = 0, dayJunk = 0;
                for (const [slot, arr] of Object.entries(meals)) {
                  if (!Array.isArray(arr)) continue;
                  for (const m of arr) {
                    const c = +m?.calories || 0;
                    dayCal += c;
                    if (slot === 'snacks') {
                      dayJunk += c;
                      const k = (m?.name || 'Unknown').trim();
                      const prev = junkAgg.get(k) || { count: 0, totalCal: 0 };
                      junkAgg.set(k, { count: prev.count + 1, totalCal: prev.totalCal + c });
                    }
                  }
                }
                if (dayCal > 0) {
                  const w = dowLabels[new Date(d + 'T12:00:00').getDay()];
                  if (!dowAgg[w]) dowAgg[w] = { count: 0, totalCal: 0, totalJunk: 0 };
                  dowAgg[w].count++;
                  dowAgg[w].totalCal += dayCal;
                  dowAgg[w].totalJunk += dayJunk;
                  _grandJunk += dayJunk;
                }
              }
              const dowRows = dowLabels
                .map(w => dowAgg[w] ? {
                  day: w,
                  daysLogged: dowAgg[w].count,
                  avgCal: Math.round(dowAgg[w].totalCal / dowAgg[w].count),
                  avgJunk: Math.round(dowAgg[w].totalJunk / dowAgg[w].count),
                } : null)
                .filter(Boolean);
              const maxAvgJunk = Math.max(1, ...dowRows.map(r => r.avgJunk));
              const topJunk = [...junkAgg.entries()]
                .map(([name, v]) => ({ name, count: v.count, totalCal: Math.round(v.totalCal), avgCal: Math.round(v.totalCal / v.count) }))
                .sort((a, b) => b.totalCal - a.totalCal)
                .slice(0, 8);
              const SWAP_HINT = (name) => {
                const n = name.toLowerCase();
                if (/milkshake/.test(n))      return 'Try a whey shake — saves ~400 kcal each';
                if (/freezo|frapp|sundae/.test(n)) return 'Plain iced coffee + milk — saves ~250 kcal';
                if (/fries|chips/.test(n) && /small|medium|large|regular/.test(n)) return 'Halve the portion or swap for side salad — saves ~150–400 kcal';
                if (/coke|coca[- ]?cola|fanta|sprite|pepsi|lemonade|cool drink|soft drink|ginger beer/.test(n)) return 'Sparkling water + lemon — saves ~140 kcal';
                if (/(pastille|gum|sour|jelly|candy|haribo|maynard)/.test(n)) return 'Fruit (berries, grapes) — saves ~100 kcal + adds fibre';
                if (/(chocolate|kit ?kat|tex|mars|snickers|bounty|aero|cadbury)/.test(n)) return 'Square of 85% dark chocolate — saves ~150 kcal';
                if (/(ice ?cream|gelato|sundae|sorbet|magnum|cornetto)/.test(n)) return 'Greek yoghurt + frozen berries — saves ~150 kcal + 15g protein';
                if (/(crisp|chip|pringle|simba|niknak|willards|lay)/.test(n)) return 'Roasted chickpeas / biltong — saves ~80 kcal + adds protein';
                if (/(doughnut|donut|muffin|brownie|cupcake|pastry)/.test(n)) return 'Banana + 1 tbsp peanut butter — saves ~200 kcal';
                if (/popcorn/.test(n)) return 'Air-popped is OK in moderation; cap at 30 g';
                return 'Halve the portion next time';
              };
              if (!dowRows.length && !topJunk.length) return null;
              return (
                <>
                  {/* Day-of-week bars */}
                  {dowRows.length > 0 && (
                    <div style={{margin:"18px 16px 0",padding:"14px 14px 12px",background:s1,border:`1px solid ${border}`,borderRadius:12}}>
                      <div style={{fontSize:"10px",fontWeight:800,color:gold,letterSpacing:"0.12em",fontFamily:"monospace",marginBottom:10}}>↻ DAY OF WEEK · JUNK KCAL/DAY</div>
                      <div style={{display:"flex",alignItems:"flex-end",gap:6,height:96,padding:"0 2px"}}>
                        {dowRows.map((r, i) => {
                          const h = Math.max(4, Math.round((r.avgJunk / maxAvgJunk) * 80));
                          const isWorst = r.avgJunk === maxAvgJunk && r.avgJunk > 0;
                          return (
                            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                              <span style={{fontSize:"9.5px",color:isWorst?'#ff8a65':muted,fontVariantNumeric:"tabular-nums",fontWeight:700}}>{r.avgJunk}</span>
                              <div style={{width:"100%",height:h,borderRadius:"4px 4px 2px 2px",background:isWorst?'linear-gradient(180deg,#ff8a65,#c66c4a)':`linear-gradient(180deg,${accent},${accent}88)`}}/>
                              <span style={{fontSize:"9px",color:muted,fontWeight:700,letterSpacing:"0.04em"}}>{r.day.toUpperCase()}</span>
                            </div>
                          );
                        })}
                      </div>
                      {dowRows.length > 0 && (
                        <div style={{marginTop:8,fontSize:"11px",color:muted,lineHeight:1.5,letterSpacing:"-0.005em"}}>
                          {(() => {
                            const worst = dowRows.reduce((b, r) => r.avgJunk > b.avgJunk ? r : b, dowRows[0]);
                            if (worst.avgJunk <= 50) return "No day stands out — junk intake is even across the week.";
                            return `Worst day: ${worst.day} (avg ${worst.avgJunk} kcal junk). Lock that day down first.`;
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Top junk items with substitution coach */}
                  {topJunk.length > 0 && (
                    <div style={{margin:"14px 16px 0",padding:"14px 14px 6px",background:s1,border:`1px solid ${border}`,borderRadius:12}}>
                      <div style={{fontSize:"10px",fontWeight:800,color:gold,letterSpacing:"0.12em",fontFamily:"monospace",marginBottom:10}}>✦ SUBSTITUTION COACH · TOP JUNK</div>
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {topJunk.map((it, i) => (
                          <div key={i} style={{padding:"10px 12px",background:s2,borderRadius:10,border:`1px solid ${border}`}}>
                            <div style={{display:"flex",justifyContent:"space-between",gap:8,marginBottom:4}}>
                              <span style={{fontSize:"13px",fontWeight:700,color:text,lineHeight:1.3,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</span>
                              <span style={{fontSize:"11px",color:muted,fontVariantNumeric:"tabular-nums",fontWeight:700,letterSpacing:"-0.01em",whiteSpace:"nowrap"}}>{it.count}× · {it.totalCal.toLocaleString()} kcal</span>
                            </div>
                            <div style={{fontSize:"11.5px",color:'#ffb84d',lineHeight:1.5,letterSpacing:"-0.005em"}}>→ {SWAP_HINT(it.name)}</div>
                          </div>
                        ))}
                      </div>
                      {/* Counterfactual body-fat calculation. 7,700 kcal ≈ 1 kg
                          of body fat (standard reference). Frames the junk total
                          in terms the user actually cares about: "this is how much
                          fat you didn't lose because of this." */}
                      {(() => {
                        const kgFat = _grandJunk / 7700;
                        if (kgFat < 0.05) return null;
                        // Round to 1 dp for ≥ 0.1, else 2 dp.
                        const kgFmt = kgFat >= 0.1 ? kgFat.toFixed(1) : kgFat.toFixed(2);
                        const annualKgFmt = (kgFat * (365 / reportDays)).toFixed(1);
                        return (
                          <div style={{marginTop:10,padding:"10px 12px",background:`linear-gradient(135deg, rgba(255,138,101,0.12), rgba(255,138,101,0.04))`,border:`1px solid rgba(255,138,101,0.28)`,borderRadius:10}}>
                            <div style={{fontSize:"9px",fontWeight:800,color:'#ff8a65',letterSpacing:"0.14em",fontFamily:"monospace",marginBottom:4}}>↯ COUNTERFACTUAL</div>
                            <div style={{fontSize:"13px",color:text,lineHeight:1.45,letterSpacing:"-0.005em"}}>
                              Had you skipped this junk, you'd have lost an additional <span style={{color:'#ff8a65',fontWeight:700,fontVariantNumeric:"tabular-nums"}}>~{kgFmt} kg</span> of body fat over {reportDays} days.
                            </div>
                            <div style={{fontSize:"11px",color:muted,lineHeight:1.5,marginTop:4,letterSpacing:"-0.005em"}}>
                              At this pace, that's <span style={{color:'#ff8a65',fontWeight:700}}>~{annualKgFmt} kg/year</span>. Math: 7,700 kcal ≈ 1 kg of fat.
                            </div>
                          </div>
                        );
                      })()}
                      <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${border}`,fontSize:"10.5px",color:muted,letterSpacing:"0.04em",textAlign:"right",fontFamily:"monospace"}}>
                        {reportDays}-DAY JUNK TOTAL · <span style={{color:'#ff8a65',fontWeight:700}}>{Math.round(_grandJunk).toLocaleString()} kcal</span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* ── AI Pattern Insights ── */}
            {getGeminiKey() && (() => {
              const TYPE_COLOR = { positive: "#00e676", warning: "#ffb300", neutral: accent };
              const insights = patternInsights?.insights;
              return (
                <div style={{margin:"12px 16px 0"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:"10px",fontWeight:800,color:gold,letterSpacing:"0.12em",fontFamily:"monospace",display:"inline-flex",alignItems:"baseline",gap:8}}>
                      <span>✦ AI PATTERNS · {reportDays} DAYS</span>
                      <AICostBadge feature="patternInsights" prefix="·" fontSize={9} color={muted} />
                    </div>
                    {(insights || patternInsightsError) && !patternInsightsLoading && (
                      <button onClick={() => { setPatternInsightsError(null); generatePatternInsights(); }} style={{background:"transparent",border:"none",color:muted,fontSize:"10px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:"2px 4px",letterSpacing:"0.04em"}}>↻ Refresh</button>
                    )}
                  </div>
                  {!insights && !patternInsightsLoading && !patternInsightsError && (
                    <button onClick={generatePatternInsights} style={{width:"100%",padding:"12px 14px",background:s1,border:`1px dashed ${border}`,borderRadius:12,color:muted,fontSize:"12.5px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",textAlign:"center",letterSpacing:"0.04em"}}>
                      ✦ Discover patterns in your data
                    </button>
                  )}
                  {patternInsightsError && !patternInsightsLoading && (
                    <div style={{padding:"12px 14px",borderRadius:12,border:"1px solid rgba(255,107,107,0.3)",background:"rgba(255,107,107,0.06)"}}>
                      <div style={{fontSize:"11.5px",color:"#ff6b6b",marginBottom:8,lineHeight:1.5}}>{patternInsightsError}</div>
                      <button onClick={() => { setPatternInsightsError(null); generatePatternInsights(); }} style={{background:"transparent",border:"1px solid rgba(255,107,107,0.4)",borderRadius:8,color:"#ff6b6b",fontSize:"11px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:"5px 12px"}}>Try again</button>
                    </div>
                  )}
                  {patternInsightsLoading && (
                    <div style={{padding:"14px",borderRadius:12,background:s1,border:`1px solid ${border}`,textAlign:"center",fontSize:"11px",color:muted,fontFamily:"monospace",letterSpacing:"0.1em",animation:"pulse 1.4s ease-in-out infinite"}}>ANALYSING {reportDays} DAYS…</div>
                  )}
                  {insights && !patternInsightsLoading && (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {insights.map((ins, i) => {
                        const c = TYPE_COLOR[ins.type] || accent;
                        return (
                          <div key={i} style={{padding:"12px 14px",borderRadius:12,background:s1,border:`1px solid ${border}`,borderLeft:`3px solid ${c}`}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                              <span style={{fontSize:18}}>{ins.icon}</span>
                              <span style={{fontSize:"12.5px",fontWeight:800,color:text}}>{ins.title}</span>
                              <span style={{marginLeft:"auto",fontSize:"9px",fontWeight:700,color:c,letterSpacing:"0.08em",fontFamily:"monospace",textTransform:"uppercase",padding:"1px 6px",border:`1px solid ${c}44`,borderRadius:99,background:`${c}10`}}>{ins.type}</span>
                            </div>
                            <div style={{fontSize:"12px",color:muted,lineHeight:1.6}}>{ins.body}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
        {reportTab === "macros" && (
          <>
            <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{fontSize:"13px",fontWeight:700,color:text,marginBottom:8}}>Macronutrients · {reportDays} days</div>
              {tappedDay != null && (() => {
                const dt = days[tappedDay];
                const p = dayMacro(dt,"protein"); const c = dayMacro(dt,"carbs"); const f = dayMacro(dt,"fat");
                return (
                  <div style={{marginBottom:10,padding:"8px 12px",background:s2,border:`0.5px solid ${border}`,borderRadius:8,fontFamily:"monospace",fontSize:11,color:text,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}}>
                    <span style={{color:muted,letterSpacing:"0.1em",fontWeight:700}}>{fullDayLabel(dt)}</span>
                    <span><span style={{color:gold,fontWeight:700}}>P {p}g</span> · <span style={{color:teal,fontWeight:700}}>C {c}g</span> · <span style={{color:accent,fontWeight:700}}>F {f}g</span></span>
                  </div>
                );
              })()}
              <svg width="100%" height="170" viewBox="0 0 320 170" style={{display:"block"}}>
                {(() => {
                  const maxK = Math.max(...days.map(d=>{const pp=dayMacro(d,"protein");const cc=dayMacro(d,"carbs");const ff=dayMacro(d,"fat");return pp*4+cc*4+ff*9;}), goals.calories) * 1.05;
                  return days.map((dt,i)=>(
                    <AnimatedMacroBar
                      key={i}
                      p={dayMacro(dt,"protein")}
                      c={dayMacro(dt,"carbs")}
                      f={dayMacro(dt,"fat")}
                      x={xOf(i)}
                      width={barW}
                      hitWidth={stepW}
                      maxK={maxK}
                      label={dayLabel(dt)}
                      colors={{ gold, teal, accent, text, muted }}
                      delay={180 + Math.min(i, 14) * 50}
                      tapped={tappedDay === i}
                      showLabel={showLabelAt(i)}
                      showValue={showValueAt}
                      onTap={()=>setTappedDay(prev => prev === i ? null : i)}
                    />
                  ));
                })()}
              </svg>
              <div style={{display:"flex",justifyContent:"center",gap:14,marginTop:6,fontSize:"10px",color:muted,fontWeight:700}}>
                <span><span style={{display:"inline-block",width:10,height:10,background:gold,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>Protein</span>
                <span><span style={{display:"inline-block",width:10,height:10,background:teal,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>Carbs</span>
                <span><span style={{display:"inline-block",width:10,height:10,background:accent,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>Fat</span>
              </div>
            </div>
            <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>{reportDays}-DAY AVERAGES vs GOALS</div>
              {[{l:"Protein",val:avg(wkP),goal:goals.protein,c:gold,unit:"g"},{l:"Carbs",val:avg(wkC),goal:goals.carbs,c:teal,unit:"g"},{l:"Fat",val:avg(wkF),goal:goals.fat,c:accent,unit:"g"}].map((m, i) => {
                const delay = 240 + i * 90;
                return (
                  <div key={m.l} style={{padding:"6px 0",borderBottom:`1px solid ${border}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                      <span style={{fontSize:"12px",color:m.c,fontWeight:700}}>{m.l}</span>
                      <span style={{fontSize:"12px",color:text,fontWeight:700,fontVariantNumeric:"tabular-nums"}}><AnimatedNumber value={m.val} delay={delay}/>{m.unit} <span style={{color:muted,fontWeight:600}}>/ {m.goal}{m.unit}</span></span>
                    </div>
                    <AnimatedProgressBar value={m.val} max={m.goal} color={m.c} bg={border} height={5} delay={delay}/>
                  </div>
                );
              })}
            </div>
            {(() => {
              const avgKcal    = avg(wkP) * 4 + avg(wkC) * 4 + avg(wkF) * 9;
              const proteinPct = goals.protein ? Math.round((avg(wkP) / goals.protein) * 100) : 0;
              const carbsPct   = goals.carbs   ? Math.round((avg(wkC) / goals.carbs  ) * 100) : 0;
              const fatPct     = goals.fat     ? Math.round((avg(wkF) / goals.fat    ) * 100) : 0;
              const snap = {
                range_days: reportDays,
                logged_days: loggedDays,
                daily_avg:  { kcal: Math.round(avgKcal), protein_g: avg(wkP), carbs_g: avg(wkC), fat_g: avg(wkF) },
                daily_goal: { kcal: goals.calories,      protein_g: goals.protein, carbs_g: goals.carbs, fat_g: goals.fat },
                adherence_pct: { protein: proteinPct, carbs: carbsPct, fat: fatPct },
              };
              const sources = [
                `Window · ${reportDays}d (${loggedDays} logged)`,
                `Protein · ${avg(wkP)}g / ${goals.protein}g (${proteinPct}%)`,
                `Carbs · ${avg(wkC)}g / ${goals.carbs}g (${carbsPct}%)`,
                `Fat · ${avg(wkF)}g / ${goals.fat}g (${fatPct}%)`,
                `Calories · ${Math.round(avgKcal)} avg / ${goals.calories}`,
              ];
              return (
                <ReportInsightCard
                  kind="macros"
                  rangeDays={reportDays}
                  snapshot={snap}
                  task="Read this macro window and return bullets covering: (1) protein adherence — adequate for lean-mass retention, low, or excessive — with the exact gram and % number; (2) carb / fat split vs targets and what it implies for training fuel or fat loss; (3) the single biggest macro miss (most under or over) and the directional fix; (4) one concrete next-step adjustment (food swap, portion change, or goal recalibration)."
                  sources={sources}
                  colors={{ text, muted, border, teal, danger }}
                />
              );
            })()}
          </>
        )}
        {reportTab === "nutrients" && (
          <>
            <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
              <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>{reportDays}-DAY NUTRIENT AVERAGES</div>
              {[{l:"Fiber",val:avg(wkFi),unit:"g",rec:"25-30g/day",c:accent},{l:"Sugar",val:avg(wkS),unit:"g",rec:"<25g/day",c:gold},{l:"Sodium",val:avg(wkSo),unit:"mg",rec:"<2300mg/day",c:danger}].map((n, i)=>(
                <div key={n.l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${border}`}}>
                  <div>
                    <div style={{fontSize:"13px",color:text,fontWeight:700}}>{n.l}</div>
                    <div style={{fontSize:"10.5px",color:muted}}>Recommended: {n.rec}</div>
                  </div>
                  <div style={{fontSize:"18px",fontWeight:900,color:n.c,fontVariantNumeric:"tabular-nums"}}><AnimatedNumber value={n.val} delay={160 + i * 80}/>{n.unit}</div>
                </div>
              ))}
            </div>
            {(() => {
              const fiberAvg  = avg(wkFi);
              const sugarAvg  = avg(wkS);
              const sodiumAvg = avg(wkSo);
              const snap = {
                range_days: reportDays,
                logged_days: loggedDays,
                daily_avg: { fiber_g: fiberAvg, sugar_g: sugarAvg, sodium_mg: sodiumAvg },
                recommended: { fiber_g: "25-30 g/day", sugar_g: "<25 g/day", sodium_mg: "<2300 mg/day" },
              };
              const sources = [
                `Window · ${reportDays}d (${loggedDays} logged)`,
                `Fiber · ${fiberAvg}g avg (rec 25–30g)`,
                `Sugar · ${sugarAvg}g avg (rec <25g)`,
                `Sodium · ${sodiumAvg}mg avg (rec <2300mg)`,
              ];
              return (
                <ReportInsightCard
                  kind="nutrients"
                  rangeDays={reportDays}
                  snapshot={snap}
                  task="Read this micronutrient window and return bullets covering: (1) fiber adequacy with the exact gram figure vs the 25–30 g/day target; (2) sugar load vs the <25 g/day target and what it implies for fat-loss/insulin; (3) sodium load vs the <2300 mg/day target with hydration/blood-pressure context; (4) one concrete next-step adjustment (specific food add/swap) anchored to the biggest gap."
                  sources={sources}
                  colors={{ text, muted, border, teal, danger }}
                />
              );
            })()}
          </>
        )}

        {reportTab === "junk" && (() => {
          const stats = computeRangeJunkStats(calorieLog, reportDays, DEFAULT_JUNK_THRESHOLD);
          const reportManualFreezes = (() => { try { return JSON.parse(localStorage.getItem("junkFreezesUsed") || "[]"); } catch { return []; } })();
          const streak = computeJunkStreak(calorieLog, DEFAULT_JUNK_THRESHOLD, today, reportManualFreezes);
          const tealColor = teal || "#4FD9C2";
          const dangerC = danger || "#ff6b6b";
          // Donut math — single ring
          const cleanFrac = (() => {
            const totals = stats.byWeek.reduce((s, w) => ({ tot: s.tot + (w.totalKcal || 0), junk: s.junk + (w.junkKcal || 0) }), { tot: 0, junk: 0 });
            const tot = stats.series.reduce((s, d) => s + d.totalKcal, 0);
            const junk = stats.series.reduce((s, d) => s + d.junkKcal, 0);
            if (tot === 0) return 0;
            return (tot - junk) / tot;
          })();
          const cleanPct = Math.round(cleanFrac * 100);
          const junkPct = 100 - cleanPct;
          const R = 56;
          const C = 2 * Math.PI * R;
          const dash = C * cleanFrac;
          // 8w max for trend scaling
          const maxWeekPct = Math.max(40, ...stats.byWeek.map(w => w.pct || 0));
          const wowDelta = stats.wow.delta;
          const wowGood = wowDelta != null && wowDelta < 0;
          const wowBad = wowDelta != null && wowDelta > 0;
          return (
            <>
              {/* Hero — donut + headline number */}
              <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>JUNK · {reportDays} DAYS</div>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <svg width="140" height="140" viewBox="0 0 140 140" style={{flex:"0 0 auto"}}>
                    <circle cx="70" cy="70" r={R} fill="none" stroke={s2} strokeWidth="14"/>
                    <circle
                      cx="70" cy="70" r={R}
                      fill="none"
                      stroke={cleanPct >= 80 ? tealColor : cleanPct >= 60 ? gold : dangerC}
                      strokeWidth="14"
                      strokeDasharray={`${dash} ${C}`}
                      strokeDashoffset={C * 0.25}
                      strokeLinecap="round"
                      transform="rotate(-90 70 70)"
                    />
                    <text x="70" y="68" textAnchor="middle" fontSize="26" fontWeight="900" fill={text} style={{fontFamily:"inherit"}}>{cleanPct}%</text>
                    <text x="70" y="86" textAnchor="middle" fontSize="9" fill={muted} fontWeight="700" letterSpacing="0.12em">CLEAN</text>
                  </svg>
                  <div style={{flex:1,display:"flex",flexDirection:"column",gap:8}}>
                    <div>
                      <div style={{fontSize:11,color:muted,fontWeight:700,letterSpacing:"0.06em"}}>JUNK %</div>
                      <div style={{fontSize:24,fontWeight:900,color:cleanPct >= 80 ? tealColor : cleanPct >= 60 ? gold : dangerC,fontVariantNumeric:"tabular-nums"}}>{junkPct}%</div>
                      <div style={{fontSize:10,color:muted,marginTop:2}}>goal: under {DEFAULT_JUNK_THRESHOLD}%</div>
                    </div>
                    <div style={{display:"flex",gap:10}}>
                      <div>
                        <div style={{fontSize:10,color:muted,fontWeight:700,letterSpacing:"0.06em"}}>CLEAN DAYS</div>
                        <div style={{fontSize:16,fontWeight:800,color:text,fontVariantNumeric:"tabular-nums"}}>{stats.cleanDays}/{stats.loggedDays}</div>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:muted,fontWeight:700,letterSpacing:"0.06em"}}>STREAK</div>
                        <div style={{fontSize:16,fontWeight:800,color:gold,fontVariantNumeric:"tabular-nums"}}>🔥 {streak.current}d</div>
                      </div>
                      <div>
                        <div style={{fontSize:10,color:muted,fontWeight:700,letterSpacing:"0.06em"}}>BEST</div>
                        <div style={{fontSize:16,fontWeight:800,color:text,fontVariantNumeric:"tabular-nums"}}>{streak.best}d</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week-over-week callout */}
              <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.5px"}}>THIS WEEK vs LAST WEEK</div>
                <div style={{display:"flex",alignItems:"baseline",gap:10}}>
                  <span style={{fontSize:28,fontWeight:900,color:wowGood?tealColor:wowBad?dangerC:text,fontVariantNumeric:"tabular-nums"}}>
                    {wowDelta != null ? `${wowDelta > 0 ? "▲" : "▼"} ${Math.abs(wowDelta)} pts` : "—"}
                  </span>
                  <span style={{fontSize:12,color:muted}}>
                    {stats.wow.lastWeek != null ? `${stats.wow.lastWeek}% → ${stats.wow.thisWeek}%` : "log a full week to see the trend"}
                  </span>
                </div>
              </div>

              {/* 8-week trend bars */}
              <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>JUNK % · LAST 8 WEEKS</div>
                <svg width="100%" height="120" viewBox="0 0 320 120" style={{display:"block"}}>
                  {/* goal line at threshold */}
                  {(() => {
                    const yGoal = 100 - (DEFAULT_JUNK_THRESHOLD / maxWeekPct) * 90;
                    return <line x1="14" y1={yGoal} x2="306" y2={yGoal} stroke={tealColor} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.6"/>;
                  })()}
                  {stats.byWeek.map((w, i) => {
                    const x = 14 + i * ((306 - 14) / 8);
                    const bw = ((306 - 14) / 8) - 6;
                    const p = w.pct ?? 0;
                    const h = w.pct == null ? 2 : Math.max(3, (p / maxWeekPct) * 90);
                    const y = 100 - h;
                    const c = w.pct == null ? muted : p < DEFAULT_JUNK_THRESHOLD ? tealColor : p < DEFAULT_JUNK_THRESHOLD * 1.5 ? gold : dangerC;
                    const isThisWeek = i === stats.byWeek.length - 1;
                    return (
                      <g key={w.weekStart}>
                        <rect x={x} y={y} width={bw} height={h} rx="3" fill={c} opacity={isThisWeek ? 1 : 0.75}/>
                        <text x={x + bw/2} y={114} textAnchor="middle" fontSize="9" fill={isThisWeek ? gold : muted} fontWeight="700" style={{fontFamily:"inherit"}}>
                          {isThisWeek ? "now" : `−${7 - i}`}
                        </text>
                        {w.pct != null && (
                          <text x={x + bw/2} y={y - 3} textAnchor="middle" fontSize="9" fill={text} fontWeight="700" style={{fontFamily:"inherit"}}>{Math.round(p)}</text>
                        )}
                      </g>
                    );
                  })}
                </svg>
                <div style={{fontSize:10,color:muted,marginTop:6,textAlign:"center"}}>dashed line = {DEFAULT_JUNK_THRESHOLD}% goal</div>
              </div>

              {/* Day-of-week heatmap */}
              <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>WHICH DAY ARE YOU WEAKEST?</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6}}>
                  {stats.byWeekday.map((d) => {
                    const has = d.avgPct != null;
                    const intensity = has ? Math.min(1, d.avgPct / (DEFAULT_JUNK_THRESHOLD * 2)) : 0;
                    const bg = has
                      ? `rgba(255,107,107,${0.12 + intensity * 0.55})`
                      : s2;
                    return (
                      <div key={d.label} style={{padding:"10px 4px",borderRadius:8,background:bg,textAlign:"center"}}>
                        <div style={{fontSize:9,color:muted,fontWeight:700,letterSpacing:"0.08em"}}>{d.label.toUpperCase()}</div>
                        <div style={{fontSize:15,fontWeight:800,color:has?text:muted,fontVariantNumeric:"tabular-nums",marginTop:2}}>
                          {has ? `${Math.round(d.avgPct)}%` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top offenders */}
              <div style={{margin:"12px 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>TOP JUNK · BY KCAL</div>
                {stats.topByKcal.length === 0 ? (
                  <div style={{fontSize:12,color:muted}}>No junk logged in the last {reportDays} days. 🎉</div>
                ) : stats.topByKcal.map((o, i) => {
                  const maxK = stats.topByKcal[0].kcal || 1;
                  return (
                    <div key={o.name} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom: i < stats.topByKcal.length - 1 ? `1px solid ${border}` : "none"}}>
                      <div style={{fontSize:13,color:text,fontWeight:700,flex:"0 0 50%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.name}</div>
                      <div style={{flex:1,height:6,background:s2,borderRadius:99,overflow:"hidden"}}>
                        <div style={{width:`${(o.kcal/maxK)*100}%`,height:"100%",background:dangerC}}/>
                      </div>
                      <div style={{fontSize:12,color:dangerC,fontWeight:800,minWidth:60,textAlign:"right",fontVariantNumeric:"tabular-nums"}}>{o.kcal} kcal</div>
                    </div>
                  );
                })}
              </div>

              {stats.topByFreq.length > 0 && (
                <div style={{margin:"12px 16px 24px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                  <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>TOP JUNK · BY FREQUENCY</div>
                  {stats.topByFreq.map((o, i) => (
                    <div key={o.name} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom: i < stats.topByFreq.length - 1 ? `1px solid ${border}` : "none"}}>
                      <div style={{fontSize:13,color:text,fontWeight:700}}>{o.name}</div>
                      <div style={{fontSize:12,color:gold,fontWeight:800,fontVariantNumeric:"tabular-nums"}}>{o.count}×</div>
                    </div>
                  ))}
                </div>
              )}
              {(() => {
                const totals = stats.byWeek.reduce((s, w) => ({
                  tot:  s.tot  + (w.totalKcal || 0),
                  junk: s.junk + (w.junkKcal  || 0),
                }), { tot: 0, junk: 0 });
                const junkPct = totals.tot > 0 ? Math.round((totals.junk / totals.tot) * 100) : 0;
                const snap = {
                  range_days: reportDays,
                  threshold_pct: DEFAULT_JUNK_THRESHOLD,
                  junk_share_of_kcal_pct: junkPct,
                  junk_kcal_total: Math.round(totals.junk),
                  total_kcal: Math.round(totals.tot),
                  avg_junk_pct_per_day: Math.round(stats.avgPct || 0),
                  clean_days: stats.cleanDays,
                  logged_days: stats.loggedDays,
                  streak_current: streak.current,
                  streak_best: streak.best,
                  top_by_kcal: (stats.topByKcal || []).slice(0, 5).map(o => ({ name: o.name, kcal: Math.round(o.kcal) })),
                  top_by_frequency: (stats.topByFreq || []).slice(0, 5).map(o => ({ name: o.name, count: o.count })),
                };
                const sources = [
                  `Window · ${reportDays}d (${stats.loggedDays} logged)`,
                  `Junk share · ${junkPct}% of ${Math.round(totals.tot).toLocaleString()} kcal`,
                  `Clean days · ${stats.cleanDays}/${stats.loggedDays}`,
                  `Current streak · ${streak.current} (best ${streak.best})`,
                  ...(snap.top_by_kcal.slice(0, 2).map(o => `Top · ${o.name} (${o.kcal} kcal)`)),
                ];
                return (
                  <ReportInsightCard
                    kind="junk"
                    rangeDays={reportDays}
                    snapshot={snap}
                    task={`Read this junk-food window (threshold ${DEFAULT_JUNK_THRESHOLD}% of daily kcal) and return bullets covering: (1) how bad the overall junk share is and how it compares to the user's clean-day streak; (2) the single biggest repeat offender by kcal or frequency and the impact it's having on calorie/junk budgets; (3) one concrete swap or rule (e.g. replace X with Y, cap Z to once/week) that would have the biggest effect; (4) a recovery target (clean days needed to drop the share below threshold).`}
                    sources={sources}
                    colors={{ text, muted, border, teal, danger }}
                  />
                );
              })()}
            </>
          );
        })()}

        {reportTab === "fasts" && (() => {
          const dangerC = danger || "#ff6b6b";
          const tealColor = teal || "#4FD9C2";
          let fastingPrefs = {};
          let fullPrefs = {};
          try { fullPrefs = JSON.parse(localStorage.getItem("hayahPrefs") || "{}") || {}; fastingPrefs = fullPrefs.fasting || {}; } catch {}
          const overrides = (() => { try { return JSON.parse(localStorage.getItem("fastingOverrides") || "{}"); } catch { return {}; } })();
          const protocol = fastingPrefs.protocol || "16:8";
          const protoCfg = FASTING_PROTOCOLS[protocol] || FASTING_PROTOCOLS["16:8"];
          const goalHours = Number.isFinite(fastingPrefs.goalHours) ? fastingPrefs.goalHours : protoCfg.goalHours;
          const goodHours = Number.isFinite(fastingPrefs.goodHours) ? fastingPrefs.goodHours : protoCfg.goodHours;
          const okHours   = Number.isFinite(fastingPrefs.okHours)   ? fastingPrefs.okHours   : protoCfg.okHours;
          const fastSettings = { tolerance: fastingPrefs.tolerance || 0, allowList: fastingPrefs.allowList || [], bedtimeOffsetMin: fastingPrefs.bedtimeOffsetMin };

          const daily = days.map(d => {
            const w = computeFastingWindow(calorieLog, d, overrides, fastSettings);
            return { date: d, hours: (w && w.hours != null) ? +w.hours.toFixed(2) : null, inProgress: !!(w && w.inProgress), source: w?.source, overridden: !!(w && w.override) };
          });
          const completed = daily.filter(f => f.hours != null && !f.inProgress);
          const hoursList = completed.map(f => f.hours);
          const daysLogged = completed.length;
          const avgH = hoursList.length ? hoursList.reduce((a,b)=>a+b,0)/hoursList.length : 0;
          const maxH = hoursList.length ? Math.max(...hoursList) : 0;
          const minH = hoursList.length ? Math.min(...hoursList) : 0;
          const goalHits = completed.filter(f => f.hours >= goalHours).length;
          const goodHits = completed.filter(f => f.hours >= goodHours).length;
          const okHits   = completed.filter(f => f.hours >= okHours).length;
          const underOk  = completed.filter(f => f.hours < okHours).length;
          const goalRate = daysLogged ? Math.round(goalHits / daysLogged * 100) : 0;
          // current goal-hit streak (most recent backwards)
          let streak = 0;
          for (let i = daily.length - 1; i >= 0; i--) { const f = daily[i]; if (f.hours == null) { if (f.inProgress) continue; break; } if (f.hours >= goalHours) streak++; else break; }
          // deepest-stage distribution across completed fasts
          const stageDist = {};
          for (const f of completed) { const st = stageForHours(f.hours); if (st) stageDist[st.slug] = (stageDist[st.slug] || 0) + 1; }
          // live state
          let currentMeal = null;
          try { currentMeal = computeCurrentMealWindow(calorieLog, today, fullPrefs); } catch {}

          const fmtH = (h) => { if (h == null) return "—"; const hh = Math.floor(h); const mm = Math.round((h - hh) * 60); return mm ? `${hh}h ${mm}m` : `${hh}h`; };
          const barColorFor = (h) => h == null ? border : h >= goalHours ? tealColor : h >= okHours ? gold : dangerC;

          const chartMax = Math.max(goalHours * 1.1, maxH * 1.05, 16);
          const goalY = 150 - (goalHours / chartMax) * 130;

          return (
            <>
              {/* Live fast state */}
              {currentMeal && (
                <div style={{margin:"0 16px 12px",padding:14,borderRadius:14,border:`1px solid ${currentMeal.mode === "fasting" ? "rgba(255,179,0,0.3)" : border}`,background: currentMeal.mode === "fasting" ? "rgba(255,179,0,0.06)" : s1}}>
                  <div style={{fontSize:"10px",color:muted,fontWeight:800,letterSpacing:"0.6px",marginBottom:6}}>RIGHT NOW</div>
                  <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                    <span style={{fontSize:"22px",fontWeight:900,color: currentMeal.mode === "fasting" ? gold : tealColor,fontVariantNumeric:"tabular-nums"}}>
                      {currentMeal.mode === "fasting" ? "🔥 " : "🍽 "}{fmtH(currentMeal.hoursSinceLastMeal)}
                    </span>
                    <span style={{fontSize:"12px",color:text,fontWeight:700}}>
                      {currentMeal.mode === "fasting" ? "fasting" : "since"} {currentMeal.lastMealName ? `· ${currentMeal.lastMealName}` : currentMeal.lastMealSlot ? `· ${currentMeal.lastMealSlot}` : ""}
                    </span>
                  </div>
                  {currentMeal.stage && (
                    <div style={{fontSize:"11px",color:muted,marginTop:5}}>
                      Stage · <span style={{color:text,fontWeight:700}}>{currentMeal.stage.label}</span>
                      {currentMeal.nextStage && currentMeal.hoursToNextStage != null && <> · next: {currentMeal.nextStage.label} in {fmtH(currentMeal.hoursToNextStage)}</>}
                    </div>
                  )}
                </div>
              )}

              {/* Daily fast hours chart */}
              <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"13px",fontWeight:700,color:text,marginBottom:2}}>Fasting hours · {reportDays} days</div>
                <div style={{fontSize:"10.5px",color:muted,marginBottom:8}}>Goal {goalHours}h · {protocol} protocol</div>
                <svg width="100%" height="170" viewBox="0 0 320 170" style={{display:"block"}}>
                  {/* goal line */}
                  <line x1={chartPadX} y1={goalY} x2={320 - chartPadX} y2={goalY} stroke={tealColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.6"/>
                  <text x={320 - chartPadX} y={goalY - 4} textAnchor="end" fontSize="8" fill={tealColor} fontWeight="700">GOAL {goalHours}h</text>
                  {daily.map((f, i) => {
                    const h = f.hours;
                    const bh = h == null ? 0 : Math.max(2, (Math.min(h, chartMax) / chartMax) * 130);
                    const x = xOf(i);
                    const y = 150 - bh;
                    return (
                      <g key={i}>
                        <rect x={x} y={y} width={barW} height={bh} rx={Math.min(2, barW/2)} fill={f.inProgress ? gold : barColorFor(h)} opacity={f.inProgress ? 0.55 : 1}/>
                        {showLabelAt(i) && <text x={x + barW/2} y={164} textAnchor="middle" fontSize="8" fill={muted}>{dayLabel(f.date)}</text>}
                      </g>
                    );
                  })}
                </svg>
                <div style={{display:"flex",justifyContent:"center",gap:14,marginTop:6,fontSize:"9.5px",color:muted,fontWeight:700}}>
                  <span><span style={{display:"inline-block",width:9,height:9,background:tealColor,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>≥ goal</span>
                  <span><span style={{display:"inline-block",width:9,height:9,background:gold,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>≥ ok</span>
                  <span><span style={{display:"inline-block",width:9,height:9,background:dangerC,borderRadius:2,marginRight:4,verticalAlign:"middle"}}/>under</span>
                </div>
              </div>

              {/* Stats */}
              <div style={{margin:"12px 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[
                  { l:"AVG FAST", v: fmtH(avgH), c: text },
                  { l:"LONGEST", v: fmtH(maxH), c: tealColor },
                  { l:"GOAL HIT", v: `${goalRate}%`, sub:`${goalHits}/${daysLogged} days`, c: goalRate >= 70 ? tealColor : goalRate >= 40 ? gold : dangerC },
                  { l:"STREAK", v: `${streak}`, sub: streak === 1 ? "day at goal" : "days at goal", c: streak >= 3 ? tealColor : gold },
                ].map((s, i) => (
                  <div key={s.l} style={{padding:"12px 14px",borderRadius:12,border:`1px solid ${border}`,background:s1}}>
                    <div style={{fontSize:"9.5px",color:muted,fontWeight:800,letterSpacing:"0.5px",marginBottom:4}}>{s.l}</div>
                    <div style={{fontSize:"20px",fontWeight:900,color:s.c,fontVariantNumeric:"tabular-nums"}}>{s.v}</div>
                    {s.sub && <div style={{fontSize:"10px",color:muted,marginTop:2}}>{s.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Tier breakdown */}
              <div style={{margin:"0 16px",padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
                <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>{reportDays}-DAY CONSISTENCY</div>
                {[
                  { l:`Hit goal (≥${goalHours}h)`, n: goalHits, c: tealColor },
                  { l:`Hit good (≥${goodHours}h)`, n: goodHits, c: gold },
                  { l:`Hit ok (≥${okHours}h)`, n: okHits, c: accent },
                  { l:`Under ok (<${okHours}h)`, n: underOk, c: dangerC },
                ].map((r) => {
                  const frac = daysLogged ? r.n / daysLogged : 0;
                  return (
                    <div key={r.l} style={{padding:"6px 0",borderBottom:`1px solid ${border}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                        <span style={{fontSize:"12px",color:text,fontWeight:600}}>{r.l}</span>
                        <span style={{fontSize:"12px",color:r.c,fontWeight:700,fontVariantNumeric:"tabular-nums"}}>{r.n} {r.n === 1 ? "day" : "days"}</span>
                      </div>
                      <div style={{height:5,borderRadius:3,background:border,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(frac*100)}%`,background:r.c,borderRadius:3}}/></div>
                    </div>
                  );
                })}
                {daysLogged === 0 && <div style={{fontSize:"11px",color:muted,textAlign:"center",padding:"10px 0"}}>No completed fasts logged in this window yet.</div>}
              </div>

              {(() => {
                const snap = {
                  range_days: reportDays,
                  protocol,
                  thresholds: { ok_hours: okHours, good_hours: goodHours, goal_hours: goalHours },
                  days_logged: daysLogged,
                  daily_avg_hours: +avgH.toFixed(2),
                  longest_hours: +maxH.toFixed(2),
                  shortest_hours: +minH.toFixed(2),
                  goal_hit_rate_pct: goalRate,
                  current_goal_streak_days: streak,
                  days_hitting: { goal: goalHits, good: goodHits, ok: okHits, under_ok: underOk },
                  deepest_stage_distribution: stageDist,
                  live: currentMeal ? {
                    mode: currentMeal.mode,
                    hours_since_last_meal: +(currentMeal.hoursSinceLastMeal || 0).toFixed(2),
                    last_meal: currentMeal.lastMealName || currentMeal.lastMealSlot || null,
                    stage: currentMeal.stage ? currentMeal.stage.label : null,
                    next_stage: currentMeal.nextStage ? currentMeal.nextStage.label : null,
                  } : null,
                  daily: daily.map(f => ({ date: f.date, hours: f.hours, in_progress: f.inProgress })),
                };
                const sources = [
                  `Window · ${reportDays}d (${daysLogged} fasts logged)`,
                  `Protocol · ${protocol} (goal ${goalHours}h)`,
                  `Avg · ${fmtH(avgH)} · longest ${fmtH(maxH)}`,
                  `Goal-hit · ${goalRate}% (${goalHits}/${daysLogged})`,
                  `Streak · ${streak} day${streak === 1 ? "" : "s"} at goal`,
                  ...(currentMeal ? [`Now · ${currentMeal.mode === "fasting" ? "fasting" : "eating"} ${fmtH(currentMeal.hoursSinceLastMeal)}${currentMeal.stage ? ` (${currentMeal.stage.label})` : ""}`] : []),
                ];
                return (
                  <div style={{marginTop:12}}>
                    <ReportInsightCard
                      kind="fasts"
                      rangeDays={reportDays}
                      snapshot={snap}
                      task={`Read this intermittent-fasting window (${protocol}, goal ${goalHours}h, ok ${okHours}h) and return bullets covering: (1) overall consistency — how the goal-hit rate and streak reflect adherence, with the exact % and day numbers; (2) the spread between longest and shortest fasts and what it implies about routine stability vs erratic eating windows; (3) the metabolic stages typically reached (digesting/glycogen/ketosis/autophagy) given the average duration, and the physiological benefit being captured or missed; (4) one concrete, specific next-step adjustment (shift last-meal time earlier, push the eating window, target an extra stage) anchored to the biggest gap.`}
                      sources={sources}
                      colors={{ text, muted, border, teal, danger }}
                    />
                  </div>
                );
              })()}
            </>
          );
        })()}

        {reportTab === "ai" && (() => {
          const STATUS_COLOR = { excellent:"#00e676", good:"#4FD9C2", warning:"#ffb300", critical:"#ff6b6b" };
          const OVERALL_BG = { green:"rgba(0,230,118,0.06)", amber:"rgba(255,179,0,0.06)", red:"rgba(255,107,107,0.06)" };
          const OVERALL_BORDER = { green:"rgba(0,230,118,0.25)", amber:"rgba(255,179,0,0.25)", red:"rgba(255,107,107,0.25)" };
          const report = aiCoachCache?.report;
          const genAt = aiCoachCache?.generatedAt;
          const fmtAgo = ts => {
            if (!ts) return "";
            const mins = Math.round((Date.now()-ts)/60000);
            if (mins < 2) return "just now";
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins/60);
            if (hrs < 24) return `${hrs}h ago`;
            return `${Math.floor(hrs/24)}d ago`;
          };
          const hasKey = !!getGeminiKey();
          if (!hasKey) return (
            <div style={{margin:"0 16px",padding:20,borderRadius:14,border:`1px solid ${border}`,background:s1,textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:12}}>🔑</div>
              <div style={{fontSize:14,fontWeight:700,color:text,marginBottom:6}}>Gemini API key required</div>
              <div style={{fontSize:12,color:muted,lineHeight:1.6}}>Add your Gemini API key in Hayah Settings to unlock AI coaching.</div>
            </div>
          );
          return (
            <div style={{padding:"0 16px 8px",display:"flex",flexDirection:"column",gap:12}}>
              {/* Hero header */}
              <div style={{padding:"18px 16px",borderRadius:16,background:report ? OVERALL_BG[report.statusColor||"amber"] : s1,border:`1px solid ${report ? OVERALL_BORDER[report.statusColor||"amber"] : border}`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:report?10:0}}>
                  <div style={{fontSize:9,fontWeight:800,color:gold,letterSpacing:"0.14em",fontFamily:"monospace"}}>✦ AI COACH · {reportDays}-DAY ANALYSIS</div>
                  <div style={{display:"inline-flex",alignItems:"baseline",gap:6}}>
                    {genAt && <span style={{fontSize:9,color:muted,fontFamily:"monospace"}}>{fmtAgo(genAt)}</span>}
                    <AICostBadge feature="coachReport" prefix="·" fontSize={9} color={muted} />
                  </div>
                </div>
                {report ? (
                  <div style={{fontSize:15,fontWeight:700,color:text,lineHeight:1.45}}>{report.headline}</div>
                ) : (
                  <div style={{fontSize:13,color:muted,lineHeight:1.6,marginTop:6}}>
                    Your food log, calorie burn, weight trend, and goals are ready. Get a science-backed coaching report with specific actions to hit your targets faster.
                  </div>
                )}
              </div>

              {/* Generate / loading button */}
              {!report && !aiCoachLoading && (
                <button onClick={generateAiCoachReport} style={{width:"100%",padding:16,background:gold,border:"none",borderRadius:14,color:"#000",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit",letterSpacing:"0.3px",boxShadow:`0 6px 20px ${gold}33`}}>
                  Generate My Coaching Report
                </button>
              )}
              {aiCoachLoading && (
                <div style={{padding:20,borderRadius:14,border:`1px solid ${border}`,background:s1,textAlign:"center"}}>
                  <div style={{fontSize:12,color:muted,fontFamily:"monospace",letterSpacing:"0.1em",animation:"pulse 1.4s ease-in-out infinite"}}>ANALYSING {reportDays} DAYS OF DATA…</div>
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} style={{height:56,borderRadius:12,background:s2,margin:"10px 0",opacity:0.5+0.1*(i%2)}}/>
                  ))}
                </div>
              )}
              {aiCoachError && (
                <div style={{padding:"10px 14px",borderRadius:10,border:"1px solid rgba(255,107,107,0.3)",background:"rgba(255,107,107,0.06)",fontSize:12,color:"#ff6b6b"}}>{aiCoachError}</div>
              )}

              {/* Section cards */}
              {report?.sections?.map((sec, i) => {
                const sc = STATUS_COLOR[sec.status] || gold;
                return (
                  <div key={i} style={{borderRadius:14,border:`1px solid ${border}`,background:s1,overflow:"hidden",borderLeft:`3px solid ${sc}`}}>
                    <div style={{padding:"14px 14px 0"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:18}}>{sec.icon}</span>
                          <span style={{fontSize:13,fontWeight:800,color:text,letterSpacing:"-0.01em"}}>{sec.title}</span>
                        </div>
                        <span style={{fontSize:9,fontWeight:700,color:sc,letterSpacing:"0.1em",fontFamily:"monospace",textTransform:"uppercase",padding:"2px 7px",border:`1px solid ${sc}44`,borderRadius:99,background:`${sc}14`}}>{sec.status}</span>
                      </div>
                      <p style={{fontSize:13,color:muted,lineHeight:1.65,margin:"0 0 12px"}}>{sec.body}</p>
                    </div>
                    {sec.actions?.length > 0 && (
                      <div style={{padding:"10px 14px 14px",borderTop:`1px solid ${border}`,background:`${sc}08`}}>
                        {sec.actions.map((a, j) => (
                          <div key={j} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"4px 0"}}>
                            <span style={{fontSize:10,color:sc,fontWeight:900,marginTop:2,flexShrink:0}}>→</span>
                            <span style={{fontSize:12.5,color:text,lineHeight:1.5}}>{a}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Regenerate */}
              {report && !aiCoachLoading && (
                <button onClick={generateAiCoachReport} style={{width:"100%",padding:11,background:"transparent",border:`1px solid ${border}`,borderRadius:12,color:muted,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ↻ Regenerate Report
                </button>
              )}
              <div style={{textAlign:"center",fontSize:10,color:muted,padding:"0 8px 4px",lineHeight:1.5,fontStyle:"italic"}}>
                Analysis uses your last {reportDays} days of logged data. Log consistently for the most accurate coaching.
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const GoalsView = () => {
    const draft = goalsDraft;
    const setDraft = setGoalsDraft;
    const wp = wizardProfile;
    const setWp = (v) => { setWizardProfile(v); localStorage.setItem("ghidhaProfile", JSON.stringify(v)); };
    const wInputStyle = {width:"100%",padding:"8px 10px",borderRadius:9,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontWeight:700,textAlign:"center",fontFamily:"inherit",outline:"none"};
    const chipBase = {padding:"6px 12px",borderRadius:20,fontSize:"11.5px",fontWeight:700,cursor:"pointer",border:"1px solid",fontFamily:"inherit"};
    const ACTIVITIES = [
      { label:"Desk",   val:"1.2"  },
      { label:"Light",  val:"1.375"},
      { label:"Active", val:"1.55" },
      { label:"Sport",  val:"1.725"},
      { label:"Athlete",val:"1.9"  },
    ];
    const GOALS = [
      { label:"Lose −0.5kg/wk",  val:"-500" },
      { label:"Maintain",         val:"0"    },
      { label:"Gain +0.3kg/wk",   val:"300"  },
    ];
    const ACTIVITY_STEPS = { "1.2":5000, "1.375":7500, "1.55":10000, "1.725":12500, "1.9":15000 };
    const ACTIVITY_BURN  = { "1.2":300,  "1.375":500,  "1.55":700,   "1.725":1000,  "1.9":1400  };
    function runWizard() {
      const cals = calcTDEE(wp);
      if (!cals) { showToast("Fill all fields first"); return; }
      const split = MACRO_PRESETS[0];
      const actKey = wp.activity || "1.55";
      setDraft({ ...draft,
        calories: cals,
        protein: Math.round((cals * split.p) / 4),
        carbs:   Math.round((cals * split.c) / 4),
        fat:     Math.round((cals * split.f) / 9),
        steps:   ACTIVITY_STEPS[actKey] ?? 10000,
        burn:    ACTIVITY_BURN[actKey]  ?? 700,
        sleep:   8,
      });
      setWizardOpen(false);
      showToast(`Targets set to ${cals} kcal`);
    }
    // Compare draft to the saved goals so the Save button only "lights up"
    // when there are unsaved changes. Stable JSON stringify is fine here —
    // the goals object is small and a flat record of primitives.
    const isDirty = JSON.stringify(draft) !== JSON.stringify(goalsRaw);
    return (
      <div style={{padding:"16px 16px 80px"}}>

        {/* Body Composition was moved to Exercise → Goals (top of screen).
            It belongs with body-recomp tracking, not calorie targets. */}

        {/* AI Calorie Target — Gemini-powered personalised kcal target. */}
        <AICalorieTarget
          colors={{ text, muted, border, s1, s2, gold, teal, accent, danger, darkMode }}
          calorieLog={calorieLog}
          today={today}
          weightLog={weightLog}
          wizardProfile={wizardProfile}
          goalsRaw={goalsRaw}
          saveGoals={saveGoals}
          showToast={showToast}
          forceExpand={aiTargetForceExpand}
          onConsumeForceExpand={() => setAiTargetForceExpand(false)}
        />

        {/* TDEE Wizard — cold-start only. The AI Calorie Target above supersedes
            this formula calculator, so hide it once a target has been computed.
            Profile stats stay editable in Hayah → Settings → Profile. */}
        {!loadAITarget() && (
        <div style={{borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12,overflow:"hidden"}}>
          <button onClick={()=>setWizardOpen(o=>!o)} style={{width:"100%",padding:"13px 16px",background:"transparent",border:"none",color:text,cursor:"pointer",fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:"13px",fontWeight:800}}>⚡ Calculate my targets</span>
            <span style={{fontSize:"13px",color:muted}}>{wizardOpen ? "▲" : "▼"}</span>
          </button>
          {wizardOpen && (
            <div style={{padding:"0 14px 14px",display:"flex",flexDirection:"column",gap:12}}>
              {/* Sex */}
              <div>
                <div style={{fontSize:"10px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.5px"}}>SEX</div>
                <div style={{display:"flex",gap:8}}>
                  {["M","F"].map(s => (
                    <button key={s} onClick={()=>setWp({...wp,sex:s})} style={{...chipBase,flex:1,background:wp.sex===s?gold:"transparent",color:wp.sex===s?"#000":muted,borderColor:wp.sex===s?gold:border}}>
                      {s === "M" ? "Male" : "Female"}
                    </button>
                  ))}
                </div>
              </div>
              {/* Age / Height / Weight */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[{k:"age",l:"Age (yrs)"},{k:"height",l:"Height (cm)"},{k:"weight",l:"Weight (kg)"}].map(f => (
                  <div key={f.k}>
                    <div style={{fontSize:"10px",color:muted,fontWeight:700,marginBottom:4,letterSpacing:"0.4px",textAlign:"center"}}>{f.l}</div>
                    <input type="number" inputMode="numeric" value={wp[f.k]||""} onChange={e=>setWp({...wp,[f.k]:e.target.value})} style={wInputStyle} placeholder="—"/>
                  </div>
                ))}
              </div>
              {/* Activity */}
              <div>
                <div style={{fontSize:"10px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.5px"}}>ACTIVITY LEVEL</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {ACTIVITIES.map(a => (
                    <button key={a.val} onClick={()=>setWp({...wp,activity:a.val})} style={{...chipBase,background:wp.activity===a.val?teal:"transparent",color:wp.activity===a.val?"#000":muted,borderColor:wp.activity===a.val?teal:border}}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Goal */}
              <div>
                <div style={{fontSize:"10px",color:muted,fontWeight:700,marginBottom:6,letterSpacing:"0.5px"}}>GOAL</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {GOALS.map(g => (
                    <button key={g.val} onClick={()=>setWp({...wp,goal:g.val})} style={{...chipBase,textAlign:"left",background:wp.goal===g.val?accent:"transparent",color:wp.goal===g.val?"#000":muted,borderColor:wp.goal===g.val?accent:border}}>
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Preview + Apply */}
              {calcTDEE(wp) && (
                <div style={{textAlign:"center",fontSize:"12px",color:teal,fontWeight:700,padding:"4px 0"}}>
                  Estimated: {calcTDEE(wp)} kcal/day
                </div>
              )}
              <button onClick={runWizard} style={{width:"100%",padding:12,background:gold,border:"none",borderRadius:12,color:"#000",fontSize:"13px",fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>
                Apply Targets
              </button>
            </div>
          )}
        </div>
        )}

        {/* Goal Mode + manual macro editor — cold-start only. Once the AI
            Calorie Target has a result it owns target + macros + training-day,
            so this manual editor is hidden to avoid two competing controls. */}
        {!loadAITarget() && (
        <MacroEditor
          draft={draft} setDraft={setDraft}
          wp={wp}
          MACRO_PRESETS={MACRO_PRESETS}
          GOAL_MODES={GOAL_MODES}
          calcTDEE={calcTDEE}
          matchedPresetName={matchedPresetName}
          macroPercentsFromDraft={macroPercentsFromDraft}
          theme={{ text, muted, border, gold, teal, accent, danger, s1, s2 }}
          chipBase={chipBase}
        />
        )}

        {/* Other nutrition targets (fiber) */}
        <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1,marginBottom:12}}>
          <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>OTHER NUTRITION TARGETS</div>
          {[{k:"fiber",l:"Fiber",unit:"g"}].map(g => {
            const raw = draft[g.k];
            const display = raw === "" || raw == null ? "" : String(raw);
            return (
              <div key={g.k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${border}`}}>
                <span style={{fontSize:"13px",color:text,fontWeight:700}}>{g.l}</span>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={display}
                    onFocus={e=>e.target.select()}
                    onChange={e=>{
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      if (v === "") { setDraft({...draft, [g.k]: ""}); return; }
                      const n = parseInt(v, 10);
                      if (!Number.isNaN(n)) setDraft({...draft, [g.k]: n});
                    }}
                    onBlur={()=>{
                      if (draft[g.k] === "" || draft[g.k] == null) setDraft({...draft, [g.k]: DEFAULT_GOALS[g.k] || 0});
                    }}
                    style={{width:80,padding:"7px 10px",borderRadius:9,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontWeight:700,textAlign:"right",fontFamily:"inherit",outline:"none",touchAction:"manipulation"}}
                  />
                  <span style={{fontSize:"11px",color:muted,minWidth:32}}>{g.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Activity & sleep targets */}
        <div style={{padding:14,borderRadius:14,border:`1px solid ${border}`,background:s1}}>
          <div style={{fontSize:"11px",color:muted,fontWeight:700,marginBottom:10,letterSpacing:"0.5px"}}>ACTIVITY & SLEEP TARGETS</div>
          {[
            {k:"steps", l:"Daily Steps",        unit:"steps", inputMode:"numeric", decimal:false},
            {k:"burn",  l:"Active Burn Goal",   unit:"kcal",  inputMode:"numeric", decimal:false},
            {k:"sleep", l:"Sleep Goal",         unit:"hours", inputMode:"decimal", decimal:true},
          ].map(g => {
            const raw = draft[g.k] ?? DEFAULT_GOALS[g.k];
            const display = raw === "" || raw == null ? "" : String(raw);
            return (
              <div key={g.k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${border}`}}>
                <span style={{fontSize:"13px",color:text,fontWeight:700}}>{g.l}</span>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <input
                    type="text"
                    inputMode={g.inputMode}
                    value={display}
                    onFocus={e=>e.target.select()}
                    onChange={e=>{
                      const cleaned = g.decimal ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value.replace(/[^0-9]/g, "");
                      if (cleaned === "") { setDraft({...draft, [g.k]: ""}); return; }
                      const n = g.decimal ? parseFloat(cleaned) : parseInt(cleaned, 10);
                      if (!Number.isNaN(n)) setDraft({...draft, [g.k]: n});
                    }}
                    onBlur={()=>{
                      if (draft[g.k] === "" || draft[g.k] == null) setDraft({...draft, [g.k]: DEFAULT_GOALS[g.k] || 0});
                    }}
                    style={{width:80,padding:"7px 10px",borderRadius:9,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontWeight:700,textAlign:"right",fontFamily:"inherit",outline:"none",touchAction:"manipulation"}}
                  />
                  <span style={{fontSize:"11px",color:muted,minWidth:32}}>{g.unit}</span>
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={()=>{
            if (!isDirty) return;
            saveGoals(draft);
            try { hapticConfirm(); } catch {}
            showToast("Goals saved ✓");
          }}
          disabled={!isDirty}
          style={{
            width:"100%", padding:14, marginTop:14,
            background: isDirty ? gold : "transparent",
            border: isDirty ? "none" : `1px solid ${border}`,
            borderRadius:14,
            color: isDirty ? "#000" : muted,
            fontSize:"14px", fontWeight:900,
            cursor: isDirty ? "pointer" : "default",
            fontFamily:"inherit",
            transition: "background 0.18s, color 0.18s, border-color 0.18s",
            opacity: isDirty ? 1 : 0.85,
          }}>
          {isDirty ? "Save Goals" : "✓ All changes saved"}
        </button>
        <button onClick={()=>{try{hapticWarning();}catch{}setDraft(DEFAULT_GOALS);}} style={{width:"100%",padding:12,marginTop:8,background:"transparent",border:`1px solid ${border}`,borderRadius:14,color:muted,fontSize:"12px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Reset to Defaults</button>
        <div style={{textAlign:"center",fontSize:"10.5px",color:muted,marginTop:18,fontStyle:"italic",lineHeight:1.5,padding:"0 8px"}}>
          Prophet ﷺ said: "A son of Adam fills no vessel worse than his stomach. A few morsels are sufficient. If he must, then a third for food, a third for drink, and a third for breath."
          <div style={{marginTop:4,fontWeight:700,opacity:0.7}}>— Tirmidhi</div>
        </div>
      </div>
    );
  };

  function openShareCard() {
    const d = new Date();
    const dateStr = d.toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric' });
    const estimatedBMR = calcBMR(wizardProfile);
    const canvas = drawGhidhaSummaryCard({
      date: dateStr,
      calories: Math.round(totalCalories),
      calorieGoal: goals.calories,
      protein: Math.round(totalProtein),
      carbs: Math.round(totalCarbs),
      fat: Math.round(totalFat),
      proteinGoal: goals.protein,
      carbsGoal: goals.carbs,
      fatGoal: goals.fat,
      steps: todayData.steps || 0,
      activeBurned: caloriesBurned,
      basalBurned: todayData.basalBurned > 0 ? todayData.basalBurned : (estimatedBMR || 0),
      sleepMins: todayData.sleepMinutes || 0,
      deepMins: todayData.deepMinutes || 0,
      remMins: todayData.remMinutes || 0,
      water,
      waterGoal: goals.water,
      sharedBy: '',
    });
    setGhidhaShareImg(canvas.toDataURL('image/png'));
    canvas.toBlob(b => setGhidhaShareBlob(b), 'image/png');
    setGhidhaShareOpen(true);
  }
  async function doShare() {
    if (!ghidhaShareBlob) return;
    const file = new File([ghidhaShareBlob], 'ghidha-summary.png', { type:'image/png' });
    if (navigator.canShare?.({ files:[file] })) {
      try { await navigator.share({ files:[file] }); } catch {}
    } else {
      const url = URL.createObjectURL(ghidhaShareBlob);
      const a = document.createElement('a');
      a.href = url; a.download = 'ghidha-summary.png'; a.click();
      URL.revokeObjectURL(url);
      showToast('Image saved — attach in WhatsApp');
    }
  }

  return (
    <div ref={containerRef} className="ghidha-root" style={{display:"flex",flexDirection:"column",height:"100%",background:darkMode?"#07090c":"#f5f1ea",overflowX:"hidden",overflowY: view==="dashboard" ? "hidden" : "auto",color:text}}>
      <style>{`.ghidha-no-sb::-webkit-scrollbar{display:none}.ghidha-no-sb{scrollbar-width:none}.ghidha-root button,.ghidha-root [role=button],.ghidha-root [data-tap]{-webkit-tap-highlight-color:transparent}.ghidha-trash-btn{transition:color 0.15s ease,border-color 0.15s ease}@media(hover:hover){.ghidha-trash-btn:hover{color:#ff6b6b!important;border-color:rgba(255,107,107,0.4)!important}}.ghidha-trash-btn:active{color:#ff6b6b!important;border-color:rgba(255,107,107,0.4)!important}.ghidha-press{transition:transform 0.12s cubic-bezier(0.22,0.61,0.36,1)}.ghidha-press:active{transform:scale(0.985)}.ghidha-tile{transition:transform 0.12s cubic-bezier(0.22,0.61,0.36,1),box-shadow 0.18s ease}.ghidha-tile:active{transform:scale(0.97)}.ghidha-slot-expand{display:grid;grid-template-rows:0fr;transition:grid-template-rows 0.28s cubic-bezier(0.22,0.61,0.36,1)}.ghidha-slot-expand>div{overflow:hidden;min-height:0}.ghidha-slot-expand.open{grid-template-rows:1fr}.ghidha-slot-expand.open>div{animation:ghidha-slot-fade 0.32s cubic-bezier(0.22,0.61,0.36,1) both}@keyframes ghidha-slot-fade{0%{opacity:0;transform:translateY(-2px)}100%{opacity:1;transform:translateY(0)}}@keyframes ghidha-slot-flash{0%{box-shadow:0 0 0 0 rgba(0,201,167,0)}30%{box-shadow:0 0 0 3px rgba(0,201,167,0.28),0 0 18px rgba(0,201,167,0.22)}100%{box-shadow:0 0 0 0 rgba(0,201,167,0)}}.ghidha-slot-flash{animation:ghidha-slot-flash 1.2s cubic-bezier(0.22,0.61,0.36,1)}.ghidha-chev{transition:transform 0.25s cubic-bezier(0.22,0.61,0.36,1)}@media(prefers-reduced-motion:reduce){.ghidha-press,.ghidha-tile,.ghidha-slot-expand,.ghidha-chev{transition:none}.ghidha-slot-flash,.ghidha-slot-expand.open>div{animation:none}}`}</style>
      {!(isLandscape && hostedInHayah && view === "dashboard") && Header()}
      {view === "dashboard" && Dashboard()}
      {view === "log" && LogView()}
      {view === "search" && SearchView()}
      {view === "barcode" && BarcodeView()}
      {view === "food-detail" && FoodDetail()}
      {view === "favorites" && FavoritesView()}
      {view === "history" && HistoryView()}
      {view === "weekly" && WeeklyReport()}
      {view === "goals" && GoalsView()}
      {view === "plan" && (
        <MealPlanView
          colors={{ text, muted, border, s1, s2, gold, teal, accent, danger, darkMode }}
          calorieLog={calorieLog}
          calorieGoals={goalsRaw}
          wizardProfile={wizardProfile}
          showToast={showToast}
        />
      )}
      <ProteinFocusOverlay
        open={proteinFocusOpen}
        onClose={() => {
          setProteinFocusOpen(false);
          if (wasInitialProteinRef.current) {
            wasInitialProteinRef.current = false;
            if (onInitialMetricClosed) onInitialMetricClosed();
          }
        }}
        totalProtein={totalProtein}
        proteinGoal={goals.protein}
        todayData={todayData}
        wheyProducts={PROTEIN_FOCUS_WHEY_PRODUCTS}
        milkProducts={PROTEIN_FOCUS_MILK_PRODUCTS}
        addFoodEntry={addFoodEntry}
        today={today}
        onAfterLog={() => { setProteinFocusOpen(false); setView("log"); }}
      />
      <CappuccinoFocusOverlay
        open={cappuccinoFocusOpen}
        onClose={() => setCappuccinoFocusOpen(false)}
        milkProducts={CAPPUCCINO_MILK_PRODUCTS}
        addFoodEntry={addFoodEntry}
        today={today}
        onAfterLog={() => { setCappuccinoFocusOpen(false); try { showToast && showToast("☕ Cappuccino logged"); } catch {} }}
      />
      {Modals()}
      <StreakInfoSheet
        open={logStreakInfoOpen}
        onClose={() => setLogStreakInfoOpen(false)}
        title="Logging streak"
        accent={gold}
        lines={[
          `Counts consecutive days you've logged at least one meal AND stayed within your calorie cap.`,
          `🔥 ${intel.streak} day${intel.streak === 1 ? '' : 's'} = your last ${intel.streak} day${intel.streak === 1 ? '' : 's'} hit both bars.`,
          { label: 'Today', value: intel.streak > 0 ? 'counts (you\'ve logged something)' : 'log a meal to extend' },
          { label: 'Calorie cap', value: goals.calories ? `${goals.calories.toLocaleString()} kcal` : 'not set' },
          `An empty day or a day over the cap breaks the streak — but an empty morning today won't reset yesterday's run.`,
        ]}
      />
      {toast &&<div role="status" aria-live="polite" aria-atomic="true" style={{position:"fixed",bottom:"calc(110px + env(safe-area-inset-bottom))",left:"50%",transform:"translateX(-50%)",background:darkMode?"#1a1a1a":"#fff",color:text,padding:"10px 18px",borderRadius:24,fontSize:"12.5px",fontWeight:700,boxShadow:"0 4px 20px rgba(0,0,0,0.4)",border:`1px solid ${border}`,zIndex:60,whiteSpace:"nowrap"}}>{toast}</div>}
      {flowCtx.active && view === "search" && Object.keys(searchSelections).length > 0 && (
        <div style={{position:"fixed", bottom: kbOpen ? "8px" : `calc(110px + env(safe-area-inset-bottom, 0px) + var(--nav-inset, 0px))`, left:"50%", transform:"translateX(-50%)", display:"flex", alignItems:"center", gap:10, padding:"10px 14px 10px 18px", background:darkMode?"#1a1a1a":"#fff", color:text, borderRadius:24, fontSize:"13px", fontWeight:700, boxShadow:"0 8px 28px rgba(0,0,0,0.5)", border:`1px solid ${border}`, zIndex:55, maxWidth:"calc(100% - 24px)", whiteSpace:"nowrap", transition:"bottom 0.2s ease"}}>
          <span>{Object.keys(searchSelections).length} item{Object.keys(searchSelections).length===1?"":"s"} selected</span>
          {Object.keys(searchSelections).length >= 2 && (
            <button onClick={()=>setSaveMealSheet({ name: "" })} style={{background:"transparent",border:`1px solid ${gold}`,color:gold,fontWeight:800,cursor:"pointer",fontSize:"11.5px",fontFamily:"inherit",letterSpacing:"0.06em",padding:"5px 10px",borderRadius:14,touchAction:"manipulation",WebkitTapHighlightColor:"transparent",whiteSpace:"nowrap"}}>⭐ Save as meal</button>
          )}
          <button onClick={clearAllSelections} style={{background:"transparent",border:"none",color:gold,fontWeight:900,cursor:"pointer",fontSize:"12px",fontFamily:"inherit",letterSpacing:"0.12em",padding:"4px 8px",touchAction:"manipulation",WebkitTapHighlightColor:"transparent",whiteSpace:"nowrap"}}>UNSELECT ALL</button>
        </div>
      )}
      {saveMealSheet && (
        <div onClick={()=>setSaveMealSheet(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:70,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px calc(24px + env(safe-area-inset-bottom))",borderTop:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:4,textAlign:"center"}}>Save this meal</div>
            <div style={{fontSize:"11px",color:muted,marginBottom:14,textAlign:"center"}}>Add to Quick Add for one-tap re-logging</div>
            <div className="ghidha-no-sb" style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:12,marginBottom:8}}>
              {Object.values(searchSelections).map((sel, i) => (
                <div key={i} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",borderRadius:999,background:darkMode?"rgba(0,201,167,0.10)":"rgba(0,158,135,0.08)",border:`1px solid ${darkMode?"rgba(0,201,167,0.30)":"rgba(0,158,135,0.25)"}`,whiteSpace:"nowrap",flexShrink:0,fontSize:"11px",color:text,fontWeight:600}}>
                  <span>{sel.food?.name || "—"}</span>
                  <span style={{fontFamily:"monospace",fontSize:"10px",color:muted}}>{sel.servingLabel}</span>
                </div>
              ))}
            </div>
            <input
              type="text"
              value={saveMealSheet.name}
              onChange={e=>setSaveMealSheet({...saveMealSheet, name: e.target.value})}
              onFocus={e=>e.target.select()}
              placeholder="Name this meal (e.g. Pre-workout shake)"
              autoFocus
              style={{width:"100%",padding:"11px 12px",borderRadius:10,border:`1px solid ${border}`,background:s2,color:text,fontSize:"13px",fontFamily:"inherit",boxSizing:"border-box",outline:"none",marginBottom:12}}
            />
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{try{hapticConfirm();}catch{}saveSelectionsAsPreset(saveMealSheet.name, false);}} disabled={!saveMealSheet.name.trim()} style={{flex:1,padding:"12px 10px",background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:saveMealSheet.name.trim()?text:muted,fontSize:"13px",fontWeight:700,cursor:saveMealSheet.name.trim()?"pointer":"default",fontFamily:"inherit",opacity:saveMealSheet.name.trim()?1:0.5}}>Save only</button>
              <button onClick={()=>{try{hapticConfirm();}catch{}saveSelectionsAsPreset(saveMealSheet.name, true);}} disabled={!saveMealSheet.name.trim()} style={{flex:1,padding:"12px 10px",background:saveMealSheet.name.trim()?gold:s2,border:"none",borderRadius:10,color:saveMealSheet.name.trim()?"#000":muted,fontSize:"13px",fontWeight:900,cursor:saveMealSheet.name.trim()?"pointer":"default",fontFamily:"inherit",opacity:saveMealSheet.name.trim()?1:0.5}}>Save &amp; Log</button>
            </div>
            <button onClick={()=>setSaveMealSheet(null)} style={{width:"100%",marginTop:8,padding:"10px",background:"transparent",border:"none",color:muted,fontSize:"12px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}
      {historyAddPicker && (
        <div onClick={()=>setHistoryAddPicker(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:55,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",borderTop:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:4,textAlign:"center"}}>Add to which meal?</div>
            <div style={{fontSize:"11px",color:muted,marginBottom:14,textAlign:"center",fontFamily:"monospace"}}>{historyDate}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
              {MEAL_SLOTS.map(slot => (
                <button key={slot.key} onClick={()=>{ setHistoryAddPicker(false); startFlow({ slot: slot.key, date: historyDate }); }} style={{padding:"14px 12px",background:s2,border:`1px solid ${border}`,borderRadius:12,color:text,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:"18px"}}>{slot.glyph}</span>
                  <span>{isFastingToday?slot.labelFasting:slot.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {searchSlotPickerOpen && (
        <div onClick={()=>setSearchSlotPickerOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:55,display:"flex",alignItems:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",background:s1,borderRadius:"20px 20px 0 0",padding:"20px 16px 32px",borderTop:`1px solid ${border}`}}>
            <div style={{fontSize:"15px",fontWeight:800,color:text,marginBottom:14,textAlign:"center"}}>Add to which meal?</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
              {MEAL_SLOTS.map(slot => {
                const active = selectedMealSlot === slot.key;
                return (
                  <button key={slot.key} onClick={()=>{ try { hapticHayahSelection(); } catch {} setSelectedMealSlot(slot.key); setSearchSlotPickerOpen(false); }} style={{padding:"14px 12px",background: active ? gold : s2, border:`1px solid ${active ? gold : border}`,borderRadius:12,color: active ? "#000" : text,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8,touchAction:"manipulation",WebkitTapHighlightColor:"transparent"}}>
                    <span style={{fontSize:"18px"}}>{slot.glyph}</span>
                    <span>{isFastingToday?slot.labelFasting:slot.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {undoToast && (
        <div style={{position:"fixed",bottom:96,left:"50%",transform:"translateX(-50%)",display:"flex",alignItems:"center",gap:14,padding:"10px 14px 10px 18px",background:darkMode?"#1a1a1a":"#fff",color:text,borderRadius:24,fontSize:"13px",fontWeight:700,boxShadow:"0 8px 28px rgba(0,0,0,0.5)",border:`1px solid ${border}`,zIndex:600,maxWidth:"calc(100% - 24px)"}}>
          <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:200}}>Removed <span style={{opacity:0.7}}>{undoToast.entry.name}</span></span>
          <button
            onClick={()=>{ restoreEntry(undoToast.slot, undoToast.entry, undoToast.date); setUndoToast(null); }}
            style={{background:"transparent",border:"none",color:gold,fontWeight:900,cursor:"pointer",fontSize:"12px",fontFamily:"inherit",letterSpacing:"0.12em",padding:"4px 8px",touchAction:"manipulation"}}
          >UNDO</button>
        </div>
      )}
      {ghidhaShareOpen && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:200,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{fontSize:"11px",fontWeight:700,color:muted,letterSpacing:"0.8px",marginBottom:14}}>TODAY'S SUMMARY</div>
          {ghidhaShareImg
            ? <img src={ghidhaShareImg} style={{width:"100%",maxWidth:340,borderRadius:16,marginBottom:20,boxShadow:"0 8px 32px rgba(0,0,0,0.6)"}} alt="Ghidha summary card"/>
            : <div style={{width:320,height:320,background:s1,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",color:muted,fontSize:"12px",marginBottom:20}}>Generating…</div>
          }
          <div style={{display:"flex",gap:10}}>
            <button onClick={doShare} disabled={!ghidhaShareBlob} style={{padding:"12px 28px",background:teal,border:"none",borderRadius:12,color:"#000",fontSize:"14px",fontWeight:800,cursor:"pointer",fontFamily:"inherit",opacity:ghidhaShareBlob?1:0.5}}>Share ↗</button>
            <button onClick={()=>setGhidhaShareOpen(false)} style={{padding:"12px 20px",background:"transparent",border:`1px solid ${border}`,borderRadius:12,color:muted,fontSize:"13px",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
